import { StrictMode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, FormEvent, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  ArrowUpRight,
  CalendarDays,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Download,
  Eye,
  Filter,
  Globe2,
  LayoutDashboard,
  Lock,
  LockKeyhole,
  Menu,
  MoreHorizontal,
  Mail,
  Plus,
  Puzzle,
  RefreshCw,
  Search,
  Settings2,
  Share2,
  ShieldCheck,
  Sparkles,
  UserPlus,
  UserRound,
  Users,
  Video,
  X
} from "lucide-react";
import {
  DEFAULT_CONFIRMATION_WINDOW_HOURS,
  calendarConnections,
  fredrick,
  meetingTypes,
  personas
} from "@scheduler/core";
import type { Booking, BookingGuestInput, CalendarConnection, CalendarResource, DateOverride, Host, Invitation, MeetingType, Persona, Provider, Role, TimeInterval } from "@scheduler/core";
import "./styles.css";

const appOrigin = (import.meta.env.VITE_PUBLIC_APP_URL ?? "https://trueavail.app").replace(/\/$/, "");
const shortLinkOrigin = (import.meta.env.VITE_SHORT_LINK_URL ?? "https://tavl.app").replace(/\/$/, "");
const cognitoRegion = "us-east-1";
const cognitoClientId = "5qim01ikf84c6e1kcogg8end4e";
const authStorageKey = "scheduler.auth";
// Stamped from extension/manifest.json at build time by extensionBundle.ts.
declare const __EXTENSION_VERSION__: string;
const SESSION_EXPIRED_EVENT = "timebridge:session-expired";

/**
 * Fetch wrapper for authenticated admin endpoints. A 401 means the Cognito session
 * is gone, so the workspace signs out and returns to sign-in rather than surfacing a
 * raw status code from inside whatever form the host happened to be filling in.
 * 403 is deliberately NOT treated this way: it means signed in but not permitted,
 * which is a real answer the caller should show.
 */
async function apiFetch(input: string, init?: globalThis.RequestInit): Promise<globalThis.Response> {
  const response = await fetch(input, init);
  if (response.status === 401) {
    globalThis.dispatchEvent(new globalThis.CustomEvent(SESSION_EXPIRED_EVENT));
  }
  return response;
}
const authStorageVersion = 2;
type View = "overview" | "profile" | "personas" | "persona-create" | "persona-edit" | "calendars" | "connect" | "calendar-manage" | "bookings" | "booking-detail" | "book" | "availability" | "links" | "extension" | "onboarding" | "users" | "host-create" | "operations";
type CalendarManageMode = "details" | "edit" | "reconnect";
type LoadState = "loading" | "live" | "error";

/**
 * One day of the host's real schedule: bookings taken through TrueAvail merged with
 * the events already sitting on every connected calendar. Mirrors the payload of
 * GET /v1/admin/schedule.
 */
interface ScheduleEventView {
  id: string;
  source: "timebridge" | "calendar";
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  detail: string;
  personaId?: string;
  personaName?: string;
  personaColor?: string;
  bookingId?: string;
  status?: string;
  provider?: Provider;
  connectionId?: string;
  calendarLabel?: string;
  transparency?: string;
  joinUrl?: string;
}

interface ScheduleSourceView {
  connectionId: string;
  provider: Provider;
  label: string;
  calendars: string[];
  status: CalendarConnection["status"];
}

interface ScheduleResponse {
  date: string;
  timeZone: string;
  range: { start: string; end: string };
  events: ScheduleEventView[];
  sources: ScheduleSourceView[];
  failedSources: Array<{ connectionId: string; label: string; reason: string }>;
  summary: {
    total: number;
    timebridge: number;
    imported: number;
    busyMinutes: number;
    openMinutes: number;
    /** Absent when nothing bookable is left in the day. */
    nextOpen?: { start: string; end: string };
  };
}
type BookingState =
  | { status: "idle" }
  | { status: "submitting" }
  | { status: "confirmed"; booking: Booking; manageLinks?: { cancel: string; reschedule: string } }
  | { status: "error"; message: string };

interface PublicPersonaResponse {
  host: { slug: string; displayName: string; avatarUrl?: string };
  persona: Persona;
  meetingTypes: MeetingType[];
}

interface BookingResponse {
  booking: Booking;
  manageLinks?: {
    cancel: string;
    reschedule: string;
  };
}

interface RescheduleLookupResponse {
  booking: Booking;
  context?: {
    host: { slug: string; displayName: string };
    persona: { slug: string; displayName: string };
    meetingType: { slug: string; title: string; durationMinutes: number; horizonDays: number };
  };
}

type OnboardingStatus = "idle" | "submitting" | "done" | "error";
type HostOnboardingState = "needs_host" | "needs_persona" | "needs_calendar" | "needs_publish" | "complete";

interface HostSignupResponse {
  host: Host;
  identity: {
    provider: "cognito" | "local";
    userId: string;
    delivery: "email" | "suppressed";
  };
}

interface InvitationPreviewResponse {
  invitation: Invitation;
  signupEnabled: boolean;
}

interface PersonaSetupResponse {
  persona: Persona;
  meetingType: MeetingType;
}

interface PersonaUpdateResponse {
  persona: Persona;
}

interface MeetingTypeUpdateResponse {
  meetingType: MeetingType;
}


interface AdminInvitationsResponse {
  invitations: Invitation[];
}

interface AdminInvitationCreateResponse {
  invitation: Invitation;
  inviteUrl: string;
}

interface HostUpdateResponse {
  host: Host;
}

interface AdminFeatureFlags {
  ALLOW_PUBLIC_HOST_SIGNUP: boolean;
  ALLOW_ADMIN_INVITES: boolean;
  ENABLE_GOOGLE_CALENDAR: boolean;
  ENABLE_MICROSOFT_CALENDAR: boolean;
  ENABLE_ZOHO_CALENDAR: boolean;
  ENABLE_CALDAV_CALENDAR: boolean;
}

interface AdminStatusResponse {
  featureFlags: AdminFeatureFlags;
  hosts: Host[];
  personas: Persona[];
  meetingTypes: MeetingType[];
  calendarConnections: CalendarConnection[];
  calendarResources: CalendarResource[];
}

interface AdminBookingsResponse {
  bookings: Booking[];
  hosts: Array<{ id: string; slug: string; displayName: string }>;
  range: TimeInterval;
}

interface AdminOverviewResponse {
  range: TimeInterval;
  metrics: {
    todayBookings: number;
    upcomingBookings: number;
    bookingsThisMonth: number;
    hoursBookedThisMonth: number;
    activePersonas: number;
    meetingTypes: number;
    calendarConnections: number;
  };
  setup: {
    percent: number;
    items: Array<{ key: string; label: string; done: boolean }>;
  };
  agenda: Booking[];
  /** Today as one day: bookings merged with every connected calendar's events. */
  today?: ScheduleResponse;
  /** Somewhere to point on a quiet day. */
  nextBooking?: Booking;
  personaPerformance: Array<{
    personaId: string;
    meetingTypeCount: number;
    bookingsThisMonth: number;
  }>;
}

interface AdminOperationsResponse {
  checkedAt: string;
  metrics: {
    activeHosts: number;
    pendingInvites: number;
    bookingsLast30Days: number;
    bookingChangeLabel: string;
    successfulSyncPercent: number;
    connectionCount: number;
  };
  providerHealth: Array<{
    key: string;
    label: string;
    status: string;
    tone: "green" | "orange" | "neutral";
    successRate: string;
  }>;
  bookingVolume: Array<{
    date: string;
    label: string;
    count: number;
    heightPercent: number;
  }>;
  attentionItems: Array<{
    id: string;
    severity: "Low" | "Medium" | "High";
    title: string;
    detail: string;
    action: string;
  }>;
}

type ManagementRoute =
  | { kind: "cancel" | "reschedule"; token: string }
  /** `preselect` is which button the host pressed in the email, not a decision. */
  | { kind: "approve"; token: string; preselect: "approve" | "decline" }
  | null;
type InviteClaimRoute = { token: string } | null;
type PublicBookingRoute = { hostSlug: string; personaSlug: string; meetingSlug?: string } | null;

type GuestActionState =
  | { status: "idle"; booking?: Booking }
  | { status: "loading"; booking?: Booking }
  | { status: "submitting"; booking?: Booking }
  | { status: "done"; booking: Booking; previousInterval?: TimeInterval }
  | { status: "error"; message: string; booking?: Booking };

type AuthState =
  | { status: "signed_out"; message?: string }
  | { status: "signing_in" }
  | { status: "new_password_required"; email: string; session: string }
  | { status: "signed_in"; email: string; idToken: string; accessToken?: string; roles: string[]; hostId?: string; version?: number }
  | { status: "error"; message: string };

function Root() {
  const managementRoute = readManagementRoute();
  const inviteClaimRoute = readInviteClaimRoute();
  const publicRoute = readPublicBookingRoute();
  if (managementRoute?.kind === "cancel") {
    return <GuestCancelView token={managementRoute.token} />;
  }
  if (managementRoute?.kind === "approve") {
    return <HostApprovalView token={managementRoute.token} preselect={managementRoute.preselect} />;
  }
  if (managementRoute?.kind === "reschedule") {
    return <GuestRescheduleView token={managementRoute.token} />;
  }
  if (inviteClaimRoute) {
    return <InviteSignupScreen token={inviteClaimRoute.token} />;
  }
  if (publicRoute) {
    return <PublicBookingView route={publicRoute} />;
  }
  return <App />;
}

function PublicBookingView({ route }: { route: Exclude<PublicBookingRoute, null> }) {
  const [step, setStep] = useState<"date" | "time" | "details" | "confirmed">("date");
  const [selectedDate, setSelectedDate] = useState(() => initialDateKey());
  const [selectedSlot, setSelectedSlot] = useState<TimeInterval | null>(null);
  const [bookingState, setBookingState] = useState<BookingState>({ status: "idle" });
  const [publicState, setPublicState] = useState<LoadState>("loading");
  const [slotState, setSlotState] = useState<LoadState>("loading");
  const [persona, setPersona] = useState<Persona | null>(null);
  const [host, setHost] = useState<PublicPersonaResponse["host"] | null>(null);
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [slots, setSlots] = useState<TimeInterval[]>([]);

  const meeting = (route.meetingSlug ? meetingTypes.find((item) => item.slug === route.meetingSlug) : undefined) ?? meetingTypes[0];
  const availableDates = useMemo(() => new Set(slots.map((slot) => dateKey(slot.start))), [slots]);
  const daySlots = slots.filter((slot) => dateKey(slot.start) === selectedDate);

  useEffect(() => {
    const controller = new globalThis.AbortController();
    setPublicState("loading");
    fetch(`/v1/public/hosts/${route.hostSlug}/personas/${route.personaSlug}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`public_metadata_${response.status}`);
        }
        return (await response.json()) as PublicPersonaResponse;
      })
      .then((body) => {
        setHost(body.host);
        setPersona(body.persona);
        setMeetingTypes(body.meetingTypes);
        setPublicState("live");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setPublicState("error");
        }
      });
    return () => controller.abort();
  }, [route.hostSlug, route.personaSlug]);

  useEffect(() => {
    if (!meeting) {
      setSlotState(publicState === "loading" ? "loading" : "error");
      return;
    }
    const controller = new globalThis.AbortController();
    const startDate = new Date();
    startDate.setUTCHours(0, 0, 0, 0);
    const endDate = new Date(startDate);
    endDate.setUTCDate(endDate.getUTCDate() + meeting.horizonDays);
    setSlotState("loading");
    setSlots([]);
    fetch(
      `/v1/public/hosts/${route.hostSlug}/personas/${route.personaSlug}/${meeting.slug}/slots?start=${encodeURIComponent(startDate.toISOString())}&end=${encodeURIComponent(endDate.toISOString())}`,
      { signal: controller.signal }
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`public_slots_${response.status}`);
        }
        return (await response.json()) as { slots: TimeInterval[] };
      })
      .then((body) => {
        setSlots(body.slots);
        if (body.slots.length > 0 && !body.slots.some((slot) => dateKey(slot.start) === selectedDate)) {
          setSelectedDate(dateKey(body.slots[0]!.start));
        }
        setSlotState("live");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setSlotState("error");
        }
      });
    return () => controller.abort();
  }, [meeting, publicState, route.hostSlug, route.personaSlug]);

  async function submitBooking(guest: BookingGuestInput) {
    if (!selectedSlot || !meeting) {
      return;
    }
    setBookingState({ status: "submitting" });
    try {
      const response = await fetch("/v1/public/bookings", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-idempotency-key": globalThis.crypto.randomUUID()
        },
        body: JSON.stringify({
          hostSlug: route.hostSlug,
          personaSlug: route.personaSlug,
          meetingSlug: meeting.slug,
          start: selectedSlot.start,
          end: selectedSlot.end,
          guest
        })
      });
      const body = (await response.json().catch(() => ({}))) as Partial<BookingResponse> & { error?: { message?: string; code?: string } };
      if (!response.ok || !body.booking) {
        throw new Error(body.error?.message ?? body.error?.code ?? `booking_failed_${response.status}`);
      }
      setBookingState({
        status: "confirmed",
        booking: body.booking,
        ...(body.manageLinks ? { manageLinks: body.manageLinks } : {})
      });
      setStep("confirmed");
      setSlots((current) => current.filter((slot) => slot.start !== selectedSlot.start));
    } catch (cause) {
      setBookingState({ status: "error", message: cause instanceof Error ? cause.message : "Booking failed." });
    }
  }

  const selectedDateHasTimes = daySlots.length > 0;
  const location = persona ? locationLabel(persona) : "Video meeting";
  /** This persona holds requests for the host to answer rather than booking them. */
  const reviewsRequests = persona?.requiresConfirmation === true;
  const firstName = persona?.displayName.split(" ")[0] ?? host?.displayName.split(" ")[0] ?? "your host";

  return (
    <main className="public-booking-page">
      <nav className="public-booking-nav" aria-label="Booking page">
        <a href={`/${route.hostSlug}/${route.personaSlug}`}>
          <Avatar className="public-nav-avatar" src={persona?.logoUrl ?? persona?.avatarUrl} initials={personaInitials(persona ?? undefined)} />
          <span>
            <strong>{persona?.name ?? "TrueAvail"}</strong>
            <small>with {host?.displayName ?? firstName}</small>
          </span>
        </a>
        <div>
          <button type="button" className="public-timezone">
            <Globe2 size={15} /> {viewerTimeZoneLabel}
          </button>
          <a className="public-close" href="/">
            <X size={18} />
            <span>Close</span>
          </a>
        </div>
      </nav>

      <section className="public-booking-card">
        <aside className="public-brand-panel">
          <span className="public-host-avatar-wrap">
            <Avatar
              className="public-host-avatar"
              src={persona?.logoUrl ?? persona?.avatarUrl ?? host?.avatarUrl}
              initials={hostInitials(host?.displayName ?? persona?.displayName)}
            />
            <i />
          </span>
          <span className="public-persona-pill">{persona?.name ?? route.personaSlug}</span>
          <h1>
            {step === "confirmed"
              ? reviewsRequests
                ? "Request sent"
                : "You're booked!"
              : meeting?.title ?? "Loading meeting"}
          </h1>
          <p>
            {step === "confirmed"
              ? reviewsRequests
                ? "You will hear back once it has been reviewed."
                : "A calendar invitation is on its way."
              : meeting?.description || persona?.headline || "Loading availability."}
          </p>
          {meeting && step !== "confirmed" ? (
            <div className="public-facts">
              <span><Clock size={16} /> {meeting.durationMinutes} minutes</span>
              <span><Video size={16} /> {location}</span>
              <span><Globe2 size={16} /> {viewerTimeZoneLabel}</span>
            </div>
          ) : null}
          <div className="public-private-note">
            <ShieldCheck size={17} />
            <span>
              <strong>Private scheduling</strong>
              <small>Your information is only used for this meeting.</small>
            </span>
          </div>
        </aside>

        <section className="public-booker">
          {publicState === "error" ? <p className="form-error">This booking link is not available.</p> : null}
          {/* The guest's first wait: the page before the times. */}
          {!meeting && publicState !== "error" ? (
            <SkeletonRegion label="Loading booking page" className="skeleton-public-booker">
              <Skeleton className="skeleton-line-sm" width="140px" />
              <Skeleton className="skeleton-line-lg" width="min(300px, 70%)" />
              <Skeleton height="292px" radius="16px" />
              <Skeleton height="48px" radius="12px" />
            </SkeletonRegion>
          ) : null}
          {meeting && step === "date" ? (
            <>
              <PublicBackButton href={`/${route.hostSlug}/${route.personaSlug}`} label="Booking page" />
              <PublicStepTitle step="Step 1 of 3" title="Choose a date" detail={`Times shown in ${viewerTimeZoneLabel}`} />
              <PublicMonthCalendar selectedDate={selectedDate} availableDates={availableDates} loading={slotState === "loading"} onSelect={(date) => {
                setSelectedDate(date);
                setSelectedSlot(null);
                setBookingState({ status: "idle" });
              }} />
              <button className="public-primary" type="button" disabled={!selectedDateHasTimes || slotState === "loading"} onClick={() => setStep("time")}>
                {slotState === "loading" ? "Loading available times" : "View available times"} <ArrowRight size={16} />
              </button>
            </>
          ) : null}
          {meeting && step === "time" ? (
            <>
              <PublicBackButton label="Back to calendar" onClick={() => setStep("date")} />
              <PublicStepTitle step="Step 2 of 3" title={formatFullDate(selectedDate)} detail={`Select a ${meeting.durationMinutes}-minute time`} />
              <PublicDateStrip selectedDate={selectedDate} availableDates={availableDates} loading={slotState === "loading"} onSelect={(date) => {
                setSelectedDate(date);
                setSelectedSlot(null);
                setBookingState({ status: "idle" });
              }} />
              <h3 className="public-time-group">Available times</h3>
              <PublicTimeSlots
                slots={daySlots}
                selectedSlot={selectedSlot}
                loading={slotState === "loading"}
                emptyMessage={slotState === "loading" ? "Loading availability." : slotState === "error" ? "Availability could not be loaded." : "No times remain for this day."}
                onSelect={(slot) => {
                  setSelectedSlot(slot);
                  setBookingState({ status: "idle" });
                }}
              />
              <button className="public-primary" type="button" disabled={!selectedSlot} onClick={() => setStep("details")}>
                Continue{selectedSlot ? ` with ${formatTime(selectedSlot.start)}` : ""} <ArrowRight size={16} />
              </button>
            </>
          ) : null}
          {meeting && step === "details" ? (
            <>
              <PublicBackButton label="Choose another time" onClick={() => setStep("time")} />
              <PublicStepTitle step="Step 3 of 3" title="Tell me about you" />
              {selectedSlot ? (
                <div className="public-summary">
                  <CalendarCheck size={19} />
                  <span>
                    <strong>{formatFullDate(dateKey(selectedSlot.start))} at {formatTime(selectedSlot.start)}</strong>
                    <small>{meeting.durationMinutes} minutes · {location}</small>
                  </span>
                </div>
              ) : null}
              <form className="public-intake" onSubmit={(event) => {
                event.preventDefault();
                const form = new globalThis.FormData(event.currentTarget);
                submitBooking({
                  name: String(form.get("name") ?? ""),
                  email: String(form.get("email") ?? ""),
                  organization: String(form.get("organization") ?? ""),
                  reason: String(form.get("reason") ?? ""),
                  consentAccepted: true
                });
              }}>
                <label>Your name<input name="name" required placeholder="Full name" /></label>
                <label>Email address<input name="email" required type="email" placeholder="you@example.com" /></label>
                <label>Organization <em>Optional</em><input name="organization" placeholder="Organization or team" /></label>
                <label>What would you like to discuss? <em>Optional</em><textarea name="reason" rows={4} maxLength={500} placeholder="A little context helps us make the most of our time." /></label>
                <button className="public-primary" disabled={!selectedSlot || bookingState.status === "submitting"}>
                  {bookingState.status === "submitting"
                    ? reviewsRequests
                      ? "Sending request"
                      : "Confirming booking"
                    : reviewsRequests
                      ? "Request this time"
                      : "Confirm booking"}{" "}
                  <CalendarCheck size={16} />
                </button>
              </form>
              {/* Saying this after the fact would be a surprise; the guest should know
                  before they fill the form in that the time is not theirs yet. */}
              <p className="public-fine-print">
                {reviewsRequests
                  ? "This time is reviewed before it is confirmed. It will be held for you in the meantime, and you will hear either way."
                  : "By booking, you agree to receive emails about this meeting."}
              </p>
              {bookingState.status === "error" ? <p className="form-error" aria-live="polite">{bookingState.message}</p> : null}
            </>
          ) : null}
          {bookingState.status === "confirmed" && step === "confirmed" ? (
            <PublicBookingSuccess bookingState={bookingState} location={location} />
          ) : null}
        </section>
      </section>

      <footer className="public-powered">Powered by <strong>TrueAvail</strong> · Secure scheduling</footer>
    </main>
  );
}

function PublicBackButton({ href, label, onClick }: { href?: string; label: string; onClick?: () => void }) {
  if (href) {
    return <a className="public-back" href={href}><ChevronLeft size={15} /> {label}</a>;
  }
  return <button className="public-back" type="button" onClick={onClick}><ChevronLeft size={15} /> {label}</button>;
}

function PublicStepTitle({ step, title, detail }: { step: string; title: string; detail?: string | undefined }) {
  return (
    <div className="public-step-title">
      <span>{step}</span>
      <h2>{title}</h2>
      {detail ? <p>{detail}</p> : null}
    </div>
  );
}

function PublicMonthCalendar({
  selectedDate,
  availableDates,
  loading,
  onSelect
}: {
  selectedDate: string;
  availableDates: Set<string>;
  loading?: boolean;
  onSelect: (date: string) => void;
}) {
  const [visibleMonth, setVisibleMonth] = useState(() => selectedDate.slice(0, 7));

  useEffect(() => {
    setVisibleMonth(selectedDate.slice(0, 7));
  }, [selectedDate]);

  const monthStart = new Date(`${visibleMonth}-01T00:00:00.000Z`);
  const gridStart = new Date(monthStart);
  gridStart.setUTCDate(monthStart.getUTCDate() - monthStart.getUTCDay());
  const days = Array.from({ length: 35 }, (_, index) => {
    const day = new Date(gridStart);
    day.setUTCDate(gridStart.getUTCDate() + index);
    return day;
  });
  const monthLabel = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric", timeZone: "UTC" }).format(monthStart);

  return (
    <section className="public-calendar" aria-label="Choose a date">
      <div className="public-monthbar">
        <button type="button" aria-label="Previous month" onClick={() => setVisibleMonth(shiftMonth(visibleMonth, -1))}>
          <ChevronLeft size={18} />
        </button>
        <strong>{monthLabel}</strong>
        <button type="button" aria-label="Next month" onClick={() => setVisibleMonth(shiftMonth(visibleMonth, 1))}>
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="public-weekdays" aria-hidden="true">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => <span key={day}>{day}</span>)}
      </div>
      {loading ? (
        <SkeletonRegion label="Loading available dates" className="public-days">
          <SkeletonRows count={35}>
            {(index) => <Skeleton key={index} className="skeleton-day-cell" height="40px" radius="10px" />}
          </SkeletonRows>
        </SkeletonRegion>
      ) : (
      <div className="public-days">
        {days.map((day) => {
          const key = day.toISOString().slice(0, 10);
          const available = availableDates.has(key);
          const inVisibleMonth = key.startsWith(visibleMonth);
          return (
            <button
              key={key}
              type="button"
              disabled={!available || !inVisibleMonth}
              className={`${selectedDate === key ? "selected " : ""}${available && inVisibleMonth ? "available" : ""}${inVisibleMonth ? "" : " outside"}`}
              onClick={() => onSelect(key)}
              aria-label={`${formatFullDate(key)}${available && inVisibleMonth ? ", times available" : ", no times available"}`}
            >
              {day.getUTCDate()}
            </button>
          );
        })}
      </div>
      )}
    </section>
  );
}

function PublicDateStrip({
  selectedDate,
  availableDates,
  loading,
  onSelect
}: {
  selectedDate: string;
  availableDates: Set<string>;
  loading?: boolean;
  onSelect: (date: string) => void;
}) {
  const dates = [...availableDates].sort().slice(0, 7);
  if (loading && dates.length === 0) {
    return (
      <SkeletonRegion label="Loading available dates" className="public-date-strip">
        <SkeletonRows count={7}>
          {(index) => <Skeleton key={index} className="skeleton-date-pill" height="58px" radius="12px" />}
        </SkeletonRows>
      </SkeletonRegion>
    );
  }
  return (
    <div className="public-date-strip">
      {dates.map((date) => {
        const day = new Date(`${date}T12:00:00.000Z`);
        return (
          <button key={date} type="button" className={date === selectedDate ? "selected" : ""} onClick={() => onSelect(date)}>
            <span>{new Intl.DateTimeFormat(undefined, { weekday: "short", timeZone: "UTC" }).format(day)}</span>
            <strong>{day.getUTCDate()}</strong>
          </button>
        );
      })}
    </div>
  );
}

function PublicTimeSlots({
  slots,
  selectedSlot,
  emptyMessage,
  loading,
  onSelect
}: {
  slots: TimeInterval[];
  selectedSlot: TimeInterval | null;
  emptyMessage: string;
  loading?: boolean;
  onSelect: (slot: TimeInterval) => void;
}) {
  if (loading) {
    return (
      <SkeletonRegion label="Loading available times" className="public-times">
        <SkeletonRows count={6}>
          {(index) => <Skeleton key={index} className="skeleton-time-slot" height="46px" radius="12px" />}
        </SkeletonRows>
      </SkeletonRegion>
    );
  }
  return (
    <div className="public-times">
      {slots.length > 0 ? slots.map((slot) => (
        <button key={slot.start} type="button" className={selectedSlot?.start === slot.start ? "selected" : ""} onClick={() => onSelect(slot)}>
          {formatTime(slot.start)}
          {selectedSlot?.start === slot.start ? <Check size={15} /> : null}
        </button>
      )) : <p className="empty-state">{emptyMessage}</p>}
    </div>
  );
}

function PublicBookingSuccess({ bookingState, location }: { bookingState: Extract<BookingState, { status: "confirmed" }>; location: string }) {
  // A persona that reviews requests has not agreed to anything yet, and this screen
  // must not imply otherwise: no confirmation badge, no invitation, and nothing to
  // add to a calendar until the host answers.
  const pending = bookingState.booking.status === "pending_approval";
  return (
    <div className={pending ? "public-success pending" : "public-success"} aria-live="polite">
      <i>{pending ? <Clock size={30} /> : <Check size={30} />}</i>
      <span className="public-success-badge">{pending ? "Requested" : "Confirmed"}</span>
      <h2>{formatFullDate(dateKey(bookingState.booking.interval.start))}</h2>
      <strong>{formatTime(bookingState.booking.interval.start)} - {formatTime(bookingState.booking.interval.end)} {viewerTimeZoneLabel}</strong>
      <div>
        {pending ? (
          <>
            <p>
              <Clock size={18} />
              <span>
                <strong>Waiting on the host</strong>
                <small>
                  {bookingState.booking.approvalExpiresAt
                    ? `This time is held for you until ${formatBookingWhen(bookingState.booking.approvalExpiresAt)}.`
                    : "This time is held for you while the request is reviewed."}
                </small>
              </span>
            </p>
            <p>
              <Mail size={18} />
              <span><strong>We will email you</strong><small>You will hear either way, and nothing is confirmed until then.</small></span>
            </p>
          </>
        ) : (
          <>
            <p><Video size={18} /><span><strong>{location}</strong><small>The details are included in your invitation.</small></span></p>
            <p><Mail size={18} /><span><strong>Invitation sent</strong><small>Check your inbox for meeting details.</small></span></p>
          </>
        )}
      </div>
      {bookingState.manageLinks ? (
        <nav>
          {pending ? null : (
            <a className="public-add-calendar" href={calendarFileHref(bookingState.booking, location)} download={`trueavail-${bookingState.booking.id}.ics`}>
              <CalendarCheck size={16} /> Add to calendar
            </a>
          )}
          {pending ? null : <a href={bookingState.manageLinks.reschedule}>Reschedule</a>}
          <a href={bookingState.manageLinks.cancel}>{pending ? "Withdraw request" : "Cancel booking"}</a>
        </nav>
      ) : null}
    </div>
  );
}

function calendarFileHref(booking: Booking, location: string): string {
  const start = icsDate(booking.interval.start);
  const end = icsDate(booking.interval.end);
  const title = "TrueAvail booking";
  const description = `Meeting with ${booking.guestName}`;
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//TrueAvail//Scheduling//EN",
    "BEGIN:VEVENT",
    // Not renamed with the brand: the UID is this event's identity, and changing it
    // would make a guest's re-download land as a second entry rather than an update.
    `UID:${booking.id}@timebridge`,
    `DTSTAMP:${icsDate(new Date().toISOString())}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${escapeIcs(title)}`,
    `DESCRIPTION:${escapeIcs(description)}`,
    `LOCATION:${escapeIcs(location)}`,
    "END:VEVENT",
    "END:VCALENDAR"
  ].join("\r\n");
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

function icsDate(value: string): string {
  return new Date(value).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

function escapeIcs(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");
}

/**
 * A photo where there is one, initials where there is not — including when the photo
 * fails to load, which is why the fallback is state rather than a CSS background.
 */
function Avatar({
  src,
  initials,
  className,
  style
}: {
  src?: string | undefined;
  initials: string;
  className: string;
  style?: CSSProperties | undefined;
}) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [src]);
  if (!src || broken) {
    return <span className={className} style={style}>{initials}</span>;
  }
  return (
    <span className={`${className} has-photo`} style={style}>
      <img src={src} alt="" loading="lazy" onError={() => setBroken(true)} />
    </span>
  );
}

/** Avatars are square everywhere they appear, and this is the size they are stored at. */
const AVATAR_OUTPUT_PIXELS = 512;
const AVATAR_VIEWPORT_PIXELS = 288;

/**
 * Choose which part of a photo becomes the avatar, then hand back just that part.
 *
 * Cropping in the browser means the file that reaches storage is a predictable
 * square rather than whatever came off a phone camera — a few tens of kilobytes
 * instead of several megabytes, and no surprise aspect ratio to letterbox later.
 */
function AvatarCropDialog({
  file,
  title,
  onCancel,
  onConfirm
}: {
  file: globalThis.File;
  title: string;
  onCancel: () => void;
  onConfirm: (image: globalThis.Blob) => void;
}) {
  const [source, setSource] = useState<globalThis.HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const dragRef = useRef<{ pointerId: number; startX: number; startY: number; originX: number; originY: number } | null>(null);

  useEffect(() => {
    const url = globalThis.URL.createObjectURL(file);
    const image = new globalThis.Image();
    image.onload = () => {
      setSource(image);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    };
    image.onerror = () => setFailed(true);
    image.src = url;
    return () => globalThis.URL.revokeObjectURL(url);
  }, [file]);

  // The photo is scaled so its shorter side exactly fills the square, which is the
  // smallest zoom that can leave no empty corner.
  const coverScale = source ? AVATAR_VIEWPORT_PIXELS / Math.min(source.width, source.height) : 1;
  const drawnWidth = source ? source.width * coverScale * zoom : 0;
  const drawnHeight = source ? source.height * coverScale * zoom : 0;
  const maxOffsetX = Math.max(0, (drawnWidth - AVATAR_VIEWPORT_PIXELS) / 2);
  const maxOffsetY = Math.max(0, (drawnHeight - AVATAR_VIEWPORT_PIXELS) / 2);
  const clamp = (value: number, limit: number) => Math.min(limit, Math.max(-limit, value));
  const position = { x: clamp(offset.x, maxOffsetX), y: clamp(offset.y, maxOffsetY) };

  function startDrag(event: ReactPointerEvent<globalThis.HTMLDivElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, originX: position.x, originY: position.y };
  }

  function moveDrag(event: ReactPointerEvent<globalThis.HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    setOffset({
      x: clamp(drag.originX + (event.clientX - drag.startX), maxOffsetX),
      y: clamp(drag.originY + (event.clientY - drag.startY), maxOffsetY)
    });
  }

  function endDrag(event: ReactPointerEvent<globalThis.HTMLDivElement>) {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
    }
  }

  /** Redraw the chosen square at output size and hand back the encoded image. */
  async function confirm() {
    if (!source) {
      return;
    }
    setBusy(true);
    try {
      const canvas = globalThis.document.createElement("canvas");
      canvas.width = AVATAR_OUTPUT_PIXELS;
      canvas.height = AVATAR_OUTPUT_PIXELS;
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("This browser cannot prepare the image.");
      }
      // Everything on screen is in viewport pixels; one multiplication moves the
      // whole arrangement to output pixels, so what was framed is what is saved.
      const ratio = AVATAR_OUTPUT_PIXELS / AVATAR_VIEWPORT_PIXELS;
      context.imageSmoothingQuality = "high";
      context.drawImage(
        source,
        (AVATAR_OUTPUT_PIXELS - drawnWidth * ratio) / 2 + position.x * ratio,
        (AVATAR_OUTPUT_PIXELS - drawnHeight * ratio) / 2 + position.y * ratio,
        drawnWidth * ratio,
        drawnHeight * ratio
      );
      const blob = await new Promise<globalThis.Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
      if (!blob) {
        throw new Error("This browser cannot prepare the image.");
      }
      onConfirm(blob);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="avatar-crop-backdrop" role="dialog" aria-modal="true" aria-labelledby="avatar-crop-title">
      <div className="avatar-crop-dialog">
        <div className="avatar-crop-head">
          <h3 id="avatar-crop-title">{title}</h3>
          <p>Drag to reposition, then zoom until it looks right.</p>
        </div>

        {failed ? (
          <p className="form-error">That image could not be read. Try a PNG, JPEG or WebP file.</p>
        ) : (
          <>
            <div
              className="avatar-crop-viewport"
              onPointerDown={startDrag}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              {source ? (
                <img
                  src={source.src}
                  alt=""
                  draggable={false}
                  style={{
                    width: `${drawnWidth}px`,
                    height: `${drawnHeight}px`,
                    transform: `translate(${position.x}px, ${position.y}px)`
                  }}
                />
              ) : (
                <Skeleton height="100%" radius="0" />
              )}
            </div>

            <label className="avatar-crop-zoom">
              <span>Zoom</span>
              <input
                type="range"
                min={1}
                max={4}
                step={0.01}
                value={zoom}
                disabled={!source}
                onChange={(event) => setZoom(Number(event.currentTarget.value))}
              />
            </label>
          </>
        )}

        <div className="avatar-crop-actions">
          <button type="button" className="editor-outline-button" onClick={onCancel}>Cancel</button>
          <button type="button" className="confirm" disabled={!source || busy || failed} onClick={() => void confirm()}>
            <Check size={16} /> {busy ? "Preparing" : "Use photo"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Uploads a photo and returns the URL it will be served from.
 *
 * The file goes straight from the browser to storage using a short-lived URL the API
 * signs, so a multi-megabyte image never travels through the API. The caller saves
 * the returned URL on the host or persona itself.
 */
async function uploadAvatar(
  idToken: string,
  scope: "host" | "persona",
  targetId: string,
  file: globalThis.Blob
): Promise<string> {
  if (file.size > MAX_AVATAR_BYTES) {
    throw new Error(`Choose an image under ${Math.round(MAX_AVATAR_BYTES / 1_000_000)}MB.`);
  }
  const response = await apiFetch("/v1/admin/avatar-upload-url", {
    method: "POST",
    headers: { authorization: `Bearer ${idToken}`, "content-type": "application/json" },
    body: JSON.stringify({ scope, targetId, contentType: file.type })
  });
  const body = (await response.json().catch(() => ({}))) as {
    uploadUrl?: string;
    avatarUrl?: string;
    error?: { message?: string; code?: string };
  };
  if (!response.ok || !body.uploadUrl || !body.avatarUrl) {
    throw new Error(body.error?.message ?? body.error?.code ?? `avatar_upload_url_${response.status}`);
  }
  const put = await globalThis.fetch(body.uploadUrl, {
    method: "PUT",
    headers: { "content-type": file.type },
    body: file
  });
  if (!put.ok) {
    throw new Error(`The photo could not be uploaded (${put.status}).`);
  }
  return body.avatarUrl;
}

const MAX_AVATAR_BYTES = 5_000_000;
const AVATAR_ACCEPT = "image/png,image/jpeg,image/webp";

function hostInitials(value: string | undefined): string {
  return (value ?? "FR")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "FR";
}

function GuestCancelView({ token }: { token: string }) {
  const [state, setState] = useState<GuestActionState>({ status: token ? "idle" : "error", message: "Cancellation link is missing a token." });

  async function submitCancel(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    if (!token) {
      return;
    }
    const form = new globalThis.FormData(event.currentTarget);
    setState({ status: "submitting" });
    try {
      const response = await fetch("/v1/public/bookings/cancel", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          token,
          reason: String(form.get("reason") ?? "")
        })
      });
      const body = (await response.json().catch(() => ({}))) as { booking?: Booking; error?: { message?: string; code?: string } };
      if (!response.ok || !body.booking) {
        throw new Error(body.error?.message ?? body.error?.code ?? `cancel_failed_${response.status}`);
      }
      setState({ status: "done", booking: body.booking });
    } catch (cause) {
      setState({ status: "error", message: cause instanceof Error ? cause.message : "Cancellation failed." });
    }
  }

  return (
    <GuestManageShell title="Cancel booking" eyebrow="Guest booking">
      {state.status === "done" ? (
        <section className="success-panel" aria-live="polite">
          <CalendarCheck size={22} />
          <div>
            <h3>Booking canceled</h3>
            <p>{state.booking.id}</p>
          </div>
        </section>
      ) : (
        <form className="guest-manage-form" onSubmit={(event) => void submitCancel(event)}>
          <label>
            Reason
            <textarea name="reason" maxLength={500} placeholder="Optional note for the host" />
          </label>
          <button className="confirm" disabled={!token || state.status === "submitting"}>
            <CalendarCheck size={18} /> {state.status === "submitting" ? "Canceling" : "Cancel booking"}
          </button>
        </form>
      )}
      {state.status === "error" ? <p className="form-error" aria-live="polite">{state.message}</p> : null}
    </GuestManageShell>
  );
}

function GuestRescheduleView({ token }: { token: string }) {
  const [state, setState] = useState<GuestActionState>({ status: token ? "loading" : "error", message: "Reschedule link is missing a token." });
  const [context, setContext] = useState<RescheduleLookupResponse["context"]>();
  const [slotState, setSlotState] = useState<LoadState>("loading");
  const [slots, setSlots] = useState<TimeInterval[]>([]);
  const [selectedDate, setSelectedDate] = useState(() => initialDateKey());
  const [selectedSlot, setSelectedSlot] = useState<TimeInterval | null>(null);

  useEffect(() => {
    if (!token) {
      return;
    }
    const controller = new globalThis.AbortController();
    setState({ status: "loading" });
    fetch(`/v1/public/bookings/reschedule?token=${encodeURIComponent(token)}`, { signal: controller.signal })
      .then(async (response) => {
        const body = (await response.json().catch(() => ({}))) as Partial<RescheduleLookupResponse> & { error?: { message?: string; code?: string } };
        if (!response.ok || !body.booking) {
          throw new Error(body.error?.message ?? body.error?.code ?? `reschedule_lookup_failed_${response.status}`);
        }
        setContext(body.context);
        setSelectedDate(dateKey(body.booking.interval.start));
        setState({ status: "idle", booking: body.booking });
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setState({ status: "error", message: cause instanceof Error ? cause.message : "Reschedule link is not valid." });
        }
      });
    return () => controller.abort();
  }, [token]);

  useEffect(() => {
    if (!context) {
      setSlotState("error");
      return;
    }
    const controller = new globalThis.AbortController();
    const startDate = new Date();
    startDate.setUTCHours(0, 0, 0, 0);
    const endDate = new Date(startDate);
    endDate.setUTCDate(endDate.getUTCDate() + context.meetingType.horizonDays);
    setSlotState("loading");
    setSlots([]);
    fetch(
      `/v1/public/hosts/${context.host.slug}/personas/${context.persona.slug}/${context.meetingType.slug}/slots?start=${encodeURIComponent(startDate.toISOString())}&end=${encodeURIComponent(endDate.toISOString())}`,
      { signal: controller.signal }
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`reschedule_slots_${response.status}`);
        }
        return (await response.json()) as { slots: TimeInterval[] };
      })
      .then((body) => {
        setSlots(body.slots);
        setSlotState("live");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setSlotState("error");
        }
      });
    return () => controller.abort();
  }, [context]);

  async function submitReschedule() {
    if (!token || !selectedSlot) {
      return;
    }
    setState({ status: "submitting", ...(state.status === "idle" || state.status === "error" ? { booking: state.booking } : {}) });
    try {
      const response = await fetch("/v1/public/bookings/reschedule", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-idempotency-key": globalThis.crypto.randomUUID()
        },
        body: JSON.stringify({
          token,
          start: selectedSlot.start,
          end: selectedSlot.end
        })
      });
      const body = (await response.json().catch(() => ({}))) as {
        booking?: Booking;
        previousInterval?: TimeInterval;
        error?: { message?: string; code?: string };
      };
      if (!response.ok || !body.booking) {
        throw new Error(body.error?.message ?? body.error?.code ?? `reschedule_failed_${response.status}`);
      }
      setState({
        status: "done",
        booking: body.booking,
        ...(body.previousInterval ? { previousInterval: body.previousInterval } : {})
      });
    } catch (cause) {
      setState({ status: "error", message: cause instanceof Error ? cause.message : "Reschedule failed.", ...(state.status === "idle" || state.status === "error" ? { booking: state.booking } : {}) });
    }
  }

  const booking = "booking" in state ? state.booking : undefined;
  const availableDates = useMemo(() => new Set(slots.map((slot) => dateKey(slot.start))), [slots]);
  const daySlots = slots.filter((slot) => dateKey(slot.start) === selectedDate && slot.start !== booking?.interval.start);

  return (
    <GuestManageShell title="Reschedule booking" eyebrow="Guest booking">
      {state.status === "loading" ? <p className="inline-note" aria-live="polite">Loading booking</p> : null}
      {booking ? (
        <div className="guest-booking-summary">
          <span>Current time</span>
          <strong>{formatFullDate(dateKey(booking.interval.start))} {formatTime(booking.interval.start)}</strong>
          <em>{booking.guestEmail}</em>
        </div>
      ) : null}
      {context ? (
        <div className="guest-booking-summary">
          <span>Meeting</span>
          <strong>{context.meetingType.title}</strong>
          <em>{context.persona.displayName}</em>
        </div>
      ) : null}
      {state.status === "done" ? (
        <section className="success-panel" aria-live="polite">
          <CalendarCheck size={22} />
          <div>
            <h3>Booking rescheduled</h3>
            <p>{formatFullDate(dateKey(state.booking.interval.start))} {formatTime(state.booking.interval.start)}</p>
          </div>
        </section>
      ) : (
        <div className="guest-reschedule-picker">
          <CalendarPicker
            selectedDate={selectedDate}
            availableDates={availableDates}
            onSelect={(date) => {
              setSelectedDate(date);
              setSelectedSlot(null);
            }}
          />
          <TimeSlotList
            date={selectedDate}
            slots={daySlots}
            selectedSlot={selectedSlot}
            label={`${context?.meetingType.durationMinutes ?? 30} minutes`}
            loading={slotState === "loading"}
            emptyMessage={slotState === "loading" ? "Loading times" : slotState === "error" ? "Times are unavailable" : "No times available for this day"}
            onSelect={setSelectedSlot}
          />
          <button className="confirm" type="button" onClick={() => void submitReschedule()} disabled={!token || !selectedSlot || state.status === "loading" || state.status === "submitting"}>
            <Clock size={18} /> {state.status === "submitting" ? "Rescheduling" : "Reschedule booking"}
          </button>
        </div>
      )}
      {state.status === "error" ? <p className="form-error" aria-live="polite">{state.message}</p> : null}
    </GuestManageShell>
  );
}

/**
 * Where an emailed approve or decline link lands.
 *
 * Following the link only reads the request. Mail providers and security scanners
 * fetch every link in a message before anyone opens it, so a link that decided a
 * booking by being followed would answer requests nobody had read. The decision is
 * a button on this page, and the button sends a POST.
 */
function HostApprovalView({ token, preselect }: { token: string; preselect: "approve" | "decline" }) {
  const [state, setState] = useState<GuestActionState>({
    status: token ? "loading" : "error",
    message: "Approval link is missing a token."
  });
  const [context, setContext] = useState<RescheduleLookupResponse["context"]>();
  const [answered, setAnswered] = useState<"approved" | "declined" | "already" | null>(null);

  useEffect(() => {
    if (!token) {
      return;
    }
    const controller = new globalThis.AbortController();
    setState({ status: "loading" });
    fetch(`/v1/public/bookings/approve?token=${encodeURIComponent(token)}`, { signal: controller.signal })
      .then(async (response) => {
        const body = (await response.json().catch(() => ({}))) as Partial<RescheduleLookupResponse> & {
          answered?: boolean;
          error?: { message?: string; code?: string };
        };
        if (!response.ok || !body.booking) {
          throw new Error(body.error?.message ?? body.error?.code ?? `approval_lookup_failed_${response.status}`);
        }
        setContext(body.context);
        if (body.answered) {
          setAnswered("already");
        }
        setState({ status: "idle", booking: body.booking });
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setState({ status: "error", message: cause instanceof Error ? cause.message : "Approval link is not valid." });
        }
      });
    return () => controller.abort();
  }, [token]);

  async function answer(decision: "approve" | "decline") {
    setState((current) => ({ status: "submitting", ...(current.booking ? { booking: current.booking } : {}) }));
    try {
      const response = await fetch("/v1/public/bookings/approve", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, decision })
      });
      const body = (await response.json().catch(() => ({}))) as {
        booking?: Booking;
        decision?: string;
        answered?: boolean;
        error?: { message?: string; code?: string };
      };
      if (!response.ok || !body.booking) {
        throw new Error(body.error?.message ?? body.error?.code ?? `approval_failed_${response.status}`);
      }
      setAnswered(body.answered ? "already" : body.decision === "declined" ? "declined" : "approved");
      setState({ status: "done", booking: body.booking });
    } catch (cause) {
      setState({ status: "error", message: cause instanceof Error ? cause.message : "That could not be saved." });
    }
  }

  const booking = state.booking;
  const busy = state.status === "submitting";

  return (
    <GuestManageShell title="Approve this request" eyebrow="Booking request">
      {state.status === "loading" ? (
        <SkeletonRegion label="Loading the request">
          <Skeleton height="18px" width="60%" />
          <Skeleton height="18px" width="80%" />
          <Skeleton height="18px" width="45%" />
        </SkeletonRegion>
      ) : null}

      {booking ? (
        <ul className="approval-facts">
          {context?.persona.displayName ? (
            <li><span>Persona</span><strong>{context.persona.displayName}</strong></li>
          ) : null}
          {context?.meetingType.title ? (
            <li><span>Meeting</span><strong>{context.meetingType.title}</strong></li>
          ) : null}
          <li>
            <span>When</span>
            <strong>{`${formatBookingWhen(booking.interval.start)} \u2013 ${formatTime(booking.interval.end)}`}</strong>
          </li>
          <li><span>Guest</span><strong>{booking.guestName} ({booking.guestEmail})</strong></li>
          {booking.guestOrganization ? <li><span>Organization</span><strong>{booking.guestOrganization}</strong></li> : null}
          {booking.guestPhone ? <li><span>Phone</span><strong>{booking.guestPhone}</strong></li> : null}
          {booking.guestReason ? <li><span>Reason</span><strong>{booking.guestReason}</strong></li> : null}
          {booking.approvalExpiresAt && !answered ? (
            <li><span>Answer by</span><strong>{formatBookingWhen(booking.approvalExpiresAt)}</strong></li>
          ) : null}
        </ul>
      ) : null}

      {answered ? (
        <section className="success-panel" aria-live="polite">
          <CalendarCheck size={22} />
          <div>
            <h3>
              {answered === "approved"
                ? "Approved"
                : answered === "declined"
                  ? "Declined"
                  : "Already answered"}
            </h3>
            <p>
              {answered === "approved"
                ? "The meeting is confirmed and the guest has been invited."
                : answered === "declined"
                  ? "The time has been released and the guest has been told."
                  : "This request was already answered, so nothing changed."}
            </p>
          </div>
        </section>
      ) : booking ? (
        <div className="approval-actions">
          <button
            type="button"
            className={preselect === "approve" ? "confirm" : "editor-outline-button"}
            disabled={busy}
            onClick={() => void answer("approve")}
          >
            <Check size={18} /> {busy ? "Saving" : "Approve"}
          </button>
          <button
            type="button"
            className={preselect === "decline" ? "confirm danger" : "editor-outline-button"}
            disabled={busy}
            onClick={() => void answer("decline")}
          >
            Decline
          </button>
        </div>
      ) : null}

      {state.status === "error" ? <p className="form-error" aria-live="polite">{state.message}</p> : null}
    </GuestManageShell>
  );
}

function GuestManageShell({ eyebrow, title, children }: { eyebrow: string; title: string; children: ReactNode }) {
  return (
    <main className="guest-manage-shell">
      <section className="guest-manage-panel">
        <div className="brand-lockup">
          <img className="brand-logo" src="/logo.png" alt="TrueAvail" width={200} height={48} />
          <span>trueavail.app</span>
        </div>
        <div className="section-heading tight">
          <div>
            <p className="eyebrow">{eyebrow}</p>
            <h1>{title}</h1>
          </div>
        </div>
        {children}
      </section>
    </main>
  );
}

function App() {
  const [activeView, setActiveView] = useState<View>("overview");
  const [editingPersonaId, setEditingPersonaId] = useState<string | null>(null);
  const [viewingBookingId, setViewingBookingId] = useState<string | null>(null);
  const [selectedPersonaId, setSelectedPersonaId] = useState("persona_fms");
  const [selectedSlot, setSelectedSlot] = useState<TimeInterval | null>(null);
  const [selectedDate, setSelectedDate] = useState(() => initialDateKey());
  const [bookingState, setBookingState] = useState<BookingState>({ status: "idle" });
  const [publicState, setPublicState] = useState<LoadState>("loading");
  const [slotState, setSlotState] = useState<LoadState>("loading");
  const [livePersona, setLivePersona] = useState<Persona | null>(null);
  const [liveMeetingTypes, setLiveMeetingTypes] = useState<MeetingType[]>([]);
  const [liveSlots, setLiveSlots] = useState<TimeInterval[]>([]);
  const [authState, setAuthState] = useState<AuthState>(() => storedAuthState());
  const [adminState, setAdminState] = useState<LoadState>("loading");
  const [adminData, setAdminData] = useState<AdminStatusResponse | null>(null);
  const [managedConnection, setManagedConnection] = useState<{ id: string; mode: CalendarManageMode | "disconnect" } | null>(null);
  const [connectRoute, setConnectRoute] = useState<ConnectWizardRoute | null>(() => readConnectWizardRoute());
  const [navOpen, setNavOpen] = useState(false);

  const onboardingState = onboardingStatusForHost(authState, adminData);
  // Onboarding is a first-run wizard, not a permanent gate. A host who has already
  // published a persona and then removes their last calendar should land back on
  // Calendars to fix it, not be thrown into setup with the rest of the app locked.
  const hasPublishedPersona =
    authState.status === "signed_in" &&
    Boolean(adminData?.personas.some((persona) => persona.hostId === authState.hostId && persona.publicState === "active"));
  const onboardingRequired =
    authState.status === "signed_in" &&
    adminData &&
    onboardingState !== "complete" &&
    onboardingState !== "needs_publish" &&
    !(onboardingState === "needs_calendar" && hasPublishedPersona);
  const visiblePersonas = adminData ? adminData.personas : personas;
  const visibleMeetingTypes = adminData
    ? adminData.meetingTypes
    : liveMeetingTypes.length > 0
      ? liveMeetingTypes
      : meetingTypes;
  const visibleConnections = adminData ? adminData.calendarConnections : calendarConnections;
  const fallbackPersona = visiblePersonas.find((item) => item.id === selectedPersonaId) ?? personas.find((item) => item.id === selectedPersonaId) ?? visiblePersonas[0] ?? personas[0]!;
  const persona = livePersona?.id === selectedPersonaId ? livePersona : fallbackPersona;
  const personaMeetings = visibleMeetingTypes.filter((item) => item.personaId === persona.id);
  const meeting = personaMeetings[0] ?? meetingTypes[0]!;
  const slots = liveSlots;
  const availableDates = useMemo(() => new Set(slots.map((slot) => dateKey(slot.start))), [slots]);
  const daySlots = slots.filter((slot) => dateKey(slot.start) === selectedDate);
  const isSignedIn = authState.status === "signed_in";
  const renderedView = onboardingRequired ? "onboarding" : activeView;
  // The workspace footer named the booking domain but did nothing with it.
  const workspaceLink = `${appOrigin}/${signedInHost(authState, adminData)?.slug ?? fredrick.slug}`;
  const workspaceHost = workspaceLink.replace(/^https?:\/\//, "");
  const [workspaceCopied, setWorkspaceCopied] = useState(false);

  // Under 980px the sidebar becomes an off-canvas drawer, so it needs the manners of a
  // dialog: Escape closes it, the page behind it stops scrolling, focus moves into it,
  // and it never lingers open once the viewport grows back to a desktop width.
  const navToggleRef = useRef<globalThis.HTMLButtonElement | null>(null);
  const navCloseRef = useRef<globalThis.HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!navOpen) {
      return;
    }
    navCloseRef.current?.focus();
    const onKey = (keyEvent: globalThis.KeyboardEvent) => {
      if (keyEvent.key === "Escape") {
        setNavOpen(false);
        navToggleRef.current?.focus();
      }
    };
    const desktop = globalThis.matchMedia("(min-width: 981px)");
    const closeOnDesktop = () => setNavOpen(false);
    const bodyStyle = globalThis.document.body.style;
    const previousOverflow = bodyStyle.overflow;
    bodyStyle.overflow = "hidden";
    globalThis.document.addEventListener("keydown", onKey);
    desktop.addEventListener("change", closeOnDesktop);
    return () => {
      bodyStyle.overflow = previousOverflow;
      globalThis.document.removeEventListener("keydown", onKey);
      desktop.removeEventListener("change", closeOnDesktop);
    };
  }, [navOpen]);

  const openView = useCallback((view: View) => {
    setActiveView(view);
    setNavOpen(false);
  }, []);

  useEffect(() => {
    if (authState.status !== "signed_in") {
      setAdminData(null);
      setAdminState("error");
      return;
    }

    const controller = new globalThis.AbortController();
    setAdminState("loading");
    apiFetch("/v1/admin/status", {
      signal: controller.signal,
      headers: { authorization: `Bearer ${authState.idToken}` }
    })
      .then(async (response) => {
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            globalThis.localStorage.removeItem(authStorageKey);
            setAuthState({ status: "signed_out" });
          }
          throw new Error(`admin_status_${response.status}`);
        }
        return (await response.json()) as AdminStatusResponse;
      })
      .then((body) => {
        setAdminData(body);
        setAdminState("live");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setAdminState("error");
        }
      });
    return () => controller.abort();
  }, [authState]);

  useEffect(() => {
    if (onboardingRequired && activeView !== "onboarding") {
      setActiveView("onboarding");
    }
  }, [activeView, onboardingRequired]);

  // Any authenticated call answering 401 means the session is gone: clear it and
  // show the sign-in screen instead of leaving a status code in a form.
  useEffect(() => {
    const onSessionExpired = () => {
      globalThis.localStorage.removeItem(authStorageKey);
      setAuthState({ status: "signed_out" });
      setAdminData(null);
      setAdminState("live");
    };
    globalThis.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => globalThis.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  // The OAuth callback redirects to /calendars/connect; open the wizard there.
  useEffect(() => {
    if (connectRoute && authState.status === "signed_in") {
      setActiveView("connect");
    }
  }, [connectRoute, authState.status]);

  useEffect(() => {
    const controller = new globalThis.AbortController();
    setPublicState("loading");
    fetch(`/v1/public/hosts/${fredrick.slug}/personas/${fallbackPersona.slug}`, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`public_metadata_${response.status}`);
        }
        return (await response.json()) as PublicPersonaResponse;
      })
      .then((body) => {
        setLivePersona(body.persona);
        setLiveMeetingTypes(body.meetingTypes);
        setPublicState("live");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setPublicState("error");
        }
      });
    return () => controller.abort();
  }, [fallbackPersona.slug]);

  useEffect(() => {
    const controller = new globalThis.AbortController();
    const startDate = new Date();
    startDate.setUTCHours(0, 0, 0, 0);
    const endDate = new Date(startDate);
    endDate.setUTCDate(endDate.getUTCDate() + meeting.horizonDays);
    setSlotState("loading");
    setLiveSlots([]);
    fetch(
      `/v1/public/hosts/${fredrick.slug}/personas/${persona.slug}/${meeting.slug}/slots?start=${encodeURIComponent(startDate.toISOString())}&end=${encodeURIComponent(endDate.toISOString())}`,
      { signal: controller.signal }
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`public_slots_${response.status}`);
        }
        return (await response.json()) as { slots: TimeInterval[] };
      })
      .then((body) => {
        setLiveSlots(body.slots);
        setSlotState("live");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setSlotState("error");
        }
      });
    return () => controller.abort();
  }, [meeting.horizonDays, meeting.slug, persona.slug]);

  async function submitBooking(guest: BookingGuestInput) {
    if (!selectedSlot) {
      return;
    }
    setBookingState({ status: "submitting" });
    try {
      const response = await fetch("/v1/public/bookings", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-idempotency-key": globalThis.crypto.randomUUID()
        },
        body: JSON.stringify({
          hostSlug: fredrick.slug,
          personaSlug: persona.slug,
          meetingSlug: meeting.slug,
          start: selectedSlot.start,
          end: selectedSlot.end,
          guest
        })
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { error?: { message?: string; code?: string } };
        throw new Error(body.error?.message ?? body.error?.code ?? `booking_failed_${response.status}`);
      }
      const body = (await response.json()) as BookingResponse;
      setBookingState({
        status: "confirmed",
        booking: body.booking,
        ...(body.manageLinks ? { manageLinks: body.manageLinks } : {})
      });
      setLiveSlots((current) => current.filter((slot) => slot.start !== selectedSlot.start));
    } catch (cause) {
      setBookingState({ status: "error", message: cause instanceof Error ? cause.message : "Booking failed." });
    }
  }

  if (!isSignedIn) {
    return <SignInScreen authState={authState} onAuthChange={setAuthState} />;
  }

  return (
    <main className={navOpen ? "app-shell nav-open" : "app-shell"}>
      {/* The phone-width header: the nav lives behind the hamburger, the primary action stays reachable. */}
      <header className="mobile-bar">
        <button
          ref={navToggleRef}
          type="button"
          className="nav-toggle"
          aria-label={navOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={navOpen}
          aria-controls="primary-nav"
          onClick={() => setNavOpen((current) => !current)}
        >
          <Menu size={22} />
        </button>
        <img className="brand-logo" src="/logo.png" alt="TrueAvail" width={200} height={48} />
        <button
          type="button"
          className="mobile-bar-book"
          aria-label="Book a meeting"
          disabled={Boolean(onboardingRequired)}
          onClick={() => openView("book")}
        >
          <CalendarCheck size={19} />
        </button>
      </header>

      <div className="nav-scrim" role="presentation" onClick={() => setNavOpen(false)} />

      <aside id="primary-nav" className="sidebar" aria-label="Primary navigation">
        <button ref={navCloseRef} type="button" className="nav-close" aria-label="Close navigation menu" onClick={() => setNavOpen(false)}>
          <X size={20} />
        </button>
        <div className="brand-lockup">
          <img className="brand-logo" src="/logo.png" alt="TrueAvail" width={200} height={48} />
          <span>{authState.status === "signed_in" ? authState.email : publicState === "live" ? fredrick.displayName : "Personal scheduling"}</span>
        </div>
        <nav>
          <NavButton icon={<LayoutDashboard size={18} />} label="Overview" view="overview" activeView={renderedView} onboardingRequired={Boolean(onboardingRequired)} onSelect={openView} />
          <NavButton icon={<Users size={18} />} label="Profile" view="profile" activeView={renderedView} onboardingRequired={Boolean(onboardingRequired)} onSelect={openView} />
          <NavButton icon={<Users size={18} />} label="Personas" view="personas" activeView={renderedView} onboardingRequired={Boolean(onboardingRequired)} onSelect={openView} />
          <NavButton icon={<CalendarCheck size={18} />} label="Calendars" view="calendars" activeView={renderedView} onboardingRequired={Boolean(onboardingRequired)} onSelect={openView} />
          <NavButton icon={<Clock size={18} />} label="Bookings" view="bookings" activeView={renderedView} onboardingRequired={Boolean(onboardingRequired)} onSelect={openView} />
          <NavButton icon={<CalendarCheck size={18} />} label="Availability" view="availability" activeView={renderedView} onboardingRequired={Boolean(onboardingRequired)} onSelect={openView} />
          <NavButton icon={<Share2 size={18} />} label="Share links" view="links" activeView={renderedView === "extension" ? "links" : renderedView} onboardingRequired={Boolean(onboardingRequired)} onSelect={openView} />
          {onboardingRequired || onboardingState === "needs_publish" || activeView === "onboarding" ? (
            <NavButton icon={<UserPlus size={18} />} label="Onboarding" view="onboarding" activeView={renderedView} onboardingRequired={Boolean(onboardingRequired)} onSelect={openView} />
          ) : null}
          {isSignedIn && authState.roles.includes("platform_admin") ? <NavButton icon={<UserPlus size={18} />} label="Users & invitations" view="users" activeView={renderedView} onboardingRequired={Boolean(onboardingRequired)} onSelect={openView} /> : null}
          <NavButton icon={<Activity size={18} />} label="Operations" view="operations" activeView={renderedView} onboardingRequired={Boolean(onboardingRequired)} onSelect={openView} />
        </nav>
        <div className="sidebar-footer">
          <span>Scheduling workspace</span>
          <a href={workspaceLink} target="_blank" rel="noreferrer" title={`Open ${workspaceHost}`}>
            <strong>{workspaceHost}</strong>
            <ArrowUpRight size={14} />
          </a>
          <button
            type="button"
            onClick={() => {
              void globalThis.navigator.clipboard.writeText(workspaceLink);
              setWorkspaceCopied(true);
              globalThis.setTimeout(() => setWorkspaceCopied(false), 2000);
            }}
          >
            <Copy size={13} /> {workspaceCopied ? "Copied" : "Copy link"}
          </button>
        </div>
      </aside>

      <section className="workspace">
        <header className="topbar">
          <div>
            <p className="eyebrow">{formatFullDate(todayDateKey())}</p>
            <h1>{viewTitle(renderedView)}</h1>
          </div>
          <div className="topbar-actions">
            {isSignedIn ? <AuthPanel authState={authState} onAuthChange={setAuthState} /> : null}
            {isSignedIn ? <button type="button" className="topbar-book" disabled={Boolean(onboardingRequired)} onClick={() => setActiveView("book")}>
              <CalendarCheck size={18} /> Book
            </button> : null}
          </div>
        </header>

        {/* The first paint of the console: the shape of the Overview it will become. */}
        {isSignedIn && adminState === "loading" && !adminData ? (
          <SkeletonRegion label="Loading workspace" className="skeleton-workspace">
            <Skeleton className="skeleton-workspace-hero" height="196px" radius="20px" />
            <div className="skeleton-workspace-metrics">
              <SkeletonRows count={4}>{(index) => <Skeleton key={index} height="104px" radius="18px" />}</SkeletonRows>
            </div>
            <div className="skeleton-workspace-grid">
              <Skeleton height="430px" radius="18px" />
              <Skeleton height="430px" radius="18px" />
            </div>
          </SkeletonRegion>
        ) : null}
        {isSignedIn && adminState === "error" && !adminData ? <WorkspaceState title="Workspace unavailable" detail="Your scheduling setup could not be loaded." tone="error" /> : null}
        {isSignedIn && adminData ? (
          <>
            {onboardingRequired ? (
              <OnboardingView authState={authState} adminData={adminData} onAdminDataChange={setAdminData} onOpenView={setActiveView} />
            ) : null}
            {!onboardingRequired && activeView === "overview" ? (
              <Overview
                authState={authState}
                adminData={adminData}
                persona={persona}
                meeting={meeting}
                slots={slots}
                personas={visiblePersonas}
                meetingTypes={visibleMeetingTypes}
                personaCount={visiblePersonas.length}
                connectionCount={visibleConnections.length}
                onOpenView={setActiveView}
                onOpenBooking={(id) => {
                  setViewingBookingId(id);
                  setActiveView("booking-detail");
                }}
              />
            ) : null}
            {!onboardingRequired && activeView === "profile" ? <ProfileView authState={authState} adminData={adminData} onAdminDataChange={setAdminData} /> : null}
            {!onboardingRequired && activeView === "personas" ? (
              <PersonasView
                personas={visiblePersonas}
                meetingTypes={visibleMeetingTypes}
                authState={authState}
                adminData={adminData}
                onAdminDataChange={setAdminData}
                onEditPersona={(personaId) => {
                  setEditingPersonaId(personaId);
                  setActiveView("persona-edit");
                }}
                onCreatePersona={() => setActiveView("persona-create")}
                onSelect={(id) => {
                  setSelectedPersonaId(id);
                  setSelectedSlot(null);
                  setBookingState({ status: "idle" });
                  setSelectedDate(initialDateKey());
                }}
              />
            ) : null}
            {!onboardingRequired && activeView === "persona-create" ? (
              <CreatePersonaView
                authState={authState}
                adminData={adminData}
                onAdminDataChange={setAdminData}
                onExit={() => setActiveView("personas")}
                onCreated={(personaId) => {
                  setEditingPersonaId(personaId);
                  setActiveView("persona-edit");
                }}
              />
            ) : null}
            {!onboardingRequired && activeView === "persona-edit" ? (
              <EditPersonaView
                personaId={editingPersonaId ?? ""}
                authState={authState}
                adminData={adminData}
                onAdminDataChange={setAdminData}
                onPersonaChange={setLivePersona}
                onMeetingTypesChange={setLiveMeetingTypes}
                onExit={() => setActiveView("personas")}
                onOpenView={setActiveView}
              />
            ) : null}
            {!onboardingRequired && activeView === "calendars" ? (
              <CalendarsView
                authState={authState}
                adminData={adminData}
                connections={visibleConnections}
                onAdminDataChange={setAdminData}
                onStartConnect={() => setActiveView("connect")}
                onManageConnection={(connectionId, mode) => {
                  setManagedConnection({ id: connectionId, mode });
                  setActiveView("calendar-manage");
                }}
              />
            ) : null}
            {!onboardingRequired && activeView === "calendar-manage" && managedConnection ? (
              <ManageCalendarView
                key={`${managedConnection.id}:${managedConnection.mode}`}
                authState={authState}
                adminData={adminData}
                connectionId={managedConnection.id}
                initialMode={managedConnection.mode === "disconnect" ? "details" : managedConnection.mode}
                openDisconnect={managedConnection.mode === "disconnect"}
                onAdminDataChange={setAdminData}
                onExit={() => {
                  setManagedConnection(null);
                  setActiveView("calendars");
                }}
              />
            ) : null}
            {!onboardingRequired && activeView === "connect" ? (
              <ConnectCalendarView
                authState={authState}
                adminData={adminData}
                onAdminDataChange={setAdminData}
                {...(connectRoute?.connectionId ? { initialConnectionId: connectRoute.connectionId } : {})}
                {...(connectRoute?.failure ? { initialFailure: connectRoute.failure } : {})}
                onExit={() => {
                  setConnectRoute(null);
                  globalThis.history.replaceState(null, "", "/");
                  setActiveView("calendars");
                }}
              />
            ) : null}
            {!onboardingRequired && activeView === "bookings" ? (
              <BookingsView
                authState={authState}
                adminData={adminData}
                onOpenView={setActiveView}
                onOpenBooking={(id) => {
                  setViewingBookingId(id);
                  setActiveView("booking-detail");
                }}
              />
            ) : null}
            {!onboardingRequired && activeView === "booking-detail" ? (
              <BookingDetailView
                bookingId={viewingBookingId ?? ""}
                authState={authState}
                adminData={adminData}
                onExit={() => setActiveView("bookings")}
              />
            ) : null}
            {!onboardingRequired && activeView === "book" ? (
              <BookMeetingView adminData={adminData} onExit={() => setActiveView("overview")} onOpenView={setActiveView} />
            ) : null}
            {!onboardingRequired && activeView === "availability" ? (
              <AvailabilityView
                meeting={meeting}
                selectedDate={selectedDate}
                selectedSlot={selectedSlot}
                bookingState={bookingState}
                slotState={slotState}
                availableDates={availableDates}
                daySlots={daySlots}
                authState={authState}
                adminData={adminData}
                onAdminDataChange={setAdminData}
                onMeetingTypeChange={(updated) => setLiveMeetingTypes((current) => current.map((item) => item.id === updated.id ? updated : item))}
                onSelectDate={(nextDate) => {
                  setSelectedDate(nextDate);
                  setSelectedSlot(null);
                  setBookingState({ status: "idle" });
                }}
                onSelectSlot={(slot) => {
                  setSelectedSlot(slot);
                  setBookingState({ status: "idle" });
                }}
                onSubmitBooking={(guest) => void submitBooking(guest)}
              />
            ) : null}
            {!onboardingRequired && activeView === "links" ? (
              <LinksView
                authState={authState}
                adminData={adminData}
                persona={persona}
                personas={visiblePersonas}
                meeting={meeting}
                meetingTypes={visibleMeetingTypes}
                hostSlug={signedInHost(authState, adminData)?.slug ?? fredrick.slug}
                onOpenView={openView}
              />
            ) : null}
            {!onboardingRequired && activeView === "extension" ? <ChromeExtensionView onExit={() => openView("links")} /> : null}
            {!onboardingRequired && activeView === "onboarding" ? <OnboardingView authState={authState} adminData={adminData} onAdminDataChange={setAdminData} onOpenView={setActiveView} /> : null}
            {activeView === "users" && authState.roles.includes("platform_admin") ? <UsersInvitationsView authState={authState} adminData={adminData} onAdminDataChange={setAdminData} onOpenView={setActiveView} /> : null}
            {activeView === "host-create" && authState.roles.includes("platform_admin") ? (
              <CreateHostView
                authState={authState}
                onExit={() => setActiveView("users")}
                onCreated={() => setActiveView("users")}
              />
            ) : null}
            {!onboardingRequired && activeView === "operations" ? (
              <OperationsView
                authState={authState}
                adminData={adminData}
                onAdminDataChange={setAdminData}
              />
            ) : null}
          </>
        ) : null}
      </section>
    </main>
  );
}

function SignInScreen({ authState, onAuthChange }: { authState: AuthState; onAuthChange: (state: AuthState) => void }) {
  return (
    <main className="signin-screen">
      <section className="signin-art" aria-label="TrueAvail scheduling">
        <div className="signin-brand">
          <span><img src="/logo-mark.png" alt="" width={39} height={39} /></span>
          <strong>TrueAvail</strong>
        </div>
        <div className="signin-hero">
          <p className="signin-pill">One schedule. Every side of you.</p>
          <h1>Your time,<br />perfectly aligned.</h1>
          <p>Bring work, business, and life together, then share the right booking experience with every audience.</p>
          <div className="signin-calendar-stack" aria-hidden="true">
            <div>
              <i className="google">G</i>
              <strong>Personal</strong>
              <small>Available</small>
            </div>
            <div>
              <i className="ms">M</i>
              <strong>Professional</strong>
              <small>2:00 PM busy</small>
            </div>
            <div>
              <i className="zoho">Z</i>
              <strong>Fund My Squad</strong>
              <small>Available</small>
            </div>
            <article>
              <Check size={18} />
              <span>
                <strong>3:30 PM</strong>
                <small>Truly available</small>
              </span>
            </article>
          </div>
        </div>
        <small>Private by design · Your calendar details stay yours</small>
      </section>
      <section className="signin-login">
        <div className="signin-mobile-brand">
          <img className="brand-logo" src="/logo.png" alt="TrueAvail" width={190} height={46} />
        </div>
        <div className="signin-card">
          <p className="signin-badge">Welcome back</p>
          <h2>{authState.status === "new_password_required" ? "Create your workspace password" : "Sign in to your workspace"}</h2>
          <p>Manage your personas, calendars, and bookings.</p>
          <AuthPanel authState={authState} onAuthChange={onAuthChange} />
          <small>Need help? <a href="mailto:froby@robyweb.com">Contact support</a></small>
        </div>
      </section>
    </main>
  );
}

function InviteSignupScreen({ token }: { token: string }) {
  const [previewState, setPreviewState] = useState<LoadState>("loading");
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "done" | "error">("idle");
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (!token) {
      setPreviewState("error");
      setMessage("Invitation token is missing.");
      return;
    }
    const controller = new globalThis.AbortController();
    setPreviewState("loading");
    fetch(`/v1/onboarding/invitations/claim?token=${encodeURIComponent(token)}`, { signal: controller.signal })
      .then(async (response) => {
        const body = (await response.json().catch(() => ({}))) as Partial<InvitationPreviewResponse> & { error?: { message?: string; code?: string } };
        if (!response.ok || !body.invitation) {
          throw new Error(body.error?.message ?? body.error?.code ?? `invite_preview_${response.status}`);
        }
        return body as InvitationPreviewResponse;
      })
      .then((body) => {
        setInvitation(body.invitation);
        setPreviewState(body.signupEnabled ? "live" : "error");
        setMessage(body.signupEnabled ? "" : "Host signup is not enabled.");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setPreviewState("error");
          setMessage(cause instanceof Error ? cause.message : "Invitation link is not valid.");
        }
      });
    return () => controller.abort();
  }, [token]);

  async function submitInviteClaim(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    if (!invitation) {
      return;
    }
    const form = new globalThis.FormData(event.currentTarget);
    const firstName = String(form.get("firstName") ?? "").trim();
    const lastName = String(form.get("lastName") ?? "").trim();
    const password = String(form.get("password") ?? "");
    if (!strongSignupPassword(password)) {
      setSubmitState("error");
      setMessage("Use at least 10 characters with uppercase, lowercase, and a number.");
      return;
    }
    if (form.get("terms") !== "on") {
      setSubmitState("error");
      setMessage("Accept the Terms of Service and Privacy Policy to continue.");
      return;
    }

    setSubmitState("submitting");
    setMessage("");
    try {
      const response = await fetch("/v1/onboarding/invitations/claim", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          token,
          slug: slugFromEmail(invitation.email),
          displayName: [firstName, lastName].filter(Boolean).join(" "),
          defaultTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || fredrick.defaultTimeZone
        })
      });
      const body = (await response.json().catch(() => ({}))) as HostSignupResponse & { error?: { code?: string; details?: string[]; message?: string } };
      if (!response.ok || !body.host) {
        throw new Error(body.error?.details?.join(", ") ?? body.error?.message ?? body.error?.code ?? `claim_${response.status}`);
      }
      setSubmitState("done");
      setMessage("Account created. Check your email for the TrueAvail sign-in invitation.");
    } catch (cause) {
      setSubmitState("error");
      setMessage(cause instanceof Error ? cause.message : "Account creation failed.");
    }
  }

  const passwordIsStrong = strongSignupPassword(password);
  const canSubmit = previewState === "live" && submitState !== "submitting" && submitState !== "done";

  return (
    <main className="signin-screen signup-screen">
      <section className="signin-art" aria-label="TrueAvail invitation">
        <div className="signin-brand">
          <span><img src="/logo-mark.png" alt="" width={39} height={39} /></span>
          <strong>TrueAvail</strong>
        </div>
        <div className="signin-hero signup-hero">
          <p className="signin-pill">Your invitation is ready</p>
          <h1>One place for<br />all your time.</h1>
          <p>Create your private scheduling workspace, connect every calendar that matters, and share booking experiences that feel like you.</p>
          <div className="signup-benefits">
            <p><i><Check size={14} /></i><span><strong>Prevent calendar conflicts</strong><small>Availability is checked across all connected accounts.</small></span></p>
            <p><i><Check size={14} /></i><span><strong>Create distinct personas</strong><small>Share the right identity with every audience.</small></span></p>
            <p><i><Check size={14} /></i><span><strong>Stay in control</strong><small>Your event details remain private.</small></span></p>
          </div>
        </div>
        <small>Invitation-only access · Secure by design</small>
      </section>

      <section className="signin-login">
        <div className="signin-mobile-brand">
          <img className="brand-logo" src="/logo.png" alt="TrueAvail" width={190} height={46} />
        </div>
        <form className="signup-card" onSubmit={(event) => void submitInviteClaim(event)}>
          <a className="signup-back" href="/">
            <ArrowLeft size={15} /> Back to sign in
          </a>
          <p className={`signup-verified ${previewState === "live" ? "" : "pending"}`}>
            {previewState === "live" ? <Check size={13} /> : <LockKeyhole size={13} />}
            {previewState === "loading" ? "Checking invitation" : previewState === "live" ? "Invitation verified" : "Invitation unavailable"}
          </p>
          <h2>Create your account</h2>
          <p>You’ve been invited to start a TrueAvail workspace.</p>
          <div className="signup-name-row">
            <label>First name<input name="firstName" required placeholder="First name" disabled={previewState !== "live"} /></label>
            <label>Last name<input name="lastName" required placeholder="Last name" disabled={previewState !== "live"} /></label>
          </div>
          <label>Email address
            <div className="signup-locked-input">
              <input value={invitation?.email ?? ""} readOnly placeholder="Checking invitation" />
              <LockKeyhole size={15} />
            </div>
            <small>This invitation is tied to your email address.</small>
          </label>
          <label>Create password
            <div className="signup-password-input">
              <input name="password" required minLength={10} type={showPassword ? "text" : "password"} disabled={previewState !== "live"} value={password} onChange={(event) => setPassword(event.currentTarget.value)} />
              <button type="button" onClick={() => setShowPassword((current) => !current)}>{showPassword ? "Hide" : "Show"}</button>
            </div>
          </label>
          <div className={`signup-password-meter ${passwordIsStrong ? "strong" : ""}`}>
            <i /><i /><i /><i /><span>Strong password</span>
          </div>
          <ul className="signup-password-rules">
            <li>At least 10 characters</li>
            <li>Uppercase, lowercase, and a number</li>
          </ul>
          <label className="signup-consent">
            <input name="terms" type="checkbox" defaultChecked disabled={previewState !== "live"} />
            <span>I agree to the <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.</span>
          </label>
          {message ? <p className={submitState === "done" ? "signup-success" : "form-error"} aria-live="polite">{message}</p> : null}
          <button className="signup-submit" disabled={!canSubmit}>
            {submitState === "submitting" ? "Creating account" : "Create account"} <ArrowRight size={16} />
          </button>
          <small>Already have an account? <a href="/">Sign in</a></small>
        </form>
      </section>
    </main>
  );
}

function WorkspaceState({ title, detail, tone = "neutral" }: { title: string; detail: string; tone?: "neutral" | "error" }) {
  return (
    <section className={`panel span-3 workspace-state ${tone === "error" ? "workspace-state-error" : ""}`}>
      <div>
        <p className="eyebrow">Scheduling</p>
        <h2>{title}</h2>
      </div>
      <p>{detail}</p>
    </section>
  );
}

function NavButton({
  icon,
  label,
  view,
  activeView,
  onboardingRequired,
  onSelect
}: {
  icon: ReactNode;
  label: string;
  view: View;
  activeView: View;
  onboardingRequired: boolean;
  onSelect: (view: View) => void;
}) {
  const locked = onboardingRequired && view !== "onboarding" && view !== "users";
  return (
    <button type="button" className={activeView === view ? "active" : ""} disabled={locked} onClick={() => onSelect(locked ? "onboarding" : view)}>
      {icon}
      <span>{label}</span>
    </button>
  );
}

function AuthPanel({ authState, onAuthChange }: { authState: AuthState; onAuthChange: (state: AuthState) => void }) {
  const [expanded, setExpanded] = useState(authState.status !== "signed_in");
  const [message, setMessage] = useState("");

  async function signIn(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    const form = new globalThis.FormData(event.currentTarget);
    const email = String(form.get("email") ?? "");
    const password = String(form.get("password") ?? "");
    setMessage("");
    onAuthChange({ status: "signing_in" });
    try {
      const body = await cognitoRequest<CognitoInitiateAuthResponse>("AWSCognitoIdentityProviderService.InitiateAuth", {
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: cognitoClientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password
        }
      });
      if (body.ChallengeName === "NEW_PASSWORD_REQUIRED" && body.Session) {
        onAuthChange({ status: "new_password_required", email, session: body.Session });
        return;
      }
      completeAuth(body.AuthenticationResult?.IdToken, email, onAuthChange, setExpanded, body.AuthenticationResult?.AccessToken);
    } catch (cause) {
      const error = authErrorMessage(cause);
      setMessage(error);
      onAuthChange({ status: "error", message: error });
    }
  }

  async function setNewPassword(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    if (authState.status !== "new_password_required") {
      return;
    }
    const form = new globalThis.FormData(event.currentTarget);
    const password = String(form.get("newPassword") ?? "");
    setMessage("");
    onAuthChange({ status: "signing_in" });
    try {
      const body = await cognitoRequest<CognitoInitiateAuthResponse>("AWSCognitoIdentityProviderService.RespondToAuthChallenge", {
        ChallengeName: "NEW_PASSWORD_REQUIRED",
        ClientId: cognitoClientId,
        Session: authState.session,
        ChallengeResponses: {
          USERNAME: authState.email,
          NEW_PASSWORD: password
        }
      });
      completeAuth(body.AuthenticationResult?.IdToken, authState.email, onAuthChange, setExpanded, body.AuthenticationResult?.AccessToken);
    } catch (cause) {
      const error = authErrorMessage(cause);
      setMessage(error);
      onAuthChange({ status: "error", message: error });
    }
  }

  function signOut() {
    globalThis.localStorage.removeItem(authStorageKey);
    onAuthChange({ status: "signed_out" });
    setExpanded(true);
  }

  if (authState.status === "signed_in" && !expanded) {
    return (
      <button type="button" className="auth-chip" onClick={() => setExpanded(true)}>
        <ShieldCheck size={18} /> <span>{authState.email}</span>
      </button>
    );
  }

  return (
    <section className="auth-panel">
      {authState.status === "signed_in" ? (
        <>
          <div>
            <strong>{authState.email}</strong>
            <span>{authState.roles.join(", ") || "signed in"}</span>
          </div>
          <button type="button" onClick={() => setExpanded(false)}>Close</button>
          <button type="button" onClick={signOut}>Sign out</button>
        </>
      ) : authState.status === "new_password_required" ? (
        <form onSubmit={(event) => void setNewPassword(event)}>
          <label>New password<input name="newPassword" type="password" minLength={8} required /></label>
          <button type="submit">Set password</button>
        </form>
      ) : (
        <form onSubmit={(event) => void signIn(event)}>
          <label>Email address<input name="email" type="email" required defaultValue="froby@robyweb.com" /></label>
          <label>Password<input name="password" type="password" required /></label>
          <button type="submit" disabled={authState.status === "signing_in"}>
            {authState.status === "signing_in" ? "Signing in" : "Sign in"} <ArrowRight size={16} />
          </button>
        </form>
      )}
      {message ? <p>{message}</p> : null}
      {authState.status === "error" && !message ? <p>{authState.message}</p> : null}
    </section>
  );
}

function Overview({
  authState,
  adminData,
  persona,
  meeting,
  slots,
  personas: availablePersonas,
  meetingTypes: availableMeetingTypes,
  personaCount,
  connectionCount,
  onOpenView,
  onOpenBooking
}: {
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  persona: Persona;
  meeting: MeetingType;
  slots: TimeInterval[];
  personas: Persona[];
  meetingTypes: MeetingType[];
  personaCount: number;
  connectionCount: number;
  onOpenView: (view: View) => void;
  onOpenBooking: (bookingId: string) => void;
}) {
  const [bookingsState, setBookingsState] = useState<LoadState>("loading");
  const [overviewData, setOverviewData] = useState<AdminOverviewResponse | null>(null);
  const [weekSlots, setWeekSlots] = useState<number | null>(null);
  const [weekSlotsState, setWeekSlotsState] = useState<LoadState>("loading");
  const host = signedInHost(authState, adminData);
  const hostFirstName = splitDisplayName(host?.displayName ?? persona.displayName).firstName;
  const visiblePersonas = availablePersonas.length > 0 ? availablePersonas : [persona];
  const visibleMeetingTypes = availableMeetingTypes.length > 0 ? availableMeetingTypes : [meeting];
  const fallbackSetupItems = [
    { key: "profile", label: "Create your profile", done: Boolean(host) },
    { key: "persona", label: "Add a persona", done: personaCount > 0 },
    { key: "calendar", label: "Connect a calendar", done: connectionCount > 0 },
    { key: "availability", label: "Set your availability", done: slots.length > 0 }
  ];
  const setupItems = overviewData?.setup.items ?? fallbackSetupItems;
  const setupPercent = overviewData?.setup.percent ?? Math.round((setupItems.filter((item) => item.done).length / setupItems.length) * 100);
  const setupDash = `${setupPercent}, 100`;
  const [linkCopied, setLinkCopied] = useState(false);
  const publicPersona = visiblePersonas.find((item) => item.publicState === "active") ?? visiblePersonas[0] ?? persona;
  const publicLink = `${appOrigin}/${host?.slug ?? fredrick.slug}/${publicPersona.slug}`;
  const metrics = overviewData?.metrics;
  const today = overviewData?.today;
  const todayZone = today?.timeZone ?? host?.defaultTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";
  const greeting = timeOfDayGreeting(todayZone);
  const allDayToday = (today?.events ?? []).filter((item) => item.allDay);
  const agenda = (today?.events ?? []).filter((item) => !item.allDay).slice(0, 6);
  const todayPersonas = new Map(
    (today?.events ?? [])
      .filter((item) => item.personaId && item.personaColor)
      .map((item) => [item.personaId as string, { name: item.personaName ?? "", color: item.personaColor as string }])
  );
  const legend = visiblePersonas
    .filter((item) => todayPersonas.has(item.id))
    .map((item) => ({ id: item.id, name: item.name, color: item.accentColor }));
  const hasUnattributed = (today?.events ?? []).some((item) => !item.personaId);
  const todayFailed = today?.failedSources ?? [];
  const personaBookings = new Map((overviewData?.personaPerformance ?? []).map((item) => [item.personaId, item.bookingsThisMonth]));
  const personaMeetingTypeCounts = new Map((overviewData?.personaPerformance ?? []).map((item) => [item.personaId, item.meetingTypeCount]));
  const meetingTypesByPersona = new Map<string, MeetingType[]>();
  for (const item of visibleMeetingTypes) {
    meetingTypesByPersona.set(item.personaId, [...(meetingTypesByPersona.get(item.personaId) ?? []), item]);
  }

  // "This week" must actually mean the next seven days across every active meeting
  // type, not one meeting type's full booking horizon.
  useEffect(() => {
    const controller = new globalThis.AbortController();
    const hostSlug = host?.slug ?? fredrick.slug;
    const activePersonas = visiblePersonas.filter((item) => item.publicState !== "inactive");
    const targets = visibleMeetingTypes
      .filter((meetingType) => meetingType.active)
      .map((meetingType) => ({ meetingType, persona: activePersonas.find((item) => item.id === meetingType.personaId) }))
      .filter((entry): entry is { meetingType: MeetingType; persona: Persona } => Boolean(entry.persona));

    if (targets.length === 0) {
      setWeekSlots(0);
      setWeekSlotsState("live");
      return () => controller.abort();
    }

    const start = new Date();
    const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
    setWeekSlotsState("loading");

    Promise.all(
      targets.map(async (entry) => {
        const response = await fetch(
          `/v1/public/hosts/${hostSlug}/personas/${entry.persona.slug}/${entry.meetingType.slug}/slots?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`,
          { signal: controller.signal }
        );
        if (!response.ok) {
          throw new Error(`week_slots_${response.status}`);
        }
        const body = (await response.json()) as { slots: TimeInterval[] };
        return body.slots.length;
      })
    )
      .then((counts) => {
        setWeekSlots(counts.reduce((total, count) => total + count, 0));
        setWeekSlotsState("live");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setWeekSlots(null);
          setWeekSlotsState("error");
        }
      });

    return () => controller.abort();
  }, [host?.slug, visiblePersonas, visibleMeetingTypes]);

  useEffect(() => {
    if (authState.status !== "signed_in") {
      setBookingsState("error");
      setOverviewData(null);
      return;
    }
    const controller = new globalThis.AbortController();
    const start = new Date();
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setDate(end.getDate() + 90);
    const query = new globalThis.URLSearchParams({
      start: start.toISOString(),
      end: end.toISOString(),
      limit: "100"
    });
    if (host) {
      query.set("hostId", host.id);
    }
    setBookingsState("loading");
    apiFetch(`/v1/admin/overview?${query.toString()}`, {
      signal: controller.signal,
      headers: { authorization: `Bearer ${authState.idToken}` }
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`overview_${response.status}`);
        }
        return (await response.json()) as AdminOverviewResponse;
      })
      .then((body) => {
        setOverviewData(body);
        setBookingsState("live");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setOverviewData(null);
          setBookingsState("error");
        }
      });
    return () => controller.abort();
  }, [authState, host]);

  return (
    <div className="overview-screen">
      <section className="overview-welcome">
        <div>
          <span>Your day at a glance</span>
          <h2>{greeting}, {hostFirstName}.</h2>
          {bookingsState === "loading" && !overviewData ? (
            <SkeletonRegion label="Loading your day" className="skeleton-welcome">
              <Skeleton width="min(340px, 70%)" />
            </SkeletonRegion>
          ) : (
            <p>
              {today
                ? `You have ${today.summary.total} ${today.summary.total === 1 ? "event" : "events"} today, including ${today.summary.timebridge} TrueAvail ${today.summary.timebridge === 1 ? "booking" : "bookings"}.`
                : `You have ${metrics?.todayBookings ?? 0} ${metrics?.todayBookings === 1 ? "meeting" : "meetings"} today and ${weekSlots ?? 0} open booking ${weekSlots === 1 ? "slot" : "slots"} in the next seven days.`}
            </p>
          )}
        </div>
        <a href={publicLink} target="_blank" rel="noreferrer">
          <Eye size={17} /> View public page
        </a>
      </section>

      <section className="overview-metrics" aria-label="Workspace metrics">
        <OverviewMetric
          icon={<CalendarCheck size={21} />}
          tone="blue"
          label="Today's events"
          loading={bookingsState === "loading" && !overviewData}
          value={today ? today.summary.total : metrics?.todayBookings ?? 0}
          detail={
            bookingsState === "live"
              ? today
                ? `Across ${today.sources.length} ${today.sources.length === 1 ? "calendar" : "calendars"}`
                : "Confirmed bookings"
              : dataStateLabel(bookingsState, "Confirmed bookings")
          }
        />
        <OverviewMetric icon={<Users size={21} />} tone="violet" label="Bookings this month" loading={bookingsState === "loading" && !overviewData} value={metrics?.bookingsThisMonth ?? 0} detail={bookingsState === "live" ? "Across all personas" : dataStateLabel(bookingsState, "Across all personas")} />
        <OverviewMetric
          icon={<Clock size={21} />}
          tone="green"
          loading={bookingsState === "loading" && !overviewData}
          label={today ? "Open time today" : "Hours booked"}
          value={today ? `${formatHours(today.summary.openMinutes / 60)}h` : formatHours(metrics?.hoursBookedThisMonth ?? 0)}
          detail={
            today
              ? today.summary.nextOpen
                ? `Next opening at ${formatOpeningClock(today.summary.nextOpen.start, todayZone)}`
                : "Nothing bookable left today"
              : `Across ${metrics?.activePersonas ?? personaCount} ${(metrics?.activePersonas ?? personaCount) === 1 ? "persona" : "personas"}`
          }
        />
        <OverviewMetric icon={<Activity size={21} />} tone="orange" label="Booking slots" loading={weekSlotsState === "loading" && weekSlots === null} value={weekSlots ?? 0} detail={dataStateLabel(weekSlotsState, "Open in the next 7 days")} />
      </section>

      <div className="overview-grid">
        <section className="overview-card overview-agenda unified-agenda">
          <div className="unified-agenda-head">
            <div>
              <p className="eyebrow">{formatFullDate(today?.date ?? todayDateKey())}</p>
              <h2>Today's agenda</h2>
            </div>
            <button type="button" onClick={() => onOpenView("bookings")}>
              Open day view <ArrowRight size={15} />
            </button>
            <div className="overview-legend">
              {legend.map((item) => (
                <span key={item.id}>
                  <i style={{ background: item.color }} />
                  {item.name}
                </span>
              ))}
              {hasUnattributed ? (
                <span>
                  <i style={{ background: NEUTRAL_EVENT_COLOR }} />
                  No persona
                </span>
              ) : null}
            </div>
          </div>

          {/* An unreadable calendar and a free day look identical; say which this is. */}
          {todayFailed.length > 0 ? (
            <p className="schedule-warning" role="status">
              {todayFailed.length === 1 ? "One calendar" : `${todayFailed.length} calendars`} could not be read, so today may be
              incomplete: {todayFailed.map((item) => item.label).join(", ")}.
            </p>
          ) : null}

          {allDayToday.map((item) => (
            <div className="agenda-all-day" key={item.id}>
              <time>ALL DAY</time>
              <span>{item.title}</span>
              <em style={{ "--persona-accent": item.personaColor ?? NEUTRAL_EVENT_COLOR } as CSSProperties}>
                {item.personaName ?? "No persona"}
              </em>
            </div>
          ))}

          <div className="overview-agenda-list">
            {bookingsState === "loading" && !overviewData ? (
              <SkeletonRegion label="Loading today's agenda">
                <SkeletonRows count={4}>
                  {(index) => (
                    <div className="overview-event skeleton-event" key={index}>
                      <Skeleton width="52px" />
                      <Skeleton height="38px" radius="5px" />
                      <div className="skeleton-stack">
                        <Skeleton className="skeleton-line-lg" width={`${58 + index * 9}%`} />
                        <Skeleton className="skeleton-line-sm" width="45%" />
                      </div>
                      <Skeleton width="38px" height="38px" radius="10px" />
                    </div>
                  )}
                </SkeletonRows>
              </SkeletonRegion>
            ) : agenda.length > 0 ? (
              agenda.map((item) => {
                const isBooking = item.source === "timebridge";
                // Join belongs on a booking that actually has a meeting to join.
                const joinable = isBooking && Boolean(item.joinUrl);
                return (
                  <article
                    key={item.id}
                    className="overview-event"
                    style={{ "--event-accent": item.personaColor ?? NEUTRAL_EVENT_COLOR } as CSSProperties}
                  >
                    <time>{formatScheduleClock(item.start, todayZone)}</time>
                    <i />
                    <div>
                      <strong>{item.title}</strong>
                      <small>
                        <em>{item.personaName ?? "No persona"}</em>
                        <span>{isBooking ? "TrueAvail booking" : "Calendar event"}</span>
                        <span>
                          {item.calendarLabel
                            ? `${calendarProviderLabel(item.provider ?? "google")} · ${item.calendarLabel}`
                            : item.detail}
                        </span>
                      </small>
                    </div>
                    {joinable ? (
                      <a className="overview-join" href={item.joinUrl} target="_blank" rel="noreferrer">
                        <Video size={15} /> Join
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="overview-event-open"
                        aria-label={isBooking ? `Open ${item.title}` : `${item.title} — open the day view`}
                        onClick={() =>
                          isBooking && item.bookingId ? onOpenBooking(item.bookingId) : onOpenView("bookings")
                        }
                      >
                        <ChevronRight size={17} />
                      </button>
                    )}
                  </article>
                );
              })
            ) : (
              <div className="overview-empty">
                <strong>
                  {bookingsState === "live" ? "Nothing on your calendar today." : dataStateLabel(bookingsState, "Loading your day")}
                </strong>
                <p>
                  {overviewData?.nextBooking
                    ? `Next up: ${overviewData.nextBooking.guestName} on ${formatBookingWhen(overviewData.nextBooking.interval.start)}.`
                    : "Share a booking page or add more availability to start filling your agenda."}
                </p>
              </div>
            )}
          </div>
        </section>

        {setupPercent >= 100 ? (
          /* Every step is done, so the card stops reporting progress and starts
             offering the things a host actually comes here to do. */
          <section className="overview-card overview-quick-actions">
            <div className="overview-card-title">
              <div>
                <p className="eyebrow">Get booked</p>
                <h2>Your links</h2>
              </div>
            </div>
            <div className="quick-action-list">
              <button
                type="button"
                onClick={() => {
                  void globalThis.navigator.clipboard.writeText(publicLink);
                  setLinkCopied(true);
                  globalThis.setTimeout(() => setLinkCopied(false), 2000);
                }}
              >
                <i className="blue"><Copy size={17} /></i>
                <span>
                  <strong>{linkCopied ? "Booking link copied" : "Copy booking link"}</strong>
                  <small>{displayUrl(publicLink)}</small>
                </span>
                <ChevronRight size={16} />
              </button>
              <a href={publicLink} target="_blank" rel="noreferrer">
                <i className="violet"><Eye size={17} /></i>
                <span>
                  <strong>Preview public page</strong>
                  <small>See what a guest sees</small>
                </span>
                <ChevronRight size={16} />
              </a>
              <button type="button" onClick={() => onOpenView("personas")}>
                <i className="green"><Plus size={17} /></i>
                <span>
                  <strong>Add a meeting type</strong>
                  <small>Offer another kind of conversation</small>
                </span>
                <ChevronRight size={16} />
              </button>
              <button type="button" onClick={() => onOpenView("calendars")}>
                <i className="orange"><CalendarCheck size={17} /></i>
                <span>
                  <strong>Connect another calendar</strong>
                  <small>More conflicts checked before a slot is offered</small>
                </span>
                <ChevronRight size={16} />
              </button>
            </div>
          </section>
        ) : (
        <section className="overview-card overview-setup">
          <div className="overview-card-title">
            <div>
              <p className="eyebrow">Get the most from TrueAvail</p>
              <h2>Setup progress</h2>
            </div>
          </div>
          <div className="overview-ring" style={{ "--setup-dash": setupDash } as CSSProperties}>
            <svg viewBox="0 0 42 42" role="img" aria-label={`${setupPercent}% complete`}>
              <circle cx="21" cy="21" r="16" />
              <circle cx="21" cy="21" r="16" />
            </svg>
            <span><strong>{setupPercent}%</strong><small>complete</small></span>
          </div>
          <ul className="overview-checklist">
            {setupItems.map((item, index) => (
              <li key={item.label}>
                <span>{item.done ? <Check size={14} /> : index + 1}</span>
                {item.label}
              </li>
            ))}
          </ul>
          <button type="button" onClick={() => onOpenView("availability")}>
            Finish setup <ArrowRight size={15} />
          </button>
        </section>
        )}
      </div>

      <section className="overview-card overview-personas">
        <div className="overview-card-title">
          <div>
            <p className="eyebrow">Your identities</p>
            <h2>Persona performance</h2>
          </div>
          <button type="button" onClick={() => onOpenView("personas")}>
            Manage personas <ArrowRight size={15} />
          </button>
        </div>
        <div className="overview-persona-grid">
          {visiblePersonas.map((item) => {
            const personaMeetings = meetingTypesByPersona.get(item.id) ?? [];
            const meetingTypeCount = personaMeetingTypeCounts.get(item.id) ?? personaMeetings.length;
            const bookingCount = personaBookings.get(item.id) ?? 0;
            return (
              <article key={item.id} className="overview-persona-row" style={{ "--persona-accent": item.accentColor } as CSSProperties}>
                <Avatar className="overview-persona-avatar" src={item.avatarUrl} initials={personaInitials(item)} />
                <div>
                  <strong>{item.name}</strong>
                  <small>{item.headline}</small>
                </div>
                {bookingsState === "loading" && !overviewData ? (
                  <>
                    <Skeleton className="skeleton-line-sm" width="48px" />
                    <Skeleton className="skeleton-line-sm" width="62px" />
                  </>
                ) : (
                  <>
                    <em>{meetingTypeCount} {meetingTypeCount === 1 ? "type" : "types"}</em>
                    <em>{bookingCount} {bookingCount === 1 ? "booking" : "bookings"}</em>
                  </>
                )}
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function OverviewMetric({
  icon,
  tone,
  label,
  value,
  detail,
  loading
}: {
  icon: ReactNode;
  tone: "blue" | "violet" | "green" | "orange";
  label: string;
  value: number | string;
  detail: string;
  loading?: boolean;
}) {
  return (
    <article className={`overview-metric ${tone}`} aria-busy={loading ? "true" : undefined}>
      <span>{icon}</span>
      <div>
        <small>{label}</small>
        {loading ? (
          <>
            <Skeleton className="skeleton-metric-value" width="52px" />
            <Skeleton className="skeleton-line-sm skeleton-metric-detail" width="80%" />
          </>
        ) : (
          <>
            <strong>{value}</strong>
            <p>{detail}</p>
          </>
        )}
      </div>
    </article>
  );
}

function ProfileView({
  authState,
  adminData,
  onAdminDataChange
}: {
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  onAdminDataChange: (data: AdminStatusResponse | null) => void;
}) {
  const [status, setStatus] = useState<OnboardingStatus>("idle");
  const [message, setMessage] = useState("");
  const [photoState, setPhotoState] = useState<"idle" | "uploading">("idle");
  const [cropping, setCropping] = useState<globalThis.File | null>(null);
  const host = signedInHost(authState, adminData);
  const isPlatformAdmin = authState.status === "signed_in" && authState.roles.includes("platform_admin");
  const nameParts = splitDisplayName(host?.displayName ?? "");

  /** Upload then save, or save an empty URL to remove the photo. */
  async function saveHostPhoto(file: globalThis.Blob | null) {
    if (authState.status !== "signed_in" || !host) {
      return;
    }
    setPhotoState("uploading");
    setMessage("");
    try {
      const avatarUrl = file ? await uploadAvatar(authState.idToken, "host", host.id, file) : "";
      const response = await apiFetch(`/v1/admin/hosts/${host.id}`, {
        method: "PUT",
        headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
        body: JSON.stringify({ avatarUrl })
      });
      const body = (await response.json().catch(() => ({}))) as HostUpdateResponse & { error?: { message?: string; code?: string } };
      if (!response.ok) {
        throw new Error(body.error?.message ?? body.error?.code ?? `host_update_${response.status}`);
      }
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setStatus("done");
      setMessage(file ? "Photo updated." : "Photo removed.");
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "The photo could not be saved.");
    } finally {
      setPhotoState("idle");
    }
  }

  async function updateHost(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    if (authState.status !== "signed_in" || !host) {
      setStatus("error");
      setMessage("Sign in as a host before editing your profile.");
      return;
    }

    const form = new globalThis.FormData(event.currentTarget);
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch(`/v1/admin/hosts/${host.id}`, {
        method: "PUT",
        headers: {
          authorization: `Bearer ${authState.idToken}`,
          "content-type": "application/json"
        },
        body: JSON.stringify({
          displayName: [String(form.get("firstName") ?? ""), String(form.get("lastName") ?? "")]
            .map((part) => part.trim())
            .filter(Boolean)
            .join(" "),
          defaultTimeZone: String(form.get("defaultTimeZone") ?? ""),
          notificationEmail: String(form.get("notificationEmail") ?? "")
        })
      });
      const body = (await response.json().catch(() => ({}))) as HostUpdateResponse & { error?: { details?: string[]; message?: string; code?: string } };
      if (!response.ok) {
        throw new Error(body.error?.details?.join(", ") ?? body.error?.message ?? body.error?.code ?? `host_update_${response.status}`);
      }
      if (adminData) {
        onAdminDataChange({
          ...adminData,
          hosts: adminData.hosts.map((item) => item.id === body.host.id ? body.host : item)
        });
      }
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setStatus("done");
      setMessage("Profile saved.");
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Profile update failed.");
    }
  }

  return (
    <div className="profile-screen">
      <section className="profile-title">
        <p className="eyebrow">Account</p>
        <h2>Your profile</h2>
        <p>The identity used inside your private scheduling workspace.</p>
      </section>

      {host ? (
        <section className="profile-summary-card">
          {cropping ? (
            <AvatarCropDialog
              file={cropping}
              title="Crop your profile photo"
              onCancel={() => setCropping(null)}
              onConfirm={(image) => {
                setCropping(null);
                void saveHostPhoto(image);
              }}
            />
          ) : null}
          <Avatar className="profile-summary-avatar" src={host.avatarUrl} initials={hostInitials(host.displayName)} />
          <div>
            <h3>{host.displayName}</h3>
            <p>{host.defaultTimeZone} · English (US)</p>
            <em><Check size={13} /> Profile complete</em>
          </div>
          <div className="avatar-actions">
            <label className="avatar-upload">
              <input
                type="file"
                accept={AVATAR_ACCEPT}
                disabled={photoState === "uploading"}
                onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  event.currentTarget.value = "";
                  if (file) {
                    setCropping(file);
                  }
                }}
              />
              <span>{photoState === "uploading" ? "Uploading" : host.avatarUrl ? "Change photo" : "Add photo"}</span>
            </label>
            {host.avatarUrl ? (
              <button type="button" className="avatar-remove" disabled={photoState === "uploading"} onClick={() => void saveHostPhoto(null)}>
                Remove
              </button>
            ) : null}
          </div>
        </section>
      ) : (
        <p className="empty-state">Sign in to manage host profile settings.</p>
      )}

      {message ? <p className={status === "error" ? "form-error" : "inline-note"} aria-live="polite">{message}</p> : null}

      {host ? (
        <section className="profile-details-card">
          <div>
            <h2>Personal details</h2>
            <p>Used for account notifications and internal settings.</p>
          </div>
          <form key={host.id} className="profile-form" onSubmit={(event) => void updateHost(event)}>
            <label>First name<input name="firstName" required defaultValue={nameParts.firstName} /></label>
            <label>Last name<input name="lastName" required defaultValue={nameParts.lastName} /></label>
            <label className="profile-span-2">Account email<input value={authState.status === "signed_in" ? authState.email : ""} readOnly /></label>
            <label className="profile-span-2">
              Booking notifications
              <input
                name="notificationEmail"
                type="email"
                defaultValue={host.notificationEmail ?? ""}
                placeholder={authState.status === "signed_in" ? authState.email : "you@example.com"}
              />
              <small>Where to email you when someone books, cancels or reschedules. Leave empty to receive nothing.</small>
            </label>
            <label>Time zone<TimeZoneSelect name="defaultTimeZone" value={host.defaultTimeZone} /></label>
            <label>Language<select defaultValue="English (US)"><option>English (US)</option></select></label>
            <div className="profile-actions">
              <button type="reset">Cancel</button>
              <button disabled={authState.status !== "signed_in" || status === "submitting"}>
                {status === "submitting" ? "Saving" : "Save changes"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <AccountSecurityCard authState={authState} />

      {isPlatformAdmin ? (
        <section className="profile-admin-card">
          <details>
            <summary>
              <span>
                <strong>Platform admin</strong>
                <small>Host directory and account status controls</small>
              </span>
              <em>{adminData?.hosts.length ?? 0} {adminData?.hosts.length === 1 ? "host" : "hosts"}</em>
            </summary>
            <HostDirectory authState={authState} adminData={adminData} onAdminDataChange={onAdminDataChange} embedded />
          </details>
        </section>
      ) : null}
    </div>
  );
}

function splitDisplayName(value: string): { firstName: string; lastName: string } {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { firstName: parts[0] ?? "", lastName: "" };
  }
  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" ")
  };
}

/**
 * Everything here is read from Cognito or the signed-in token. The card used to
 * claim "Password updated 18 days ago · MFA recommended", which was invented: no
 * such data was ever fetched or stored.
 */
function AccountSecurityCard({ authState }: { authState: AuthState }) {
  const [open, setOpen] = useState(false);
  const [account, setAccount] = useState<CognitoUserResponse | null>(null);
  const [accountState, setAccountState] = useState<LoadState>("loading");
  const [status, setStatus] = useState<"idle" | "saving" | "done" | "error">("idle");
  const [message, setMessage] = useState("");

  const accessToken = authState.status === "signed_in" ? authState.accessToken : undefined;
  const sessionExpiry =
    authState.status === "signed_in" ? Number(decodeJwtPayload(authState.idToken).exp ?? 0) * 1000 : 0;

  useEffect(() => {
    if (!open || !accessToken) {
      return;
    }
    setAccountState("loading");
    cognitoRequest<CognitoUserResponse>("AWSCognitoIdentityProviderService.GetUser", { AccessToken: accessToken })
      .then((body) => {
        setAccount(body);
        setAccountState("live");
      })
      .catch(() => setAccountState("error"));
  }, [open, accessToken]);

  async function changePassword(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    if (!accessToken) {
      return;
    }
    const form = event.currentTarget;
    const data = new globalThis.FormData(form);
    const proposed = String(data.get("newPassword") ?? "");
    if (proposed !== String(data.get("confirmPassword") ?? "")) {
      setStatus("error");
      setMessage("The new passwords do not match.");
      return;
    }
    setStatus("saving");
    setMessage("");
    try {
      await cognitoRequest("AWSCognitoIdentityProviderService.ChangePassword", {
        AccessToken: accessToken,
        PreviousPassword: String(data.get("currentPassword") ?? ""),
        ProposedPassword: proposed
      });
      // The form is reset immediately so the values do not sit in the DOM.
      form.reset();
      setStatus("done");
      setMessage("Password changed.");
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "The password could not be changed.");
    }
  }

  async function signOutEverywhere() {
    if (!accessToken) {
      return;
    }
    setStatus("saving");
    setMessage("");
    try {
      await cognitoRequest("AWSCognitoIdentityProviderService.GlobalSignOut", { AccessToken: accessToken });
      globalThis.localStorage.removeItem(authStorageKey);
      globalThis.dispatchEvent(new globalThis.CustomEvent(SESSION_EXPIRED_EVENT));
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Sessions could not be revoked.");
    }
  }

  const mfa = account?.PreferredMfaSetting ?? (account?.UserMFASettingList ?? [])[0];

  return (
    <section className={open ? "profile-security-card is-open" : "profile-security-card"}>
      <span><ShieldCheck size={21} /></span>
      <div>
        <h3>Account security</h3>
        <p>
          {authState.status === "signed_in" ? authState.email : "Not signed in"}
          {sessionExpiry > 0 ? ` · Session valid until ${formatTime(new Date(sessionExpiry).toISOString())}` : ""}
        </p>
      </div>
      <button type="button" onClick={() => setOpen((current) => !current)}>
        {open ? "Close" : "Manage security"}
      </button>

      {open ? (
        <div className="security-panel">
          {!accessToken ? (
            <p className="inline-note">
              Sign out and back in to manage security here. Your current session was started before the app kept the token these
              operations need.
            </p>
          ) : (
            <>
              <div className="security-facts">
                <div>
                  <span>Multi-factor authentication</span>
                  <strong>
                    {accountState === "loading" ? "Checking" : accountState === "error" ? "Unavailable" : mfa ? mfa.replace(/_MFA$/, "") : "Not enabled"}
                  </strong>
                </div>
                <div>
                  <span>Sign-in email</span>
                  <strong>{account?.UserAttributes?.find((attribute) => attribute.Name === "email")?.Value ?? (authState.status === "signed_in" ? authState.email : "")}</strong>
                </div>
              </div>

              <form className="security-form" onSubmit={(event) => void changePassword(event)}>
                <h4>Change password</h4>
                <label>Current password<input name="currentPassword" type="password" required autoComplete="current-password" /></label>
                <label>New password<input name="newPassword" type="password" required minLength={8} autoComplete="new-password" /></label>
                <label>Confirm new password<input name="confirmPassword" type="password" required minLength={8} autoComplete="new-password" /></label>
                <button className="confirm" disabled={status === "saving"}>{status === "saving" ? "Saving" : "Change password"}</button>
              </form>

              <div className="security-danger">
                <div>
                  <strong>Sign out everywhere</strong>
                  <small>Ends this session and every other device signed in as you.</small>
                </div>
                <button type="button" className="danger" disabled={status === "saving"} onClick={() => void signOutEverywhere()}>
                  Sign out everywhere
                </button>
              </div>

              {message ? <p className={status === "error" ? "form-error" : "inline-note"} aria-live="polite">{message}</p> : null}
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}

function HostDirectory({
  authState,
  adminData,
  onAdminDataChange,
  embedded = false
}: {
  authState: Extract<AuthState, { status: "signed_in" }>;
  adminData: AdminStatusResponse | null;
  onAdminDataChange: (data: AdminStatusResponse | null) => void;
  embedded?: boolean;
}) {
  const [pendingHostId, setPendingHostId] = useState<string | null>(null);
  const [message, setMessage] = useState("");

  async function setHostStatus(host: Host) {
    const action = host.status === "suspended" ? "reactivate" : "suspend";
    setPendingHostId(host.id);
    setMessage("");
    try {
      const response = await apiFetch(`/v1/admin/hosts/${host.id}/${action}`, {
        method: "POST",
        headers: { authorization: `Bearer ${authState.idToken}` }
      });
      const body = (await response.json().catch(() => ({}))) as HostUpdateResponse & { error?: { message?: string; code?: string } };
      if (!response.ok) {
        throw new Error(body.error?.message ?? body.error?.code ?? `host_${action}_${response.status}`);
      }
      if (adminData) {
        onAdminDataChange({
          ...adminData,
          hosts: adminData.hosts.map((item) => item.id === body.host.id ? body.host : item)
        });
      }
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setMessage(action === "suspend" ? "Host suspended." : "Host reactivated.");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Host status update failed.");
    } finally {
      setPendingHostId(null);
    }
  }

  return (
    <section className={embedded ? "host-directory-embedded" : "panel span-2"}>
      {!embedded ? (
        <div className="section-heading">
          <div>
            <p className="eyebrow">Platform admin</p>
            <h2>Host directory</h2>
          </div>
          <div className="inline-status"><Users size={16} /> {adminData?.hosts.length ?? 0} hosts</div>
        </div>
      ) : null}
      {message ? <p className={message.includes("failed") || message.includes("_") ? "form-error" : "inline-note"} aria-live="polite">{message}</p> : null}
      <div className="status-list host-directory">
        {(adminData?.hosts ?? []).map((host) => {
          const personaCount = adminData?.personas.filter((persona) => persona.hostId === host.id).length ?? 0;
          const connectionCount = adminData?.calendarConnections.filter((connection) => connection.hostId === host.id).length ?? 0;
          const suspended = host.status === "suspended";
          return (
            <div className="status-row host-directory-row" key={host.id}>
              <div>
                <strong>{host.displayName}</strong>
                <span>{host.slug} · {personaCount} personas · {connectionCount} calendars</span>
              </div>
              <span className={suspended ? "danger-label" : "success-label"}>{suspended ? "Suspended" : "Active"}</span>
              <button type="button" onClick={() => void setHostStatus(host)} disabled={pendingHostId === host.id}>
                {suspended ? <Check size={16} /> : <Lock size={16} />}
                {pendingHostId === host.id ? "Updating" : suspended ? "Reactivate" : "Suspend"}
              </button>
            </div>
          );
        })}
        {!adminData?.hosts.length ? <p className="empty-state">No hosts found.</p> : null}
      </div>
    </section>
  );
}

function PersonasView({
  personas,
  meetingTypes,
  authState,
  adminData,
  onAdminDataChange,
  onEditPersona,
  onCreatePersona,
  onSelect
}: {
  personas: Persona[];
  meetingTypes: MeetingType[];
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  onAdminDataChange: (data: AdminStatusResponse | null) => void;
  onSelect: (id: string) => void;
  onEditPersona: (personaId: string) => void;
  onCreatePersona: () => void;
}) {
  const [pendingDeletePersona, setPendingDeletePersona] = useState<Persona | null>(null);
  const [status, setStatus] = useState<OnboardingStatus>("idle");
  const [message, setMessage] = useState("");
  const host = signedInHost(authState, adminData);
  const shortUrls = useShortLinks(
    authState,
    host ? personas.map((persona) => ({ targetUrl: `${appOrigin}/${host.slug}/${persona.slug}`, source: `persona:${persona.slug}` })) : []
  );



  async function setPersonaVisibility(persona: Persona, publicState: Persona["publicState"]) {
    if (authState.status !== "signed_in") {
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch(`/v1/admin/personas/${encodeURIComponent(persona.id)}`, {
        method: "PUT",
        headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
        body: JSON.stringify({ publicState })
      });
      const body = (await response.json().catch(() => ({}))) as { error?: { code?: string; message?: string } };
      if (!response.ok) {
        throw new Error(body.error?.message ?? body.error?.code ?? `persona_update_${response.status}`);
      }
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setStatus("done");
      setMessage(publicState === "active" ? `${persona.name} is live again.` : `${persona.name} is hidden from the public page.`);
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Could not update the persona.");
    }
  }

  async function deletePersona(persona: Persona) {
    if (authState.status !== "signed_in") {
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch(`/v1/admin/personas/${encodeURIComponent(persona.id)}`, {
        method: "DELETE",
        headers: { authorization: `Bearer ${authState.idToken}` }
      });
      const body = (await response.json().catch(() => ({}))) as {
        meetingTypesDeleted?: number;
        error?: { code?: string; message?: string };
      };
      if (!response.ok) {
        throw new Error(body.error?.message ?? body.error?.code ?? `persona_delete_${response.status}`);
      }
      setPendingDeletePersona(null);
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setStatus("done");
      setMessage(`Deleted ${persona.name}${body.meetingTypesDeleted ? ` and ${body.meetingTypesDeleted} meeting type(s)` : ""}.`);
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Could not delete the persona.");
    }
  }

  return (
    <div className="personas-screen">
      <section className="personas-title">
        <div>
          <p className="eyebrow">Public identities</p>
          <h2>Personas</h2>
          <p>Give every part of your life its own booking experience, without creating calendar conflicts.</p>
        </div>
        <button type="button" onClick={onCreatePersona}>
          <Plus size={17} /> New persona
        </button>
      </section>

      {message ? <p className={status === "error" ? "form-error" : "inline-note"} aria-live="polite">{message}</p> : null}

      {pendingDeletePersona ? (
        <section className="persona-confirm" role="alertdialog" aria-labelledby="persona-delete-title">
          <div>
            <h3 id="persona-delete-title">Delete {pendingDeletePersona.name}?</h3>
            <p>
              This removes the persona and its {meetingTypes.filter((meeting) => meeting.personaId === pendingDeletePersona.id).length} meeting
              type(s) permanently. The booking link <code>{displayUrl(`${appOrigin}/${host?.slug ?? "fred"}/${pendingDeletePersona.slug}`)}</code> will stop working.
            </p>
            <p className="persona-confirm-hint">
              Prefer to keep past bookings and the link intact? Choose <strong>Hide from public page</strong> instead — it takes the persona
              offline without deleting anything.
            </p>
          </div>
          <div className="persona-confirm-actions">
            <button type="button" onClick={() => setPendingDeletePersona(null)}>Keep persona</button>
            <button
              type="button"
              className="danger"
              disabled={status === "submitting"}
              onClick={() => void deletePersona(pendingDeletePersona)}
            >
              {status === "submitting" ? "Deleting" : "Delete permanently"}
            </button>
          </div>
        </section>
      ) : null}

      <section className="persona-card-grid" aria-label="Personas">
        {personas.map((persona) => {
          const meetings = meetingTypes.filter((meeting) => meeting.personaId === persona.id);
          const bookingCount: number = 0;
          const publicUrl = `${appOrigin}/${host?.slug ?? "fred"}/${persona.slug}`;
          const shareUrl = shortUrls[publicUrl] ?? publicUrl;
          return (
            <article key={persona.id} className="persona-card" style={{ "--persona-accent": persona.accentColor } as CSSProperties}>
              <div className="persona-cover">
                <Avatar className="persona-card-avatar" src={persona.avatarUrl} initials={personaInitials(persona)} />
                <em>{persona.publicState === "active" ? "Live" : persona.publicState === "private_link" ? "Private" : "Draft"}</em>
                <ActionMenu
                  label={`More options for ${persona.name}`}
                  items={[
                    {
                      label: "Edit persona",
                      onSelect: () => {
                        onSelect(persona.id);
                        onEditPersona(persona.id);
                      }
                    },
                    {
                      label: "Copy booking link",
                      onSelect: () => {
                        void globalThis.navigator.clipboard.writeText(shareUrl);
                        setStatus("done");
                        setMessage("Booking link copied.");
                      }
                    },
                    { label: "Open public page", href: publicUrl },
                    {
                      label: persona.publicState === "active" ? "Hide from public page" : "Make live",
                      onSelect: () => void setPersonaVisibility(persona, persona.publicState === "active" ? "inactive" : "active")
                    },
                    {
                      label: "Delete persona",
                      danger: true,
                      onSelect: () => setPendingDeletePersona(persona)
                    }
                  ]}
                />
                <h3>{persona.name}</h3>
                <p>{persona.headline}</p>
              </div>
              <div className="persona-card-body">
                <div className="persona-url">
                  <Globe2 size={15} />
                  <span>{displayUrl(shareUrl)}</span>
                  <button type="button" aria-label={`Copy ${persona.name} link`} onClick={() => void globalThis.navigator.clipboard.writeText(shareUrl)}>
                    <Copy size={14} />
                  </button>
                </div>
                <p><strong>{meetings.length}</strong> {meetings.length === 1 ? "meeting type" : "meeting types"} · <strong>{bookingCount}</strong> {bookingCount === 1 ? "booking" : "bookings"} this month</p>
                <div>
                  <button type="button" onClick={() => {
                    onSelect(persona.id);
                    onEditPersona(persona.id);
                  }}>Edit</button>
                  <a href={publicUrl} target="_blank" rel="noreferrer"><Eye size={15} /> Preview</a>
                </div>
              </div>
            </article>
          );
        })}
      </section>

      <section className="persona-callout">
        <span><Sparkles size={20} /></span>
        <div>
          <strong>Make each persona feel intentional</strong>
          <p>Use a distinct description, color, and meeting experience for every audience.</p>
        </div>
      </section>


    </div>
  );
}

type PersonaEditorSection = "profile" | "meetings" | "scheduling" | "messages";

const PERSONA_ACCENTS = ["#635BFF", "#315BEA", "#0F9F75", "#E24D6D", "#F59E0B"];
const PERSONA_EDITOR_FORM = "persona-editor-form";

const CREATE_STEPS: Array<{ label: string; detail: string }> = [
  { label: "Identity & brand", detail: "Public profile" },
  { label: "Scheduling", detail: "Time and location" },
  { label: "First meeting type", detail: "Bookable conversation" },
  { label: "Review & publish", detail: "Final check" }
];

/**
 * The working window an administrator chose on the create-host form, expressed as one
 * more entry in the preset list so the wizard has nothing special to know about it.
 * Returns nothing when the host signed themselves up, or when the window is already
 * one of the standing presets.
 */
function invitedAvailabilityPreset(host: Host | undefined): { id: string; label: string; startHour: number; endHour: number } | undefined {
  const window = host?.invitedDefaults?.workingHours;
  if (!window) {
    return undefined;
  }
  const startHour = Math.floor(window.startMinute / 60);
  const endHour = Math.ceil(window.endMinute / 60);
  if (AVAILABILITY_PRESETS.some((preset) => preset.startHour === startHour && preset.endHour === endHour)) {
    return undefined;
  }
  return {
    id: "invited",
    label: `Set for you \u00b7 ${formatHourLabel(startHour)}\u2013${formatHourLabel(endHour)}`,
    startHour,
    endHour
  };
}

const AVAILABILITY_PRESETS: Array<{ id: string; label: string; startHour: number; endHour: number }> = [
  { id: "standard", label: "Standard workweek · 9:00 AM–5:00 PM", startHour: 9, endHour: 17 },
  { id: "mornings", label: "Mornings only · 8:00 AM–12:00 PM", startHour: 8, endHour: 12 },
  { id: "afternoons", label: "Afternoons only · 12:00 PM–5:00 PM", startHour: 12, endHour: 17 },
  { id: "extended", label: "Extended day · 8:00 AM–6:00 PM", startHour: 8, endHour: 18 }
];

interface PersonaDraft {
  name: string;
  displayName: string;
  headline: string;
  organization: string;
  bio: string;
  slug: string;
  slugTouched: boolean;
  accentColor: string;
  defaultTimeZone: string;
  locationKind: string;
  locationUrl: string;
  locationInstructions: string;
  destinationCalendarResourceId: string;
  availabilityPreset: string;
  minimumNoticeMinutes: number;
  bufferMinutes: number;
  meetingTitle: string;
  meetingSlug: string;
  meetingSlugTouched: boolean;
  durationMinutes: number;
  meetingDescription: string;
  confirmationCopy: string;
  privacyUrl: string;
  termsUrl: string;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

function CreatePersonaView({
  authState,
  adminData,
  onAdminDataChange,
  onExit,
  onCreated
}: {
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  onAdminDataChange: (data: AdminStatusResponse | null) => void;
  onExit: () => void;
  onCreated: (personaId: string) => void;
}) {
  const host = signedInHost(authState, adminData);
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState<OnboardingStatus>("idle");
  const [message, setMessage] = useState("");
  const [draft, setDraft] = useState<PersonaDraft>(() => ({
    name: "",
    displayName: host?.displayName ?? "",
    headline: "",
    organization: "",
    bio: "",
    slug: "",
    slugTouched: false,
    accentColor: "#315BEA",
    defaultTimeZone: host?.defaultTimeZone ?? "America/Los_Angeles",
    locationKind: "google_meet",
    locationUrl: "",
    locationInstructions: "",
    destinationCalendarResourceId: "",
    availabilityPreset: "standard",
    minimumNoticeMinutes: 720,
    bufferMinutes: 15,
    meetingTitle: "",
    meetingSlug: "",
    meetingSlugTouched: false,
    durationMinutes: 30,
    meetingDescription: "",
    confirmationCopy: "Your meeting is confirmed. I look forward to speaking with you.",
    privacyUrl: "",
    termsUrl: ""
  }));

  function set(patch: Partial<PersonaDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  // The URL follows the name until the host edits it themselves.
  function setName(value: string) {
    setDraft((current) => ({ ...current, name: value, ...(current.slugTouched ? {} : { slug: slugify(value) }) }));
  }

  function setMeetingTitle(value: string) {
    setDraft((current) => ({ ...current, meetingTitle: value, ...(current.meetingSlugTouched ? {} : { meetingSlug: slugify(value) }) }));
  }

  const slugValid = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/.test(draft.slug);
  const meetingSlugValid = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/.test(draft.meetingSlug);
  const identityDone = Boolean(draft.name.trim() && draft.headline.trim() && draft.displayName.trim() && draft.bio.trim() && slugValid);
  const meetingDone = Boolean(draft.meetingTitle.trim() && meetingSlugValid && draft.meetingDescription.trim());
  const connections = (adminData?.calendarConnections ?? []).filter((item) => item.status === "connected");
  const destinationOptions = destinationCalendarOptions(adminData);
  const defaultDestination = defaultDestinationFor(adminData, draft.locationKind);
  const blockingCalendars = (adminData?.calendarResources ?? []).filter((item) => item.blocksAvailability);
  const calendarsProtected = connections.length > 0;

  const checklist = [
    {
      key: "identity",
      title: "Public identity",
      copy: identityDone ? `${draft.name} · ${appOrigin.replace(/^https?:\/\//, "")}/${host?.slug ?? "fred"}/${draft.slug}` : "Add a name, headline, bio and URL",
      done: identityDone,
      step: 1
    },
    {
      key: "scheduling",
      title: "Scheduling",
      copy: `${draft.defaultTimeZone} · ${locationKindLabel(draft.locationKind)} · ${
        destinationOptions.find((option) => option.id === draft.destinationCalendarResourceId)?.label ??
        (defaultDestination ? `${defaultDestination.label} (default)` : "no booking destination")
      }`,
      done: Boolean(draft.destinationCalendarResourceId || defaultDestination),
      step: 2
    },
    {
      key: "meeting",
      title: "Meeting type",
      copy: meetingDone ? `${draft.meetingTitle} · ${draft.durationMinutes} minutes` : "Add a meeting title, URL and description",
      done: meetingDone,
      step: 3
    },
    {
      key: "calendars",
      title: "Calendar protection",
      copy: calendarsProtected
        ? `${connections.length} ${connections.length === 1 ? "account" : "accounts"} · ${blockingCalendars.length} ${blockingCalendars.length === 1 ? "calendar" : "calendars"}`
        : "No calendars connected — availability will not be protected",
      done: calendarsProtected,
      step: 2
    }
  ];
  const outstanding = checklist.filter((item) => !item.done).length;

  async function createPersona(publish: boolean) {
    if (authState.status !== "signed_in" || !host) {
      setStatus("error");
      setMessage("Sign in as a host before creating personas.");
      return;
    }
    if (!identityDone) {
      setStep(1);
      setStatus("error");
      setMessage("A persona needs a name, display name, headline, bio and a valid URL.");
      return;
    }
    if (!meetingDone) {
      setStep(3);
      setStatus("error");
      setMessage("A persona needs one bookable meeting type before it can be created.");
      return;
    }

    const preset = AVAILABILITY_PRESETS.find((item) => item.id === draft.availabilityPreset) ?? AVAILABILITY_PRESETS[0]!;
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch("/v1/admin/personas", {
        method: "POST",
        headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
        body: JSON.stringify({
          hostId: host.id,
          hostSlug: host.slug,
          slug: draft.slug,
          name: draft.name,
          displayName: draft.displayName,
          headline: draft.headline,
          organization: draft.organization,
          bio: draft.bio,
          accentColor: draft.accentColor,
          defaultTimeZone: draft.defaultTimeZone,
          locationKind: draft.locationKind,
          locationUrl: draft.locationUrl,
          locationInstructions: draft.locationInstructions,
          destinationCalendarResourceId: draft.destinationCalendarResourceId,
          // A draft persona must not be reachable, so it is created hidden.
          publicState: publish ? "active" : "inactive",
          confirmationCopy: draft.confirmationCopy,
          privacyUrl: draft.privacyUrl,
          termsUrl: draft.termsUrl,
          meetingSlug: draft.meetingSlug,
          meetingTitle: draft.meetingTitle,
          meetingDescription: draft.meetingDescription,
          durationMinutes: draft.durationMinutes,
          minimumNoticeMinutes: draft.minimumNoticeMinutes,
          preBufferMinutes: draft.bufferMinutes,
          postBufferMinutes: draft.bufferMinutes,
          startHour: preset.startHour,
          endHour: preset.endHour
        })
      });
      const body = (await response.json().catch(() => ({}))) as PersonaSetupResponse & { error?: { details?: string[]; message?: string; code?: string } };
      if (!response.ok) {
        throw new Error(body.error?.details?.join(", ") ?? body.error?.message ?? body.error?.code ?? `persona_create_${response.status}`);
      }
      await refreshAdminData(authState.idToken, onAdminDataChange);
      onCreated(body.persona.id);
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Persona creation failed.");
    }
  }

  const urlPrefix = `${appOrigin.replace(/^https?:\/\//, "")}/${host?.slug ?? "fred"}/`;
  const previewName = draft.displayName.split(/\s+/)[0] || "you";
  const busy = status === "submitting";

  return (
    <div className="create-persona-page" style={{ "--persona-accent": draft.accentColor } as CSSProperties}>
      <button type="button" className="connect-back" onClick={onExit}>
        <ArrowLeft size={17} /> Back to personas
      </button>

      <section className="create-persona-head">
        <div>
          <span className="editor-tag violet">New persona</span>
          <h2>Create a booking identity</h2>
          <p>Build a focused booking page for one part of your life. All connected calendars will still protect your availability.</p>
        </div>
        <div className="editor-head-actions">
          <button type="button" className="editor-outline-button" disabled={busy} onClick={() => void createPersona(false)}>Save draft</button>
          <button
            type="button"
            className="confirm"
            disabled={busy}
            onClick={() => (step === 4 ? void createPersona(true) : setStep(step + 1))}
          >
            {step === 4 ? (busy ? "Publishing" : "Publish persona") : "Continue"} <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <ol className="create-progress">
        {CREATE_STEPS.map((item, index) => (
          <li key={item.label}>
            <button type="button" className={step > index + 1 ? "done" : step === index + 1 ? "current" : ""} onClick={() => setStep(index + 1)}>
              <i>{step > index + 1 ? <Check size={14} /> : index + 1}</i>
              <span>
                <strong>{item.label}</strong>
                <small>{item.detail}</small>
              </span>
            </button>
            {index < CREATE_STEPS.length - 1 ? <em /> : null}
          </li>
        ))}
      </ol>

      {message ? <p className={status === "error" ? "form-error" : "inline-note"} aria-live="polite">{message}</p> : null}

      <div className="create-persona-layout">
        <main className="create-persona-main">
          {step === 1 ? (
            <>
              <section className="editor-form">
                <div className="editor-section-head">
                  <div>
                    <h3>Public identity</h3>
                    <p>Give this audience a clear reason to book time with you.</p>
                  </div>
                  <span className="editor-tag gray">Draft</span>
                </div>
                <div className="formgrid">
                  <label>Persona name<input value={draft.name} placeholder="e.g., Family &amp; friends" onChange={(event) => setName(event.currentTarget.value)} /></label>
                  <label>Public display name<input value={draft.displayName} onChange={(event) => set({ displayName: event.currentTarget.value })} /></label>
                  <label>Headline<input value={draft.headline} placeholder="e.g., Let's catch up" onChange={(event) => set({ headline: event.currentTarget.value })} /></label>
                  <label>Organization <em className="optional">(optional)</em><input value={draft.organization} placeholder="Organization or affiliation" onChange={(event) => set({ organization: event.currentTarget.value })} /></label>
                  <label className="span2">Bio<textarea rows={4} value={draft.bio} placeholder="Tell visitors what they can schedule with you and what to expect." onChange={(event) => set({ bio: event.currentTarget.value })} /></label>
                  <label className="span2">
                    Persona URL
                    <div className="prefix-input editable">
                      <span>{urlPrefix}</span>
                      <input value={draft.slug} placeholder="family" onChange={(event) => set({ slug: slugify(event.currentTarget.value), slugTouched: true })} />
                    </div>
                    <small className={draft.slug && !slugValid ? "field-help invalid" : "field-help"}>
                      {draft.slug && !slugValid
                        ? "Use letters, numbers and hyphens, at least three characters."
                        : "Use a short, memorable name. You can change it before publishing."}
                    </small>
                  </label>
                </div>
              </section>

              <section className="editor-form">
                <div className="editor-section-head">
                  <div>
                    <h3>Brand appearance</h3>
                    <p>Make this booking page easy to recognize.</p>
                  </div>
                </div>
                <div className="brand-editor">
                  <div>
                    <label htmlFor="create-accent-hex">Accent color</label>
                    <div className="swatches">
                      {["#315BEA", "#635BFF", "#0F9F75", "#E24D6D", "#F59E0B"].map((color) => (
                        <button
                          key={color}
                          type="button"
                          className={color.toLowerCase() === draft.accentColor.toLowerCase() ? "selected" : ""}
                          style={{ background: color }}
                          aria-label={`Use ${color}`}
                          aria-pressed={color.toLowerCase() === draft.accentColor.toLowerCase()}
                          onClick={() => set({ accentColor: color })}
                        >
                          {color.toLowerCase() === draft.accentColor.toLowerCase() ? <Check size={14} /> : null}
                        </button>
                      ))}
                      <span className="hex-field">
                        <i style={{ background: draft.accentColor }} />
                        <input id="create-accent-hex" value={draft.accentColor} pattern="#[0-9a-fA-F]{6}" aria-label="Accent color hex value" onChange={(event) => set({ accentColor: event.currentTarget.value })} />
                      </span>
                    </div>
                  </div>
                </div>
              </section>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <section className="editor-form">
                <div className="editor-section-head">
                  <div>
                    <h3>Scheduling defaults</h3>
                    <p>Choose the default time zone and meeting location for this persona.</p>
                  </div>
                  <span className="editor-tag blue">Uses all calendars</span>
                </div>
                <div className="formgrid">
                  <label>Time zone<TimeZoneSelect name="defaultTimeZone" value={draft.defaultTimeZone} ariaLabel="Persona time zone" onChange={(value) => set({ defaultTimeZone: value })} /></label>
                  <label>
                    Default location
                    <select value={draft.locationKind} onChange={(event) => set({ locationKind: event.currentTarget.value })}>
                      <option value="google_meet">Google Meet</option>
                      <option value="microsoft_teams">Microsoft Teams</option>
                      <option value="zoho_meeting">Zoho Meeting</option>
                      <option value="custom_url">Custom link</option>
                      <option value="phone">Phone call</option>
                      <option value="decide_later">Decide later</option>
                    </select>
                  </label>
                  <label className="span2">Location URL<input value={draft.locationUrl} placeholder="Generated automatically when supported" onChange={(event) => set({ locationUrl: event.currentTarget.value })} /></label>
                  <label className="span2">Location instructions<textarea rows={3} value={draft.locationInstructions} placeholder="Optional instructions included in invitations." onChange={(event) => set({ locationInstructions: event.currentTarget.value })} /></label>
                  <label className="span2">
                    Booking destination
                    <select value={draft.destinationCalendarResourceId} onChange={(event) => set({ destinationCalendarResourceId: event.currentTarget.value })}>
                      <option value="">
                        {defaultDestination ? `Use the default calendar (${defaultDestination.label})` : "Use the default calendar"}
                      </option>
                      {destinationOptions.map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </select>
                    <small className={!defaultDestination && !draft.destinationCalendarResourceId ? "field-help invalid" : "field-help"}>
                      {destinationOptions.length === 0
                        ? "No calendars are connected yet. Connect one before publishing or bookings will fail."
                        : !defaultDestination && !draft.destinationCalendarResourceId
                          ? "No calendar is marked as a booking destination. Pick one here, or bookings for this persona will fail."
                          : "Bookings for this persona are written to this calendar."}
                    </small>
                  </label>
                </div>
              </section>

              <section className="editor-form">
                <div className="editor-section-head">
                  <div>
                    <h3>Availability &amp; booking rules</h3>
                    <p>These become the rules for the first meeting type. You can change them per meeting type later.</p>
                  </div>
                  <span className="editor-tag gray">Applied on create</span>
                </div>
                <div className="rule-list">
                  <div>
                    <span>
                      <strong>Availability schedule</strong>
                      <small>Weekdays only, in {draft.defaultTimeZone}</small>
                    </span>
                    <select value={draft.availabilityPreset} onChange={(event) => set({ availabilityPreset: event.currentTarget.value })}>
                      {AVAILABILITY_PRESETS.map((preset) => (
                        <option key={preset.id} value={preset.id}>{preset.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <span>
                      <strong>Minimum notice</strong>
                      <small>Prevent last-minute bookings</small>
                    </span>
                    <select value={draft.minimumNoticeMinutes} onChange={(event) => set({ minimumNoticeMinutes: Number(event.currentTarget.value) })}>
                      <option value={60}>1 hour</option>
                      <option value={240}>4 hours</option>
                      <option value={720}>12 hours</option>
                      <option value={1440}>24 hours</option>
                      <option value={2880}>2 days</option>
                    </select>
                  </div>
                  <div>
                    <span>
                      <strong>Buffer between meetings</strong>
                      <small>Protect time before and after calls</small>
                    </span>
                    <select value={draft.bufferMinutes} onChange={(event) => set({ bufferMinutes: Number(event.currentTarget.value) })}>
                      <option value={0}>No buffer</option>
                      <option value={10}>10 minutes</option>
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                    </select>
                  </div>
                </div>
              </section>
            </>
          ) : null}

          {step === 3 ? (
            <section className="editor-form">
              <div className="editor-section-head">
                <div>
                  <h3>First meeting type</h3>
                  <p>Create the first conversation visitors can book from this persona.</p>
                </div>
                <span className="editor-tag violet">Required</span>
              </div>
              <div className="formgrid">
                <label>Meeting title<input value={draft.meetingTitle} placeholder="e.g., Family catch-up" onChange={(event) => setMeetingTitle(event.currentTarget.value)} /></label>
                <label>
                  Meeting URL
                  <div className="prefix-input editable">
                    <span>…/{draft.slug || "persona"}/</span>
                    <input value={draft.meetingSlug} placeholder="catch-up" onChange={(event) => set({ meetingSlug: slugify(event.currentTarget.value), meetingSlugTouched: true })} />
                  </div>
                </label>
                <label>
                  Duration
                  <select value={draft.durationMinutes} onChange={(event) => set({ durationMinutes: Number(event.currentTarget.value) })}>
                    {[15, 20, 30, 45, 60, 90].map((minutes) => (
                      <option key={minutes} value={minutes}>{minutes} minutes</option>
                    ))}
                  </select>
                </label>
                <label>Location<input value={`Uses the persona default · ${locationKindLabel(draft.locationKind)}`} readOnly /></label>
                <label className="span2">Description<textarea rows={4} value={draft.meetingDescription} placeholder="Describe what you'll cover and anything the guest should prepare." onChange={(event) => set({ meetingDescription: event.currentTarget.value })} /></label>
              </div>
              <div className="create-note">
                <CalendarCheck size={18} />
                <div>
                  <strong>Calendar conflicts are checked automatically</strong>
                  <p>
                    {calendarsProtected
                      ? `Busy time from ${blockingCalendars.length} connected ${blockingCalendars.length === 1 ? "calendar" : "calendars"} is removed before visitors see available slots.`
                      : "No calendars are connected yet, so nothing will be removed from your availability. Connect one before publishing."}
                  </p>
                </div>
              </div>
            </section>
          ) : null}

          {step === 4 ? (
            <>
              <section className="editor-form create-review">
                <div className="editor-section-head">
                  <div>
                    <h3>Review your persona</h3>
                    <p>Confirm the essentials before making the page available.</p>
                  </div>
                  <span className={`editor-tag ${outstanding === 0 ? "green" : "orange"}`}>
                    {outstanding === 0 ? "Ready to publish" : `${outstanding} ${outstanding === 1 ? "item" : "items"} needed`}
                  </span>
                </div>
                <div className="create-review-list">
                  {checklist.map((item) => (
                    <article key={item.key}>
                      <i className={item.done ? "complete" : ""}>{item.done ? <Check size={16} /> : <X size={16} />}</i>
                      <span>
                        <strong>{item.title}</strong>
                        <small>{item.copy}</small>
                      </span>
                      <button type="button" className="booking-link" onClick={() => (item.key === "calendars" ? onExit() : setStep(item.step))}>
                        {item.key === "calendars" && !item.done ? "Connect" : item.done ? "Edit" : "Complete"} <ChevronRight size={14} />
                      </button>
                    </article>
                  ))}
                </div>
              </section>

              <section className="editor-form">
                <div className="editor-section-head">
                  <div>
                    <h3>Guest messages &amp; policies</h3>
                    <p>Optional details can be added now or after publishing.</p>
                  </div>
                  <span className="editor-tag gray">Optional</span>
                </div>
                <div className="formgrid">
                  <label className="span2">Confirmation message<textarea rows={3} value={draft.confirmationCopy} onChange={(event) => set({ confirmationCopy: event.currentTarget.value })} /></label>
                  <label>Privacy URL<input type="url" value={draft.privacyUrl} placeholder="https://…" onChange={(event) => set({ privacyUrl: event.currentTarget.value })} /></label>
                  <label>Terms URL<input type="url" value={draft.termsUrl} placeholder="https://…" onChange={(event) => set({ termsUrl: event.currentTarget.value })} /></label>
                </div>
              </section>
            </>
          ) : null}

          <div className="create-footer">
            <button type="button" className="booking-link" onClick={onExit}>Cancel</button>
            <span />
            <button
              type="button"
              className="editor-outline-button"
              disabled={busy}
              onClick={() => (step === 1 ? void createPersona(false) : setStep(step - 1))}
            >
              {step === 1 ? "Save as draft" : "Back"}
            </button>
            <button
              type="button"
              className="confirm"
              disabled={busy}
              onClick={() => (step === 4 ? void createPersona(true) : setStep(step + 1))}
            >
              {step === 4 ? (busy ? "Publishing" : "Publish persona") : "Continue"} <ArrowRight size={16} />
            </button>
          </div>
        </main>

        <aside className="create-persona-side">
          <section className="persona-preview">
            <div className="persona-preview-head">
              <span>Live preview</span>
              <span className="editor-tag gray">Draft</span>
            </div>
            <div className="persona-preview-window">
              <span className="editor-avatar">{personaInitialsFromText(draft.name || draft.displayName)}</span>
              <span className="preview-chip">{draft.name || "New persona"}</span>
              <h3>Meet with {previewName}</h3>
              <p>{draft.bio || "Your introduction will appear here and help visitors choose the right conversation."}</p>
              <article>
                <i />
                <span>
                  <strong>{draft.meetingTitle || "Your first meeting type"}</strong>
                  <small>{draft.durationMinutes} minutes · {locationKindLabel(draft.locationKind)}</small>
                </span>
                <ChevronRight size={16} />
              </article>
            </div>
            <p className="preview-url">{urlPrefix}{draft.slug || "…"}</p>
          </section>

          <section className="calendar-protection">
            <i><ShieldCheck size={21} /></i>
            <div>
              <strong>Your time stays protected</strong>
              <p>This persona will check every connected calendar before showing availability.</p>
            </div>
            <span><strong>{blockingCalendars.length}</strong> {blockingCalendars.length === 1 ? "calendar" : "calendars"} checked</span>
          </section>
        </aside>
      </div>
    </div>
  );
}

/** Calendars a booking can be written to, across every connected account. */
function destinationCalendarOptions(adminData: AdminStatusResponse | null): Array<{ id: string; label: string; provider: Provider }> {
  return (adminData?.calendarConnections ?? [])
    .filter((connection) => connection.status === "connected")
    .flatMap((connection) =>
      (adminData?.calendarResources ?? [])
        .filter((resource) => resource.connectionId === connection.id)
        .map((resource) => ({
          id: resource.id,
          label: `${connection.label || calendarProviderLabel(connection.provider)} · ${resource.label}`,
          provider: connection.provider
        }))
    );
}

/**
 * The calendar a persona with no pinned destination will be written to. Mirrors
 * the server rule in selectDestination: the location's provider is preferred, then
 * the lowest destinationPriority wins.
 */
function defaultDestinationFor(
  adminData: AdminStatusResponse | null,
  locationKind: string
): { id: string; label: string; provider: Provider } | undefined {
  const connected = new Map(
    (adminData?.calendarConnections ?? []).filter((connection) => connection.status === "connected").map((connection) => [connection.id, connection])
  );
  const candidates = (adminData?.calendarResources ?? [])
    .filter((resource) => resource.destinationPriority > 0 && connected.has(resource.connectionId))
    .sort((left, right) => left.destinationPriority - right.destinationPriority);
  const required = locationProvider(locationKind);
  const preferred = required ? candidates.filter((resource) => connected.get(resource.connectionId)?.provider === required) : [];
  const chosen = (preferred.length > 0 ? preferred : candidates)[0];
  if (!chosen) {
    return undefined;
  }
  const connection = connected.get(chosen.connectionId)!;
  return {
    id: chosen.id,
    label: `${connection.label || calendarProviderLabel(connection.provider)} · ${chosen.label}`,
    provider: connection.provider
  };
}

/** The provider a location kind needs, when it needs a specific one. */
function locationProvider(kind: string): Provider | undefined {
  if (kind === "google_meet") return "google";
  if (kind === "microsoft_teams") return "microsoft";
  if (kind === "zoho_meeting") return "zoho";
  return undefined;
}

function personaInitialsFromText(value: string): string {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "NP";
  }
  return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("");
}

function locationKindLabel(kind: string): string {
  switch (kind) {
    case "google_meet":
      return "Google Meet";
    case "microsoft_teams":
      return "Microsoft Teams";
    case "zoho_meeting":
      return "Zoho Meeting";
    case "custom_url":
      return "Custom link";
    case "phone":
      return "Phone call";
    default:
      return "Decide later";
  }
}

function EditPersonaView({
  personaId,
  authState,
  adminData,
  onAdminDataChange,
  onPersonaChange,
  onMeetingTypesChange,
  onExit,
  onOpenView
}: {
  personaId: string;
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  onAdminDataChange: (data: AdminStatusResponse | null) => void;
  onPersonaChange: (persona: Persona | null) => void;
  onMeetingTypesChange: (meetingTypes: MeetingType[]) => void;
  onExit: () => void;
  onOpenView: (view: View) => void;
}) {
  const [section, setSection] = useState<PersonaEditorSection>("profile");
  const [status, setStatus] = useState<OnboardingStatus>("idle");
  const [photoState, setPhotoState] = useState<"idle" | "uploading">("idle");
  const [croppingPersonaPhoto, setCroppingPersonaPhoto] = useState<globalThis.File | null>(null);
  const [logoState, setLogoState] = useState<"idle" | "uploading">("idle");
  const [croppingPersonaLogo, setCroppingPersonaLogo] = useState<globalThis.File | null>(null);
  const [message, setMessage] = useState("");
  const [meetingFormId, setMeetingFormId] = useState<string | null>(null);
  const formRef = useRef<globalThis.HTMLFormElement | null>(null);
  const persona = (adminData?.personas ?? []).find((item) => item.id === personaId);
  const [accent, setAccent] = useState(persona?.accentColor ?? PERSONA_ACCENTS[0]!);
  const [requiresConfirmation, setRequiresConfirmation] = useState(persona?.requiresConfirmation === true);

  useEffect(() => {
    setAccent(persona?.accentColor ?? PERSONA_ACCENTS[0]!);
  }, [persona?.accentColor]);

  useEffect(() => {
    setRequiresConfirmation(persona?.requiresConfirmation === true);
  }, [persona?.requiresConfirmation]);

  // A required field inside a collapsed section cannot be focused, so the browser
  // blocks the submit with nothing on screen. Reveal the offending section instead.
  useEffect(() => {
    const form = formRef.current;
    if (!form) {
      return;
    }
    const reveal = (event: globalThis.Event) => {
      const field = event.target as globalThis.HTMLElement | null;
      const owner = field?.closest("[data-editor-section]")?.getAttribute("data-editor-section");
      if (owner) {
        setSection(owner as PersonaEditorSection);
      }
    };
    // Capture, because "invalid" does not bubble.
    form.addEventListener("invalid", reveal, true);
    return () => form.removeEventListener("invalid", reveal, true);
  }, []);

  const host = signedInHost(authState, adminData);
  const meetingTypes = (adminData?.meetingTypes ?? []).filter((item) => item.personaId === personaId);
  const publicUrl = `${appOrigin}/${host?.slug ?? "fred"}/${persona?.slug ?? ""}`;

  // What this screen shows and copies is what the host will paste somewhere, so it
  // is the short form, the same as the Personas and Share links screens. Anything
  // that fails to shorten falls back to the long URL rather than disappearing.
  const shortUrls = useShortLinks(
    authState,
    persona
      ? [
          { targetUrl: publicUrl, source: `persona:${persona.slug}` },
          ...meetingTypes.map((meeting) => ({
            targetUrl: `${publicUrl}/${meeting.slug}`,
            source: `persona:${persona.slug}:meeting:${meeting.slug}`
          }))
        ]
      : []
  );
  const personaShareUrl = shortUrls[publicUrl] ?? publicUrl;
  const meetingShareUrl = (meetingSlug: string) => shortUrls[`${publicUrl}/${meetingSlug}`] ?? `${publicUrl}/${meetingSlug}`;

  if (!persona) {
    return (
      <div className="edit-persona-page">
        <button type="button" className="connect-back" onClick={onExit}>
          <ArrowLeft size={17} /> Back to personas
        </button>
        <WorkspaceState title="Persona unavailable" detail="This persona could not be loaded. It may have been deleted." tone="error" />
      </div>
    );
  }

  /** Upload then save, or save an empty URL to remove the photo. */
  async function savePersonaPhoto(file: globalThis.Blob | null) {
    if (authState.status !== "signed_in" || !persona) {
      return;
    }
    setPhotoState("uploading");
    setMessage("");
    try {
      const avatarUrl = file ? await uploadAvatar(authState.idToken, "persona", persona.id, file) : "";
      const response = await apiFetch(`/v1/admin/personas/${persona.id}`, {
        method: "PUT",
        headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
        body: JSON.stringify({ avatarUrl })
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { error?: { message?: string; code?: string } };
        throw new Error(body.error?.message ?? body.error?.code ?? `persona_update_${response.status}`);
      }
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setStatus("done");
      setMessage(file ? "Photo updated." : "Photo removed.");
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "The photo could not be saved.");
    } finally {
      setPhotoState("idle");
    }
  }

  /** The logo uses the same crop and signed-upload flow as the profile photo. */
  async function savePersonaLogo(file: globalThis.Blob | null) {
    if (authState.status !== "signed_in" || !persona) {
      return;
    }
    setLogoState("uploading");
    setMessage("");
    try {
      const logoUrl = file ? await uploadAvatar(authState.idToken, "persona", persona.id, file) : "";
      const response = await apiFetch(`/v1/admin/personas/${persona.id}`, {
        method: "PUT",
        headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
        body: JSON.stringify({ logoUrl })
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { error?: { message?: string; code?: string } };
        throw new Error(body.error?.message ?? body.error?.code ?? `persona_logo_update_${response.status}`);
      }
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setStatus("done");
      setMessage(file ? "Logo updated." : "Logo removed.");
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "The logo could not be saved.");
    } finally {
      setLogoState("idle");
    }
  }

  async function savePersona(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    if (authState.status !== "signed_in" || !persona) {
      setStatus("error");
      setMessage("Sign in before editing a persona.");
      return;
    }
    const form = new globalThis.FormData(event.currentTarget);
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch(`/v1/admin/personas/${persona.id}`, {
        method: "PUT",
        headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
        body: JSON.stringify({
          name: String(form.get("name") ?? ""),
          displayName: String(form.get("displayName") ?? ""),
          headline: String(form.get("headline") ?? ""),
          organization: String(form.get("organization") ?? ""),
          bio: String(form.get("bio") ?? ""),
          accentColor: String(form.get("accentColor") ?? "#635bff"),
          defaultTimeZone: String(form.get("defaultTimeZone") ?? ""),
          locationKind: String(form.get("locationKind") ?? "decide_later"),
          locationUrl: String(form.get("locationUrl") ?? ""),
          locationInstructions: String(form.get("locationInstructions") ?? ""),
          publicState: String(form.get("publicState") ?? "active"),
          destinationCalendarResourceId: String(form.get("destinationCalendarResourceId") ?? ""),
          confirmationCopy: String(form.get("confirmationCopy") ?? ""),
          cancellationCopy: String(form.get("cancellationCopy") ?? ""),
          requiresConfirmation: form.get("requiresConfirmation") === "on",
          confirmationWindowHours: Number(form.get("confirmationWindowHours") ?? DEFAULT_CONFIRMATION_WINDOW_HOURS),
          privacyUrl: String(form.get("privacyUrl") ?? ""),
          termsUrl: String(form.get("termsUrl") ?? "")
        })
      });
      const body = (await response.json().catch(() => ({}))) as PersonaUpdateResponse & { error?: { details?: string[]; message?: string; code?: string } };
      if (!response.ok) {
        throw new Error(body.error?.details?.join(", ") ?? body.error?.message ?? body.error?.code ?? `persona_update_${response.status}`);
      }
      onPersonaChange(body.persona);
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setStatus("done");
      setMessage("Changes saved.");
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Persona update failed.");
    }
  }

  async function saveMeetingType(event: FormEvent<globalThis.HTMLFormElement>, existing?: MeetingType) {
    event.preventDefault();
    if (authState.status !== "signed_in" || !persona) {
      return;
    }
    const form = new globalThis.FormData(event.currentTarget);
    const shared = {
      meetingTitle: String(form.get("meetingTitle") ?? ""),
      title: String(form.get("meetingTitle") ?? ""),
      meetingDescription: String(form.get("meetingDescription") ?? ""),
      description: String(form.get("meetingDescription") ?? ""),
      durationMinutes: Number(form.get("durationMinutes") ?? 30),
      slotIntervalMinutes: Number(form.get("slotIntervalMinutes") ?? 15),
      minimumNoticeMinutes: Number(form.get("minimumNoticeMinutes") ?? 120),
      horizonDays: Number(form.get("horizonDays") ?? 21),
      startHour: Number(form.get("startHour") ?? 9),
      endHour: Number(form.get("endHour") ?? 17)
    };
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch(
        existing ? `/v1/admin/meeting-types/${existing.id}` : "/v1/admin/meeting-types",
        {
          method: existing ? "PUT" : "POST",
          headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
          body: JSON.stringify(
            existing
              ? { ...shared, active: form.get("active") === "on" }
              : { ...shared, personaId: persona.id, meetingSlug: String(form.get("meetingSlug") ?? "") }
          )
        }
      );
      const body = (await response.json().catch(() => ({}))) as MeetingTypeUpdateResponse & { error?: { details?: string[]; message?: string; code?: string } };
      if (!response.ok) {
        throw new Error(body.error?.details?.join(", ") ?? body.error?.message ?? body.error?.code ?? `meeting_save_${response.status}`);
      }
      onMeetingTypesChange([...(adminData?.meetingTypes ?? []).filter((item) => item.id !== body.meetingType.id), body.meetingType]);
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setStatus("done");
      setMessage(existing ? `Saved ${body.meetingType.title}.` : `Created ${body.meetingType.slug}.`);
      setMeetingFormId(null);
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Meeting type could not be saved.");
    }
  }

  async function duplicateMeetingType(meetingType: MeetingType) {
    if (authState.status !== "signed_in") {
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch(`/v1/admin/meeting-types/${meetingType.id}/duplicate`, {
        method: "POST",
        headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
        body: JSON.stringify({
          meetingSlug: nextMeetingSlug(meetingType.slug, meetingTypes),
          meetingTitle: `${meetingType.title} copy`
        })
      });
      const body = (await response.json().catch(() => ({}))) as MeetingTypeUpdateResponse & { error?: { message?: string; code?: string } };
      if (!response.ok) {
        throw new Error(body.error?.message ?? body.error?.code ?? `meeting_duplicate_${response.status}`);
      }
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setStatus("done");
      setMessage(`Duplicated as ${body.meetingType.slug}.`);
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Meeting type duplication failed.");
    }
  }

  async function setMeetingActive(meetingType: MeetingType, active: boolean) {
    if (authState.status !== "signed_in") {
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch(`/v1/admin/meeting-types/${meetingType.id}`, {
        method: "PUT",
        headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
        body: JSON.stringify({ active })
      });
      if (!response.ok) {
        throw new Error(`meeting_state_${response.status}`);
      }
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setStatus("done");
      setMessage(active ? `${meetingType.title} is bookable.` : `${meetingType.title} is hidden.`);
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Meeting type could not be updated.");
    }
  }

  const destinationOptions = destinationCalendarOptions(adminData);
  const defaultDestination = defaultDestinationFor(adminData, persona.locationPreference.kind);
  const pinnedDestination = destinationOptions.find((option) => option.id === persona.destinationCalendarResourceId);
  const requiredProvider = locationProvider(persona.locationPreference.kind);
  const destinationMismatch = Boolean(pinnedDestination && requiredProvider && pinnedDestination.provider !== requiredProvider);

  const stateBadge =
    persona.publicState === "active" ? { label: "Live", tone: "live" } : persona.publicState === "private_link" ? { label: "Private", tone: "private" } : { label: "Draft", tone: "draft" };
  const signatureText = `${persona.displayName}\n${persona.organization ?? persona.headline}\nSchedule a conversation with me - ${personaShareUrl}`;
  const activeMeetings = meetingTypes.filter((item) => item.active);
  const firstWindow = activeMeetings[0]?.availability[0];

  return (
    <div className="edit-persona-page" style={{ "--persona-accent": accent } as CSSProperties}>
      <button type="button" className="connect-back" onClick={onExit}>
        <ArrowLeft size={17} /> Back to personas
      </button>

      <section className="persona-editor-head">
        <div className="editor-identity">
          <div className="editor-avatar-edit">
            {croppingPersonaPhoto ? (
              <AvatarCropDialog
                file={croppingPersonaPhoto}
                title={`Crop the ${persona.name} photo`}
                onCancel={() => setCroppingPersonaPhoto(null)}
                onConfirm={(image) => {
                  setCroppingPersonaPhoto(null);
                  void savePersonaPhoto(image);
                }}
              />
            ) : null}
            <Avatar className="editor-avatar" src={persona.avatarUrl} initials={personaInitials(persona)} />
            <label className="avatar-upload compact">
              <input
                type="file"
                accept={AVATAR_ACCEPT}
                disabled={photoState === "uploading"}
                onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  event.currentTarget.value = "";
                  if (file) {
                    setCroppingPersonaPhoto(file);
                  }
                }}
              />
              <span>{photoState === "uploading" ? "Uploading" : persona.avatarUrl ? "Change" : "Add photo"}</span>
            </label>
            {persona.avatarUrl ? (
              <button type="button" className="avatar-remove" disabled={photoState === "uploading"} onClick={() => void savePersonaPhoto(null)}>
                Remove
              </button>
            ) : null}
          </div>
          <div>
            <div className="editor-identity-line">
              <h2>{persona.name}</h2>
              <span className={`editor-state ${stateBadge.tone}`}>{stateBadge.label}</span>
            </div>
            <p>{persona.headline} · {displayUrl(personaShareUrl)}</p>
          </div>
        </div>
        <div className="editor-head-actions">
          <a className="editor-outline-button" href={publicUrl} target="_blank" rel="noreferrer"><Eye size={16} /> Preview</a>
          <button type="submit" form={PERSONA_EDITOR_FORM} className="confirm" disabled={authState.status !== "signed_in" || status === "submitting"}>
            <Check size={16} /> {status === "submitting" ? "Saving" : "Save changes"}
          </button>
        </div>
      </section>

      {message ? <p className={status === "error" ? "form-error" : "inline-note"} aria-live="polite">{message}</p> : null}

      <div className="persona-editor-layout">
        <aside className="editor-nav" aria-label="Persona settings">
          <small>Persona settings</small>
          {([
            ["profile", "Profile & branding", <UserRound key="p" size={17} />],
            ["meetings", "Meeting types", <Video key="m" size={17} />],
            ["scheduling", "Scheduling defaults", <Clock key="s" size={17} />],
            ["messages", "Messages & policies", <ShieldCheck key="g" size={17} />]
          ] as Array<[PersonaEditorSection, string, ReactNode]>).map(([key, label, icon]) => (
            <button key={key} type="button" className={section === key ? "active" : ""} onClick={() => setSection(key)}>
              {icon}
              {label}
              <ChevronRight size={15} />
            </button>
          ))}
          <div className="editor-danger">
            <span>Persona actions</span>
            <button type="button" onClick={() => onOpenView("links")}>Share this persona</button>
            <button type="button" onClick={() => onOpenView("personas")}>Delete persona</button>
          </div>
        </aside>

        <main className="editor-main">
          <form id={PERSONA_EDITOR_FORM} key={persona.id} ref={formRef} className="editor-form-stack" onSubmit={(event) => void savePersona(event)}>
            {/* Every section stays mounted so one Save writes the whole persona,
                whichever tab happens to be open. */}
            <section className="editor-form" data-editor-section="profile" hidden={section !== "profile"}>
              <div className="editor-section-head">
                <div>
                  <h3>Public profile</h3>
                  <p>How this persona appears to people booking time with you.</p>
                </div>
                <label className="visibility-control">
                  Visibility
                  <select name="publicState" defaultValue={persona.publicState}>
                    <option value="active">Active</option>
                    <option value="private_link">Private link</option>
                    <option value="inactive">Hidden</option>
                  </select>
                </label>
              </div>
              <div className="formgrid">
                <label>Persona name<input name="name" required defaultValue={persona.name} /></label>
                <label>Public display name<input name="displayName" required defaultValue={persona.displayName} /></label>
                <label>Headline<input name="headline" required defaultValue={persona.headline} /></label>
                <label>Organization<input name="organization" defaultValue={persona.organization ?? ""} /></label>
                <label className="span2">Bio<textarea name="bio" rows={4} required defaultValue={persona.bio} /></label>
                <label className="span2">
                  Persona slug
                  <div className="prefix-input">
                    <span>{appOrigin.replace(/^https?:\/\//, "")}/{host?.slug ?? "fred"}/</span>
                    <input value={persona.slug} readOnly aria-readonly="true" />
                  </div>
                  <small className="field-help">The booking link is fixed once a persona exists, so links you have shared keep working.</small>
                </label>
              </div>
            </section>

            <section className="editor-form" data-editor-section="profile" hidden={section !== "profile"}>
              <div className="editor-section-head">
                <div>
                  <h3>Brand appearance</h3>
                  <p>Use a distinct color and identity for this audience.</p>
                </div>
              </div>
              <div className="brand-editor">
                <div>
                  <label htmlFor="persona-accent-hex">Accent color</label>
                  <div className="swatches">
                    {PERSONA_ACCENTS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={color.toLowerCase() === accent.toLowerCase() ? "selected" : ""}
                        style={{ background: color }}
                        aria-label={`Use ${color}`}
                        aria-pressed={color.toLowerCase() === accent.toLowerCase()}
                        onClick={() => setAccent(color)}
                      >
                        {color.toLowerCase() === accent.toLowerCase() ? <Check size={14} /> : null}
                      </button>
                    ))}
                    <span className="hex-field">
                      <i style={{ background: accent }} />
                      <input
                        id="persona-accent-hex"
                        name="accentColor"
                        value={accent}
                        onChange={(event) => setAccent(event.currentTarget.value)}
                        pattern="#[0-9a-fA-F]{6}"
                        aria-label="Accent color hex value"
                      />
                    </span>
                  </div>
                </div>
                <div className="brand-logo-editor">
                  <span className="field-label">Persona logo</span>
                  <p className="field-help">Shown on this persona's public booking page.</p>
                  {croppingPersonaLogo ? (
                    <AvatarCropDialog
                      file={croppingPersonaLogo}
                      title={`Crop the ${persona.name} logo`}
                      onCancel={() => setCroppingPersonaLogo(null)}
                      onConfirm={(image) => {
                        setCroppingPersonaLogo(null);
                        void savePersonaLogo(image);
                      }}
                    />
                  ) : null}
                  <Avatar className="editor-avatar" src={persona.logoUrl} initials={personaInitials(persona)} />
                  <label className="avatar-upload compact">
                    <input
                      type="file"
                      accept={AVATAR_ACCEPT}
                      disabled={logoState === "uploading"}
                      onChange={(event) => {
                        const file = event.currentTarget.files?.[0];
                        event.currentTarget.value = "";
                        if (file) {
                          setCroppingPersonaLogo(file);
                        }
                      }}
                    />
                    <span>{logoState === "uploading" ? "Uploading" : persona.logoUrl ? "Change logo" : "Add logo"}</span>
                  </label>
                  {persona.logoUrl ? (
                    <button type="button" className="avatar-remove" disabled={logoState === "uploading"} onClick={() => void savePersonaLogo(null)}>
                      Remove
                    </button>
                  ) : null}
                </div>
              </div>
            </section>

            <section className="editor-form" data-editor-section="scheduling" hidden={section !== "scheduling"}>
              <div className="editor-section-head">
                <div>
                  <h3>Scheduling defaults</h3>
                  <p>Default time zone and meeting location for this persona.</p>
                </div>
                <span className="editor-tag blue">Persona-level</span>
              </div>
              <div className="formgrid schedule-defaults">
                <label>Time zone<TimeZoneSelect name="defaultTimeZone" value={persona.defaultTimeZone} ariaLabel="Persona time zone" /></label>
                <label>
                  Default location
                  <select name="locationKind" defaultValue={persona.locationPreference.kind}>
                    <option value="google_meet">Google Meet</option>
                    <option value="microsoft_teams">Microsoft Teams</option>
                    <option value="zoho_meeting">Zoho Meeting</option>
                    <option value="custom_url">Custom link</option>
                    <option value="phone">Phone call</option>
                    <option value="decide_later">Decide later</option>
                  </select>
                </label>
                <label className="span2">Location URL<input name="locationUrl" type="url" defaultValue={personaLocationUrl(persona)} placeholder="Generated automatically when supported" /></label>
                <label className="span2">Location instructions<textarea name="locationInstructions" rows={3} defaultValue={personaLocationInstructions(persona)} placeholder="Optional instructions guests receive with the invitation." /></label>
                <label className="span2">
                  Booking destination
                  <select name="destinationCalendarResourceId" defaultValue={persona.destinationCalendarResourceId ?? ""}>
                    <option value="">
                      {defaultDestination ? `Use the default calendar (${defaultDestination.label})` : "Use the default calendar"}
                    </option>
                    {destinationOptions.map((option) => (
                      <option key={option.id} value={option.id}>{option.label}</option>
                    ))}
                  </select>
                  <small className={!defaultDestination && !persona.destinationCalendarResourceId ? "field-help invalid" : "field-help"}>
                    {destinationOptions.length === 0
                      ? "No calendars are connected yet, so bookings have nowhere to go."
                      : !defaultDestination && !persona.destinationCalendarResourceId
                        ? "No calendar is marked as a booking destination, so bookings for this persona will fail. Pick one here, or set a destination on the Calendars screen."
                        : destinationMismatch
                          ? `This calendar is not ${locationLabel(persona)}, so the meeting link cannot be created automatically. Set the location to match, or use a custom link.`
                          : "Bookings for this persona are written to this calendar."}
                  </small>
                </label>
              </div>
            </section>

            <section className="editor-form" data-editor-section="scheduling" hidden={section !== "scheduling"}>
              <div className="editor-section-head">
                <div>
                  <h3>Booking approval</h3>
                  <p>Decide whether requests for this persona are confirmed straight away.</p>
                </div>
                <span className="editor-tag blue">Persona-level</span>
              </div>
              <label className="editor-switch">
                <input
                  type="checkbox"
                  name="requiresConfirmation"
                  checked={requiresConfirmation}
                  onChange={(event) => setRequiresConfirmation(event.currentTarget.checked)}
                />
                <span>
                  <strong>Review requests before they are confirmed</strong>
                  <small>
                    Guests are told the time is requested, not booked. The slot is held on your calendar as tentative
                    while you decide, so nobody else can take it.
                  </small>
                </span>
              </label>
              {requiresConfirmation ? (
                <div className="formgrid approval-fields">
                  <label>
                    Answer within
                    <select name="confirmationWindowHours" defaultValue={String(persona.confirmationWindowHours ?? DEFAULT_CONFIRMATION_WINDOW_HOURS)}>
                      <option value="4">4 hours</option>
                      <option value="12">12 hours</option>
                      <option value="24">1 day</option>
                      <option value="48">2 days</option>
                      <option value="72">3 days</option>
                      <option value="168">1 week</option>
                    </select>
                    <small className="field-help">
                      If you have not answered by then - or by the time the meeting is due to start - the request is
                      declined, the guest is told, and the time goes back on sale.
                    </small>
                  </label>
                </div>
              ) : null}
            </section>

            <section className="editor-form" data-editor-section="messages" hidden={section !== "messages"}>
              <div className="editor-section-head">
                <div>
                  <h3>Guest messages</h3>
                  <p>Customize the messages guests see after a booking changes.</p>
                </div>
              </div>
              <div className="formgrid message-fields">
                <label className="span2">Confirmation copy<textarea name="confirmationCopy" rows={3} required defaultValue={persona.confirmationCopy} /></label>
                <label className="span2">Cancellation copy<textarea name="cancellationCopy" rows={3} required defaultValue={persona.cancellationCopy} /></label>
              </div>
            </section>

            <section className="editor-form" data-editor-section="messages" hidden={section !== "messages"}>
              <div className="editor-section-head">
                <div>
                  <h3>Policies and sharing</h3>
                  <p>Add legal links and copy the public booking page.</p>
                </div>
              </div>
              <div className="formgrid policy-fields">
                <label>Privacy URL<input name="privacyUrl" type="url" defaultValue={persona.privacyUrl ?? ""} placeholder="https://example.com/privacy" /></label>
                <label>Terms URL<input name="termsUrl" type="url" defaultValue={persona.termsUrl ?? ""} placeholder="https://example.com/terms" /></label>
              </div>
              <div className="share-url-large">
                <Globe2 size={18} />
                <span>{displayUrl(personaShareUrl)}</span>
                <button type="button" className="confirm" onClick={() => void globalThis.navigator.clipboard.writeText(personaShareUrl)}>
                  <Copy size={15} /> Copy link
                </button>
              </div>
              <div className="signature-card">
                <div>
                  <strong>{persona.name}</strong>
                  <span>Schedule a conversation with {persona.displayName}</span>
                </div>
                <button type="button" className="editor-outline-button" onClick={() => void globalThis.navigator.clipboard.writeText(signatureText)}>
                  Copy signature
                </button>
              </div>
            </section>
          </form>

          {section === "scheduling" ? (
            <section className="editor-form">
              <div className="editor-section-head">
                <div>
                  <h3>Availability &amp; booking rules</h3>
                  <p>These come from this persona&apos;s meeting types, not the persona itself.</p>
                </div>
                <span className="editor-tag gray">{activeMeetings.length} active</span>
              </div>
              <div className="rule-list">
                <div>
                  <span>
                    <strong>Availability schedule</strong>
                    <small>
                      {firstWindow
                        ? `${minuteToTimeInput(firstWindow.startMinute)}–${minuteToTimeInput(firstWindow.endMinute)} · ${firstWindow.timeZone}`
                        : "No active meeting types yet"}
                    </small>
                  </span>
                  <button type="button" className="editor-outline-button" onClick={() => onOpenView("availability")}>Edit hours</button>
                </div>
                <div>
                  <span>
                    <strong>Minimum notice</strong>
                    <small>{activeMeetings[0] ? `${activeMeetings[0].minimumNoticeMinutes} minutes before a booking` : "Set per meeting type"}</small>
                  </span>
                  <button type="button" className="editor-outline-button" onClick={() => setSection("meetings")}>Meeting types</button>
                </div>
                <div>
                  <span>
                    <strong>Buffer between meetings</strong>
                    <small>
                      {activeMeetings[0]
                        ? `${activeMeetings[0].preBufferMinutes} min before · ${activeMeetings[0].postBufferMinutes} min after`
                        : "Set per meeting type"}
                    </small>
                  </span>
                  <button type="button" className="editor-outline-button" onClick={() => setSection("meetings")}>Meeting types</button>
                </div>
              </div>
            </section>
          ) : null}

          {section === "meetings" ? (
            <section className="editor-form">
              <div className="editor-section-head">
                <div>
                  <h3>Meeting types</h3>
                  <p>Conversations visitors can book through this persona.</p>
                </div>
                <button type="button" className="confirm" onClick={() => setMeetingFormId((current) => (current === "new" ? null : "new"))}>
                  <Plus size={16} /> New meeting type
                </button>
              </div>

              {meetingFormId === "new" ? (
                <form className="meeting-type-form" onSubmit={(event) => void saveMeetingType(event)}>
                  <label>Meeting slug<input name="meetingSlug" required pattern="[a-z0-9][a-z0-9-]{1,61}[a-z0-9]" placeholder="strategy" /></label>
                  <label>Title<input name="meetingTitle" required placeholder="Strategy session" /></label>
                  <label>Duration<input name="durationMinutes" type="number" min="15" max="240" step="15" defaultValue="30" /></label>
                  <label>Slot cadence<input name="slotIntervalMinutes" type="number" min="5" max="60" step="5" defaultValue="15" /></label>
                  <label>Minimum notice<input name="minimumNoticeMinutes" type="number" min="0" max="10080" step="15" defaultValue="120" /></label>
                  <label>Horizon days<input name="horizonDays" type="number" min="1" max="90" defaultValue="21" /></label>
                  <label>Start hour<input name="startHour" type="number" min="0" max="23" defaultValue="9" /></label>
                  <label>End hour<input name="endHour" type="number" min="1" max="24" defaultValue="17" /></label>
                  <label className="span2">Description<textarea name="meetingDescription" rows={3} required defaultValue="A focused introductory meeting." /></label>
                  <div className="meeting-type-form-actions">
                    <button type="button" onClick={() => setMeetingFormId(null)}>Cancel</button>
                    <button className="confirm" disabled={status === "submitting"}>{status === "submitting" ? "Creating" : "Create meeting type"}</button>
                  </div>
                </form>
              ) : null}

              <div className="meeting-type-list">
                {meetingTypes.length === 0 ? (
                  <p className="empty-state">No meeting types yet. Guests need at least one before they can book.</p>
                ) : null}
                {meetingTypes.map((meeting) => (
                  <article key={meeting.id}>
                    <i className={meeting.active ? "" : "soft"} />
                    <div>
                      <strong>{meeting.title}</strong>
                      <small>{meeting.durationMinutes} minutes · {locationLabel(persona)} · {availabilityDayLabel(meeting)}</small>
                      <span>{displayUrl(meetingShareUrl(meeting.slug))}</span>
                    </div>
                    <span className={`editor-tag ${meeting.active ? "green" : "gray"}`}>{meeting.active ? "Active" : "Hidden"}</span>
                    <button
                      type="button"
                      className="editor-outline-button"
                      onClick={() => setMeetingFormId((current) => (current === meeting.id ? null : meeting.id))}
                    >
                      Edit
                    </button>
                    <ActionMenu
                      label={`More actions for ${meeting.title}`}
                      items={[
                        { label: "Open booking page", href: `${publicUrl}/${meeting.slug}` },
                        {
                          label: "Copy meeting link",
                          onSelect: () => void globalThis.navigator.clipboard.writeText(meetingShareUrl(meeting.slug))
                        },
                        { label: "Duplicate", onSelect: () => void duplicateMeetingType(meeting) },
                        {
                          label: meeting.active ? "Hide from public page" : "Make bookable",
                          onSelect: () => void setMeetingActive(meeting, !meeting.active)
                        }
                      ]}
                    />
                    {meetingFormId === meeting.id ? (
                      <form className="meeting-type-form inline" onSubmit={(event) => void saveMeetingType(event, meeting)}>
                        <label>Title<input name="meetingTitle" required defaultValue={meeting.title} /></label>
                        <label>Duration<input name="durationMinutes" type="number" min="15" max="240" step="15" defaultValue={meeting.durationMinutes} /></label>
                        <label>Slot cadence<input name="slotIntervalMinutes" type="number" min="5" max="60" step="5" defaultValue={meeting.slotIntervalMinutes} /></label>
                        <label>Minimum notice<input name="minimumNoticeMinutes" type="number" min="0" max="10080" step="15" defaultValue={meeting.minimumNoticeMinutes} /></label>
                        <label>Horizon days<input name="horizonDays" type="number" min="1" max="90" defaultValue={meeting.horizonDays} /></label>
                        <label>Start hour<input name="startHour" type="number" min="0" max="23" defaultValue={Math.floor((meeting.availability[0]?.startMinute ?? 540) / 60)} /></label>
                        <label>End hour<input name="endHour" type="number" min="1" max="24" defaultValue={Math.floor((meeting.availability[0]?.endMinute ?? 960) / 60)} /></label>
                        <label className="checkbox-field"><input name="active" type="checkbox" defaultChecked={meeting.active} /> Bookable by guests</label>
                        <label className="span2">Description<textarea name="meetingDescription" rows={3} required defaultValue={meeting.description} /></label>
                        <div className="meeting-type-form-actions">
                          <button type="button" onClick={() => setMeetingFormId(null)}>Cancel</button>
                          <button className="confirm" disabled={status === "submitting"}>{status === "submitting" ? "Saving" : "Save meeting type"}</button>
                        </div>
                      </form>
                    ) : null}
                  </article>
                ))}
              </div>
            </section>
          ) : null}
        </main>

        <aside className="persona-preview" aria-label="Live preview">
          <div className="persona-preview-head">
            <span>Live preview</span>
            <span className={`editor-tag ${persona.publicState === "active" ? "green" : "gray"}`}>
              {persona.publicState === "active" ? "Published" : stateBadge.label}
            </span>
          </div>
          <div className="persona-preview-window">
            <Avatar className="editor-avatar" src={persona.avatarUrl} initials={personaInitials(persona)} />
            <span className="preview-chip">{persona.name}</span>
            <h3>Meet with {persona.displayName}</h3>
            <p>{persona.bio}</p>
            {activeMeetings.length === 0 ? <p className="preview-empty">No bookable meeting types yet.</p> : null}
            {activeMeetings.map((meeting) => (
              <article key={meeting.id}>
                <i />
                <span>
                  <strong>{meeting.title}</strong>
                  <small>{meeting.durationMinutes} minutes · {locationLabel(persona)}</small>
                </span>
                <ChevronRight size={16} />
              </article>
            ))}
          </div>
          <a className="persona-preview-link" href={publicUrl} target="_blank" rel="noreferrer">
            <Eye size={15} /> Open public page
          </a>
        </aside>
      </div>
    </div>
  );
}

function availabilityDayLabel(meeting: MeetingType): string {
  const days = [...new Set(meeting.availability.map((window) => window.dayOfWeek))].sort((left, right) => left - right);
  if (days.length === 0) {
    return "No days set";
  }
  const names = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const isRun = days.every((day, index) => index === 0 || day === (days[index - 1] ?? day) + 1);
  return isRun && days.length > 1 ? `${names[days[0]!]}–${names[days.at(-1)!]}` : days.map((day) => names[day]).join(", ");
}

function AvailabilityView({
  meeting,
  selectedDate,
  selectedSlot,
  bookingState,
  slotState,
  availableDates,
  daySlots,
  authState,
  adminData,
  onAdminDataChange,
  onMeetingTypeChange,
  intakeTitle = "Booking test",
  onSelectDate,
  onSelectSlot,
  onSubmitBooking
}: {
  meeting: MeetingType;
  selectedDate: string;
  selectedSlot: TimeInterval | null;
  bookingState: BookingState;
  slotState: LoadState;
  availableDates: Set<string>;
  daySlots: TimeInterval[];
  authState?: AuthState;
  adminData?: AdminStatusResponse | null;
  onAdminDataChange?: ((data: AdminStatusResponse | null) => void) | undefined;
  onMeetingTypeChange?: ((meetingType: MeetingType) => void) | undefined;
  intakeTitle?: string;
  onSelectDate: (date: string) => void;
  onSelectSlot: (slot: TimeInterval) => void;
  onSubmitBooking: (guest: BookingGuestInput) => void;
}) {
  if (authState?.status === "signed_in") {
    return (
      <AvailabilitySettings
        meeting={meeting}
        authState={authState}
        adminData={adminData ?? null}
        onAdminDataChange={onAdminDataChange}
        onMeetingTypeChange={onMeetingTypeChange}
      />
    );
  }

  return (
    <div className="view-grid">
      <section className="panel span-2">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Availability</p>
            <h2>{meeting.title}</h2>
          </div>
          <div className="inline-status">
            <Clock size={16} /> {meeting.durationMinutes} min
          </div>
        </div>
        <div className="scheduler-step">
          <CalendarPicker selectedDate={selectedDate} availableDates={availableDates} onSelect={onSelectDate} />
          <div className="time-panel">
            <div className="time-panel-heading">
              <p className="eyebrow">Available times</p>
              <h3>{formatFullDate(selectedDate)}</h3>
            </div>
            <TimeSlotList
              date={selectedDate}
              slots={daySlots}
              selectedSlot={selectedSlot}
              label={`${meeting.durationMinutes} minutes`}
              loading={slotState === "loading"}
              emptyMessage={slotState === "loading" ? "Loading availability." : slotState === "error" ? "Availability could not be loaded." : "No times remain for this day."}
              onSelect={onSelectSlot}
            />
          </div>
        </div>
      </section>
      <section className="panel">
        <div className="section-heading tight">
          <div>
            <p className="eyebrow">Guest intake</p>
            <h2>{intakeTitle}</h2>
          </div>
        </div>
        <form
          className="intake"
          onSubmit={(event) => {
            event.preventDefault();
            const form = new globalThis.FormData(event.currentTarget);
            onSubmitBooking({
              name: String(form.get("name") ?? ""),
              email: String(form.get("email") ?? ""),
              reason: String(form.get("reason") ?? ""),
              consentAccepted: form.get("consentAccepted") === "on"
            });
          }}
        >
          <label>
            Name
            <input name="name" required placeholder="Guest name" />
          </label>
          <label>
            Email
            <input name="email" required type="email" placeholder="guest@example.com" />
          </label>
          <label>
            Reason
            <textarea name="reason" required maxLength={500} placeholder="What should Fredrick know before the meeting?" />
          </label>
          <label className="checkbox-row">
            <input name="consentAccepted" required type="checkbox" />
            <span>I agree to receive booking and calendar emails for this meeting.</span>
          </label>
          <label className="honeypot">
            Website
            <input tabIndex={-1} autoComplete="off" />
          </label>
          <button className="confirm" disabled={!selectedSlot || bookingState.status === "submitting"}>
            <CalendarCheck size={18} /> {bookingState.status === "submitting" ? "Confirming" : "Confirm selected time"}
          </button>
        </form>
        {bookingState.status === "confirmed" ? (
          <section className="success-panel" aria-live="polite">
            <CalendarCheck size={22} />
            <div>
              <h3>Booking confirmed</h3>
              <p>{bookingState.booking.id}</p>
              {bookingState.manageLinks ? (
                <div className="manage-actions">
                  <a href={bookingState.manageLinks.cancel}>Cancel</a>
                  <a href={bookingState.manageLinks.reschedule}>Reschedule</a>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}
        {bookingState.status === "error" ? <p className="form-error" aria-live="polite">{bookingState.message}</p> : null}
      </section>
    </div>
  );
}

function AvailabilitySettings({
  meeting,
  authState,
  adminData,
  onAdminDataChange,
  onMeetingTypeChange
}: {
  meeting: MeetingType;
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  onAdminDataChange?: ((data: AdminStatusResponse | null) => void) | undefined;
  onMeetingTypeChange?: ((meetingType: MeetingType) => void) | undefined;
}) {
  const [status, setStatus] = useState<OnboardingStatus>("idle");
  const [message, setMessage] = useState("");
  const firstWindow = meeting.availability[0];
  const [draftDays, setDraftDays] = useState(() => availabilityDraftDays(meeting));
  const [showOverrideForm, setShowOverrideForm] = useState(false);
  const overrides = [...(meeting.dateOverrides ?? [])].sort((left, right) => left.date.localeCompare(right.date));

  async function saveOverrides(next: DateOverride[], successMessage: string) {
    if (authState.status !== "signed_in") {
      setStatus("error");
      setMessage("Sign in as a host before editing availability.");
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch(`/v1/admin/meeting-types/${encodeURIComponent(meeting.id)}`, {
        method: "PUT",
        headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
        body: JSON.stringify({ dateOverrides: next })
      });
      const body = (await response.json().catch(() => ({}))) as { meetingType?: MeetingType; error?: { code?: string; message?: string } };
      if (!response.ok || !body.meetingType) {
        throw new Error(body.error?.message ?? body.error?.code ?? `override_save_${response.status}`);
      }
      onMeetingTypeChange?.(body.meetingType);
      if (onAdminDataChange) {
        await refreshAdminData(authState.idToken, onAdminDataChange);
      }
      setShowOverrideForm(false);
      setStatus("done");
      setMessage(successMessage);
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Could not save the date override.");
    }
  }

  function addOverride(formEvent: FormEvent<globalThis.HTMLFormElement>) {
    formEvent.preventDefault();
    const form = new globalThis.FormData(formEvent.currentTarget);
    const date = String(form.get("date") ?? "");
    if (!date) {
      setStatus("error");
      setMessage("Choose a date for the override.");
      return;
    }
    const unavailable = String(form.get("mode") ?? "unavailable") === "unavailable";
    const startMinute = timeInputToMinute(String(form.get("start") ?? "09:00"));
    const endMinute = timeInputToMinute(String(form.get("end") ?? "17:00"));
    if (!unavailable && endMinute <= startMinute) {
      setStatus("error");
      setMessage("The end time must be after the start time.");
      return;
    }
    const note = String(form.get("note") ?? "").trim();
    const next: DateOverride[] = [
      ...overrides.filter((override) => override.date !== date),
      {
        id: `override_${date}`,
        date,
        unavailable,
        ...(unavailable ? {} : { startMinute, endMinute }),
        ...(note ? { note } : {})
      }
    ];
    void saveOverrides(next, unavailable ? `Blocked ${formatOverrideDate(date)}.` : `Custom hours saved for ${formatOverrideDate(date)}.`);
  }

  useEffect(() => {
    setDraftDays(availabilityDraftDays(meeting));
  }, [meeting.id, meeting.availability]);

  function updateDraftDay(dayOfWeek: number, update: Partial<AvailabilityDraftDay>) {
    setDraftDays((current) => current.map((day) => day.dayOfWeek === dayOfWeek ? { ...day, ...update } : day));
  }

  async function updateMeeting(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    if (authState.status !== "signed_in") {
      setStatus("error");
      setMessage("Sign in as a host before editing availability.");
      return;
    }
    const form = new globalThis.FormData(event.currentTarget);
    const timeZone = String(form.get("timeZone") ?? firstWindow?.timeZone ?? "America/Los_Angeles");
    const availability = draftDays
      .filter((day) => day.enabled)
      .map((day) => ({
        dayOfWeek: day.dayOfWeek,
        startMinute: timeInputToMinute(day.start),
        endMinute: timeInputToMinute(day.end),
        timeZone
      }));
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch(`/v1/admin/meeting-types/${meeting.id}`, {
        method: "PUT",
        headers: {
          authorization: `Bearer ${authState.idToken}`,
          "content-type": "application/json"
        },
        body: JSON.stringify({
          title: meeting.title,
          description: meeting.description,
          active: meeting.active,
          durationMinutes: Number(form.get("durationMinutes") ?? meeting.durationMinutes),
          slotIntervalMinutes: Number(form.get("slotIntervalMinutes") ?? meeting.slotIntervalMinutes),
          minimumNoticeMinutes: Number(form.get("minimumNoticeMinutes") ?? meeting.minimumNoticeMinutes),
          horizonDays: Number(form.get("horizonDays") ?? meeting.horizonDays),
          preBufferMinutes: meeting.preBufferMinutes,
          postBufferMinutes: meeting.postBufferMinutes,
          maxBookingsPerDay: Number(form.get("maxBookingsPerDay") ?? meeting.maxBookingsPerDay ?? 6),
          timeZone,
          availability
        })
      });
      const body = (await response.json().catch(() => ({}))) as MeetingTypeUpdateResponse & { error?: { details?: string[]; message?: string; code?: string } };
      if (!response.ok) {
        throw new Error(body.error?.details?.join(", ") ?? body.error?.message ?? body.error?.code ?? `meeting_update_${response.status}`);
      }
      if (adminData && onAdminDataChange) {
        onAdminDataChange({
          ...adminData,
          meetingTypes: adminData.meetingTypes.map((item) => item.id === body.meetingType.id ? body.meetingType : item)
        });
      }
      onMeetingTypeChange?.(body.meetingType);
      setStatus("done");
      setMessage("Availability saved.");
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Availability update failed.");
    }
  }

  return (
    <div className="availability-screen">
      <form className="availability-layout" onSubmit={(event) => void updateMeeting(event)}>
        <section className="availability-title">
          <div>
            <p className="eyebrow">Working hours</p>
            <h2>Availability</h2>
            <p>Set the windows when people may book. Calendar conflicts are removed automatically.</p>
          </div>
          <button type="submit" disabled={authState.status !== "signed_in" || status === "submitting"}>
            {status === "submitting" ? "Saving" : "Save changes"}
          </button>
        </section>

        {message ? <p className={status === "error" ? "form-error" : "inline-note"} aria-live="polite">{message}</p> : null}

        <section className="availability-workweek">
          <div className="availability-card-title">
            <div>
              <h3>Standard workweek</h3>
              <span>{firstWindow?.timeZone ?? "America/Los_Angeles"}</span>
            </div>
            <label className="availability-timezone">
              <Globe2 size={17} />
              <TimeZoneSelect name="timeZone" value={firstWindow?.timeZone ?? "America/Los_Angeles"} ariaLabel="Time zone" />
            </label>
          </div>

          <div className="availability-days">
            {draftDays.map((day) => (
              <article key={day.dayOfWeek} className={day.enabled ? "enabled" : ""}>
                <label className="availability-toggle">
                  <input
                    name="availabilityDays"
                    type="checkbox"
                    value={day.dayOfWeek}
                    checked={day.enabled}
                    onChange={(event) => updateDraftDay(day.dayOfWeek, { enabled: event.currentTarget.checked })}
                  />
                  <span />
                </label>
                <strong>{fullDayName(day.dayOfWeek)}</strong>
                {day.enabled ? (
                  <>
                    <input
                      type="time"
                      aria-label={`${fullDayName(day.dayOfWeek)} start time`}
                      value={day.start}
                      onChange={(event) => updateDraftDay(day.dayOfWeek, { start: event.currentTarget.value })}
                    />
                    <em>-</em>
                    <input
                      type="time"
                      aria-label={`${fullDayName(day.dayOfWeek)} end time`}
                      value={day.end}
                      onChange={(event) => updateDraftDay(day.dayOfWeek, { end: event.currentTarget.value })}
                    />
                    <button type="button" aria-label={`Add ${fullDayName(day.dayOfWeek)} time window`} disabled>
                      <Plus size={17} />
                    </button>
                  </>
                ) : (
                  <span className="availability-unavailable">Unavailable</span>
                )}
              </article>
            ))}
          </div>
        </section>

        <aside className="availability-side">
          <section className="availability-date-overrides">
            <div className="availability-side-title">
              <h3>Date overrides</h3>
              <p>Adjust hours for travel or holidays.</p>
            </div>
            <button type="button" className="availability-outline-button" onClick={() => setShowOverrideForm((current) => !current)}>
              <Plus size={16} /> Add date override
            </button>

            {showOverrideForm ? (
              <form className="override-form" onSubmit={addOverride}>
                <label>
                  Date
                  <input name="date" type="date" required defaultValue={initialDateKey()} />
                </label>
                <label>
                  This day is
                  <select name="mode" defaultValue="unavailable">
                    <option value="unavailable">Unavailable all day</option>
                    <option value="custom">Available with different hours</option>
                  </select>
                </label>
                <div className="override-form-hours">
                  <label>From<input name="start" type="time" defaultValue="09:00" /></label>
                  <label>To<input name="end" type="time" defaultValue="17:00" /></label>
                </div>
                <label>
                  Note <span className="override-optional">optional</span>
                  <input name="note" maxLength={60} placeholder="Travel, holiday, school run" />
                </label>
                <div className="override-form-actions">
                  <button type="button" onClick={() => setShowOverrideForm(false)}>Cancel</button>
                  <button className="confirm" disabled={status === "submitting"}>
                    {status === "submitting" ? "Saving" : "Save override"}
                  </button>
                </div>
              </form>
            ) : null}

            {overrides.length === 0 ? (
              <p className="override-empty">No date overrides yet. Your weekly hours apply every week.</p>
            ) : (
              overrides.map((override) => (
                <article key={override.date} className="availability-override-row">
                  <time>
                    <strong>{override.date.slice(8, 10)}</strong>
                    <span>{monthAbbreviation(override.date)}</span>
                  </time>
                  <div>
                    <strong>{override.unavailable ? "Unavailable" : "Custom hours"}</strong>
                    <span>
                      {override.unavailable
                        ? "All day"
                        : `${minuteToTimeInput(override.startMinute ?? 0)} - ${minuteToTimeInput(override.endMinute ?? 0)}`}
                      {override.note ? ` · ${override.note}` : ""}
                    </span>
                  </div>
                  <ActionMenu
                    label={`Actions for the ${formatOverrideDate(override.date)} override`}
                    items={[
                      {
                        label: override.unavailable ? "Switch to custom hours" : "Block the whole day",
                        onSelect: () =>
                          void saveOverrides(
                            overrides.map((item) =>
                              item.date === override.date
                                ? item.unavailable
                                  ? { ...item, unavailable: false, startMinute: 9 * 60, endMinute: 17 * 60 }
                                  : { id: item.id, date: item.date, unavailable: true, ...(item.note ? { note: item.note } : {}) }
                                : item
                            ),
                            `Updated ${formatOverrideDate(override.date)}.`
                          )
                      },
                      {
                        label: "Remove override",
                        danger: true,
                        onSelect: () =>
                          void saveOverrides(
                            overrides.filter((item) => item.date !== override.date),
                            `Removed the override for ${formatOverrideDate(override.date)}.`
                          )
                      }
                    ]}
                  />
                </article>
              ))
            )}
          </section>

          <section className="availability-rules-card">
            <div className="availability-side-title">
              <h3>Booking rules</h3>
            </div>
            <label>
              <span><Clock size={16} /> Minimum notice</span>
              <input name="minimumNoticeMinutes" type="number" min="0" max="10080" step="15" defaultValue={meeting.minimumNoticeMinutes} aria-label="Minimum notice minutes" />
            </label>
            <label>
              <span><ArrowRight size={16} /> Booking horizon</span>
              <input name="horizonDays" type="number" min="1" max="90" defaultValue={meeting.horizonDays} aria-label="Booking horizon days" />
            </label>
            <label>
              <span><CalendarCheck size={16} /> Daily limit</span>
              <input name="maxBookingsPerDay" type="number" min="1" max="50" defaultValue={meeting.maxBookingsPerDay ?? 6} aria-label="Daily booking limit" />
            </label>
            <input name="durationMinutes" type="hidden" value={meeting.durationMinutes} readOnly />
            <input name="slotIntervalMinutes" type="hidden" value={meeting.slotIntervalMinutes} readOnly />
            <button type="button" className="availability-link-button">
              Edit booking rules <ArrowRight size={15} />
            </button>
          </section>
        </aside>
      </form>
    </div>
  );
}

interface AvailabilityDraftDay {
  dayOfWeek: number;
  enabled: boolean;
  start: string;
  end: string;
}

function availabilityDraftDays(meeting: MeetingType): AvailabilityDraftDay[] {
  const windowsByDay = new Map(meeting.availability.map((window) => [window.dayOfWeek, window]));
  return [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => {
    const window = windowsByDay.get(dayOfWeek);
    return {
      dayOfWeek,
      enabled: Boolean(window),
      start: minuteToTimeInput(window?.startMinute ?? 9 * 60),
      end: minuteToTimeInput(window?.endMinute ?? (dayOfWeek === 5 ? 15 * 60 : 17 * 60))
    };
  });
}

function formatOverrideDate(date: string): string {
  return new Intl.DateTimeFormat(undefined, { month: "long", day: "numeric", timeZone: "UTC" }).format(
    new Date(`${date}T12:00:00.000Z`)
  );
}

function monthAbbreviation(date: string): string {
  return new Intl.DateTimeFormat(undefined, { month: "short", timeZone: "UTC" }).format(new Date(`${date}T12:00:00.000Z`));
}

function minuteToTimeInput(minute: number): string {
  const hour = Math.floor(minute / 60);
  const minutes = minute % 60;
  return `${String(hour).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function timeInputToMinute(value: string): number {
  const [hour = "9", minute = "0"] = value.split(":");
  return Number(hour) * 60 + Number(minute);
}

function fullDayName(dayOfWeek: number): string {
  return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][dayOfWeek] ?? "Day";
}

function LinksView({
  authState,
  adminData,
  persona: initialPersona,
  personas,
  meeting,
  meetingTypes,
  hostSlug,
  onOpenView
}: {
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  persona: Persona;
  personas: Persona[];
  meeting: MeetingType;
  meetingTypes: MeetingType[];
  hostSlug: string;
  onOpenView: (view: View) => void;
}) {
  const [copyState, setCopyState] = useState("");
  // Every link on this screen belongs to one persona, so the persona is the first
  // choice made here rather than something inherited from another screen.
  const shareablePersonas = personas.length > 0 ? personas : [initialPersona];
  const [personaId, setPersonaId] = useState(initialPersona.id);
  const persona = shareablePersonas.find((item) => item.id === personaId) ?? shareablePersonas[0] ?? initialPersona;
  const personaLink = `${appOrigin}/${hostSlug}/${persona.slug}`;
  const personaMeetings = meetingTypes.filter((item) => item.personaId === persona.id && item.active !== false);
  const meetingLinks = personaMeetings.length > 0 ? personaMeetings : persona.id === initialPersona.id ? [meeting] : [];
  const shortUrls = useShortLinks(authState, [
    { targetUrl: personaLink, source: `persona:${persona.slug}` },
    ...meetingLinks.map((item) => ({ targetUrl: `${personaLink}/${item.slug}`, source: `persona:${persona.slug}:meeting:${item.slug}` }))
  ]);
  const personaShareUrl = shortUrls[personaLink] ?? personaLink;
  const signatureText = `${persona.displayName}\n${persona.organization ?? persona.headline}\nSchedule a conversation with me - ${personaShareUrl}`;
  void adminData;
  const signatureHtml = `<strong>${escapeHtml(persona.displayName)}</strong><br><span>${escapeHtml(persona.organization ?? persona.headline)}</span><br><a href="${escapeHtml(personaShareUrl)}">Schedule a conversation with me</a>`;

  async function copyValue(value: string, label: string) {
    await globalThis.navigator.clipboard.writeText(value);
    setCopyState(`${label} copied`);
  }

  async function copyShareLink(targetUrl: string, source: string, label: string) {
    const existingShortUrl = shortUrls[targetUrl];
    if (existingShortUrl) {
      await copyValue(existingShortUrl, `${label} short link`);
      return;
    }
    if (authState.status !== "signed_in") {
      await copyValue(targetUrl, label);
      return;
    }

    try {
      const response = await apiFetch("/v1/admin/short-links", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${authState.idToken}`
        },
        body: JSON.stringify({ targetUrl, source })
      });
      const body = await response.json().catch(() => ({})) as { shortUrl?: string };
      const copiedUrl = response.ok && body.shortUrl ? body.shortUrl : targetUrl;
      await copyValue(copiedUrl, response.ok && body.shortUrl ? `${label} short link` : label);
    } catch {
      await copyValue(targetUrl, label);
    }
  }

  return (
    <div className="share-links-screen">
      <section className="share-title">
        <div>
          <p className="eyebrow">Get booked</p>
          <h2>Share links</h2>
          <p>Copy a persona page, direct meeting link, or ready-made email signature.</p>
        </div>
        {shareablePersonas.length > 1 ? (
          <div className="share-persona-picker" role="group" aria-label="Choose a persona">
            {shareablePersonas.map((item) => (
              <button
                key={item.id}
                type="button"
                className={item.id === persona.id ? "active" : ""}
                aria-pressed={item.id === persona.id}
                style={{ "--persona-accent": item.accentColor } as CSSProperties}
                onClick={() => {
                  setPersonaId(item.id);
                  setCopyState("");
                }}
              >
                <i>{personaInitials(item)}</i>
                <span>
                  <strong>{item.name}</strong>
                  <small>{item.publicState === "active" ? "Live" : item.publicState === "private_link" ? "Private link" : "Draft"}</small>
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </section>

      <section className="share-feature-card">
        <div className="share-preview">
          <div className="share-browserbar">••• <span>{new URL(personaLink).hostname}</span></div>
          <span className="share-avatar" style={{ background: persona.accentColor }}>{personaInitials(persona)}</span>
          <h3>Meet with {persona.displayName.split(" ")[0] ?? persona.displayName}</h3>
          <p>Choose a conversation and find a time that works.</p>
          <article>
            <i style={{ background: persona.accentColor }} />
            <div>
              <strong>{meeting.title}</strong>
              <small>{meeting.durationMinutes} minutes · {locationLabel(persona)}</small>
            </div>
            <ChevronRight size={15} />
          </article>
        </div>
        <div className="share-copy-panel">
          <span>{persona.name}</span>
          <h3>Persona booking page</h3>
          <p>Best for your email signature or social profile. Visitors choose from all active {persona.name} meeting types.</p>
          <div className="share-copybox">
            <code>{displayUrl(personaShareUrl)}</code>
            <button type="button" onClick={() => void copyShareLink(personaLink, `persona:${persona.slug}`, "Persona link")}>
              <Copy size={16} /> Copy
            </button>
          </div>
          <a href={personaLink} target="_blank" rel="noreferrer">Open booking page <ArrowRight size={15} /></a>
        </div>
      </section>

      <div className="share-lower-grid">
        <section className="share-card">
          <h2>Direct meeting links</h2>
          <p>Send someone straight to a conversation.</p>
          <div className="share-meeting-list">
            {meetingLinks.length === 0 ? (
              <p className="empty-state">{persona.name} has no active meeting types yet.</p>
            ) : null}
            {meetingLinks.map((item) => {
              const link = `${personaLink}/${item.slug}`;
              const shareUrl = shortUrls[link] ?? link;
              return (
                <div key={item.id} className="share-meeting-row">
                  <i style={{ background: persona.accentColor }} />
                  <span>
                    <strong>{item.title}</strong>
                    <small>{displayUrl(shareUrl)} · {item.durationMinutes} minutes</small>
                  </span>
                  <button type="button" onClick={() => void copyShareLink(link, `persona:${persona.slug}:meeting:${item.slug}`, item.title)}>
                    <Copy size={15} /> Copy
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        <section className="share-card">
          <h2>Email signature</h2>
          <p>A simple call-to-action for any email client.</p>
          <div className="signature-preview">
            <strong>{persona.displayName}</strong>
            <span>{persona.organization ?? persona.headline}</span>
            <a href={personaLink}>Schedule a conversation with me →</a>
          </div>
          <div className="signature-actions">
            <button type="button" onClick={() => void copyValue(signatureHtml, "Signature HTML")}>
              <Copy size={15} /> Copy HTML
            </button>
            <button type="button" onClick={() => void copyValue(signatureText, "Signature text")}>
              <Copy size={15} /> Copy text
            </button>
          </div>
        </section>
      </div>

      <section className="share-card share-extension-card">
        <span className="share-extension-icon"><Puzzle size={22} /></span>
        <div>
          <h2>Copy links from your browser toolbar</h2>
          <p>The TrueAvail Chrome extension puts every persona link one click away while you write an email or message.</p>
        </div>
        <button type="button" onClick={() => onOpenView("extension")}>
          Get the extension <ArrowRight size={15} />
        </button>
      </section>

      <span className="share-copy-status" aria-live="polite">{copyState}</span>
    </div>
  );
}

const extensionDownloadPath = "/downloads/trueavail-share-links.zip";

/**
 * Install page for the Share Links Chrome extension. It isn't in the Chrome Web Store,
 * so hosts download the zip and load it unpacked, and this page walks them through
 * that and through replacing it when the version changes.
 */
function ChromeExtensionView({ onExit }: { onExit: () => void }) {
  const [copied, setCopied] = useState(false);

  async function copyExtensionsUrl() {
    // Chrome won't open chrome:// URLs from a web page, so the host pastes it instead.
    await globalThis.navigator.clipboard.writeText("chrome://extensions");
    setCopied(true);
    globalThis.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="extension-page">
      <button type="button" className="connect-back" onClick={onExit}>
        <ArrowLeft size={16} /> Back to share links
      </button>

      <section className="share-card extension-hero">
        <span className="share-extension-icon"><Puzzle size={26} /></span>
        <div>
          <p className="eyebrow">Chrome extension · version {__EXTENSION_VERSION__}</p>
          <h2>TrueAvail Share Links</h2>
          <p>
            Click the TrueAvail icon in your toolbar, pick a persona, and its short link is on your clipboard, ready to paste
            into an email or chat. Sign in once with your TrueAvail email and password, and it stays signed in for 30 days.
          </p>
        </div>
        <a className="extension-download" href={extensionDownloadPath} download>
          <Download size={17} /> Download for Chrome
        </a>
      </section>

      <section className="share-card">
        <h2>Install it</h2>
        <p>This takes about a minute. It works in desktop Chrome, Edge, Brave and Arc.</p>
        <ol className="extension-steps">
          <li>
            <strong>Unzip the download and move the folder somewhere permanent,</strong> like your Documents folder. Chrome loads
            the extension from that folder every time, so don't delete it or leave it in Downloads.
          </li>
          <li>
            <strong>Open your extensions page.</strong> Paste this into the address bar:
            <span className="share-copybox extension-url">
              <code>chrome://extensions</code>
              <button type="button" onClick={() => void copyExtensionsUrl()}>
                <Copy size={15} /> {copied ? "Copied" : "Copy"}
              </button>
            </span>
          </li>
          <li>
            <strong>Turn on Developer mode</strong> with the switch in the top-right corner.
          </li>
          <li>
            <strong>Click Load unpacked</strong> and choose the <code>trueavail-share-links</code> folder.
          </li>
          <li>
            <strong>Pin it.</strong> Click the puzzle-piece icon in the toolbar, then the pin next to TrueAvail Share Links.
          </li>
        </ol>
      </section>

      <div className="share-lower-grid">
        <section className="share-card">
          <h2>Use it</h2>
          <p>
            Click the icon or press <kbd>Alt</kbd>+<kbd>Shift</kbd>+<kbd>L</kbd>, then click a persona or press its number. The
            link is copied and the popup closes.
          </p>
        </section>
        <section className="share-card">
          <h2>Update it</h2>
          <p>
            It doesn't update itself. When the version above is newer than the one on your extensions page, download it again,
            replace the old folder with the new one, and click the reload arrow on the extension.
          </p>
        </section>
      </div>
    </div>
  );
}

function personaInitials(persona: Persona | undefined): string {
  if (!persona) {
    return "FM";
  }
  const nameParts = persona.name.split(/\s+/).filter(Boolean);
  const source = nameParts.length > 1 ? persona.name : persona.displayName;
  return source
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "P";
}

function displayUrl(link: string): string {
  return link.replace(/^https?:\/\//, "");
}

/**
 * Short links for a set of URLs, keyed by the long URL they stand for. The API mints
 * one code per target and hands the same one back on a repeat call, so running this
 * on mount costs nothing and never accumulates codes. A target that fails to shorten
 * is simply absent, and callers fall back to the long URL rather than showing nothing.
 */
function useShortLinks(authState: AuthState, targets: Array<{ targetUrl: string; source: string }>): Record<string, string> {
  const [shortUrls, setShortUrls] = useState<Record<string, string>>({});
  // Callers build this array inline, so it is a new object on every render. The
  // effect keys off its contents instead, which is what actually changes.
  const targetKey = targets.map((target) => `${target.source}|${target.targetUrl}`).join("\n");
  const idToken = authState.status === "signed_in" ? authState.idToken : "";

  useEffect(() => {
    if (!idToken || targetKey === "") {
      setShortUrls({});
      return;
    }
    const controller = new globalThis.AbortController();
    Promise.all(
      targetKey.split("\n").map(async (entry) => {
        const separator = entry.indexOf("|");
        const source = entry.slice(0, separator);
        const targetUrl = entry.slice(separator + 1);
        return [targetUrl, await createShortLink(idToken, targetUrl, source, controller.signal)] as const;
      })
    )
      .then((links) => setShortUrls(Object.fromEntries(links.filter((link): link is readonly [string, string] => Boolean(link[1])))))
      .catch(() => undefined);
    return () => controller.abort();
  }, [idToken, targetKey]);

  return shortUrls;
}

async function createShortLink(
  idToken: string,
  targetUrl: string,
  source: string,
  signal?: globalThis.AbortSignal
): Promise<string | undefined> {
  const response = await apiFetch("/v1/admin/short-links", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${idToken}`
    },
    body: JSON.stringify({ targetUrl, source }),
    ...(signal ? { signal } : {})
  });
  if (!response.ok) {
    return undefined;
  }
  const body = (await response.json().catch(() => ({}))) as { shortUrl?: string };
  return body.shortUrl?.startsWith(`${shortLinkOrigin}/`) ? body.shortUrl : undefined;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => {
    switch (character) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case "\"":
        return "&quot;";
      case "'":
        return "&#39;";
      default:
        return character;
    }
  });
}

function CalendarsView({
  authState,
  adminData,
  connections,
  onAdminDataChange,
  onStartConnect,
  onManageConnection
}: {
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  connections: CalendarConnection[];
  onAdminDataChange: (data: AdminStatusResponse | null) => void;
  onStartConnect: () => void;
  onManageConnection: (connectionId: string, mode: CalendarManageMode | "disconnect") => void;
}) {
  const [status, setStatus] = useState<OnboardingStatus>("idle");
  const [message, setMessage] = useState("");
  const [selectedConnectionId, setSelectedConnectionId] = useState(connections[0]?.id ?? "");
  // Kept only as a manual fallback; the guided wizard is the primary path now.
  const [showConnectPanel] = useState(false);
  const [pendingDeleteConnectionId, setPendingDeleteConnectionId] = useState<string | null>(null);
  const host = signedInHost(authState, adminData);
  const selectedConnection = connections.find((connection) => connection.id === selectedConnectionId) ?? connections[0];
  const resourcesByConnection = new Map<string, CalendarResource[]>();
  for (const resource of adminData?.calendarResources ?? []) {
    resourcesByConnection.set(resource.connectionId, [...(resourcesByConnection.get(resource.connectionId) ?? []), resource]);
  }
  const conflictCalendarCount = [...resourcesByConnection.values()]
    .flat()
    .filter((resource) => resource.blocksAvailability).length;
  const liveConnectionCount = connections.filter((connection) => connection.status === "connected").length;
  // A calendar that authenticates but cannot be read still blocks every requested
  // time, which empties the public booking page without changing any status.
  const unreadableConnections = connections.filter(
    (connection) => connection.lastReadFailure && connection.status !== "disabled"
  );

  useEffect(() => {
    if (!selectedConnectionId && connections[0]) {
      setSelectedConnectionId(connections[0].id);
    }
    if (selectedConnectionId && connections.length > 0 && !connections.some((connection) => connection.id === selectedConnectionId)) {
      setSelectedConnectionId(connections[0]!.id);
    }
  }, [connections, selectedConnectionId]);

  async function createConnection(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    if (authState.status !== "signed_in" || !host) {
      setStatus("error");
      setMessage("Sign in as a host before adding calendars.");
      return;
    }

    const form = new globalThis.FormData(event.currentTarget);
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch("/v1/admin/calendar-connections", {
        method: "POST",
        headers: {
          authorization: `Bearer ${authState.idToken}`,
          "content-type": "application/json"
        },
        body: JSON.stringify({
          hostId: host.id,
          hostSlug: host.slug,
          provider: String(form.get("provider") ?? "mock"),
          label: String(form.get("label") ?? ""),
          resourceLabel: String(form.get("resourceLabel") ?? ""),
          providerCalendarId: String(form.get("providerCalendarId") ?? "primary"),
          username: String(form.get("username") ?? ""),
          appPassword: String(form.get("appPassword") ?? ""),
          serverUrl: String(form.get("serverUrl") ?? "https://caldav.icloud.com"),
          blocksAvailability: form.get("blocksAvailability") === "on",
          destinationPriority: Number(form.get("destinationPriority") ?? 1)
        })
      });
      const body = (await response.json().catch(() => ({}))) as { connection?: CalendarConnection; error?: { details?: string[]; message?: string; code?: string } };
      if (!response.ok || !body.connection) {
        throw new Error(body.error?.details?.join(", ") ?? body.error?.message ?? body.error?.code ?? `calendar_create_${response.status}`);
      }
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setStatus("done");
      setMessage(`Saved ${body.connection.label}.`);
      event.currentTarget.reset();
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Calendar connection failed.");
    }
  }

  async function startOAuth(provider: "google" | "microsoft" | "zoho") {
    if (authState.status !== "signed_in" || !host) {
      setStatus("error");
      setMessage("Sign in as a host before connecting calendars.");
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch("/v1/admin/calendar-connections/oauth/start", {
        method: "POST",
        headers: {
          authorization: `Bearer ${authState.idToken}`,
          "content-type": "application/json"
        },
        body: JSON.stringify({
          provider,
          hostId: host.id,
          redirectUri: `${appOrigin}/v1/onboarding/calendar-connections/oauth/callback`
        })
      });
      const body = (await response.json().catch(() => ({}))) as { authorizationUrl?: string; error?: { code?: string; message?: string } };
      if (!response.ok || !body.authorizationUrl) {
        throw new Error(body.error?.message ?? body.error?.code ?? `oauth_start_${response.status}`);
      }
      globalThis.location.assign(body.authorizationUrl);
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "OAuth setup is not available.");
    }
  }


  async function deleteConnection(connectionId: string) {
    if (authState.status !== "signed_in") {
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch(`/v1/admin/calendar-connections/${encodeURIComponent(connectionId)}`, {
        method: "DELETE",
        headers: { authorization: `Bearer ${authState.idToken}` }
      });
      const body = (await response.json().catch(() => ({}))) as { deleted?: boolean; error?: { code?: string; message?: string } };
      if (!response.ok) {
        throw new Error(body.error?.message ?? body.error?.code ?? `connection_delete_${response.status}`);
      }
      setPendingDeleteConnectionId(null);
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setStatus("done");
      setMessage("Calendar connection deleted.");
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Could not delete the connection.");
    }
  }


  return (
    <div className="calendars-screen">
      <section className="calendars-title">
        <div>
          <p className="eyebrow">Unified availability</p>
          <h2>Connected calendars</h2>
          <p>Busy time from every selected calendar is combined before a booking slot is shown.</p>
        </div>
        <button type="button" onClick={onStartConnect}>
          <Plus size={17} /> Connect calendar
        </button>
      </section>

      {unreadableConnections.length > 0 ? (
        <section className="calendar-health blocked" role="alert">
          <span><ShieldCheck size={23} /></span>
          <div>
            <strong>
              {unreadableConnections.length === 1
                ? "A calendar cannot be read, so no times are being offered"
                : `${unreadableConnections.length} calendars cannot be read, so no times are being offered`}
            </strong>
            <p>
              A calendar that cannot be read blocks every time in the window, so a booking can never collide with something
              unseen. Until {unreadableConnections.length === 1 ? "it reads again" : "they read again"} your booking pages show
              no availability at all: {unreadableConnections.map((item) => item.label || calendarProviderLabel(item.provider)).join(", ")}.
            </p>
          </div>
          <em>Bookings blocked</em>
        </section>
      ) : (
        <section className={conflictCalendarCount > 0 ? "calendar-health" : "calendar-health warning"}>
          <span><ShieldCheck size={23} /></span>
          <div>
            <strong>{conflictCalendarCount > 0 ? "Your calendars protect every persona" : "Nothing is protecting your availability"}</strong>
            <p>
              {conflictCalendarCount > 0
                ? `${conflictCalendarCount} ${conflictCalendarCount === 1 ? "calendar is" : "calendars are"} checked for conflicts across ${providerSummary(connections)}.`
                : "Guests can book over the top of anything already in your day until a calendar is connected."}
            </p>
          </div>
          <em>{liveConnectionCount > 0 ? "Live sync" : "Connect calendars"}</em>
        </section>
      )}

      {message ? <p className={status === "error" ? "form-error" : "inline-note"} aria-live="polite">{message}</p> : null}

      {pendingDeleteConnectionId ? (
        <section className="persona-confirm" role="alertdialog" aria-labelledby="connection-delete-title">
          <div>
            <h3 id="connection-delete-title">Delete this calendar connection?</h3>
            <p>
              Access is revoked at the provider and the stored tokens, calendar selections and connection record are removed. Meetings already
              on your calendar are left untouched.
            </p>
            <p className="persona-confirm-hint">
              To pause a calendar without losing its settings, use <strong>Disconnect</strong> from the manage screen instead.
            </p>
          </div>
          <div className="persona-confirm-actions">
            <button type="button" onClick={() => setPendingDeleteConnectionId(null)}>Keep connection</button>
            <button
              type="button"
              className="danger"
              disabled={status === "submitting"}
              onClick={() => void deleteConnection(pendingDeleteConnectionId)}
            >
              {status === "submitting" ? "Deleting" : "Delete permanently"}
            </button>
          </div>
        </section>
      ) : null}

      {showConnectPanel ? (
        <section className="calendar-connect-panel">
          <div className="calendar-connect-actions">
            {(["google", "microsoft", "zoho"] as const).map((provider) => (
              <button key={provider} type="button" onClick={() => void startOAuth(provider)}>
                <span className={`calendar-provider-mark ${providerInitial(provider).tone}`}>{providerInitial(provider).initial}</span>
                Connect {calendarProviderLabel(provider)}
              </button>
            ))}
          </div>
          <div className="calendar-provider-guidance" role="note">
            <strong>Provider setup</strong>
            <p><b>iCloud:</b> create an Apple app-specific password at <span>appleid.apple.com</span>; your normal Apple ID password will not work.</p>
            <p><b>Zoho with MFA:</b> create and use a Zoho app-specific password from Zoho Accounts for password-based connections. OAuth is also available when your Zoho OAuth app is configured.</p>
          </div>
          <details>
            <summary>Manual calendar setup</summary>
            <form className="intake" onSubmit={(event) => void createConnection(event)}>
              <label>Provider<select name="provider" defaultValue="mock"><option value="mock">Dev mock calendar</option><option value="google">Google Calendar</option><option value="microsoft">Microsoft 365</option><option value="zoho">Zoho Calendar</option><option value="caldav">iCloud Calendar</option></select></label>
              <label>Connection label<input name="label" required defaultValue="Primary calendar" /></label>
              <label>Calendar label<input name="resourceLabel" required defaultValue="Main availability" /></label>
              <label>Provider calendar id<input name="providerCalendarId" required defaultValue="primary" /></label>
              <label>iCloud email<input name="username" type="email" autoComplete="username" placeholder="you@icloud.com" /></label>
              <label>App-specific password<input name="appPassword" type="password" autoComplete="new-password" placeholder="Required for iCloud" /></label>
              <label>CalDAV server<input name="serverUrl" type="url" defaultValue="https://caldav.icloud.com" /></label>
              <label>Destination priority<input name="destinationPriority" type="number" min="0" max="100" defaultValue="1" /></label>
              <label className="checkbox-row">
                <input name="blocksAvailability" defaultChecked type="checkbox" />
                <span>Use this calendar to block availability.</span>
              </label>
              <button className="confirm" disabled={status === "submitting"}>
                <CalendarCheck size={18} /> {status === "submitting" ? "Saving" : "Save connection"}
              </button>
            </form>
          </details>
        </section>
      ) : null}

      <section className="calendar-table-card">
        <div className="calendar-table-head">
          <span>Connection</span>
          <span>Conflict calendars</span>
          <span>Booking destination</span>
          <span>Status</span>
          <span />
        </div>
        <div className="calendar-table-body">
          {connections.length === 0 ? (
            <div className="calendar-empty-state">
              <strong>No calendars connected yet.</strong>
              <p>Connect a calendar before publishing availability for bookings.</p>
            </div>
          ) : connections.map((connection) => {
            const resources = resourcesByConnection.get(connection.id) ?? [];
            const conflictCount = resources.filter((resource) => resource.blocksAvailability).length;
            const destination = primaryCalendarDestination(resources);
            const provider = providerInitial(connection.provider);
            return (
              <article
                key={connection.id}
                className={selectedConnection?.id === connection.id ? "active" : ""}
                onClick={() => setSelectedConnectionId(connection.id)}
              >
                <button type="button" className="calendar-connection-cell" onClick={() => onManageConnection(connection.id, "details")}>
                  <i className={provider.tone}>{provider.initial}</i>
                  <span>
                    <strong>{connection.label || calendarProviderLabel(connection.provider)}</strong>
                    <small>{calendarConnectionSubtitle(connection)}</small>
                    {connection.lastReadFailure ? (
                      <em
                        className="calendar-read-failure"
                        // The column is narrow and the provider's reason is not, so the
                        // full text stays reachable rather than being lost to an ellipsis.
                        title={`Could not be read ${relativeTimeLabel(connection.lastReadFailure.at)}: ${connection.lastReadFailure.reason}`}
                      >
                        Could not be read {relativeTimeLabel(connection.lastReadFailure.at)} · {connection.lastReadFailure.reason}
                      </em>
                    ) : null}
                  </span>
                </button>
                <button type="button" className="calendar-select" onClick={() => onManageConnection(connection.id, "edit")}>
                  {conflictCount} selected <ChevronDown size={15} />
                </button>
                <button type="button" className="calendar-select" onClick={() => onManageConnection(connection.id, "edit")}>
                  {destination} <ChevronDown size={15} />
                </button>
                <span className={`calendar-status ${calendarConnectionHealth(connection).tone}`}>
                  {calendarConnectionHealth(connection).label}
                </span>
                <ActionMenu
                  label={`More actions for ${connection.label || calendarProviderLabel(connection.provider)}`}
                  items={[
                    { label: "Manage calendar", onSelect: () => onManageConnection(connection.id, "details") },
                    { label: "Edit settings", onSelect: () => onManageConnection(connection.id, "edit") },
                    {
                      label: "Reconnect account",
                      disabled: connection.provider === "caldav",
                      onSelect: () => onManageConnection(connection.id, "reconnect")
                    },
                    { label: "Disconnect…", disabled: connection.status === "disabled", onSelect: () => onManageConnection(connection.id, "disconnect") },
                    {
                      label: "Delete permanently",
                      danger: true,
                      onSelect: () => setPendingDeleteConnectionId(connection.id)
                    }
                  ]}
                />
              </article>
            );
          })}
        </div>
      </section>

      <section className="calendar-privacy-card">
        <LockKeyhole size={19} />
        <div>
          <strong>Privacy first</strong>
          <p>Visitors only see available times, never event names, guests, or which calendar blocked a slot.</p>
        </div>
      </section>

    </div>
  );
}


type BookStep = "who" | "when" | "details" | "confirmed";

/**
 * Host-side booking. Guests use the public persona page; this is the same flow
 * for someone booking on a guest's behalf, with the persona chosen up front
 * instead of implied by the URL. It deliberately posts to the same public
 * endpoint a guest would use, so a booking made here is not a special case
 * anywhere downstream.
 */
function BookMeetingView({
  adminData,
  onExit,
  onOpenView
}: {
  adminData: AdminStatusResponse | null;
  onExit: () => void;
  onOpenView: (view: View) => void;
}) {
  // An inactive persona has no public page, so it cannot be booked at all.
  const personas = useMemo(() => (adminData?.personas ?? []).filter((item) => item.publicState !== "inactive"), [adminData]);
  const meetingTypes = useMemo(() => adminData?.meetingTypes ?? [], [adminData]);
  const hosts = useMemo(() => adminData?.hosts ?? [], [adminData]);

  const [step, setStep] = useState<BookStep>("who");
  const [personaId, setPersonaId] = useState("");
  const [meetingTypeId, setMeetingTypeId] = useState("");
  const [selectedDate, setSelectedDate] = useState(() => initialDateKey());
  const [slots, setSlots] = useState<TimeInterval[]>([]);
  const [slotState, setSlotState] = useState<LoadState>("loading");
  const [selectedSlot, setSelectedSlot] = useState<TimeInterval | null>(null);
  const [bookingState, setBookingState] = useState<BookingState>({ status: "idle" });

  const persona = personas.find((item) => item.id === personaId);
  const host = hosts.find((item) => item.id === persona?.hostId);
  const personaMeetings = useMemo(
    () => meetingTypes.filter((item) => item.personaId === personaId && item.active),
    [meetingTypes, personaId]
  );
  const meeting = personaMeetings.find((item) => item.id === meetingTypeId);

  useEffect(() => {
    if (personaId === "" && personas[0]) {
      setPersonaId(personas[0].id);
    }
  }, [personaId, personas]);

  useEffect(() => {
    setMeetingTypeId(personaMeetings[0]?.id ?? "");
  }, [personaMeetings]);

  const hostSlug = host?.slug;
  const personaSlug = persona?.slug;
  const meetingSlug = meeting?.slug;
  const horizonDays = meeting?.horizonDays;

  useEffect(() => {
    if (!hostSlug || !personaSlug || !meetingSlug || horizonDays === undefined) {
      setSlots([]);
      setSlotState("live");
      return;
    }
    const controller = new globalThis.AbortController();
    const startDate = new Date();
    startDate.setUTCHours(0, 0, 0, 0);
    const endDate = new Date(startDate);
    endDate.setUTCDate(endDate.getUTCDate() + horizonDays);
    setSlotState("loading");
    setSlots([]);
    setSelectedSlot(null);
    fetch(
      `/v1/public/hosts/${hostSlug}/personas/${personaSlug}/${meetingSlug}/slots?start=${encodeURIComponent(startDate.toISOString())}&end=${encodeURIComponent(endDate.toISOString())}`,
      { signal: controller.signal }
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`slots_${response.status}`);
        }
        return (await response.json()) as { slots: TimeInterval[] };
      })
      .then((body) => {
        setSlots(body.slots);
        setSlotState("live");
        const firstDate = body.slots[0] ? dateKey(body.slots[0].start) : undefined;
        if (firstDate) {
          setSelectedDate(firstDate);
        }
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setSlotState("error");
        }
      });
    return () => controller.abort();
  }, [hostSlug, personaSlug, meetingSlug, horizonDays]);

  const availableDates = useMemo(() => new Set(slots.map((slot) => dateKey(slot.start))), [slots]);
  const slotsForDate = slots.filter((slot) => dateKey(slot.start) === selectedDate);
  // An unreadable calendar blocks the whole window, which looks identical to a
  // full diary. Say which it is, rather than showing an empty calendar.
  const brokenConnections = (adminData?.calendarConnections ?? []).filter(
    (item) => item.status === "needs_reconnect" || item.status === "error"
  );

  async function submitBooking(guest: BookingGuestInput) {
    if (!selectedSlot || !hostSlug || !personaSlug || !meetingSlug) {
      return;
    }
    setBookingState({ status: "submitting" });
    try {
      const response = await fetch("/v1/public/bookings", {
        method: "POST",
        headers: { "content-type": "application/json", "x-idempotency-key": globalThis.crypto.randomUUID() },
        body: JSON.stringify({
          hostSlug,
          personaSlug,
          meetingSlug,
          start: selectedSlot.start,
          end: selectedSlot.end,
          guest
        })
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { error?: { message?: string; code?: string } };
        throw new Error(body.error?.message ?? body.error?.code ?? `booking_failed_${response.status}`);
      }
      const body = (await response.json()) as BookingResponse;
      setBookingState({ status: "confirmed", booking: body.booking, ...(body.manageLinks ? { manageLinks: body.manageLinks } : {}) });
      setSlots((current) => current.filter((slot) => slot.start !== selectedSlot.start));
      setStep("confirmed");
    } catch (cause) {
      setBookingState({ status: "error", message: cause instanceof Error ? cause.message : "Booking failed." });
    }
  }

  function startOver() {
    setBookingState({ status: "idle" });
    setSelectedSlot(null);
    setStep("who");
  }

  const heading =
    step === "who"
      ? "Who is this meeting with?"
      : step === "when"
        ? "Pick a time"
        : step === "details"
          ? "Who is attending?"
          : "Booking confirmed";

  return (
    <section className="connect-wizard">
      <button type="button" className="connect-back" onClick={onExit}>
        <ArrowLeft size={17} /> Back to overview
      </button>

      <div className="connect-card">
        <header className="connect-header">
          <span className="connect-eyebrow">New booking</span>
          <h1>{heading}</h1>
          <p>
            {step === "who"
              ? "Choose the persona the meeting belongs to. The guest sees that persona's name, location and confirmation message."
              : step === "when"
                ? "Only times that are genuinely free across every connected calendar are shown."
                : step === "details"
                  ? "The guest receives the calendar invite and a confirmation email at this address."
                  : "The invite is on its way."}
          </p>
          {step === "confirmed" ? null : (
            <ol className="connect-steps">
              <ConnectStepPip index={1} label="Persona" state={step === "who" ? "active" : "done"} />
              <ConnectStepPip index={2} label="Time" state={step === "when" ? "active" : step === "details" ? "done" : "todo"} />
              <ConnectStepPip index={3} label="Guest" state={step === "details" ? "active" : "todo"} />
            </ol>
          )}
        </header>

        {personas.length === 0 ? (
          <div className="connect-body">
            <div className="book-blocked">
              <strong>There are no bookable personas yet.</strong>
              <p>A persona has to be live or private-link before anyone can book time against it.</p>
              <button type="button" className="confirm" onClick={() => onOpenView("personas")}>Go to personas</button>
            </div>
          </div>
        ) : null}

        {personas.length > 0 && step === "who" ? (
          <div className="connect-body connect-body-split">
            <div className="connect-provider-list">
              {personas.map((item) => {
                const itemHost = hosts.find((entry) => entry.id === item.hostId);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`connect-provider${item.id === personaId ? " is-selected" : ""}`}
                    aria-pressed={item.id === personaId}
                    onClick={() => setPersonaId(item.id)}
                  >
                    <span className="connect-provider-mark book-persona-mark" style={{ "--persona-accent": item.accentColor } as CSSProperties}>
                      {personaInitials(item)}
                    </span>
                    <span className="connect-provider-text">
                      <strong>{item.displayName || item.name}</strong>
                      <span>{item.headline || `${itemHost?.displayName ?? "Host"} · ${locationLabel(item)}`}</span>
                    </span>
                    {item.id === personaId ? <Check size={18} className="connect-provider-check" /> : <ChevronRight size={17} className="connect-provider-chevron" />}
                  </button>
                );
              })}
            </div>
            <aside className="connect-aside">
              <span className="connect-aside-icon"><CalendarCheck size={22} /></span>
              <h2>Meeting type</h2>
              {personaMeetings.length === 0 ? (
                <>
                  <p>{persona ? `${persona.displayName || persona.name} has no active meeting types, so there is nothing to book yet.` : "Choose a persona to see its meeting types."}</p>
                  <button type="button" className="connect-linkish" onClick={() => onOpenView("personas")}>Add a meeting type</button>
                </>
              ) : (
                <>
                  <p>Pick the kind of meeting this booking is for.</p>
                  <div className="book-meeting-list">
                    {personaMeetings.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`book-meeting${item.id === meetingTypeId ? " is-selected" : ""}`}
                        aria-pressed={item.id === meetingTypeId}
                        onClick={() => setMeetingTypeId(item.id)}
                      >
                        <strong>{item.title}</strong>
                        <span>{item.durationMinutes} minutes</span>
                      </button>
                    ))}
                  </div>
                  <button type="button" className="confirm connect-cta" disabled={!meeting} onClick={() => setStep("when")}>
                    Choose a time <ArrowRight size={17} />
                  </button>
                </>
              )}
            </aside>
          </div>
        ) : null}

        {personas.length > 0 && step === "when" ? (
          <div className="connect-body connect-body-split">
            <div className="book-when">
              {brokenConnections.length > 0 ? (
                <p className="form-error" role="status">
                  {brokenConnections.length === 1
                    ? "A calendar connection needs reconnecting"
                    : `${brokenConnections.length} calendar connections need reconnecting`}
                  , so every time is being treated as busy. Reconnect on the Calendars screen to see real availability.
                </p>
              ) : slotState === "live" && slots.length === 0 ? (
                <p className="inline-note" role="status">
                  No times are open in the next {meeting?.horizonDays ?? 0} days. Check your availability hours, date overrides, and which
                  calendars block time.
                </p>
              ) : null}
              <CalendarPicker selectedDate={selectedDate} availableDates={availableDates} onSelect={setSelectedDate} />
              <div className="book-times">
                <p className="eyebrow">{formatFullDate(selectedDate)}</p>
                <TimeSlotList
                  date={selectedDate}
                  slots={slotsForDate}
                  selectedSlot={selectedSlot}
                  label={meeting ? `${meeting.durationMinutes} min` : "Slot"}
                  loading={slotState === "loading"}
                  emptyMessage={
                    slotState === "loading"
                      ? "Loading availability."
                      : slotState === "error"
                        ? "Availability could not be loaded."
                        : "No times remain for this day."
                  }
                  onSelect={setSelectedSlot}
                />
              </div>
            </div>
            <aside className="connect-aside">
              <span className="connect-aside-icon"><Clock size={22} /></span>
              <h2>{persona?.displayName || persona?.name}</h2>
              <p>{meeting?.title} · {meeting?.durationMinutes} minutes{persona ? ` · ${locationLabel(persona)}` : ""}</p>
              <p className="book-slot-state">{dataStateLabel(slotState, `${slots.length} times open in the next ${meeting?.horizonDays ?? 0} days`)}</p>
              <button type="button" className="confirm connect-cta" disabled={!selectedSlot} onClick={() => setStep("details")}>
                {selectedSlot ? `Continue with ${formatTime(selectedSlot.start)}` : "Select a time"} <ArrowRight size={17} />
              </button>
              <button type="button" className="connect-linkish" onClick={() => setStep("who")}>Change persona</button>
            </aside>
          </div>
        ) : null}

        {personas.length > 0 && step === "details" ? (
          <div className="connect-body connect-body-split">
            <form
              className="book-form"
              onSubmit={(formEvent) => {
                formEvent.preventDefault();
                const form = new globalThis.FormData(formEvent.currentTarget);
                void submitBooking({
                  name: String(form.get("name") ?? ""),
                  email: String(form.get("email") ?? ""),
                  organization: String(form.get("organization") ?? ""),
                  reason: String(form.get("reason") ?? ""),
                  consentAccepted: true
                });
              }}
            >
              <label>Guest name<input name="name" required placeholder="Full name" /></label>
              <label>Guest email<input name="email" type="email" required placeholder="guest@example.com" /></label>
              <label>Organization <em>Optional</em><input name="organization" placeholder="Organization or team" /></label>
              <label className="book-form-wide">
                What is the meeting about? <em>Optional</em>
                <textarea name="reason" rows={4} maxLength={500} placeholder="A little context helps everyone make the most of the time." />
              </label>
              <div className="book-form-actions">
                <button type="button" onClick={() => setStep("when")}>Back to times</button>
                <button className="confirm" disabled={bookingState.status === "submitting"}>
                  {bookingState.status === "submitting" ? "Confirming" : "Confirm booking"} <CalendarCheck size={17} />
                </button>
              </div>
              {bookingState.status === "error" ? <p className="form-error book-form-wide" aria-live="polite">{bookingState.message}</p> : null}
            </form>
            <aside className="connect-aside">
              <span className="connect-aside-icon"><CalendarCheck size={22} /></span>
              <h2>Booking summary</h2>
              <ul className="book-summary">
                <li><span>Persona</span><strong>{persona?.displayName || persona?.name}</strong></li>
                <li><span>Meeting</span><strong>{meeting?.title}</strong></li>
                <li><span>When</span><strong>{selectedSlot ? `${formatFullDate(dateKey(selectedSlot.start))} at ${formatTime(selectedSlot.start)}` : "Not selected"}</strong></li>
                <li><span>Location</span><strong>{persona ? locationLabel(persona) : ""}</strong></li>
              </ul>
              <p className="book-fine-print">The guest receives a calendar invite from the connected calendar and a confirmation email from TrueAvail.</p>
            </aside>
          </div>
        ) : null}

        {step === "confirmed" && bookingState.status === "confirmed" ? (
          <div className="connect-body">
            <div className="book-success">
              <span className="book-success-mark"><Check size={26} /></span>
              <h2>{bookingState.booking.guestName} is booked</h2>
              <p>
                {formatFullDate(dateKey(bookingState.booking.interval.start))} at {formatTime(bookingState.booking.interval.start)}
                {persona ? ` · ${persona.displayName || persona.name}` : ""}
              </p>
              <ul className="book-summary">
                <li><span>Guest email</span><strong>{bookingState.booking.guestEmail}</strong></li>
                <li><span>Reference</span><strong>{bookingState.booking.id}</strong></li>
              </ul>
              {bookingState.manageLinks ? (
                <p className="book-fine-print">
                  Guest links: <a href={bookingState.manageLinks.reschedule}>reschedule</a> · <a href={bookingState.manageLinks.cancel}>cancel</a>
                </p>
              ) : null}
              <div className="book-form-actions">
                <button type="button" onClick={startOver}>Book another</button>
                <button type="button" className="confirm" onClick={() => onOpenView("bookings")}>View bookings</button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

interface AdminBookingDetailResponse {
  booking: Booking;
  activity?: Array<{ kind: string; status: string; recipientEmail: string; createdAt: string }>;
}

const BOOKING_DURATIONS = [15, 20, 30, 45, 60, 90, 120];

function BookingDetailView({
  bookingId,
  authState,
  adminData,
  onExit
}: {
  bookingId: string;
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  onExit: () => void;
}) {
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [detail, setDetail] = useState<AdminBookingDetailResponse | null>(null);
  const [editing, setEditing] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [pendingCancel, setPendingCancel] = useState(false);
  const [draft, setDraft] = useState({ date: "", time: "", durationMinutes: 30, guestName: "", guestEmail: "", guestOrganization: "", internalNote: "" });

  const booking = detail?.booking;
  const persona = (adminData?.personas ?? []).find((item) => item.id === booking?.personaId);
  const meetingType = (adminData?.meetingTypes ?? []).find((item) => item.id === booking?.meetingTypeId);
  const viewerZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;

  const idToken = authState.status === "signed_in" ? authState.idToken : "";

  useEffect(() => {
    if (!idToken || !bookingId) {
      setLoadState("error");
      return;
    }
    const controller = new globalThis.AbortController();
    setLoadState("loading");
    apiFetch(`/v1/admin/bookings/${bookingId}`, { signal: controller.signal, headers: { authorization: `Bearer ${idToken}` } })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`booking_${response.status}`);
        }
        return (await response.json()) as AdminBookingDetailResponse;
      })
      .then((body) => {
        setDetail(body);
        setLoadState("live");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setLoadState("error");
        }
      });
    return () => controller.abort();
  }, [bookingId, idToken]);

  function startEditing() {
    if (!booking) {
      return;
    }
    const start = new Date(booking.interval.start);
    setDraft({
      date: localDateInputValue(start),
      time: localTimeInputValue(start),
      durationMinutes: Math.round((Date.parse(booking.interval.end) - Date.parse(booking.interval.start)) / 60_000),
      guestName: booking.guestName,
      guestEmail: booking.guestEmail,
      guestOrganization: booking.guestOrganization ?? "",
      internalNote: booking.internalNote ?? ""
    });
    setMessage("");
    setErrorMessage("");
    setReviewing(false);
    setEditing(true);
  }

  const draftStart = draft.date && draft.time ? new Date(`${draft.date}T${draft.time}`) : null;
  const scheduleChanged = Boolean(
    booking &&
      draftStart &&
      (draftStart.toISOString() !== booking.interval.start ||
        draft.durationMinutes !== Math.round((Date.parse(booking.interval.end) - Date.parse(booking.interval.start)) / 60_000))
  );
  const detailChanges: Array<{ label: string; from: string; to: string }> = [];
  if (booking) {
    if (draft.guestName !== booking.guestName) {
      detailChanges.push({ label: "Guest name", from: booking.guestName, to: draft.guestName });
    }
    if (draft.guestEmail !== booking.guestEmail) {
      detailChanges.push({ label: "Email address", from: booking.guestEmail, to: draft.guestEmail });
    }
    if (draft.guestOrganization !== (booking.guestOrganization ?? "")) {
      detailChanges.push({ label: "Organization", from: booking.guestOrganization ?? "Not set", to: draft.guestOrganization || "Not set" });
    }
    if (draft.internalNote !== (booking.internalNote ?? "")) {
      detailChanges.push({ label: "Internal note", from: booking.internalNote ?? "Not set", to: draft.internalNote || "Not set" });
    }
  }
  const changeCount = detailChanges.length + (scheduleChanged ? 1 : 0);

  async function saveChanges() {
    if (!booking || !draftStart || authState.status !== "signed_in") {
      return;
    }
    setBusy(true);
    setErrorMessage("");
    try {
      let current = booking;
      if (detailChanges.length > 0) {
        const response = await apiFetch(`/v1/admin/bookings/${booking.id}`, {
          method: "PUT",
          headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
          body: JSON.stringify({
            guestName: draft.guestName,
            guestEmail: draft.guestEmail,
            guestOrganization: draft.guestOrganization,
            internalNote: draft.internalNote
          })
        });
        const body = (await response.json().catch(() => ({}))) as { booking?: Booking; error?: { details?: string[]; message?: string; code?: string } };
        if (!response.ok || !body.booking) {
          throw new Error(body.error?.details?.join(", ") ?? body.error?.message ?? body.error?.code ?? `booking_update_${response.status}`);
        }
        current = body.booking;
      }
      if (scheduleChanged) {
        const end = new Date(draftStart.getTime() + draft.durationMinutes * 60_000);
        const response = await apiFetch(`/v1/admin/bookings/${booking.id}/reschedule`, {
          method: "POST",
          headers: {
            authorization: `Bearer ${authState.idToken}`,
            "content-type": "application/json",
            "x-idempotency-key": globalThis.crypto.randomUUID()
          },
          body: JSON.stringify({ start: draftStart.toISOString(), end: end.toISOString() })
        });
        const body = (await response.json().catch(() => ({}))) as { booking?: Booking; error?: { message?: string; code?: string } };
        if (!response.ok || !body.booking) {
          throw new Error(body.error?.message ?? body.error?.code ?? `reschedule_${response.status}`);
        }
        current = body.booking;
      }
      setDetail((value) => (value ? { ...value, booking: current } : value));
      setEditing(false);
      setReviewing(false);
      setMessage(scheduleChanged ? "Saved. The guest has been sent the updated invitation." : "Saved.");
    } catch (cause) {
      setErrorMessage(cause instanceof Error ? cause.message : "Changes could not be saved.");
    } finally {
      setBusy(false);
    }
  }

  async function cancelBooking() {
    if (!booking || authState.status !== "signed_in") {
      return;
    }
    setBusy(true);
    setErrorMessage("");
    try {
      const response = await apiFetch(`/v1/admin/bookings/${booking.id}/cancel`, {
        method: "POST",
        headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
        body: JSON.stringify({ reason: "Canceled by the host." })
      });
      const body = (await response.json().catch(() => ({}))) as { booking?: Booking; error?: { message?: string; code?: string } };
      if (!response.ok || !body.booking) {
        throw new Error(body.error?.message ?? body.error?.code ?? `cancel_${response.status}`);
      }
      setDetail((value) => (value ? { ...value, booking: body.booking! } : value));
      setPendingCancel(false);
      setMessage("Booking canceled. The guest has been notified.");
    } catch (cause) {
      setErrorMessage(cause instanceof Error ? cause.message : "Cancellation failed.");
    } finally {
      setBusy(false);
    }
  }

  if (loadState === "loading") {
    return (
      <div className="booking-detail-page">
        <button type="button" className="connect-back" onClick={onExit}><ArrowLeft size={17} /> Back to bookings</button>
        <SkeletonRegion label="Loading booking">
          <section className="booking-detail-head skeleton-detail-head">
            <div className="skeleton-stack">
              <Skeleton className="skeleton-line-sm" width="180px" />
              <Skeleton className="skeleton-line-lg" width="min(420px, 70%)" />
              <Skeleton className="skeleton-line-sm" width="min(300px, 55%)" />
            </div>
            <div className="skeleton-detail-actions">
              <Skeleton width="120px" height="42px" radius="12px" />
              <Skeleton width="120px" height="42px" radius="12px" />
            </div>
          </section>
          <div className="booking-detail-layout">
            <main className="skeleton-stack skeleton-detail-main">
              <Skeleton height="132px" radius="18px" />
              <Skeleton height="196px" radius="18px" />
            </main>
            <aside className="skeleton-stack">
              <Skeleton height="150px" radius="18px" />
              <Skeleton height="118px" radius="18px" />
            </aside>
          </div>
        </SkeletonRegion>
      </div>
    );
  }

  if (loadState === "error" || !booking) {
    return (
      <div className="booking-detail-page">
        <button type="button" className="connect-back" onClick={onExit}><ArrowLeft size={17} /> Back to bookings</button>
        <WorkspaceState title="Booking unavailable" detail="This booking could not be loaded." tone="error" />
      </div>
    );
  }

  const status = bookingStatusView(booking.status);
  const start = new Date(booking.interval.start);
  const durationMinutes = Math.round((Date.parse(booking.interval.end) - Date.parse(booking.interval.start)) / 60_000);
  const guestFirstName = booking.guestName.split(/\s+/)[0] ?? booking.guestName;
  const canEdit = !["canceled_by_guest", "canceled_by_host"].includes(booking.status);

  if (editing) {
    return (
      <div className="booking-detail-page">
        <button type="button" className="connect-back" onClick={onExit}><ArrowLeft size={17} /> Back to bookings</button>

        <section className="booking-edit-head">
          <div className="booking-edit-identity">
            <button type="button" className="booking-back-round" aria-label="Stop editing" onClick={() => setEditing(false)}>
              <ArrowLeft size={17} />
            </button>
            <div>
              <span className="editor-tag blue">Editing booking</span>
              <h2>{meetingType?.title ?? "Booking"}</h2>
              <p>Changes can be sent to {guestFirstName} after you review them.</p>
            </div>
          </div>
          <div className="editor-head-actions">
            <button type="button" className="editor-outline-button" onClick={() => setEditing(false)}>Discard</button>
            <button type="button" className="confirm" disabled={changeCount === 0 || busy} onClick={() => setReviewing(true)}>
              <Check size={16} /> Review changes
            </button>
          </div>
        </section>

        {errorMessage ? <p className="form-error" aria-live="polite">{errorMessage}</p> : null}

        <div className="booking-edit-layout">
          <main className="booking-edit-main">
            <section className="editor-form">
              <div className="editor-section-head">
                <div>
                  <h3>Date and time</h3>
                  <p>All times are shown in your local time zone ({viewerZone}).</p>
                </div>
              </div>
              <div className="formgrid">
                <label>Date<input type="date" value={draft.date} onChange={(event) => setDraft({ ...draft, date: event.currentTarget.value })} /></label>
                <label>Start time<input type="time" step={300} value={draft.time} onChange={(event) => setDraft({ ...draft, time: event.currentTarget.value })} /></label>
                <label>
                  Duration
                  <select value={draft.durationMinutes} onChange={(event) => setDraft({ ...draft, durationMinutes: Number(event.currentTarget.value) })}>
                    {[...new Set([...BOOKING_DURATIONS, draft.durationMinutes])].sort((left, right) => left - right).map((minutes) => (
                      <option key={minutes} value={minutes}>{minutes} minutes</option>
                    ))}
                  </select>
                </label>
                <label>Time zone<input value={viewerZone} readOnly /></label>
              </div>
            </section>

            <section className="editor-form">
              <div className="editor-section-head">
                <div>
                  <h3>Meeting setup</h3>
                  <p>The meeting type and persona are fixed once a booking exists.</p>
                </div>
              </div>
              <div className="formgrid">
                <label>Meeting type<input value={meetingType?.title ?? booking.meetingTypeId} readOnly /></label>
                <label>Persona<input value={persona?.name ?? booking.personaId} readOnly /></label>
                <label>Location<input value={persona ? locationLabel(persona) : "Not set"} readOnly /></label>
                <label>Meeting URL<input value={booking.joinUrl ?? "Created by the calendar provider"} readOnly /></label>
                <label className="span2">
                  Internal note
                  <textarea
                    rows={3}
                    value={draft.internalNote}
                    placeholder="Add a private note about this booking."
                    onChange={(event) => setDraft({ ...draft, internalNote: event.currentTarget.value })}
                  />
                </label>
              </div>
            </section>

            <section className="editor-form">
              <div className="editor-section-head">
                <div>
                  <h3>Guest information</h3>
                  <p>Correct contact details or add context for the meeting.</p>
                </div>
              </div>
              <div className="formgrid">
                <label>Guest name<input value={draft.guestName} onChange={(event) => setDraft({ ...draft, guestName: event.currentTarget.value })} /></label>
                <label>Email address<input type="email" value={draft.guestEmail} onChange={(event) => setDraft({ ...draft, guestEmail: event.currentTarget.value })} /></label>
                <label className="span2">Organization<input value={draft.guestOrganization} onChange={(event) => setDraft({ ...draft, guestOrganization: event.currentTarget.value })} /></label>
              </div>
            </section>
          </main>

          <aside className="booking-edit-aside">
            <section className="change-summary">
              <span className="editor-tag violet">Change summary</span>
              <h3>{changeCount === 0 ? "No changes yet" : `${changeCount} ${changeCount === 1 ? "change" : "changes"}`}</h3>
              {changeCount === 0 ? <p>Modified fields will appear here before you save.</p> : null}
              {scheduleChanged && draftStart ? (
                <div className="change-row">
                  <span>Schedule</span>
                  <strong>{formatBookingWhen(draftStart.toISOString())} · {draft.durationMinutes} min</strong>
                  <small>was {formatBookingWhen(booking.interval.start)} · {durationMinutes} min</small>
                </div>
              ) : null}
              {detailChanges.map((change) => (
                <div className="change-row" key={change.label}>
                  <span>{change.label}</span>
                  <strong>{change.to}</strong>
                  <small>was {change.from}</small>
                </div>
              ))}
              <div className="current-schedule">
                <span>Current schedule</span>
                <strong>{formatBookingWhen(booking.interval.start)}</strong>
                <small>{durationMinutes} minutes · {viewerZone}</small>
              </div>
            </section>

            <section className="guest-notification">
              <h3>Guest notification</h3>
              <p className={scheduleChanged ? "notify-on" : "notify-off"}>
                {scheduleChanged
                  ? `${guestFirstName} will receive the updated calendar invitation and a reschedule email.`
                  : changeCount === 0
                    ? "Nothing to send yet."
                    : `These edits are internal. ${guestFirstName} will not be emailed.`}
              </p>
              {reviewing ? (
                <>
                  <p className="review-note">Saving applies {changeCount} {changeCount === 1 ? "change" : "changes"}.</p>
                  <button type="button" className="confirm" disabled={busy} onClick={() => void saveChanges()}>
                    <Check size={16} /> {busy ? "Saving" : "Save changes"}
                  </button>
                  <button type="button" className="editor-outline-button" onClick={() => setReviewing(false)}>Keep editing</button>
                </>
              ) : (
                <button type="button" className="confirm" disabled={changeCount === 0 || busy} onClick={() => setReviewing(true)}>
                  <Check size={16} /> Review changes
                </button>
              )}
            </section>
          </aside>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-detail-page">
      <button type="button" className="connect-back" onClick={onExit}><ArrowLeft size={17} /> Back to bookings</button>

      <section className="booking-detail-head">
        <div>
          <div className="booking-title-row">
            <span className={`booking-status ${status.tone}`}>{status.label}</span>
            {persona ? (
              <span className="booking-persona-pill" style={{ "--persona-accent": persona.accentColor } as CSSProperties}>{persona.name}</span>
            ) : null}
          </div>
          <h2>{meetingType?.title ?? "Booking"}</h2>
          <p>Booking with {booking.guestName} · Created {formatShortDate(booking.createdAt)}</p>
        </div>
        <div className="editor-head-actions">
          {booking.joinUrl ? (
            <a className="editor-outline-button" href={booking.joinUrl} target="_blank" rel="noreferrer"><Video size={16} /> Join meeting</a>
          ) : null}
          <button type="button" className="confirm" disabled={!canEdit} onClick={startEditing}><Settings2 size={16} /> Edit booking</button>
          <ActionMenu
            label={`More actions for ${booking.guestName}`}
            items={[
              { label: "Copy guest email", onSelect: () => void globalThis.navigator.clipboard.writeText(booking.guestEmail) },
              { label: "Copy booking reference", onSelect: () => void globalThis.navigator.clipboard.writeText(booking.id) },
              { label: "Cancel booking", danger: true, disabled: !canEdit, onSelect: () => setPendingCancel(true) }
            ]}
          />
        </div>
      </section>

      {message ? <p className="inline-note" aria-live="polite">{message}</p> : null}
      {errorMessage ? <p className="form-error" aria-live="polite">{errorMessage}</p> : null}

      {pendingCancel ? (
        <section className="persona-confirm" role="alertdialog" aria-labelledby="booking-cancel-title">
          <div>
            <h3 id="booking-cancel-title">Cancel this booking?</h3>
            <p>The event is removed from your calendar and {booking.guestName} is emailed that the meeting is off. This cannot be undone.</p>
          </div>
          <div className="persona-confirm-actions">
            <button type="button" onClick={() => setPendingCancel(false)}>Keep booking</button>
            <button type="button" className="danger" disabled={busy} onClick={() => void cancelBooking()}>
              {busy ? "Canceling" : "Cancel booking"}
            </button>
          </div>
        </section>
      ) : null}

      <div className="booking-detail-layout">
        <main className="booking-detail-main">
          <section className="booking-when">
            <div className="date-block">
              <span>{new Intl.DateTimeFormat(undefined, { month: "short" }).format(start).toUpperCase()}</span>
              <strong>{start.getDate()}</strong>
              <small>{new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(start).toUpperCase()}</small>
            </div>
            <div>
              <h3>{new Intl.DateTimeFormat(undefined, { weekday: "long", month: "long", day: "numeric" }).format(start)}</h3>
              <p>{formatTime(booking.interval.start)}–{formatTime(booking.interval.end)}</p>
              <span><Clock size={14} /> {durationMinutes} minutes · {viewerZone}</span>
            </div>
            <button type="button" className="editor-outline-button" disabled={!canEdit} onClick={startEditing}>Reschedule</button>
          </section>

          <section className="booking-info">
            <div className="booking-section-title">
              <h3>Meeting details</h3>
              <button type="button" className="booking-link" disabled={!canEdit} onClick={startEditing}>Edit</button>
            </div>
            <dl>
              <div>
                <dt><Video size={17} /> Location</dt>
                <dd>
                  <strong>{persona ? locationLabel(persona) : "Not set"}</strong>
                  {booking.joinUrl ? <a href={booking.joinUrl} target="_blank" rel="noreferrer">{displayUrl(booking.joinUrl)}</a> : <span>Link created by the calendar provider</span>}
                </dd>
              </div>
              <div>
                <dt><Sparkles size={17} /> Meeting type</dt>
                <dd>
                  <strong>{meetingType?.title ?? booking.meetingTypeId}</strong>
                  <span>{persona ? `${persona.name} persona` : ""}</span>
                </dd>
              </div>
              <div>
                <dt><Globe2 size={17} /> Time zone</dt>
                <dd>
                  <strong>{persona?.defaultTimeZone ?? viewerZone}</strong>
                  <span>Shown here in {viewerZone}</span>
                </dd>
              </div>
              {booking.internalNote ? (
                <div>
                  <dt><Lock size={17} /> Internal note</dt>
                  <dd><strong>{booking.internalNote}</strong><span>Only visible to hosts</span></dd>
                </div>
              ) : null}
            </dl>
          </section>

          <section className="booking-info">
            <div className="booking-section-title">
              <h3>Guest</h3>
              <a className="editor-outline-button" href={`mailto:${booking.guestEmail}`}><Mail size={15} /> Email guest</a>
            </div>
            <div className="guest-profile">
              <i>{hostInitials(booking.guestName)}</i>
              <div>
                <strong>{booking.guestName}</strong>
                <a href={`mailto:${booking.guestEmail}`}>{booking.guestEmail}</a>
                {booking.guestOrganization ? <small>{booking.guestOrganization}</small> : null}
              </div>
            </div>
            {booking.guestReason ? (
              <div className="guest-response">
                <span>What would you like to discuss?</span>
                <p>{booking.guestReason}</p>
              </div>
            ) : null}
          </section>
        </main>

        <aside className="booking-detail-aside">
          <section className="quick-actions">
            <h3>Manage booking</h3>
            <button type="button" disabled={!canEdit} onClick={startEditing}>
              <i><Settings2 size={17} /></i>
              <span><strong>Edit booking</strong><small>Change meeting details</small></span>
              <ChevronRight size={16} />
            </button>
            <button type="button" disabled={!canEdit} onClick={startEditing}>
              <i><RefreshCw size={17} /></i>
              <span><strong>Reschedule</strong><small>Choose another time</small></span>
              <ChevronRight size={16} />
            </button>
            <button type="button" className="danger" disabled={!canEdit} onClick={() => setPendingCancel(true)}>
              <i><X size={17} /></i>
              <span><strong>Cancel booking</strong><small>Notify the guest</small></span>
              <ChevronRight size={16} />
            </button>
          </section>

          <section className="booking-activity">
            <h3>Activity</h3>
            <div>
              <i />
              <span><strong>Booking created</strong><small>{formatShortDate(booking.createdAt)} · {formatTime(booking.createdAt)}</small></span>
            </div>
            {(detail?.activity ?? []).map((entry) => (
              <div key={`${entry.kind}-${entry.createdAt}`}>
                <i className={entry.status === "failed" || entry.status === "poisoned" ? "failed" : entry.status === "pending" ? "pending" : ""} />
                <span>
                  <strong>{notificationKindLabel(entry.kind)}</strong>
                  <small>{formatShortDate(entry.createdAt)} · {formatTime(entry.createdAt)} · {entry.status}</small>
                </span>
              </div>
            ))}
            {(detail?.activity ?? []).length === 0 ? <p className="activity-empty">No guest emails have been queued for this booking.</p> : null}
          </section>
        </aside>
      </div>
    </div>
  );
}

function notificationKindLabel(kind: string): string {
  switch (kind) {
    case "booking_confirmed":
      return "Confirmation sent";
    case "booking_rescheduled":
      return "Reschedule notice sent";
    case "booking_canceled":
      return "Cancellation sent";
    default:
      return capitalizeLabel(kind.replace(/_/g, " "));
  }
}

function localDateInputValue(value: Date): string {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}

function localTimeInputValue(value: Date): string {
  return `${String(value.getHours()).padStart(2, "0")}:${String(value.getMinutes()).padStart(2, "0")}`;
}

/** An event with no persona behind it still has to be drawn, in nobody's colour. */
const NEUTRAL_EVENT_COLOR = "#8994a6";
/** A working day is always drawn, so an empty schedule still reads as a day. */
const DAY_WINDOW_START_HOUR = 8;
const DAY_WINDOW_END_HOUR = 18;

/**
 * The Bookings screen: the host's whole day, not just the part TrueAvail booked.
 * The day is the default view; the booking-only list is one switch away.
 */
function BookingsView({
  authState,
  adminData,
  onOpenView,
  onOpenBooking
}: {
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  onOpenView: (view: View) => void;
  onOpenBooking: (bookingId: string) => void;
}) {
  const [mode, setMode] = useState<"day" | "bookings">("day");

  const viewSwitch = (
    <div className="view-switch" role="group" aria-label="Schedule view">
      <button
        type="button"
        className={mode === "day" ? "active" : ""}
        aria-pressed={mode === "day"}
        onClick={() => setMode("day")}
      >
        <CalendarDays size={15} /> Day
      </button>
      <button
        type="button"
        className={mode === "bookings" ? "active" : ""}
        aria-pressed={mode === "bookings"}
        onClick={() => setMode("bookings")}
      >
        <CalendarCheck size={15} /> Bookings
      </button>
    </div>
  );

  return (
    <div className="onboarding-admin-screen bookings-screen unified-bookings">
      <section className="onboarding-admin-title">
        <div>
          <p className="eyebrow">Your schedule</p>
          <h2>Bookings &amp; calendar events</h2>
          <p>Every commitment that shapes your availability, across all connected calendars and personas.</p>
        </div>
        <button type="button" onClick={() => onOpenView("book")}>
          <Plus size={17} /> Add booking
        </button>
      </section>

      {mode === "day" ? (
        <ScheduleDayView
          authState={authState}
          adminData={adminData}
          onOpenView={onOpenView}
          onOpenBooking={onOpenBooking}
          viewSwitch={viewSwitch}
        />
      ) : (
        <>
          <section className="schedule-controls">
            <p className="schedule-controls-copy">Every booking taken through TrueAvail.</p>
            <div className="schedule-view-actions">{viewSwitch}</div>
          </section>
          <BookingsListView authState={authState} adminData={adminData} onOpenBooking={onOpenBooking} />
        </>
      )}
    </div>
  );
}

/**
 * One day, drawn to scale: TrueAvail bookings and imported calendar events side by
 * side, each in its persona's colour and each saying where it came from.
 */
function ScheduleDayView({
  authState,
  adminData,
  onOpenView,
  onOpenBooking,
  viewSwitch
}: {
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  onOpenView: (view: View) => void;
  onOpenBooking: (bookingId: string) => void;
  viewSwitch: ReactNode;
}) {
  const host = signedInHost(authState, adminData);
  const hostZone = host?.defaultTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? "UTC";
  const [date, setDate] = useState(() => dateKeyInZone(new Date(), hostZone));
  const [schedule, setSchedule] = useState<ScheduleResponse | null>(null);
  const [scheduleState, setScheduleState] = useState<LoadState>("loading");
  const [personaFilter, setPersonaFilter] = useState("all");
  const [calendarFilter, setCalendarFilter] = useState("all");

  useEffect(() => {
    if (authState.status !== "signed_in" || !host) {
      setScheduleState("error");
      return;
    }
    const controller = new globalThis.AbortController();
    const params = new globalThis.URLSearchParams({ hostId: host.id, date, timeZone: hostZone });
    setScheduleState("loading");
    apiFetch(`/v1/admin/schedule?${params.toString()}`, {
      signal: controller.signal,
      headers: { authorization: `Bearer ${authState.idToken}` }
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`schedule_${response.status}`);
        }
        return (await response.json()) as ScheduleResponse;
      })
      .then((body) => {
        setSchedule(body);
        setScheduleState("live");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setScheduleState("error");
        }
      });
    return () => controller.abort();
  }, [authState, date, host, hostZone]);

  const timeZone = schedule?.timeZone ?? hostZone;
  const personas = (adminData?.personas ?? []).filter((persona) => !host || persona.hostId === host.id);

  const events = (schedule?.events ?? []).filter((item) => {
    const personaMatch =
      personaFilter === "all" || (personaFilter === "none" ? !item.personaId : item.personaId === personaFilter);
    const calendarMatch =
      calendarFilter === "all" ||
      (calendarFilter === "timebridge" ? item.source === "timebridge" : item.connectionId === calendarFilter);
    return personaMatch && calendarMatch;
  });
  const allDayEvents = events.filter((item) => item.allDay);
  const timedEvents = events.filter((item) => !item.allDay);
  const filtered = events.length !== (schedule?.events.length ?? 0);

  // The window stretches to hold whatever the day actually contains.
  const startHour = Math.min(
    DAY_WINDOW_START_HOUR,
    ...timedEvents.map((item) => Math.floor(minutesIntoDay(item.start, timeZone) / 60))
  );
  const endHour = Math.max(
    DAY_WINDOW_END_HOUR,
    ...timedEvents.map((item) => Math.ceil(minutesIntoDay(item.end, timeZone) / 60))
  );
  const hours = Array.from({ length: Math.max(1, endHour - startHour) }, (_, index) => startHour + index);
  const gridRows = { gridTemplateRows: `repeat(${hours.length}, 62px)` } as CSSProperties;
  const windowMinutes = hours.length * 60;

  const timebridgeCount = events.filter((item) => item.source === "timebridge").length;
  const importedCount = events.length - timebridgeCount;
  const busyMinutes = filtered
    ? timedEvents
        .filter((item) => item.transparency !== "transparent")
        .reduce((total, item) => total + Math.max(0, Date.parse(item.end) - Date.parse(item.start)) / 60_000, 0)
    : schedule?.summary.busyMinutes ?? 0;
  const nextOpen = schedule?.summary.nextOpen;
  const failed = schedule?.failedSources ?? [];

  return (
    <>
      <section className="schedule-controls">
        <div className="date-nav">
          <button type="button" className="icon" aria-label="Previous day" onClick={() => setDate(shiftDateKey(date, -1))}>
            <ChevronLeft size={18} />
          </button>
          <div>
            <strong>{formatScheduleDate(date)}</strong>
            <small>{timeZone}</small>
          </div>
          <button type="button" className="icon" aria-label="Next day" onClick={() => setDate(shiftDateKey(date, 1))}>
            <ChevronRight size={18} />
          </button>
          <button type="button" className="today-button" onClick={() => setDate(dateKeyInZone(new Date(), hostZone))}>
            Today
          </button>
        </div>
        <div className="schedule-view-actions">
          <label className="filter-chip">
            <i className="all-dot" aria-hidden="true" />
            <select
              aria-label="Filter by persona"
              value={personaFilter}
              onChange={(event) => setPersonaFilter(event.currentTarget.value)}
            >
              <option value="all">All personas</option>
              {personas.map((persona) => (
                <option key={persona.id} value={persona.id}>
                  {persona.name}
                </option>
              ))}
              <option value="none">No persona</option>
            </select>
            <ChevronDown size={14} />
          </label>
          <label className="filter-chip">
            <CalendarDays size={15} />
            <select
              aria-label="Filter by calendar"
              value={calendarFilter}
              onChange={(event) => setCalendarFilter(event.currentTarget.value)}
            >
              <option value="all">All calendars</option>
              <option value="timebridge">TrueAvail bookings</option>
              {(schedule?.sources ?? []).map((source) => (
                <option key={source.connectionId} value={source.connectionId}>
                  {source.label}
                </option>
              ))}
            </select>
            <ChevronDown size={14} />
          </label>
          {viewSwitch}
        </div>
      </section>

      <div className="unified-layout">
        <main className="day-calendar">
          <div className="day-calendar-head">
            <div className="day-badge">
              <span>{formatScheduleWeekday(date)}</span>
              <strong>{Number(date.slice(8, 10))}</strong>
            </div>
            <p>
              {scheduleState === "loading" ? (
                <Skeleton width="88px" height="17px" />
              ) : (
                <strong>
                  {events.length} {events.length === 1 ? "event" : "events"}
                </strong>
              )}
              {scheduleState === "loading" ? (
                <Skeleton className="skeleton-line-sm" width="210px" />
              ) : (
                <span>
                  {scheduleState === "live"
                    ? `${timebridgeCount} booked through TrueAvail · ${importedCount} imported`
                    : "Reading your calendars"}
                </span>
              )}
            </p>
            <div className="persona-legend">
              {personas.map((persona) => (
                <span key={persona.id}>
                  <i style={{ background: persona.accentColor }} />
                  {persona.name}
                </span>
              ))}
              <span>
                <i style={{ background: NEUTRAL_EVENT_COLOR }} />
                No persona
              </span>
            </div>
          </div>

          {/* An unreadable calendar and an empty day look identical; say which this is. */}
          {failed.length > 0 ? (
            <p className="schedule-warning" role="status">
              {failed.length === 1 ? "One calendar" : `${failed.length} calendars`} could not be read, so this day may be
              incomplete: {failed.map((item) => item.label).join(", ")}.
            </p>
          ) : null}

          <div className="all-day-row">
            <time>All day</time>
            <div>
              {scheduleState === "loading" ? (
                <Skeleton className="skeleton-line-sm" width="150px" />
              ) : allDayEvents.length === 0 ? (
                <small>Nothing all day</small>
              ) : (
                allDayEvents.map((item) => (
                  <span
                    key={item.id}
                    className="all-day-pill"
                    style={{ "--event-accent": item.personaColor ?? NEUTRAL_EVENT_COLOR } as CSSProperties}
                  >
                    {item.title}
                  </span>
                ))
              )}
            </div>
          </div>

          <div className="timeline">
            <div className="hour-labels" style={gridRows}>
              {hours.map((hour) => (
                <time key={hour}>{formatHourLabel(hour)}</time>
              ))}
            </div>
            <div className="time-grid" style={gridRows}>
              {hours.map((hour) => (
                <i key={hour} />
              ))}
              {scheduleState === "loading" ? (
                <SkeletonRegion label="Reading your calendars" className="skeleton-timeline">
                  {[
                    { top: "4%", height: "7%", left: "0%", width: "100%" },
                    { top: "17%", height: "5%", left: "0%", width: "100%" },
                    { top: "38%", height: "10%", left: "0%", width: "100%" },
                    { top: "62%", height: "7%", left: "0%", width: "100%" }
                  ].map((box, index) => (
                    <Skeleton key={index} radius="10px" height={box.height} className="skeleton-timeline-event" width={box.width} />
                  ))}
                </SkeletonRegion>
              ) : null}
              {scheduleState === "error" ? <p className="timeline-state">Sign in to load your schedule.</p> : null}
              {scheduleState === "live" && timedEvents.length === 0 ? (
                <p className="timeline-state">
                  {(schedule?.events.length ?? 0) === 0 ? "Nothing scheduled." : "No events match these filters."}
                </p>
              ) : null}
              {timedEvents.map((item) => {
                const from = Math.max(minutesIntoDay(item.start, timeZone), startHour * 60);
                const rawTo = minutesIntoDay(item.end, timeZone);
                // An event that runs past midnight is clamped so it cannot escape the day.
                const to = Math.min(rawTo <= from ? endHour * 60 : rawTo, endHour * 60);
                const minutes = Math.max(1, Math.round((Date.parse(item.end) - Date.parse(item.start)) / 60_000));
                const isBooking = item.source === "timebridge";
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={isBooking ? "calendar-event is-booking" : "calendar-event"}
                    style={
                      {
                        top: `${((from - startHour * 60) / windowMinutes) * 100}%`,
                        height: `${Math.max(((to - from) / windowMinutes) * 100, 100 / (hours.length * 2))}%`,
                        "--event-accent": item.personaColor ?? NEUTRAL_EVENT_COLOR
                      } as CSSProperties
                    }
                    disabled={!isBooking}
                    onClick={isBooking && item.bookingId ? () => onOpenBooking(item.bookingId as string) : undefined}
                  >
                    <span className="event-time">
                      {formatScheduleClock(item.start, timeZone)} · {formatDurationLabel(minutes)}
                    </span>
                    <strong>{item.title}</strong>
                    <small>{item.detail}</small>
                    <footer>
                      <em>{item.personaName ?? "No persona"}</em>
                      <span>{isBooking ? "TrueAvail booking" : "Calendar event"}</span>
                      {item.calendarLabel ? (
                        <span>
                          {calendarProviderLabel(item.provider ?? "google")} · {item.calendarLabel}
                        </span>
                      ) : null}
                      {isBooking ? <ChevronRight size={14} /> : null}
                    </footer>
                  </button>
                );
              })}
            </div>
          </div>
        </main>

        <aside className="schedule-aside">
          <section className="today-summary">
            <div className="aside-title">
              <div>
                <span>Today at a glance</span>
                <h3>Your unified day</h3>
              </div>
              {scheduleState === "loading" ? (
                <Skeleton width="62px" height="24px" radius="999px" />
              ) : (
                <em className={failed.length > 0 ? "schedule-badge warning" : "schedule-badge"}>
                  {failed.length > 0 ? "Partial" : "Synced"}
                </em>
              )}
            </div>
            <div className="summary-grid" aria-busy={scheduleState === "loading" ? "true" : undefined}>
              {[
                { label: "Total events", value: String(events.length), width: "34px" },
                { label: "TrueAvail", value: String(timebridgeCount), width: "34px" },
                { label: "Imported", value: String(importedCount), width: "34px" },
                { label: "In meetings", value: `${Math.round((busyMinutes / 60) * 10) / 10}h`, width: "46px" }
              ].map((tile) => (
                <article key={tile.label}>
                  {scheduleState === "loading" ? (
                    <Skeleton width={tile.width} height="20px" />
                  ) : (
                    <strong>{tile.value}</strong>
                  )}
                  <span>{tile.label}</span>
                </article>
              ))}
            </div>
            <div className="next-open">
              <Clock size={17} />
              <span>
                <strong>Next open window</strong>
                {scheduleState === "loading" ? (
                  <Skeleton className="skeleton-line-sm" width="160px" />
                ) : (
                  <small>{nextOpen ? formatOpenWindow(nextOpen, timeZone) : "No open time left today"}</small>
                )}
              </span>
            </div>
          </section>

          <section className="calendar-sources">
            <div className="aside-title">
              <div>
                <span>Calendar sources</span>
                <h3>Included today</h3>
              </div>
              <button type="button" className="link-button" onClick={() => onOpenView("calendars")}>
                Manage
              </button>
            </div>
            {scheduleState === "loading" ? (
              <SkeletonRegion label="Loading calendar sources">
                <SkeletonRows count={2}>
                  {(index) => (
                    <article className="skeleton-source" key={index}>
                      <Skeleton width="34px" height="34px" radius="10px" />
                      <div className="skeleton-stack">
                        <Skeleton width="70%" />
                        <Skeleton className="skeleton-line-sm" width="50%" />
                      </div>
                    </article>
                  )}
                </SkeletonRows>
              </SkeletonRegion>
            ) : (schedule?.sources ?? []).length === 0 ? (
              <p className="empty-state">No calendars are contributing to this day yet.</p>
            ) : (
              (schedule?.sources ?? []).map((source) => {
                const failure = failed.find((item) => item.connectionId === source.connectionId);
                return (
                  <article key={source.connectionId}>
                    <i className={providerInitial(source.provider).tone}>{providerInitial(source.provider).initial}</i>
                    <span>
                      <strong>{calendarProviderLabel(source.provider)}</strong>
                      <small>{source.calendars.join(" + ")}</small>
                    </span>
                    <em className={failure ? "failed" : ""}>
                      ● {failure ? "Unavailable" : calendarStatusLabel(source.status)}
                    </em>
                  </article>
                );
              })
            )}
          </section>

          <section className="privacy-note">
            <LockKeyhole size={18} />
            <div>
              <strong>Private by design</strong>
              <p>
                Imported events show only what your connected account can already see. Guests on your booking pages still
                see availability alone.
              </p>
            </div>
          </section>
        </aside>
      </div>
    </>
  );
}

/** The date as the host's own calendar reads it, not the browser's. */
function dateKeyInZone(value: Date, timeZone: string) {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(value);
}

function shiftDateKey(date: string, days: number) {
  const parsed = new Date(`${date}T12:00:00.000Z`);
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
}

/** Formatted in UTC so the label never disagrees with the date key it came from. */
function formatScheduleDate(date: string) {
  return new Intl.DateTimeFormat(undefined, { timeZone: "UTC", weekday: "long", month: "long", day: "numeric" }).format(
    new Date(`${date}T12:00:00.000Z`)
  );
}

function formatScheduleWeekday(date: string) {
  return new Intl.DateTimeFormat(undefined, { timeZone: "UTC", weekday: "short" })
    .format(new Date(`${date}T12:00:00.000Z`))
    .toUpperCase();
}

function formatScheduleClock(value: string, timeZone: string) {
  return new Intl.DateTimeFormat(undefined, { timeZone, hour: "numeric", minute: "2-digit" }).format(new Date(value));
}

function timeOfDayGreeting(timeZone: string, now = new Date()): string {
  const hourPart = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    hourCycle: "h23"
  })
    .formatToParts(now)
    .find((part) => part.type === "hour")?.value;
  const hour = Number(hourPart);

  if (!Number.isFinite(hour)) {
    return "Good morning";
  }
  if (hour >= 5 && hour < 12) {
    return "Good morning";
  }
  if (hour >= 12 && hour < 17) {
    return "Good afternoon";
  }
  return "Good evening";
}

function formatHourLabel(hour: number) {
  const normalized = ((hour % 24) + 24) % 24;
  const suffix = normalized < 12 ? "AM" : "PM";
  return `${normalized % 12 === 0 ? 12 : normalized % 12} ${suffix}`;
}

function formatDurationLabel(minutes: number) {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

/** Minutes past midnight in the schedule's own zone, which is how the day is drawn. */
function minutesIntoDay(value: string, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    hour: "2-digit",
    minute: "2-digit"
  }).formatToParts(new Date(value));
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? "0");
  return (read("hour") % 24) * 60 + read("minute");
}

/** An opening on the hour reads better without the minutes: "10 AM", not "10:00 AM". */
function formatOpeningClock(value: string, timeZone: string) {
  return formatScheduleClock(value, timeZone).replace(":00", "");
}

/** "10:00 AM–11:00 AM · 1 hr", in the schedule's own zone. */
function formatOpenWindow(window: { start: string; end: string }, timeZone: string) {
  const minutes = Math.round((Date.parse(window.end) - Date.parse(window.start)) / 60_000);
  return `${formatScheduleClock(window.start, timeZone)}–${formatScheduleClock(window.end, timeZone)} · ${formatDurationLabel(minutes)}`;
}

/**
 * The booking-only list: everything booked through TrueAvail, with its own search
 * and range filters. Reachable from the day view through the Bookings switch.
 */
function BookingsListView({
  authState,
  adminData,
  onOpenBooking
}: {
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  onOpenBooking: (bookingId: string) => void;
}) {
  const [bookingsState, setBookingsState] = useState<LoadState>("loading");
  const [bookingsData, setBookingsData] = useState<AdminBookingsResponse | null>(null);
  const [actionState, setActionState] = useState<{ bookingId?: string; message?: string; error?: string }>({});
  const [query, setQuery] = useState("");
  const [personaFilter, setPersonaFilter] = useState("all");
  const [rangeFilter, setRangeFilter] = useState<BookingRangeFilter>("upcoming");
  const host = signedInHost(authState, adminData);

  function loadBookings(signal?: globalThis.AbortSignal) {
    if (authState.status !== "signed_in") {
      setBookingsState("error");
      setBookingsData(null);
      return;
    }
    const start = new Date();
    start.setUTCDate(start.getUTCDate() - 30);
    const end = new Date();
    end.setUTCDate(end.getUTCDate() + 90);
    const query = new globalThis.URLSearchParams({
      start: start.toISOString(),
      end: end.toISOString(),
      limit: "100"
    });
    if (host) {
      query.set("hostId", host.id);
    }
    setBookingsState("loading");
    apiFetch(`/v1/admin/bookings?${query.toString()}`, {
      ...(signal ? { signal } : {}),
      headers: { authorization: `Bearer ${authState.idToken}` }
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`bookings_${response.status}`);
        }
        return (await response.json()) as AdminBookingsResponse;
      })
      .then((body) => {
        setBookingsData(body);
        setBookingsState("live");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setBookingsState("error");
        }
      });
  }

  useEffect(() => {
    const controller = new globalThis.AbortController();
    loadBookings(controller.signal);
    return () => controller.abort();
  }, [authState, host]);

  /** Answer a request the host is holding, without leaving the console. */
  async function answerApproval(booking: Booking, decision: "approve" | "decline") {
    if (authState.status !== "signed_in") {
      return;
    }
    setActionState({ bookingId: booking.id });
    try {
      const response = await apiFetch(`/v1/admin/bookings/${booking.id}/approval`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${authState.idToken}`,
          "content-type": "application/json"
        },
        body: JSON.stringify({ decision })
      });
      const body = (await response.json().catch(() => ({}))) as {
        booking?: Booking;
        decision?: string;
        error?: { code?: string; message?: string };
      };
      if (!response.ok || !body.booking) {
        throw new Error(body.error?.message ?? body.error?.code ?? `approval_${response.status}`);
      }
      const answered = body.booking;
      setBookingsData((current) =>
        current
          ? { ...current, bookings: current.bookings.map((item) => (item.id === answered.id ? answered : item)) }
          : current
      );
      setActionState({
        message:
          body.decision === "already_answered"
            ? "That request had already been answered."
            : decision === "approve"
              ? `Approved ${booking.guestName}. The invitation is on its way.`
              : `Declined ${booking.guestName}. The time is free again.`
      });
    } catch (cause) {
      setActionState({ error: cause instanceof Error ? cause.message : "That could not be saved." });
    }
  }

  async function cancelBooking(booking: Booking) {
    if (authState.status !== "signed_in") {
      return;
    }
    setActionState({ bookingId: booking.id });
    try {
      const response = await apiFetch(`/v1/admin/bookings/${booking.id}/cancel`, {
        method: "POST",
        headers: {
          authorization: `Bearer ${authState.idToken}`,
          "content-type": "application/json"
        },
        body: JSON.stringify({ reason: "Canceled from host console." })
      });
      const body = (await response.json().catch(() => ({}))) as { booking?: Booking; error?: { code?: string; message?: string } };
      if (!response.ok || !body.booking) {
        throw new Error(body.error?.message ?? body.error?.code ?? `cancel_${response.status}`);
      }
      setBookingsData((current) =>
        current
          ? {
              ...current,
              bookings: current.bookings.map((item) => (item.id === body.booking?.id ? body.booking : item))
            }
          : current
      );
      setActionState({ message: `Canceled ${booking.guestName}.` });
    } catch (cause) {
      setActionState({ error: cause instanceof Error ? cause.message : "Cancellation failed." });
    }
  }


  const hostNames = new Map((bookingsData?.hosts ?? adminData?.hosts ?? []).map((item) => [item.id, item.displayName]));
  const personasById = new Map((adminData?.personas ?? []).map((item) => [item.id, item]));
  const meetingNames = new Map((adminData?.meetingTypes ?? []).map((item) => [item.id, item.title]));

  const now = Date.now();
  const search = query.trim().toLowerCase();
  const visibleBookings = (bookingsData?.bookings ?? [])
    .filter((booking) => {
      if (rangeFilter === "all") {
        return true;
      }
      if (rangeFilter === "canceled") {
        return isCanceledBooking(booking);
      }
      const starts = Date.parse(booking.interval.start);
      // "Upcoming" means the meeting is still going to happen. A cancellation
      // keeps its original future start time, so filtering on the clock alone
      // left canceled bookings sitting in the list as though they were live.
      return rangeFilter === "upcoming" ? starts >= now && !isCanceledBooking(booking) : starts < now;
    })
    .filter((booking) => personaFilter === "all" || booking.personaId === personaFilter)
    .filter((booking) =>
      search === "" ||
      `${booking.guestName} ${booking.guestEmail} ${meetingNames.get(booking.meetingTypeId) ?? ""}`.toLowerCase().includes(search)
    )
    // Upcoming reads forwards from today; history reads backwards from today.
    .sort((a, b) =>
      rangeFilter === "past"
        ? Date.parse(b.interval.start) - Date.parse(a.interval.start)
        : Date.parse(a.interval.start) - Date.parse(b.interval.start)
    );

  return (
    <div className="bookings-list-panel">
      <section className="bookings-toolbar">
        <label className="bookings-search">
          <Search size={17} />
          <input value={query} onChange={(event) => setQuery(event.currentTarget.value)} placeholder="Search guest or meeting" />
        </label>
        <label className="bookings-filter">
          <Filter size={16} />
          <select value={personaFilter} onChange={(event) => setPersonaFilter(event.currentTarget.value)} aria-label="Filter by persona">
            <option value="all">All personas</option>
            {(adminData?.personas ?? []).map((persona) => (
              <option key={persona.id} value={persona.id}>{persona.name}</option>
            ))}
          </select>
          <ChevronDown size={15} />
        </label>
        <label className="bookings-filter">
          <CalendarCheck size={16} />
          <select value={rangeFilter} onChange={(event) => setRangeFilter(event.currentTarget.value as BookingRangeFilter)} aria-label="Filter by date">
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
            <option value="canceled">Canceled</option>
            <option value="all">All time</option>
          </select>
          <ChevronDown size={15} />
        </label>
      </section>

      {actionState.message ? <p className="inline-note" aria-live="polite">{actionState.message}</p> : null}
      {actionState.error ? <p className="form-error" aria-live="polite">{actionState.error}</p> : null}

      <section className="bookings-table-card">
        <div className="bookings-table-head">
          <span>Date &amp; time</span>
          <span>Meeting</span>
          <span>Guest</span>
          <span>Persona</span>
          <span>Status</span>
          <span />
        </div>
        <div className="bookings-table-body">
          {bookingsState === "loading" ? (
            <SkeletonRegion label="Loading bookings">
              <SkeletonRows count={5}>
                {(index) => (
                  <article className="skeleton-booking-row" key={index}>
                    <Skeleton className="skeleton-line-sm" width="82%" />
                    <div className="skeleton-stack">
                      <Skeleton width={`${62 + (index % 3) * 12}%`} />
                      <Skeleton className="skeleton-line-sm" width="48%" />
                    </div>
                    <div className="skeleton-guest">
                      <Skeleton className="skeleton-circle" width="32px" height="32px" />
                      <Skeleton width="70%" />
                    </div>
                    <Skeleton className="skeleton-line-sm" width="72%" />
                    <Skeleton height="24px" radius="999px" width="84px" />
                    <Skeleton width="20px" height="20px" radius="6px" />
                  </article>
                )}
              </SkeletonRows>
            </SkeletonRegion>
          ) : null}
          {bookingsState === "error" ? <p className="empty-state">Sign in to load booking records.</p> : null}
          {bookingsState === "live" && visibleBookings.length === 0 ? (
            <p className="empty-state">
              {(bookingsData?.bookings.length ?? 0) === 0
                ? "No bookings yet. Once someone books a time it appears here."
                : "No bookings match these filters."}
            </p>
          ) : null}
          {visibleBookings.map((booking) => {
            const persona = personasById.get(booking.personaId);
            const status = bookingStatusView(booking.status);
            const cancelable = ["confirmed", "pending_approval", "held", "failed_reconciliation_needed"].includes(booking.status);
            const awaitingAnswer = booking.status === "pending_approval";
            return (
              <article key={booking.id} className="is-clickable" onClick={() => onOpenBooking(booking.id)}>
                <span className="booking-when">{formatBookingWhen(booking.interval.start)}</span>
                <span className="booking-meeting">
                  <strong>{meetingNames.get(booking.meetingTypeId) ?? booking.meetingTypeId}</strong>
                  <small>{persona ? locationLabel(persona) : hostNames.get(booking.hostId) ?? ""}</small>
                </span>
                <span className="booking-guest">
                  <i>{hostInitials(booking.guestName)}</i>
                  <span>{booking.guestName}</span>
                </span>
                <span className="booking-persona">
                  <em style={{ "--persona-accent": persona?.accentColor ?? "#635bff" } as CSSProperties}>
                    {persona?.name ?? "Unknown"}
                  </em>
                </span>
                <span className={`booking-status ${status.tone}`}>{status.label}</span>
                <ActionMenu
                  label={`More actions for ${booking.guestName}`}
                  items={[
                    { label: "View details", onSelect: () => onOpenBooking(booking.id) },
                    ...(awaitingAnswer
                      ? [
                          {
                            label: "Approve request",
                            disabled: actionState.bookingId === booking.id,
                            onSelect: () => void answerApproval(booking, "approve")
                          },
                          {
                            label: "Decline request",
                            danger: true,
                            disabled: actionState.bookingId === booking.id,
                            onSelect: () => void answerApproval(booking, "decline")
                          }
                        ]
                      : []),
                    {
                      label: "Reschedule\u2026",
                      disabled: booking.status !== "confirmed",
                      onSelect: () => onOpenBooking(booking.id)
                    },
                    {
                      label: "Copy guest email",
                      onSelect: () => void globalThis.navigator.clipboard.writeText(booking.guestEmail)
                    },
                    {
                      label: "Cancel booking",
                      danger: true,
                      disabled: !cancelable || actionState.bookingId === booking.id,
                      onSelect: () => void cancelBooking(booking)
                    }
                  ]}
                />
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

const ONBOARDING_STEPS: Array<{ label: string; detail: string }> = [
  { label: "Your account", detail: "Profile & preferences" },
  { label: "Persona", detail: "Public identity" },
  { label: "Calendar", detail: "Availability sync" },
  { label: "Review", detail: "Preview & publish" }
];

const ONBOARDING_HELP: Array<{ title: string; body: string }> = [
  {
    title: "One account, many personas",
    body: "Your private account powers separate public booking identities for work, Fund My Squad, family, and more."
  },
  {
    title: "Make each identity intentional",
    body: "Use a different headline, brand color, location, and message for every audience."
  },
  {
    title: "No double-booking",
    body: "All connected calendars contribute busy time, even when a meeting belongs to another persona."
  }
];

/**
 * The four-step workspace setup. Each step's "done" state is read from records that
 * actually exist rather than from wizard progress, so re-entering onboarding never
 * claims work that was not saved, and every Continue writes through the same admin
 * endpoints the rest of the app uses.
 */
function OnboardingView({
  authState,
  adminData,
  onAdminDataChange,
  onOpenView
}: {
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  onAdminDataChange: (data: AdminStatusResponse | null) => void;
  onOpenView: (view: View) => void;
}) {
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState<OnboardingStatus>("idle");
  const [message, setMessage] = useState("");
  const [publishNow, setPublishNow] = useState(true);

  const host = signedInHost(authState, adminData);
  const hostPersonas = useMemo(
    () => (adminData?.personas ?? []).filter((item) => item.hostId === host?.id),
    [adminData, host?.id]
  );
  const persona = hostPersonas[0];
  const meetingType = (adminData?.meetingTypes ?? []).find((item) => item.personaId === persona?.id);
  const connections = useMemo(
    () => (adminData?.calendarConnections ?? []).filter((item) => item.hostId === host?.id),
    [adminData, host?.id]
  );
  const connection = connections.find((item) => item.status === "connected") ?? connections[0];
  const resources = useMemo(
    () => (adminData?.calendarResources ?? []).filter((item) => item.connectionId === connection?.id),
    [adminData, connection?.id]
  );

  const email = authState.status === "signed_in" ? authState.email : "";
  const idToken = authState.status === "signed_in" ? authState.idToken : "";
  const [firstName, setFirstName] = useState(() => (host?.displayName ?? "").split(/\s+/)[0] ?? "");
  const [lastName, setLastName] = useState(() => (host?.displayName ?? "").split(/\s+/).slice(1).join(" "));
  const [timeZone, setTimeZone] = useState(
    () => host?.defaultTimeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? fredrick.defaultTimeZone
  );

  const [draft, setDraft] = useState(() => ({
    name: persona?.name ?? "",
    displayName: persona?.displayName ?? host?.displayName ?? "",
    headline: persona?.headline ?? "",
    organization: persona?.organization ?? "",
    bio: persona?.bio ?? "",
    slug: persona?.slug ?? "",
    slugTouched: Boolean(persona),
    accentColor: persona?.accentColor ?? "#635BFF",
    locationKind: persona?.locationPreference.kind ?? "google_meet",
    meetingTitle: meetingType?.title ?? "",
    meetingSlug: meetingType?.slug ?? "",
    meetingSlugTouched: Boolean(meetingType),
    durationMinutes: meetingType?.durationMinutes ?? host?.invitedDefaults?.meetingDurations?.[0] ?? 30,
    meetingDescription: meetingType?.description ?? "",
    // Whoever invited this host already answered the availability question, so the
    // wizard opens on their answer instead of the generic workweek.
    availabilityPreset: invitedAvailabilityPreset(host) ? "invited" : "standard"
  }));

  // The invited window joins the standing presets rather than replacing them: a new
  // host is free to disagree with what was chosen for them.
  const invitedPreset = invitedAvailabilityPreset(host);
  const availabilityPresets = invitedPreset ? [invitedPreset, ...AVAILABILITY_PRESETS] : AVAILABILITY_PRESETS;

  // Calendar choices start from what is already saved for the connection, so this
  // step shows the host's real configuration rather than a fresh set of defaults.
  const [blocking, setBlocking] = useState<Set<string>>(new Set());
  const [destinationId, setDestinationId] = useState("");
  useEffect(() => {
    setBlocking(new Set(resources.filter((item) => item.blocksAvailability).map((item) => item.providerCalendarId)));
    const destination = resources
      .filter((item) => item.destinationPriority > 0)
      .sort((left, right) => left.destinationPriority - right.destinationPriority)[0];
    setDestinationId((current) => current || destination?.providerCalendarId || resources[0]?.providerCalendarId || "");
  }, [resources]);

  const accountDone = Boolean(host);
  const personaDone = Boolean(persona && meetingType);
  const calendarDone = Boolean(connection && connection.status === "connected");
  const stepDone = [accountDone, personaDone, calendarDone, persona?.publicState === "active"];
  const completed = stepDone.filter(Boolean).length;

  const slugValid = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/.test(draft.slug);
  const meetingSlugValid = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/.test(draft.meetingSlug);
  const hostSlug = host?.slug ?? slugFromEmail(email);
  const urlPrefix = `${displayUrl(appOrigin)}/${hostSlug}/`;
  const publicUrl = `${appOrigin}/${hostSlug}/${persona?.slug ?? draft.slug}`;
  const busy = status === "submitting";
  const blockingCount = resources.filter((item) => blocking.has(item.providerCalendarId)).length;

  function set(patch: Partial<typeof draft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  // The URL follows the name until the host edits it themselves.
  function setName(value: string) {
    setDraft((current) => ({ ...current, name: value, ...(current.slugTouched ? {} : { slug: slugify(value) }) }));
  }

  function setMeetingTitle(value: string) {
    setDraft((current) => ({ ...current, meetingTitle: value, ...(current.meetingSlugTouched ? {} : { meetingSlug: slugify(value) }) }));
  }

  function fail(cause: unknown, fallback: string) {
    setStatus("error");
    setMessage(cause instanceof Error ? cause.message : fallback);
    return false;
  }

  async function readError(response: globalThis.Response, code: string): Promise<never> {
    const body = (await response.json().catch(() => ({}))) as {
      error?: { details?: string[]; message?: string; code?: string };
    };
    throw new Error(body.error?.details?.join(", ") ?? body.error?.message ?? body.error?.code ?? `${code}_${response.status}`);
  }

  async function saveAccount(): Promise<boolean> {
    const displayName = `${firstName} ${lastName}`.trim();
    if (!displayName) {
      setStatus("error");
      setMessage("Add your name before continuing.");
      return false;
    }
    setStatus("submitting");
    setMessage("");
    try {
      if (!host) {
        // No host record yet: this is the first thing the workspace needs.
        const response = await apiFetch("/v1/onboarding/hosts/signup", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email, slug: hostSlug, displayName, defaultTimeZone: timeZone })
        });
        if (!response.ok) {
          await readError(response, "host_signup");
        }
      } else if (displayName !== host.displayName || timeZone !== host.defaultTimeZone) {
        const response = await apiFetch(`/v1/admin/hosts/${encodeURIComponent(host.id)}`, {
          method: "PUT",
          headers: { authorization: `Bearer ${idToken}`, "content-type": "application/json" },
          body: JSON.stringify({ displayName, defaultTimeZone: timeZone })
        });
        if (!response.ok) {
          await readError(response, "host_update");
        }
      }
      await refreshAdminData(idToken, onAdminDataChange);
      setStatus("idle");
      return true;
    } catch (cause) {
      return fail(cause, "Your account could not be saved.");
    }
  }

  async function savePersona(): Promise<boolean> {
    if (!host) {
      setStep(1);
      setStatus("error");
      setMessage("Confirm your account before creating a persona.");
      return false;
    }
    if (!draft.name.trim() || !draft.displayName.trim() || !draft.headline.trim() || !draft.bio.trim() || !slugValid) {
      setStatus("error");
      setMessage("A persona needs a name, display name, headline, short bio and a valid URL.");
      return false;
    }
    if (!draft.meetingTitle.trim() || !meetingSlugValid || !draft.meetingDescription.trim()) {
      setStatus("error");
      setMessage("The first meeting type needs a title, a valid URL and a description.");
      return false;
    }
    const preset = availabilityPresets.find((item) => item.id === draft.availabilityPreset) ?? availabilityPresets[0]!;
    setStatus("submitting");
    setMessage("");
    try {
      if (persona && meetingType) {
        // Coming back to this step edits what exists instead of creating a second persona.
        const personaResponse = await apiFetch(`/v1/admin/personas/${encodeURIComponent(persona.id)}`, {
          method: "PUT",
          headers: { authorization: `Bearer ${idToken}`, "content-type": "application/json" },
          body: JSON.stringify({
            name: draft.name,
            displayName: draft.displayName,
            headline: draft.headline,
            organization: draft.organization,
            bio: draft.bio,
            accentColor: draft.accentColor,
            defaultTimeZone: timeZone,
            locationKind: draft.locationKind
          })
        });
        if (!personaResponse.ok) {
          await readError(personaResponse, "persona_update");
        }
        const meetingResponse = await apiFetch(`/v1/admin/meeting-types/${encodeURIComponent(meetingType.id)}`, {
          method: "PUT",
          headers: { authorization: `Bearer ${idToken}`, "content-type": "application/json" },
          body: JSON.stringify({
            title: draft.meetingTitle,
            description: draft.meetingDescription,
            durationMinutes: draft.durationMinutes,
            startHour: preset.startHour,
            endHour: preset.endHour,
            // Availability is stored as wall-clock hours in a named zone, so the
            // host's zone travels with the window rather than being assumed.
            timeZone
          })
        });
        if (!meetingResponse.ok) {
          await readError(meetingResponse, "meeting_update");
        }
      } else {
        const response = await apiFetch("/v1/admin/personas", {
          method: "POST",
          headers: { authorization: `Bearer ${idToken}`, "content-type": "application/json" },
          body: JSON.stringify({
            hostId: host.id,
            hostSlug: host.slug,
            slug: draft.slug,
            name: draft.name,
            displayName: draft.displayName,
            headline: draft.headline,
            organization: draft.organization,
            bio: draft.bio,
            accentColor: draft.accentColor,
            defaultTimeZone: timeZone,
            locationKind: draft.locationKind,
            // Hidden until the review step publishes it deliberately.
            publicState: "inactive",
            meetingSlug: draft.meetingSlug,
            meetingTitle: draft.meetingTitle,
            meetingDescription: draft.meetingDescription,
            durationMinutes: draft.durationMinutes,
            startHour: preset.startHour,
            endHour: preset.endHour
          })
        });
        if (!response.ok) {
          await readError(response, "persona_create");
        }
      }
      await refreshAdminData(idToken, onAdminDataChange);
      setStatus("idle");
      return true;
    } catch (cause) {
      return fail(cause, "The persona could not be saved.");
    }
  }

  async function saveCalendar(): Promise<boolean> {
    if (!connection || resources.length === 0) {
      // Nothing to save yet. The review step reports the calendar as missing rather
      // than letting the wizard imply protection that does not exist.
      return true;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const calendars = resources
        // A placeholder id would overwrite the host's real selection with a calendar
        // that does not exist, so those rows are left exactly as they were saved.
        .filter((resource) => resource.providerCalendarId !== "redacted")
        .map((resource) => ({
          providerCalendarId: resource.providerCalendarId,
          label: resource.label,
          blocksAvailability: blocking.has(resource.providerCalendarId)
        }));
      if (calendars.length === 0) {
        setStatus("idle");
        return true;
      }
      const response = await apiFetch(`/v1/admin/calendar-connections/${encodeURIComponent(connection.id)}/calendars`, {
        method: "PUT",
        headers: { authorization: `Bearer ${idToken}`, "content-type": "application/json" },
        body: JSON.stringify({ calendars, ...(destinationId ? { destinationCalendarId: destinationId } : {}) })
      });
      if (!response.ok) {
        await readError(response, "calendars_update");
      }
      await refreshAdminData(idToken, onAdminDataChange);
      setStatus("idle");
      return true;
    } catch (cause) {
      return fail(cause, "Calendar selection could not be saved.");
    }
  }

  async function publish(): Promise<boolean> {
    if (!persona) {
      setStep(2);
      setStatus("error");
      setMessage("Create a persona before publishing.");
      return false;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch(`/v1/admin/personas/${encodeURIComponent(persona.id)}`, {
        method: "PUT",
        headers: { authorization: `Bearer ${idToken}`, "content-type": "application/json" },
        body: JSON.stringify({ publicState: publishNow ? "active" : "inactive" })
      });
      if (!response.ok) {
        await readError(response, "persona_publish");
      }
      await refreshAdminData(idToken, onAdminDataChange);
      setStatus("done");
      onOpenView("overview");
      return true;
    } catch (cause) {
      return fail(cause, "The persona could not be published.");
    }
  }

  async function advance() {
    if (step === 1 && !(await saveAccount())) return;
    if (step === 2 && !(await savePersona())) return;
    if (step === 3 && !(await saveCalendar())) return;
    if (step === 4) {
      await publish();
      return;
    }
    setStep(step + 1);
  }

  const checks = [
    {
      title: "Account",
      copy: host ? `${host.displayName} · ${host.defaultTimeZone}` : "Not created yet",
      target: 1,
      done: accountDone
    },
    {
      title: "Persona",
      copy: persona ? `${persona.name} · ${persona.publicState === "active" ? "Public" : "Hidden"}` : "Not created yet",
      target: 2,
      done: personaDone
    },
    {
      title: "Meeting type",
      copy: meetingType
        ? `${meetingType.durationMinutes} minutes · ${persona ? locationLabel(persona) : ""}`
        : "Not created yet",
      target: 2,
      done: Boolean(meetingType)
    },
    {
      title: "Calendar protection",
      copy: calendarDone
        ? `${blockingCount} ${blockingCount === 1 ? "calendar" : "calendars"} checked for conflicts`
        : "No calendar connected",
      target: 3,
      done: calendarDone && blockingCount > 0
    }
  ];

  return (
    <div className="onboarding-v2">
      <section className="onboarding-v2-title">
        <div>
          <p className="eyebrow">Workspace setup</p>
          <h2>Create your scheduling workspace</h2>
          <p>Complete four short steps to publish your first booking page.</p>
        </div>
        <span className="editor-tag blue">Step {step} of 4</span>
      </section>

      <ol className="onboard-v2-steps">
        {ONBOARDING_STEPS.map((item, index) => (
          <li key={item.label}>
            <button
              type="button"
              className={stepDone[index] ? "done" : step === index + 1 ? "current" : ""}
              aria-current={step === index + 1 ? "step" : undefined}
              onClick={() => setStep(index + 1)}
            >
              <i>{stepDone[index] ? <Check size={15} /> : index + 1}</i>
              <span>
                <strong>{item.label}</strong>
                <small>{item.detail}</small>
              </span>
            </button>
            {index < ONBOARDING_STEPS.length - 1 ? <em /> : null}
          </li>
        ))}
      </ol>

      {message ? (
        <p className={status === "error" ? "form-error" : "inline-note"} aria-live="polite">{message}</p>
      ) : null}

      <div className="onboard-v2-layout">
        <main className="onboard-v2-main">
          {step === 1 ? (
            <section className="onboard-panel">
              <div className="onboard-panel-head">
                <i><UserRound size={20} /></i>
                <div>
                  <span>Step 1</span>
                  <h3>Confirm your account</h3>
                  <p>This information stays private and is used to manage your scheduling workspace.</p>
                </div>
              </div>
              <div className="formgrid">
                <label>
                  First name
                  <input value={firstName} autoComplete="given-name" onChange={(event) => setFirstName(event.currentTarget.value)} />
                </label>
                <label>
                  Last name
                  <input value={lastName} autoComplete="family-name" onChange={(event) => setLastName(event.currentTarget.value)} />
                </label>
                <label className="span2">
                  Account email
                  <input value={email} readOnly />
                  <small className="field-help">Your sign-in address. Change it from Profile → Account security.</small>
                </label>
                <label>
                  Home time zone
                  <TimeZoneSelect name="defaultTimeZone" value={timeZone} ariaLabel="Home time zone" onChange={setTimeZone} />
                </label>
                <label>
                  Language
                  <select value="en-US" disabled onChange={() => undefined}>
                    <option value="en-US">English (US)</option>
                  </select>
                  <small className="field-help">English is the only language available today.</small>
                </label>
              </div>
              <div className="onboard-security">
                <ShieldCheck size={19} />
                <div>
                  <strong>Your booking identities stay separate</strong>
                  <p>You can use different public names, organizations, links, and video providers for each persona.</p>
                </div>
              </div>
            </section>
          ) : null}

          {step === 2 ? (
            <section className="onboard-panel">
              <div className="onboard-panel-head">
                <i className="violet"><Sparkles size={20} /></i>
                <div>
                  <span>Step 2</span>
                  <h3>Create your first persona</h3>
                  <p>Define the public identity and the first conversation visitors can book.</p>
                </div>
              </div>

              <div className="onboard-subhead">
                <strong>Public identity</strong>
                <small>What guests see on your booking page</small>
              </div>
              <div className="formgrid">
                <label>
                  Persona name
                  <input value={draft.name} placeholder="e.g., Fund My Squad" onChange={(event) => setName(event.currentTarget.value)} />
                </label>
                <label>
                  Public display name
                  <input value={draft.displayName} onChange={(event) => set({ displayName: event.currentTarget.value })} />
                </label>
                <label>
                  Headline
                  <input value={draft.headline} placeholder="e.g., Founder, Fund My Squad" onChange={(event) => set({ headline: event.currentTarget.value })} />
                </label>
                <label>
                  Organization <em className="optional">(optional)</em>
                  <input value={draft.organization} onChange={(event) => set({ organization: event.currentTarget.value })} />
                </label>
                <label className="span2">
                  Bio
                  <textarea
                    rows={3}
                    value={draft.bio}
                    placeholder="Tell visitors what they can schedule with you and what to expect."
                    onChange={(event) => set({ bio: event.currentTarget.value })}
                  />
                </label>
                <label className="span2">
                  Persona URL
                  <div className={persona ? "prefix-input" : "prefix-input editable"}>
                    <span>{urlPrefix}</span>
                    <input
                      value={draft.slug}
                      placeholder="fundmysquad"
                      readOnly={Boolean(persona)}
                      onChange={(event) => set({ slug: slugify(event.currentTarget.value), slugTouched: true })}
                    />
                  </div>
                  <small className={draft.slug && !slugValid ? "field-help invalid" : "field-help"}>
                    {persona
                      ? "The booking link is fixed once the persona exists."
                      : draft.slug && !slugValid
                        ? "Use letters, numbers and hyphens, at least three characters."
                        : "Short and memorable. This becomes part of your public link."}
                  </small>
                </label>
                <label>
                  Accent color
                  <span className="hex-field wide">
                    <i style={{ background: draft.accentColor }} />
                    <input
                      value={draft.accentColor}
                      pattern="#[0-9a-fA-F]{6}"
                      aria-label="Accent color hex value"
                      onChange={(event) => set({ accentColor: event.currentTarget.value })}
                    />
                  </span>
                </label>
                <label>
                  Default location
                  <select
                    value={draft.locationKind}
                    onChange={(event) => set({ locationKind: event.currentTarget.value as Persona["locationPreference"]["kind"] })}
                  >
                    <option value="google_meet">Google Meet</option>
                    <option value="zoho_meeting">Zoho Meeting</option>
                    <option value="microsoft_teams">Microsoft Teams</option>
                    <option value="phone">Phone call</option>
                    <option value="decide_later">Decide later</option>
                  </select>
                </label>
              </div>

              <div className="onboard-subhead meeting">
                <strong>First meeting type</strong>
                <small>You can add more after publishing</small>
              </div>
              <div className="formgrid compact">
                <label>
                  Meeting title
                  <input value={draft.meetingTitle} placeholder="e.g., Intro call" onChange={(event) => setMeetingTitle(event.currentTarget.value)} />
                </label>
                <label>
                  Meeting URL
                  <input
                    value={draft.meetingSlug}
                    placeholder="intro"
                    readOnly={Boolean(meetingType)}
                    onChange={(event) => set({ meetingSlug: slugify(event.currentTarget.value), meetingSlugTouched: true })}
                  />
                </label>
                <label>
                  Duration
                  <select value={draft.durationMinutes} onChange={(event) => set({ durationMinutes: Number(event.currentTarget.value) })}>
                    {[...new Set([...(host?.invitedDefaults?.meetingDurations ?? []), 15, 20, 30, 45, 60, 90])]
                      .sort((left, right) => left - right)
                      .map((minutes) => (
                      <option key={minutes} value={minutes}>{minutes} minutes</option>
                    ))}
                  </select>
                </label>
                <label>
                  Availability schedule
                  <select value={draft.availabilityPreset} onChange={(event) => set({ availabilityPreset: event.currentTarget.value })}>
                    {availabilityPresets.map((preset) => (
                      <option key={preset.id} value={preset.id}>{preset.label}</option>
                    ))}
                  </select>
                  <small className="field-help">Monday to Friday in {timeZone}. Fine-tune it later under Availability.</small>
                </label>
                <label className="span2">
                  Description
                  <textarea
                    rows={3}
                    value={draft.meetingDescription}
                    placeholder="What you will cover, and anything the guest should prepare."
                    onChange={(event) => set({ meetingDescription: event.currentTarget.value })}
                  />
                </label>
              </div>
            </section>
          ) : null}

          {step === 3 ? (
            <section className="onboard-panel">
              <div className="onboard-panel-head">
                <i className="blue"><CalendarCheck size={20} /></i>
                <div>
                  <span>Step 3</span>
                  <h3>Connect every calendar that protects your time</h3>
                  <p>A busy event on any selected calendar removes that slot from every persona.</p>
                </div>
              </div>

              <div className={calendarDone ? "onboard-calendar-health" : "onboard-calendar-health warning"}>
                <ShieldCheck size={21} />
                <div>
                  <strong>
                    {connections.length === 0
                      ? "No account connected"
                      : `${connections.length} ${connections.length === 1 ? "account" : "accounts"} connected`}
                  </strong>
                  <p>
                    {calendarDone
                      ? "Busy time from the selected calendars is removed before a slot is offered."
                      : "Without a connected calendar, guests can book over the top of anything already in your day."}
                  </p>
                </div>
                <span className={calendarDone ? "editor-tag green" : "editor-tag orange"}>
                  {calendarDone ? "Live sync" : "Not protected"}
                </span>
              </div>

              {connections.map((item) => (
                <article className="onboard-calendar-account" key={item.id}>
                  <i className={providerInitial(item.provider).tone}>{providerInitial(item.provider).initial}</i>
                  <div>
                    <strong>{calendarProviderLabel(item.provider)}</strong>
                    {item.accountEmail ? <small>{item.accountEmail}</small> : null}
                  </div>
                  <span className={item.status === "connected" ? "editor-tag green" : "editor-tag orange"}>
                    {calendarStatusLabel(item.status)}
                  </span>
                  <button type="button" className="editor-outline-button" onClick={() => onOpenView("calendars")}>Manage</button>
                </article>
              ))}

              {connection && resources.length > 0 ? (
                <div className="onboard-calendar-config">
                  <div>
                    <h4>Check for conflicts</h4>
                    <p>These calendars block booking times.</p>
                    {resources.map((resource) => (
                      <label key={resource.id}>
                        <input
                          type="checkbox"
                          checked={blocking.has(resource.providerCalendarId)}
                          onChange={(event) => {
                            const checked = event.currentTarget.checked;
                            setBlocking((current) => {
                              const next = new Set(current);
                              if (checked) {
                                next.add(resource.providerCalendarId);
                              } else {
                                next.delete(resource.providerCalendarId);
                              }
                              return next;
                            });
                          }}
                        />
                        <span>
                          <strong>{resource.label}</strong>
                          <small>{blocking.has(resource.providerCalendarId) ? "Blocks time" : "Ignored"}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                  <div>
                    <h4>Add new bookings to</h4>
                    <p>Invitations and meeting links are created here.</p>
                    <label className="destination">
                      <CalendarCheck size={17} />
                      <select
                        aria-label="Destination calendar"
                        value={destinationId}
                        onChange={(event) => setDestinationId(event.currentTarget.value)}
                      >
                        {resources.map((resource) => (
                          <option key={resource.id} value={resource.providerCalendarId}>{resource.label}</option>
                        ))}
                      </select>
                    </label>
                    <div className="meet-ready">
                      <Video size={17} />
                      <span>
                        <strong>{locationKindLabel(draft.locationKind)}</strong>
                        <small>
                          {draft.locationKind === "decide_later"
                            ? "No meeting link is created automatically."
                            : "A link is generated for each booking where the provider supports it."}
                        </small>
                      </span>
                    </div>
                  </div>
                </div>
              ) : null}

              <button type="button" className="add-calendar" onClick={() => onOpenView("connect")}>
                <Plus size={18} />
                <span>
                  <strong>{connections.length === 0 ? "Connect a calendar" : "Connect another calendar"}</strong>
                  <small>Google, Microsoft, Zoho, or an iCloud account.</small>
                </span>
                <ChevronRight size={17} />
              </button>
            </section>
          ) : null}

          {step === 4 ? (
            <section className="onboard-panel review-panel">
              <div className="onboard-panel-head">
                <i className="green"><Check size={20} /></i>
                <div>
                  <span>Step 4</span>
                  <h3>Your booking page is ready</h3>
                  <p>Review the experience, then publish your first persona.</p>
                </div>
              </div>

              <div
                className="publish-preview"
                style={{ "--persona-accent": persona?.accentColor ?? draft.accentColor } as CSSProperties}
              >
                <div className="publish-browser">
                  <span>● ● ●</span>
                  <strong>{displayUrl(publicUrl)}</strong>
                </div>
                <span className="editor-avatar">{personaInitialsFromText(persona?.name ?? draft.name)}</span>
                <span className="editor-tag violet">{persona?.name || draft.name || "New persona"}</span>
                <h3>Meet with {(persona?.displayName ?? draft.displayName).split(/\s+/)[0] ?? "you"}</h3>
                <p>{persona?.bio ?? draft.bio}</p>
                {meetingType ? (
                  <article>
                    <i />
                    <span>
                      <strong>{meetingType.title}</strong>
                      <small>
                        {meetingType.durationMinutes} minutes · {persona ? locationLabel(persona) : locationKindLabel(draft.locationKind)}
                      </small>
                    </span>
                    <ChevronRight size={17} />
                  </article>
                ) : null}
              </div>

              <div className="publish-checks">
                {checks.map((check) => (
                  <article key={check.title}>
                    <i className={check.done ? "complete" : "pending"}>{check.done ? <Check size={14} /> : <X size={14} />}</i>
                    <span>
                      <strong>{check.title}</strong>
                      <small>{check.copy}</small>
                    </span>
                    <button type="button" onClick={() => setStep(check.target)}>Edit</button>
                  </article>
                ))}
              </div>

              <label className="publish-confirm">
                <input type="checkbox" checked={publishNow} onChange={(event) => setPublishNow(event.currentTarget.checked)} />
                <span>
                  <strong>Publish this persona now</strong>
                  <small>The booking page becomes available immediately. You can hide it at any time.</small>
                </span>
              </label>
            </section>
          ) : null}

          <div className="onboard-v2-actions">
            <button type="button" className="ghost-button" disabled={busy} onClick={() => onOpenView("overview")}>Save and exit</button>
            <span />
            {step > 1 ? (
              <button type="button" className="editor-outline-button" disabled={busy} onClick={() => setStep(step - 1)}>
                <ArrowLeft size={16} /> Back
              </button>
            ) : null}
            <button type="button" className="confirm" disabled={busy} onClick={() => void advance()}>
              {step === 4 ? (
                <><Check size={16} /> {busy ? "Publishing" : "Publish booking page"}</>
              ) : (
                <>{busy ? "Saving" : "Continue"} <ArrowRight size={16} /></>
              )}
            </button>
          </div>
        </main>

        <aside className="onboard-v2-side">
          {step < 4 ? (
            <>
              <section className="onboarding-summary">
                <div className="aside-head">
                  <span>Setup progress</span>
                  <strong>{completed * 25}%</strong>
                </div>
                <div className="progress-track"><i style={{ width: `${completed * 25}%` }} /></div>
                {ONBOARDING_STEPS.map((item, index) => (
                  <div key={item.label} className={stepDone[index] ? "done" : step === index + 1 ? "current" : ""}>
                    <i>{stepDone[index] ? <Check size={12} /> : index + 1}</i>
                    <span>
                      <strong>{item.label}</strong>
                      <small>{stepDone[index] ? "Complete" : step === index + 1 ? "In progress" : "Not started"}</small>
                    </span>
                  </div>
                ))}
              </section>

              <section className="onboarding-help">
                <Sparkles size={20} />
                <h3>{ONBOARDING_HELP[step - 1]?.title}</h3>
                <p>{ONBOARDING_HELP[step - 1]?.body}</p>
              </section>
            </>
          ) : (
            <section className="publish-summary">
              <span className="editor-tag green">Ready to publish</span>
              <h3>What happens next?</h3>
              {[
                `Your ${persona?.name || draft.name || "persona"} link becomes active`,
                "Availability is checked across connected calendars",
                draft.locationKind === "decide_later"
                  ? "You choose the meeting location for each booking"
                  : `${locationKindLabel(draft.locationKind)} links are added automatically`,
                "You can copy the link for your email signature"
              ].map((line) => (
                <p key={line}><Check size={14} /> {line}</p>
              ))}
              <a className="editor-outline-button" href={publicUrl} target="_blank" rel="noreferrer">
                <Eye size={15} /> Preview as a guest
              </a>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

function AdminInvitationsPanel({ authState }: { authState: Extract<AuthState, { status: "signed_in" }> }) {
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [actionState, setActionState] = useState<{ invitationId?: string; message?: string; error?: string }>({});

  function loadInvitations(signal?: globalThis.AbortSignal) {
    setLoadState("loading");
    apiFetch("/v1/admin/invitations", {
      ...(signal ? { signal } : {}),
      headers: { authorization: `Bearer ${authState.idToken}` }
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`invitations_${response.status}`);
        }
        return (await response.json()) as AdminInvitationsResponse;
      })
      .then((body) => {
        setInvitations(body.invitations);
        setLoadState("live");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setLoadState("error");
        }
      });
  }

  useEffect(() => {
    const controller = new globalThis.AbortController();
    loadInvitations(controller.signal);
    return () => controller.abort();
  }, [authState.idToken]);

  async function createInvitation(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    const form = new globalThis.FormData(event.currentTarget);
    setActionState({});
    try {
      const response = await apiFetch("/v1/admin/invitations", {
        method: "POST",
        headers: {
          authorization: `Bearer ${authState.idToken}`,
          "content-type": "application/json"
        },
        body: JSON.stringify({
          email: String(form.get("email") ?? ""),
          role: String(form.get("role") ?? "host") as Role,
          expiresAt: new Date(String(form.get("expiresAt") ?? "")).toISOString()
        })
      });
      const body = (await response.json().catch(() => ({}))) as AdminInvitationCreateResponse & { error?: { code?: string; details?: string[]; message?: string } };
      if (!response.ok) {
        throw new Error(body.error?.details?.join(", ") ?? body.error?.message ?? body.error?.code ?? `invite_create_${response.status}`);
      }
      setInvitations((current) => [body.invitation, ...current.filter((invitation) => invitation.id !== body.invitation.id)]);
      setActionState({ message: absoluteInviteUrl(body.inviteUrl) });
      event.currentTarget.reset();
    } catch (cause) {
      setActionState({ error: cause instanceof Error ? cause.message : "Invitation creation failed." });
    }
  }

  async function revokeInvitation(invitation: Invitation) {
    setActionState({ invitationId: invitation.id });
    try {
      const response = await apiFetch(`/v1/admin/invitations/${invitation.id}/revoke`, {
        method: "POST",
        headers: { authorization: `Bearer ${authState.idToken}` }
      });
      const body = (await response.json().catch(() => ({}))) as { invitation?: Invitation; error?: { code?: string; message?: string } };
      if (!response.ok || !body.invitation) {
        throw new Error(body.error?.message ?? body.error?.code ?? `invite_revoke_${response.status}`);
      }
      setInvitations((current) => current.map((item) => item.id === body.invitation?.id ? body.invitation : item));
      setActionState({ message: `Revoked ${body.invitation.email}.` });
    } catch (cause) {
      setActionState({ error: cause instanceof Error ? cause.message : "Invitation revoke failed." });
    }
  }

  return (
    <section className="panel span-3">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Admin invitations</p>
          <h2>Invite hosts and admins</h2>
        </div>
        <div className="inline-status">
          <UserPlus size={16} /> {loadState === "live" ? `${invitations.length} loaded` : dataStateLabel(loadState, "Loaded")}
        </div>
      </div>
      {actionState.message ? <p className="inline-note" aria-live="polite">{actionState.message}</p> : null}
      {actionState.error ? <p className="form-error" aria-live="polite">{actionState.error}</p> : null}
      <form className="wizard-form compact-management-form" onSubmit={(event) => void createInvitation(event)}>
        <label>Email<input name="email" type="email" required placeholder="host@example.com" /></label>
        <label>Role<select name="role" defaultValue="host"><option value="host">Host</option><option value="platform_admin">Platform admin</option></select></label>
        <label>Expires<input name="expiresAt" type="datetime-local" required defaultValue={defaultInvitationExpirationLocal()} /></label>
        <button className="confirm">
          <UserPlus size={18} /> Create invite
        </button>
      </form>
      <div className="booking-list">
        {loadState === "loading" ? (
          <SkeletonRegion label="Loading invitations">
            <SkeletonRows count={4}>
              {(index) => (
                <div className="booking-row booking-row-dense skeleton-invite-dense" key={index}>
                  <div className="skeleton-stack">
                    <Skeleton width={`${58 + index * 8}%`} />
                    <Skeleton className="skeleton-line-sm" width="72%" />
                  </div>
                  <Skeleton className="skeleton-line-sm" width="60%" />
                  <Skeleton className="skeleton-line-sm" width="76%" />
                  <Skeleton className="skeleton-line-sm" width="70%" />
                  <Skeleton className="skeleton-line-sm" width="54%" />
                  <Skeleton width="72px" height="32px" radius="10px" />
                </div>
              )}
            </SkeletonRows>
          </SkeletonRegion>
        ) : null}
        {loadState === "error" ? <p className="empty-state">Invitation records are unavailable.</p> : null}
        {loadState === "live" && invitations.length === 0 ? <p className="empty-state">No invitations yet.</p> : null}
        {invitations.map((invitation) => (
          <div key={invitation.id} className="booking-row booking-row-dense">
            <div>
              <strong>{invitation.email}</strong>
              <span>{invitation.id}</span>
            </div>
            <span>{invitation.role}</span>
            <span>{formatFullDate(dateKey(invitation.expiresAt))}</span>
            <span>{invitation.claimedAt ? formatFullDate(dateKey(invitation.claimedAt)) : invitation.revokedAt ? formatFullDate(dateKey(invitation.revokedAt)) : "Not claimed"}</span>
            <em>{invitation.status}</em>
            <button
              type="button"
              disabled={invitation.status !== "pending" || actionState.invitationId === invitation.id}
              onClick={() => void revokeInvitation(invitation)}
            >
              {actionState.invitationId === invitation.id ? "Revoking" : "Revoke"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

void AdminInvitationsPanel;

function UsersInvitationsView({
  authState,
  adminData,
  onAdminDataChange,
  onOpenView
}: {
  authState: Extract<AuthState, { status: "signed_in" }>;
  adminData: AdminStatusResponse | null;
  onAdminDataChange: (data: AdminStatusResponse | null) => void;
  onOpenView: (view: View) => void;
}) {
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [actionState, setActionState] = useState<{ message?: string; error?: string; invitationId?: string }>({});
  const [draftFlags, setDraftFlags] = useState<AdminFeatureFlags>(() => adminData?.featureFlags ?? defaultAdminFeatureFlags());
  const [configState, setConfigState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [pendingRemoveHost, setPendingRemoveHost] = useState<Host | null>(null);
  const [hostActionId, setHostActionId] = useState<string | null>(null);

  const isOwner = authState.roles.includes("platform_admin");

  async function setHostAccess(host: Host, action: "suspend" | "reactivate") {
    setHostActionId(host.id);
    setActionState({});
    try {
      const response = await apiFetch(`/v1/admin/hosts/${host.id}/${action}`, {
        method: "POST",
        headers: { authorization: `Bearer ${authState.idToken}` }
      });
      const body = (await response.json().catch(() => ({}))) as { host?: Host; error?: { code?: string; message?: string } };
      if (!response.ok || !body.host) {
        throw new Error(body.error?.message ?? body.error?.code ?? `host_${action}_${response.status}`);
      }
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setActionState({
        message:
          action === "suspend"
            ? `${host.displayName} can no longer sign in, and their booking pages are offline.`
            : `${host.displayName} can sign in again.`
      });
    } catch (cause) {
      setActionState({ error: cause instanceof Error ? cause.message : `Could not update ${host.displayName}.` });
    } finally {
      setHostActionId(null);
    }
  }

  async function removeHost(host: Host) {
    setHostActionId(host.id);
    setActionState({});
    try {
      const response = await apiFetch(`/v1/admin/hosts/${host.id}`, {
        method: "DELETE",
        headers: { authorization: `Bearer ${authState.idToken}` }
      });
      const body = (await response.json().catch(() => ({}))) as {
        deleted?: boolean;
        personasDeleted?: number;
        error?: { code?: string; message?: string };
      };
      if (!response.ok || !body.deleted) {
        throw new Error(body.error?.message ?? body.error?.code ?? `host_delete_${response.status}`);
      }
      setPendingRemoveHost(null);
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setActionState({ message: `Removed ${host.displayName} and ${body.personasDeleted ?? 0} persona(s). Their sign-in account is gone.` });
    } catch (cause) {
      setActionState({ error: cause instanceof Error ? cause.message : `Could not remove ${host.displayName}.` });
    } finally {
      setHostActionId(null);
    }
  }

  useEffect(() => {
    if (adminData?.featureFlags) {
      setDraftFlags(adminData.featureFlags);
    }
  }, [adminData?.featureFlags]);

  function loadInvitations(signal?: globalThis.AbortSignal) {
    setLoadState("loading");
    apiFetch("/v1/admin/invitations", {
      ...(signal ? { signal } : {}),
      headers: { authorization: `Bearer ${authState.idToken}` }
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`invitations_${response.status}`);
        }
        return (await response.json()) as AdminInvitationsResponse;
      })
      .then((body) => {
        setInvitations(body.invitations);
        setLoadState("live");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setInvitations([]);
          setLoadState("error");
        }
      });
  }

  useEffect(() => {
    const controller = new globalThis.AbortController();
    loadInvitations(controller.signal);
    return () => controller.abort();
  }, [authState.idToken]);

  async function createInvitation(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    const form = new globalThis.FormData(event.currentTarget);
    await issueInvitation({
      email: String(form.get("email") ?? ""),
      role: "platform_admin",
      expiresAt: new Date(String(form.get("expiresAt") ?? "")).toISOString()
    });
    event.currentTarget.reset();
    setShowInviteForm(false);
  }

  async function revokeInvitation(invitation: Invitation) {
    setActionState({ invitationId: invitation.id });
    try {
      const response = await apiFetch(`/v1/admin/invitations/${invitation.id}/revoke`, {
        method: "POST",
        headers: { authorization: `Bearer ${authState.idToken}` }
      });
      const body = (await response.json().catch(() => ({}))) as { invitation?: Invitation; error?: { code?: string; message?: string } };
      if (!response.ok || !body.invitation) {
        throw new Error(body.error?.message ?? body.error?.code ?? `invite_revoke_${response.status}`);
      }
      setInvitations((current) => current.map((item) => (item.id === body.invitation!.id ? body.invitation! : item)));
      setActionState({ message: `Revoked the invitation for ${invitation.email}.` });
    } catch (cause) {
      setActionState({ error: cause instanceof Error ? cause.message : "Could not revoke the invitation." });
    }
  }

  async function resendInvitation(invitation: Invitation) {
    await issueInvitation({
      email: invitation.email,
      role: invitation.role,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60_000).toISOString(),
      // A host invitation carries the name, hours and meeting types chosen on the
      // create-host screen. Reissuing without them would quietly downgrade the
      // invitation to a blank one.
      ...(invitation.defaults ? { defaults: invitation.defaults } : {})
    }, invitation.id);
  }

  async function issueInvitation(
    body: { email: string; role: Role; expiresAt: string; defaults?: Invitation["defaults"] },
    invitationId?: string
  ) {
    setActionState(invitationId ? { invitationId } : {});
    try {
      const response = await apiFetch("/v1/admin/invitations", {
        method: "POST",
        headers: {
          authorization: `Bearer ${authState.idToken}`,
          "content-type": "application/json"
        },
        body: JSON.stringify(body)
      });
      const responseBody = (await response.json().catch(() => ({}))) as AdminInvitationCreateResponse & { error?: { code?: string; details?: string[]; message?: string } };
      if (!response.ok) {
        throw new Error(responseBody.error?.details?.join(", ") ?? responseBody.error?.message ?? responseBody.error?.code ?? `invite_${response.status}`);
      }
      setInvitations((current) => [responseBody.invitation, ...current.filter((item) => item.id !== responseBody.invitation.id)]);
      setActionState({ message: absoluteInviteUrl(responseBody.inviteUrl) });
    } catch (cause) {
      setActionState({ error: cause instanceof Error ? cause.message : "Invitation could not be created." });
    }
  }

  async function saveHostRegistration(nextValue?: boolean) {
    const nextFlags = {
      ...draftFlags,
      ...(nextValue === undefined ? {} : { ALLOW_PUBLIC_HOST_SIGNUP: nextValue })
    };
    setDraftFlags(nextFlags);
    setConfigState("saving");
    try {
      const response = await apiFetch("/v1/admin/config", {
        method: "PUT",
        headers: {
          authorization: `Bearer ${authState.idToken}`,
          "content-type": "application/json"
        },
        body: JSON.stringify({ featureFlags: nextFlags })
      });
      if (!response.ok) {
        throw new Error(`config_${response.status}`);
      }
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setConfigState("saved");
    } catch {
      setConfigState("error");
    }
  }

  const filteredInvitations = invitations.filter((invitation) => {
    const matchesQuery = `${inviteDisplayName(invitation.email)} ${invitation.email}`.toLowerCase().includes(query.trim().toLowerCase());
    const matchesStatus = statusFilter === "all" || invitation.status === statusFilter;
    return matchesQuery && matchesStatus;
  });
  const publicSignupEnabled = draftFlags.ALLOW_PUBLIC_HOST_SIGNUP;

  return (
    <div className="onboarding-admin-screen users-invitations-screen">
      <section className="onboarding-admin-title">
        <div>
          <p className="eyebrow">Administration</p>
          <h2>Users &amp; invitations</h2>
          <p>Control who can create and manage scheduling workspaces.</p>
        </div>
        <div className="users-title-actions">
          <button type="button" className="editor-outline-button" onClick={() => onOpenView("host-create")}>
            <Plus size={17} /> New host
          </button>
          <button type="button" className="confirm" onClick={() => setShowInviteForm((current) => !current)}>
            <ShieldCheck size={17} /> Invite admin
          </button>
        </div>
      </section>

      <section className="user-stat-grid" aria-label="User summary">
        <div className="user-stat-card"><span><Users size={20} /></span><small>Active users</small><strong>{adminData?.hosts.filter((host) => host.status !== "suspended").length ?? 0}</strong><em>Across your workspace</em></div>
        <div className="user-stat-card"><span><Mail size={20} /></span><small>Pending invitations</small><strong>{invitations.filter((invitation) => invitation.status === "pending").length}</strong><em>Awaiting acceptance</em></div>
        <div className="user-stat-card"><span><ShieldCheck size={20} /></span><small>Administrators</small><strong>{adminData?.hosts.filter((host) => host.roles.includes("platform_admin") && host.status !== "suspended").length ?? 0}</strong><em>Admin access</em></div>
      </section>

      <section className="onboarding-access-banner">
        <span><ShieldCheck size={23} /></span>
        <div>
          <strong>Public registration is {publicSignupEnabled ? "on" : "off"}</strong>
          <p>{publicSignupEnabled ? "People can create a host account without an invitation." : "Only people you invite can create a host account. Guests can still book without signing in."}</p>
        </div>
        <button type="button" className={`policy-switch ${publicSignupEnabled ? "on" : ""}`} aria-label="Toggle public registration" aria-pressed={publicSignupEnabled} disabled={configState === "saving"} onClick={() => void saveHostRegistration(!publicSignupEnabled)}><i /></button>
      </section>

      {showInviteForm ? (
        <section className="onboarding-invite-form">
          <p className="invite-admin-note">
            An administrator can add and remove people, see every booking, and change workspace settings. To give someone their
            own booking page instead, use <strong>New host</strong>.
          </p>
          <form onSubmit={(event) => void createInvitation(event)}>
            <label>Email<input name="email" type="email" required placeholder="admin@example.com" /></label>
            <label>Expires<input name="expiresAt" type="datetime-local" required defaultValue={defaultInvitationExpirationLocal()} /></label>
            <button type="submit"><ShieldCheck size={17} /> Send admin invite</button>
          </form>
        </section>
      ) : null}

      {actionState.message ? <p className="inline-note" aria-live="polite">{actionState.message}</p> : null}
      {actionState.error ? <p className="form-error" aria-live="polite">{actionState.error}</p> : null}

      {pendingRemoveHost ? (
        <section className="persona-confirm" role="alertdialog" aria-labelledby="host-remove-title">
          <div>
            <h3 id="host-remove-title">Remove {pendingRemoveHost.displayName}?</h3>
            <p>
              This deletes their sign-in account, their{" "}
              {adminData?.personas.filter((persona) => persona.hostId === pendingRemoveHost.id).length ?? 0} persona(s) and any calendar
              connections. Booking links under <code>/{pendingRemoveHost.slug}</code> stop working. Past bookings are kept for the record.
            </p>
            <p className="persona-confirm-hint">
              Only stepping away for a while? Choose <strong>Suspend access</strong> instead — it blocks sign-in and takes their pages offline
              without deleting anything.
            </p>
          </div>
          <div className="persona-confirm-actions">
            <button type="button" onClick={() => setPendingRemoveHost(null)}>Keep person</button>
            <button
              type="button"
              className="danger"
              disabled={hostActionId === pendingRemoveHost.id}
              onClick={() => void removeHost(pendingRemoveHost)}
            >
              {hostActionId === pendingRemoveHost.id ? "Removing" : "Remove permanently"}
            </button>
          </div>
        </section>
      ) : null}

      <section className="onboarding-invite-list users-access-list">
        <div className="users-list-heading">
          <div><h3>People with access</h3><p>Manage roles, invitations, and account access.</p></div>
        </div>
        <div className="onboarding-invite-toolbar">
          <label>
            <Search size={17} />
            <input value={query} onChange={(event) => setQuery(event.currentTarget.value)} placeholder="Search invitations" />
          </label>
          <label>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.currentTarget.value)}>
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="claimed">Accepted</option>
              <option value="revoked">Revoked</option>
              <option value="expired">Expired</option>
            </select>
            <ChevronDown size={14} />
          </label>
        </div>
        <div className="users-table-header"><span>Person</span><span>Role</span><span>Status</span><span>Last active</span><span /></div>
        <div className="onboarding-invite-rows">
          {adminData?.hosts.filter((host) => `${host.displayName} ${host.slug}`.toLowerCase().includes(query.trim().toLowerCase())).map((host) => {
            // A suspended person stays listed: hiding them would leave no way to restore access.
            const suspended = host.status === "suspended";
            const isSelf = authState.hostId === host.id;
            const busy = hostActionId === host.id;
            return (
            <article key={host.id} className="user-access-row">
              <div className="onboarding-invite-guest"><i>{hostInitials(host.displayName)}</i><span><strong>{host.displayName}</strong><small>{host.slug}</small></span></div>
              <span className="user-role">{host.roles.includes("platform_admin") ? "Admin" : "Host"}</span><span className={`onboarding-invite-status ${suspended ? "revoked" : "claimed"}`}>{suspended ? "Suspended" : "Active"}</span><time>{isSelf ? "You" : suspended ? "No access" : "Active"}</time><ActionMenu
                label={`More actions for ${host.displayName}`}
                items={[
                  { label: "Open booking page", href: `${appOrigin}/${host.slug}` },
                  {
                    label: "Copy booking link",
                    onSelect: () => void globalThis.navigator.clipboard.writeText(`${appOrigin}/${host.slug}`)
                  },
                  {
                    label: "Copy host ID",
                    onSelect: () => void globalThis.navigator.clipboard.writeText(host.id)
                  },
                  {
                    label: "Copy workspace slug",
                    onSelect: () => void globalThis.navigator.clipboard.writeText(host.slug)
                  },
                  {
                    label: suspended ? "Restore access" : "Suspend access",
                    disabled: !isOwner || isSelf || busy,
                    onSelect: () => void setHostAccess(host, suspended ? "reactivate" : "suspend")
                  },
                  {
                    label: "Remove person\u2026",
                    danger: true,
                    disabled: !isOwner || isSelf || busy,
                    onSelect: () => setPendingRemoveHost(host)
                  }
                ]}
              />
            </article>
            );
          })}
          {loadState === "loading" ? (
            <SkeletonRegion label="Loading invitations">
              <SkeletonRows count={3}>
                {(index) => (
                  <article className="skeleton-invite-row" key={index}>
                    <div className="skeleton-guest">
                      <Skeleton className="skeleton-circle" width="34px" height="34px" />
                      <div className="skeleton-stack">
                        <Skeleton width={`${54 + index * 10}%`} />
                        <Skeleton className="skeleton-line-sm" width="78%" />
                      </div>
                    </div>
                    <Skeleton height="24px" radius="999px" width="76px" />
                    <Skeleton className="skeleton-line-sm" width="64px" />
                    <Skeleton width="88px" height="34px" radius="10px" />
                    <Skeleton width="20px" height="20px" radius="6px" />
                  </article>
                )}
              </SkeletonRows>
            </SkeletonRegion>
          ) : null}
          {loadState === "error" ? <p className="empty-state">Invitation records are unavailable.</p> : null}
          {loadState === "live" && filteredInvitations.length === 0 ? <p className="empty-state">No invitations match this view.</p> : null}
          {filteredInvitations.map((invitation) => (
            <article key={invitation.id}>
              <div className="onboarding-invite-guest">
                <i>{hostInitials(invitation.defaults?.displayName || inviteDisplayName(invitation.email))}</i>
                <span>
                  <strong>{invitation.defaults?.displayName || inviteDisplayName(invitation.email)}</strong>
                  <small>{invitation.email}</small>
                </span>
              </div>
              <span className="user-role">{invitation.role === "platform_admin" ? "Admin" : "Host"}</span>
              <span className={`onboarding-invite-status ${invitation.status}`}>{inviteStatusLabel(invitation)}</span>
              {/* Date and resend share the last column so the row keeps the same five
                  cells as the people above it, under the same headings. */}
              <span className="invite-when">
                <time dateTime={invitation.createdAt}>{formatShortDate(invitation.createdAt)}</time>
                <button type="button" disabled={actionState.invitationId === invitation.id} onClick={() => void resendInvitation(invitation)}>
                  <RefreshCw size={14} /> Resend
                </button>
              </span>
              <ActionMenu
                label={`More actions for ${invitation.email}`}
                items={[
                  {
                    label: "Copy invite email",
                    onSelect: () => void globalThis.navigator.clipboard.writeText(invitation.email)
                  },
                  {
                    label: "Resend invitation",
                    disabled: invitation.status !== "pending" || actionState.invitationId === invitation.id,
                    onSelect: () => void resendInvitation(invitation)
                  },
                  {
                    label: "Revoke invitation",
                    danger: true,
                    disabled: invitation.status !== "pending" || actionState.invitationId === invitation.id,
                    onSelect: () => void revokeInvitation(invitation)
                  }
                ]}
              />
            </article>
          ))}
        </div>
      </section>

      <section className="onboarding-policy">
        <div>
          <strong>Host registration</strong>
          <p>Control whether people can create an account without an invitation.</p>
        </div>
        <span>
          <em>{publicSignupEnabled ? "On" : "Off"}</em>
          <button type="button" disabled={configState === "saving"} onClick={() => void saveHostRegistration(!publicSignupEnabled)}>
            {configState === "saving" ? "Saving" : "Manage policy"}
          </button>
        </span>
      </section>
    </div>
  );
}

/**
 * Working-hour presets an administrator can pick for someone who has no account yet.
 * Minutes from midnight, on ISO weekdays, so they survive the trip through the
 * invitation and land as real availability when onboarding reads them back.
 */
const HOST_HOUR_PRESETS: Array<{ id: string; label: string; days: number[]; startMinute: number; endMinute: number }> = [
  { id: "weekdays-9-5", label: "Monday - Friday, 9:00 AM - 5:00 PM", days: [1, 2, 3, 4, 5], startMinute: 9 * 60, endMinute: 17 * 60 },
  { id: "weekdays-8-4", label: "Monday - Friday, 8:00 AM - 4:00 PM", days: [1, 2, 3, 4, 5], startMinute: 8 * 60, endMinute: 16 * 60 },
  { id: "weekdays-10-6", label: "Monday - Friday, 10:00 AM - 6:00 PM", days: [1, 2, 3, 4, 5], startMinute: 10 * 60, endMinute: 18 * 60 },
  { id: "everyday-9-5", label: "Every day, 9:00 AM - 5:00 PM", days: [0, 1, 2, 3, 4, 5, 6], startMinute: 9 * 60, endMinute: 17 * 60 }
];

const HOST_MEETING_TEMPLATES: Array<{ minutes: number; title: string; detail: string }> = [
  { minutes: 30, title: "30 minute meeting", detail: "Quick check-ins and introductions" },
  { minutes: 60, title: "60 minute meeting", detail: "Standard meetings" }
];

const WEEKDAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"];

/**
 * Creating a host is really issuing an invitation: the account does not exist until the
 * person on the other end claims the link and sets their own password. Everything the
 * administrator decides here rides along on that invitation, so the new host's first
 * screen is already filled in rather than blank.
 */
function CreateHostView({
  authState,
  onExit,
  onCreated
}: {
  authState: Extract<AuthState, { status: "signed_in" }>;
  onExit: () => void;
  onCreated: () => void;
}) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [timeZone, setTimeZone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || fredrick.defaultTimeZone);
  const [presetId, setPresetId] = useState(HOST_HOUR_PRESETS[0]!.id);
  const [customHours, setCustomHours] = useState(false);
  const [customDays, setCustomDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [customStart, setCustomStart] = useState(9 * 60);
  const [customEnd, setCustomEnd] = useState(17 * 60);
  const [durations, setDurations] = useState<number[]>([30, 60]);
  const [allowCustomDuration, setAllowCustomDuration] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [status, setStatus] = useState<"idle" | "submitting" | "created">("idle");
  const [message, setMessage] = useState("");
  const [inviteUrl, setInviteUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const preset = HOST_HOUR_PRESETS.find((item) => item.id === presetId) ?? HOST_HOUR_PRESETS[0]!;
  const workingHours = customHours
    ? { days: [...customDays].sort(), startMinute: customStart, endMinute: customEnd }
    : { days: preset.days, startMinute: preset.startMinute, endMinute: preset.endMinute };
  const hoursAreValid = workingHours.days.length > 0 && workingHours.startMinute < workingHours.endMinute;

  async function createHost(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    if (!hoursAreValid) {
      setMessage("Choose at least one working day, and an end time after the start time.");
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch("/v1/admin/invitations", {
        method: "POST",
        headers: {
          authorization: `Bearer ${authState.idToken}`,
          "content-type": "application/json"
        },
        body: JSON.stringify({
          email: email.trim(),
          role: "host",
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60_000).toISOString(),
          defaults: {
            displayName: fullName.trim(),
            defaultTimeZone: timeZone,
            workingHours,
            meetingDurations: durations,
            allowCustomDuration
          }
        })
      });
      const body = (await response.json().catch(() => ({}))) as AdminInvitationCreateResponse & {
        error?: { code?: string; details?: string[]; message?: string };
      };
      if (!response.ok) {
        throw new Error(body.error?.details?.join(", ") ?? body.error?.message ?? body.error?.code ?? `host_create_${response.status}`);
      }
      setInviteUrl(absoluteInviteUrl(body.inviteUrl));
      setStatus("created");
    } catch (cause) {
      setStatus("idle");
      setMessage(cause instanceof Error ? cause.message : "The host could not be created.");
    }
  }

  if (status === "created") {
    return (
      <div className="create-host-page">
        <button type="button" className="connect-back" onClick={onCreated}>
          <ArrowLeft size={16} /> Back to hosts
        </button>
        <section className="create-host-done">
          <span><Check size={26} /></span>
          <h2>{fullName.trim() || email.trim()} has been invited</h2>
          <p>
            They will get an email at <strong>{email.trim()}</strong> with setup instructions. Their workspace starts with the
            time zone, hours and meeting types you chose, so their first screen is already filled in.
          </p>
          <div className="share-copybox">
            <code>{inviteUrl}</code>
            <button
              type="button"
              onClick={() => {
                void globalThis.navigator.clipboard.writeText(inviteUrl);
                setCopied(true);
                globalThis.setTimeout(() => setCopied(false), 2000);
              }}
            >
              <Copy size={15} /> {copied ? "Copied" : "Copy link"}
            </button>
          </div>
          <p className="create-host-fine">Send this link yourself if the email does not arrive. It expires in seven days.</p>
          <div className="create-host-done-actions">
            <button
              type="button"
              className="editor-outline-button"
              onClick={() => {
                setFullName("");
                setEmail("");
                setInviteUrl("");
                setStatus("idle");
              }}
            >
              Create another host
            </button>
            <button type="button" className="confirm" onClick={onCreated}>Back to hosts</button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <form className="create-host-page" onSubmit={(event) => void createHost(event)}>
      <button type="button" className="connect-back" onClick={onExit}>
        <ArrowLeft size={16} /> Back to hosts
      </button>

      <header className="create-host-head">
        <div>
          <h2>Create new host</h2>
          <p>Add a new host to manage their availability and booking settings.</p>
        </div>
        <div className="create-host-help">
          <span>Need help?</span>
          <button type="button" onClick={() => setShowHelp((current) => !current)} aria-expanded={showHelp}>
            {showHelp ? "Hide setup guide" : "View setup guide"}
          </button>
        </div>
      </header>

      {showHelp ? (
        <section className="create-host-guide" aria-label="What happens after you create a host">
          <h3>What happens next</h3>
          <ol>
            <li>They receive an invitation email with a link that expires in seven days.</li>
            <li>The link asks them to choose a password and confirm the name and time zone you set here.</li>
            <li>Their onboarding opens with these working hours and meeting types already selected.</li>
            <li>They connect their own calendars. You never see their event details, only their busy time.</li>
          </ol>
        </section>
      ) : null}

      <section className="create-host-card">
        <div className="create-host-card-head">
          <i className="basic"><UserRound size={20} /></i>
          <div>
            <h3>Basic information</h3>
            <p>Enter the host's details and connect their calendar.</p>
          </div>
        </div>
        <div className="create-host-basic">
          <div className="create-host-fields">
            <label>
              <span>Full name <em>*</em></span>
              <input
                name="fullName"
                required
                value={fullName}
                onChange={(event) => setFullName(event.currentTarget.value)}
                placeholder="Enter full name (e.g. Jane Smith)"
              />
            </label>
            <label>
              <span>Email address <em>*</em></span>
              <input
                name="email"
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.currentTarget.value)}
                placeholder="Enter email address"
              />
              <small>We'll send an invitation email with setup instructions.</small>
            </label>
            <label>
              <span>Time zone <em>*</em></span>
              <span className="create-host-select">
                <TimeZoneSelect name="defaultTimeZone" value={timeZone} ariaLabel="Time zone" onChange={setTimeZone} />
                <ChevronDown size={15} />
              </span>
              <small>This will be used for availability and meeting times.</small>
            </label>
          </div>
          <aside className="create-host-photo">
            <span aria-hidden="true"><UserRound size={44} /></span>
            <strong>Profile photo</strong>
            <p>Their photo can be added once they accept the invitation and their account exists.</p>
          </aside>
        </div>
      </section>

      <section className="create-host-card">
        <div className="create-host-card-head">
          <i className="hours"><CalendarDays size={20} /></i>
          <div>
            <h3>Availability settings</h3>
            <p>Set default availability for this host. They can always customize this later.</p>
          </div>
        </div>
        <label className="create-host-hours">
          Default working hours
          <span className="create-host-select">
            <select
              value={customHours ? "custom" : presetId}
              onChange={(event) => {
                const next = event.currentTarget.value;
                if (next === "custom") {
                  setCustomHours(true);
                  return;
                }
                setCustomHours(false);
                setPresetId(next);
              }}
            >
              {HOST_HOUR_PRESETS.map((item) => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
              <option value="custom">Custom hours</option>
            </select>
            <ChevronDown size={15} />
          </span>
        </label>
        <button type="button" className="create-host-linkish" onClick={() => setCustomHours((current) => !current)}>
          <Settings2 size={16} /> {customHours ? "Use a preset" : "Customize hours"}
        </button>

        {customHours ? (
          <div className="create-host-custom">
            <div className="create-host-days" role="group" aria-label="Working days">
              {WEEKDAY_INITIALS.map((initial, day) => (
                <button
                  key={day}
                  type="button"
                  className={customDays.includes(day) ? "active" : ""}
                  aria-pressed={customDays.includes(day)}
                  aria-label={fullDayName(day)}
                  onClick={() =>
                    setCustomDays((current) =>
                      current.includes(day) ? current.filter((item) => item !== day) : [...current, day]
                    )
                  }
                >
                  {initial}
                </button>
              ))}
            </div>
            <div className="create-host-times">
              {/* Whole hours: onboarding stores this window as start and end hours,
                  so a half hour chosen here could not survive the trip. */}
              <label>
                Starts
                <input type="time" step={3600} value={minuteToTimeInput(customStart)} onChange={(event) => setCustomStart(timeInputToMinute(event.currentTarget.value))} />
              </label>
              <label>
                Ends
                <input type="time" step={3600} value={minuteToTimeInput(customEnd)} onChange={(event) => setCustomEnd(timeInputToMinute(event.currentTarget.value))} />
              </label>
            </div>
          </div>
        ) : null}
      </section>

      <section className="create-host-card">
        <div className="create-host-card-head">
          <i className="meetings"><Clock size={20} /></i>
          <div>
            <h3>Meeting types</h3>
            <p>Choose which meeting types this host can offer.</p>
          </div>
        </div>
        <div className="create-host-options">
          {HOST_MEETING_TEMPLATES.map((template) => (
            <label key={template.minutes} className="create-host-option">
              <input
                type="checkbox"
                checked={durations.includes(template.minutes)}
                onChange={(event) =>
                  setDurations((current) =>
                    event.currentTarget.checked
                      ? [...current, template.minutes].sort((left, right) => left - right)
                      : current.filter((item) => item !== template.minutes)
                  )
                }
              />
              <span>
                <strong>{template.title}</strong>
                <small>{template.detail}</small>
              </span>
            </label>
          ))}
          <label className="create-host-option">
            <input type="checkbox" checked={allowCustomDuration} onChange={(event) => setAllowCustomDuration(event.currentTarget.checked)} />
            <span>
              <strong>Custom duration</strong>
              <small>Allow this host to set custom meeting lengths</small>
            </span>
          </label>
        </div>
      </section>

      {message ? <p className="form-error" aria-live="polite">{message}</p> : null}

      <footer className="create-host-footer">
        <button type="button" className="editor-outline-button" onClick={onExit}>Cancel</button>
        <button type="submit" className="confirm" disabled={status === "submitting"}>
          {status === "submitting" ? "Creating" : "Create host"}
        </button>
      </footer>
    </form>
  );
}

function OperationsView({
  authState,
  adminData,
  onAdminDataChange
}: {
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  onAdminDataChange: (data: AdminStatusResponse | null) => void;
}) {
  const [operationsState, setOperationsState] = useState<LoadState>("loading");
  const [operationsData, setOperationsData] = useState<AdminOperationsResponse | null>(null);
  const [configState, setConfigState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [draftFlags, setDraftFlags] = useState<AdminFeatureFlags>(() => adminData?.featureFlags ?? defaultAdminFeatureFlags());
  const isPlatformAdmin = authState.status === "signed_in" && authState.roles.includes("platform_admin");

  useEffect(() => {
    if (adminData?.featureFlags) {
      setDraftFlags(adminData.featureFlags);
    }
  }, [adminData?.featureFlags]);

  function loadOperations(signal?: globalThis.AbortSignal) {
    if (authState.status !== "signed_in" || !authState.roles.includes("platform_admin")) {
      setOperationsState("error");
      setOperationsData(null);
      return;
    }
    setOperationsState("loading");
    apiFetch("/v1/admin/operations", {
      ...(signal ? { signal } : {}),
      headers: { authorization: `Bearer ${authState.idToken}` }
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`operations_${response.status}`);
        }
        return (await response.json()) as AdminOperationsResponse;
      })
      .then((body) => {
        setOperationsData(body);
        setOperationsState("live");
      })
      .catch((cause) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setOperationsData(null);
          setOperationsState("error");
        }
      });
  }

  useEffect(() => {
    const controller = new globalThis.AbortController();
    loadOperations(controller.signal);
    return () => controller.abort();
  }, [authState]);

  async function saveConfig(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    if (authState.status !== "signed_in") {
      setConfigState("error");
      return;
    }
    setConfigState("saving");
    try {
      const response = await apiFetch("/v1/admin/config", {
        method: "PUT",
        headers: {
          authorization: `Bearer ${authState.idToken}`,
          "content-type": "application/json"
        },
        body: JSON.stringify({
          featureFlags: draftFlags
        })
      });
      if (!response.ok) {
        throw new Error(`config_${response.status}`);
      }
      await refreshAdminData(authState.idToken, onAdminDataChange);
      loadOperations();
      setConfigState("saved");
    } catch {
      setConfigState("error");
    }
  }

  const metrics = operationsData?.metrics;
  const providerRows = operationsData?.providerHealth ?? [];
  const volumeRows = operationsData?.bookingVolume ?? [];
  const attentionItems = operationsData?.attentionItems ?? [];

  return (
    <div className="operations-screen">
      <section className="operations-title">
        <div>
          <p className="eyebrow">Admin console</p>
          <h2>Operations</h2>
          <p>A privacy-safe view of platform health, integrations, and usage.</p>
        </div>
        <button type="button" onClick={() => loadOperations()} disabled={!isPlatformAdmin || operationsState === "loading"}>
          <RefreshCw size={16} /> Refresh
        </button>
      </section>

      <section className="operations-metrics">
        <OverviewMetric icon={<Users size={21} />} tone="blue" label="Active hosts" loading={operationsState === "loading" && !operationsData} value={metrics?.activeHosts ?? 0} detail={`${metrics?.pendingInvites ?? 0} pending ${(metrics?.pendingInvites ?? 0) === 1 ? "invite" : "invites"}`} />
        <OverviewMetric icon={<CalendarCheck size={21} />} tone="violet" label="Bookings · 30 days" loading={operationsState === "loading" && !operationsData} value={metrics?.bookingsLast30Days ?? 0} detail={metrics?.bookingChangeLabel ?? dataStateLabel(operationsState, "Loading")} />
        <OverviewMetric icon={<Activity size={21} />} tone="green" label="Successful syncs" loading={operationsState === "loading" && !operationsData} value={`${metrics?.successfulSyncPercent ?? 0}%`} detail={`Across ${metrics?.connectionCount ?? 0} ${(metrics?.connectionCount ?? 0) === 1 ? "connection" : "connections"}`} />
      </section>

      <div className="operations-grid">
        <section className="operations-card operations-provider">
          <div>
            <h2>Provider health</h2>
            <p>{operationsData ? `Last checked ${relativeTimeLabel(operationsData.checkedAt)}` : dataStateLabel(operationsState, "Last checked just now")}</p>
          </div>
          <div className="operations-provider-list">
            {operationsState === "loading" && !operationsData ? (
              <SkeletonRegion label="Loading provider health">
                <SkeletonRows count={3}>
                  {(index) => (
                    <article className="skeleton-provider-row" key={index}>
                      <Skeleton className="skeleton-circle" width="10px" height="10px" />
                      <Skeleton width={`${44 + index * 12}%`} />
                      <Skeleton height="22px" radius="999px" width="72px" />
                      <Skeleton className="skeleton-line-sm" width="46px" />
                    </article>
                  )}
                </SkeletonRows>
              </SkeletonRegion>
            ) : providerRows.length > 0 ? providerRows.map((row) => (
              <article key={row.key}>
                <i className={row.tone === "orange" ? "warn" : row.tone === "green" ? "ok" : "neutral"} />
                <strong>{row.label}</strong>
                <span className={`operations-badge ${row.tone}`}>{row.status}</span>
                <small>{row.successRate}</small>
              </article>
            )) : <p className="empty-state">Provider health is unavailable.</p>}
          </div>
        </section>

        <section className="operations-card operations-volume">
          <div>
            <h2>Booking volume</h2>
            <p>Confirmed meetings across all hosts</p>
          </div>
          <div className="operations-bars" aria-label="Booking volume by day">
            {operationsState === "loading" && !operationsData
              ? [38, 62, 47, 80, 55, 71, 44].map((height, index) => (
                  <div key={index} className="skeleton-bar">
                    <Skeleton height={`${height}%`} radius="6px" />
                    <Skeleton className="skeleton-line-sm" width="70%" />
                  </div>
                ))
              : null}
            {volumeRows.map((row) => (
              <div key={row.date}>
                <i style={{ height: `${row.heightPercent}%` }} title={`${row.count} bookings`} />
                <span>{row.label}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="operations-card operations-attention">
        <h2>Needs attention</h2>
        <div className="operations-attention-list">
          {operationsState === "loading" && !operationsData ? (
            <SkeletonRegion label="Loading items that need attention">
              <SkeletonRows count={2}>
                {(index) => (
                  <div className="skeleton-stack skeleton-attention-row" key={index}>
                    <Skeleton width={`${48 + index * 14}%`} />
                    <Skeleton className="skeleton-line-sm" width="82%" />
                  </div>
                )}
              </SkeletonRows>
            </SkeletonRegion>
          ) : attentionItems.length > 0 ? attentionItems.map((item) => (
            <article key={item.id}>
              <span><Activity size={19} /></span>
              <div>
                <strong>{item.title}</strong>
                <small>{item.detail}</small>
              </div>
              <em>{item.severity}</em>
              <button type="button" onClick={() => onAdminDataChange(adminData)}>
                {item.action}
              </button>
            </article>
          )) : <p className="empty-state">No operational issues need attention.</p>}
        </div>
      </section>

      <section className="operations-card operations-flags">
        <div className="operations-card-title">
          <div>
            <h2>Feature flags</h2>
            <p>Control signup access and calendar provider availability.</p>
          </div>
          <span>{isPlatformAdmin ? configStateLabel(configState) : "Platform admin access required."}</span>
        </div>
        <form className="config-form" onSubmit={(event) => void saveConfig(event)}>
          <FeatureToggle name="ALLOW_PUBLIC_HOST_SIGNUP" label="Public host signup" checked={draftFlags.ALLOW_PUBLIC_HOST_SIGNUP} disabled={!isPlatformAdmin || configState === "saving"} onChange={setDraftFlags} />
          <FeatureToggle name="ALLOW_ADMIN_INVITES" label="Admin invitations" checked={draftFlags.ALLOW_ADMIN_INVITES} disabled={!isPlatformAdmin || configState === "saving"} onChange={setDraftFlags} />
          <FeatureToggle name="ENABLE_GOOGLE_CALENDAR" label="Google Calendar" checked={draftFlags.ENABLE_GOOGLE_CALENDAR} disabled={!isPlatformAdmin || configState === "saving"} onChange={setDraftFlags} />
          <FeatureToggle name="ENABLE_MICROSOFT_CALENDAR" label="Microsoft 365" checked={draftFlags.ENABLE_MICROSOFT_CALENDAR} disabled={!isPlatformAdmin || configState === "saving"} onChange={setDraftFlags} />
          <FeatureToggle name="ENABLE_ZOHO_CALENDAR" label="Zoho Calendar" checked={draftFlags.ENABLE_ZOHO_CALENDAR} disabled={!isPlatformAdmin || configState === "saving"} onChange={setDraftFlags} />
          <FeatureToggle name="ENABLE_CALDAV_CALENDAR" label="iCloud Calendar" checked={draftFlags.ENABLE_CALDAV_CALENDAR} disabled={!isPlatformAdmin || configState === "saving"} onChange={setDraftFlags} />
          <button className="confirm" disabled={!isPlatformAdmin || configState === "saving"}>
            <ShieldCheck size={18} /> {configState === "saving" ? "Saving" : "Save controls"}
          </button>
        </form>
      </section>
    </div>
  );
}

function FeatureToggle({
  name,
  label,
  checked,
  disabled,
  onChange
}: {
  name: keyof AdminFeatureFlags;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (updater: (current: AdminFeatureFlags) => AdminFeatureFlags) => void;
}) {
  return (
    <label className="config-toggle">
      <input
        name={name}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange((current) => ({ ...current, [name]: event.currentTarget.checked }))}
      />
      <span>{label}</span>
    </label>
  );
}

function CalendarPicker({
  selectedDate,
  availableDates,
  onSelect
}: {
  selectedDate: string;
  availableDates: Set<string>;
  onSelect: (date: string) => void;
}) {
  const [visibleMonth, setVisibleMonth] = useState(() => selectedDate.slice(0, 7));

  useEffect(() => {
    setVisibleMonth(selectedDate.slice(0, 7));
  }, [selectedDate]);

  const monthStart = new Date(`${visibleMonth}-01T00:00:00.000Z`);
  const gridStart = new Date(monthStart);
  gridStart.setUTCDate(monthStart.getUTCDate() - monthStart.getUTCDay());
  const days = Array.from({ length: 42 }, (_, index) => {
    const day = new Date(gridStart);
    day.setUTCDate(gridStart.getUTCDate() + index);
    return day;
  });
  // The grid is built from UTC dates, so the label has to be read in UTC too:
  // formatting a UTC midnight in a western zone names the previous month.
  const monthLabel = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric", timeZone: "UTC" }).format(monthStart);

  return (
    <section className="calendar-picker" aria-label="Choose a date">
      <div className="calendar-toolbar">
        <button type="button" aria-label="Previous month" onClick={() => setVisibleMonth(shiftMonth(visibleMonth, -1))}>
          <ChevronLeft size={18} />
        </button>
        <div>
          <p className="eyebrow">Choose date</p>
          <h3>{monthLabel}</h3>
        </div>
        <button type="button" aria-label="Next month" onClick={() => setVisibleMonth(shiftMonth(visibleMonth, 1))}>
          <ChevronRight size={18} />
        </button>
      </div>
      <div className="weekdays" aria-hidden="true">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>
      <div className="calendar-grid">
        {days.map((day) => {
          const key = day.toISOString().slice(0, 10);
          const available = availableDates.has(key);
          const inVisibleMonth = key.startsWith(visibleMonth);
          return (
            <button
              key={key}
              type="button"
              disabled={!available || !inVisibleMonth}
              className={`${selectedDate === key ? "calendar-day selected" : "calendar-day"}${inVisibleMonth ? "" : " outside-month"}`}
              onClick={() => onSelect(key)}
              aria-label={`${formatFullDate(key)}${available && inVisibleMonth ? ", times available" : ", no times available"}`}
            >
              <span>{day.getUTCDate()}</span>
              {available ? <i aria-hidden="true" /> : null}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function TimeSlotList({
  date,
  slots,
  selectedSlot,
  label,
  emptyMessage,
  loading,
  onSelect
}: {
  date: string;
  slots: TimeInterval[];
  selectedSlot: TimeInterval | null;
  label: string;
  emptyMessage: string;
  loading?: boolean;
  onSelect: (slot: TimeInterval) => void;
}) {
  if (loading) {
    return (
      <SkeletonRegion label={`Loading times for ${formatFullDate(date)}`} className="slot-grid compact">
        <SkeletonRows count={6}>
          {(index) => <Skeleton key={index} className="skeleton-time-slot" height="58px" radius="12px" />}
        </SkeletonRows>
      </SkeletonRegion>
    );
  }
  return (
    <div className="slot-grid compact" aria-label={`Available appointment times for ${formatFullDate(date)}`}>
      {slots.length > 0 ? (
        slots.map((slot) => (
          <button key={slot.start} type="button" className={selectedSlot?.start === slot.start ? "slot selected" : "slot"} onClick={() => onSelect(slot)}>
            <span>{label}</span>
            <strong>{formatTime(slot.start)}</strong>
          </button>
        ))
      ) : (
        <p className="empty-state">{emptyMessage}</p>
      )}
    </div>
  );
}




function viewTitle(view: View) {
  switch (view) {
    case "overview":
      return "Overview";
    case "profile":
      return "Profile";
    case "personas":
      return "Personas";
    case "persona-create":
      return "Create persona";
    case "persona-edit":
      return "Edit persona";
    case "calendars":
      return "Calendars";
    case "calendar-manage":
      return "Manage calendar";
    case "bookings":
      return "Bookings";
    case "booking-detail":
      return "Booking details";
    case "book":
      return "Book a meeting";
    case "availability":
      return "Availability";
    case "links":
      return "Share links";
    case "extension":
      return "Chrome extension";
    case "onboarding":
      return "Onboarding";
    case "host-create":
      return "Create new host";
    case "users":
      return "Users & invitations";
    case "operations":
      return "Operations";
  }
}



type ConnectStep = "provider" | "authorize" | "configure";
type ConnectProviderId = "google" | "microsoft" | "zoho" | "caldav";

interface ProviderCalendarSummaryView {
  id: string;
  name: string;
  isPrimary: boolean;
  canWrite: boolean;
  timeZone?: string;
  color?: string;
}

interface CalendarDirectoryResponse {
  provider?: ConnectProviderId;
  accountEmail?: string;
  calendars: ProviderCalendarSummaryView[];
  selected?: Array<{ providerCalendarId: string; blocksAvailability: boolean; destinationPriority: number }>;
}

/** Matches the API's own cap on the text added to every event's notes. */
const EVENT_NOTE_MAX_LENGTH = 500;

const CONNECT_PROVIDERS: Array<{
  id: ConnectProviderId;
  name: string;
  blurb: string;
  initial: string;
  tone: string;
  usesOAuth: boolean;
  canReceiveBookings: boolean;
  permissions: string[];
}> = [
  {
    id: "google",
    name: "Google Calendar",
    blurb: "Personal Gmail and Google Workspace",
    initial: "G",
    tone: "google",
    usesOAuth: true,
    canReceiveBookings: true,
    permissions: ["View free and busy times", "Add and update meetings you schedule", "Generate Google Meet links"]
  },
  {
    id: "microsoft",
    name: "Microsoft 365",
    blurb: "Outlook and Microsoft work accounts",
    initial: "M",
    tone: "microsoft",
    usesOAuth: true,
    canReceiveBookings: true,
    permissions: ["View free and busy times", "Add and update meetings you schedule", "Generate Teams links when your licence allows"]
  },
  {
    id: "zoho",
    name: "Zoho Calendar",
    blurb: "Zoho Mail and Zoho Workplace",
    initial: "Z",
    tone: "zoho",
    usesOAuth: true,
    canReceiveBookings: true,
    permissions: ["View free and busy times", "Add and update meetings you schedule"]
  },
  {
    id: "caldav",
    name: "iCloud Calendar",
    blurb: "Apple ID with an app-specific password",
    initial: "",
    tone: "icloud",
    usesOAuth: false,
    canReceiveBookings: true,
    permissions: ["View free and busy times", "Add and update meetings you schedule"]
  }
];

const CONNECT_DRAFT_PREFIX = "timebridge.connect-draft.";

/** Keep an unsaved calendar selection across a re-sign-in. */
function readConnectDraft(connectionId: string): { blocking: string[]; destinationId: string } | null {
  try {
    const raw = globalThis.sessionStorage.getItem(`${CONNECT_DRAFT_PREFIX}${connectionId}`);
    return raw ? (JSON.parse(raw) as { blocking: string[]; destinationId: string }) : null;
  } catch {
    return null;
  }
}

function writeConnectDraft(connectionId: string, draft: { blocking: string[]; destinationId: string }): void {
  try {
    globalThis.sessionStorage.setItem(`${CONNECT_DRAFT_PREFIX}${connectionId}`, JSON.stringify(draft));
  } catch {
    // Storage is a convenience here; losing it must never break the wizard.
  }
}

function clearConnectDraft(connectionId: string): void {
  try {
    globalThis.sessionStorage.removeItem(`${CONNECT_DRAFT_PREFIX}${connectionId}`);
  } catch {
    // ignored
  }
}

interface ConnectionActivityEvent {
  id: string;
  action: string;
  summary: string;
  createdAt: string;
}

/** Relative for the last day, then a plain date — "3 days ago" helps nobody find a record. */
function syncTimeLabel(value: string): string {
  const elapsed = Date.now() - Date.parse(value);
  return elapsed < 24 * 60 * 60_000 ? relativeTimeLabel(value) : formatBookingWhen(value);
}

/**
 * What the provider will actually put on the invitation. A meeting link is created
 * when a persona's location asks for one, so this describes the condition rather
 * than implying a per-connection switch that does not exist.
 */
/** The account you are sent to, rather than the calendar product's full name. */
function providerShortName(provider: Provider): string {
  switch (provider) {
    case "google":
      return "Google";
    case "microsoft":
      return "Microsoft";
    case "zoho":
      return "Zoho";
    case "caldav":
      return "iCloud";
    default:
      return calendarProviderLabel(provider);
  }
}

function connectionConferencing(provider: Provider): { label: string; detail: string } {
  switch (provider) {
    case "google":
      return { label: "Google Meet", detail: "Added when a persona's location is Google Meet." };
    case "microsoft":
      return { label: "Microsoft Teams", detail: "Added when a persona's location is Microsoft Teams." };
    case "zoho":
      return { label: "Zoho Meeting", detail: "Added when a persona's location is Zoho Meeting." };
    default:
      return { label: "Not available", detail: "This connection cannot create meeting links." };
  }
}

function activityIcon(action: string) {
  if (action.endsWith("sync_failed")) {
    return <X size={15} />;
  }
  if (action.endsWith("synced") || action.endsWith("reconnected")) {
    return <Check size={15} />;
  }
  if (action.endsWith("disconnected") || action.endsWith("deleted")) {
    return <Lock size={15} />;
  }
  return <RefreshCw size={15} />;
}

function activityTitle(action: string): string {
  switch (action) {
    case "calendar_connection.synced":
      return "Sync completed";
    case "calendar_connection.sync_failed":
      return "Sync failed";
    case "calendar_connection.calendars_updated":
      return "Calendar selection updated";
    case "calendar_connection.reconnected":
      return "Account reconnected";
    case "calendar_connection.disconnected":
      return "Calendar disconnected";
    case "calendar_connection.deleted":
      return "Connection deleted";
    default:
      return capitalizeLabel(action.split(".").pop()?.replace(/_/g, " ") ?? action);
  }
}

/**
 * The connection detail screen: what this account contributes, what it is doing, and
 * the four things a host can do to it. Reconnect and disconnect both live here so a
 * broken calendar can be repaired instead of only deleted.
 */
function ManageCalendarView({
  authState,
  adminData,
  connectionId,
  initialMode,
  openDisconnect,
  onAdminDataChange,
  onExit
}: {
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  connectionId: string;
  initialMode: CalendarManageMode;
  openDisconnect?: boolean;
  onAdminDataChange: (data: AdminStatusResponse | null) => void;
  onExit: () => void;
}) {
  const [mode, setMode] = useState<CalendarManageMode>(initialMode);
  const [status, setStatus] = useState<OnboardingStatus>("idle");
  const [message, setMessage] = useState("");
  const [showDisconnect, setShowDisconnect] = useState(Boolean(openDisconnect));
  const [acknowledged, setAcknowledged] = useState(false);
  const [activity, setActivity] = useState<ConnectionActivityEvent[]>([]);
  const [activityState, setActivityState] = useState<LoadState>("loading");

  const idToken = authState.status === "signed_in" ? authState.idToken : "";
  const host = signedInHost(authState, adminData);
  const connection = (adminData?.calendarConnections ?? []).find((item) => item.id === connectionId);
  const resources = useMemo(
    () => (adminData?.calendarResources ?? []).filter((item) => item.connectionId === connectionId),
    [adminData, connectionId]
  );
  const personas = (adminData?.personas ?? []).filter((item) => item.hostId === host?.id);

  const [blocking, setBlocking] = useState<Set<string>>(new Set());
  const [destinationId, setDestinationId] = useState("");
  const [eventNote, setEventNote] = useState(connection?.eventNote ?? "");
  const [directory, setDirectory] = useState<CalendarDirectoryResponse | null>(null);
  const [directoryState, setDirectoryState] = useState<LoadState>("loading");

  // The note can only be typed in edit mode, so the saved text is picked up on the
  // way in rather than fighting the field while it is being edited.
  useEffect(() => {
    if (mode === "edit") {
      setEventNote(connection?.eventNote ?? "");
    }
  }, [connection?.eventNote, mode]);

  // The provider is the authority on what calendars exist and what their ids are.
  // Building this screen from the admin payload instead let a redacted placeholder be
  // written back as a calendar id, collapsing every calendar onto one identity.
  useEffect(() => {
    if (!idToken || !connectionId || mode !== "edit") {
      return;
    }
    const controller = new globalThis.AbortController();
    setDirectoryState("loading");
    apiFetch(`/v1/admin/calendar-connections/${encodeURIComponent(connectionId)}/available-calendars`, {
      signal: controller.signal,
      headers: { authorization: `Bearer ${idToken}` }
    })
      .then(async (response) => {
        const body = (await response.json().catch(() => ({}))) as CalendarDirectoryResponse & {
          error?: { code?: string; message?: string };
        };
        if (!response.ok) {
          throw new Error(body.error?.message ?? body.error?.code ?? `calendars_${response.status}`);
        }
        setDirectory(body);
        setDirectoryState("live");
        const selected = (body.selected ?? []).filter((item) => item.providerCalendarId !== "redacted");
        setBlocking(new Set(selected.filter((item) => item.blocksAvailability).map((item) => item.providerCalendarId)));
        setDestinationId(selected.find((item) => item.destinationPriority > 0)?.providerCalendarId ?? "");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setDirectoryState("error");
          setMessage(cause instanceof Error ? cause.message : "The calendars on this account could not be read.");
        }
      });
    return () => controller.abort();
  }, [connectionId, idToken, mode]);

  useEffect(() => {
    if (!idToken || !connectionId) {
      return;
    }
    const controller = new globalThis.AbortController();
    setActivityState("loading");
    apiFetch(`/v1/admin/calendar-connections/${encodeURIComponent(connectionId)}/activity`, {
      signal: controller.signal,
      headers: { authorization: `Bearer ${idToken}` }
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`activity_${response.status}`);
        }
        return (await response.json()) as { events: ConnectionActivityEvent[] };
      })
      .then((body) => {
        setActivity(body.events ?? []);
        setActivityState("live");
      })
      .catch((cause: unknown) => {
        if (!(cause instanceof globalThis.DOMException && cause.name === "AbortError")) {
          setActivityState("error");
        }
      });
    return () => controller.abort();
  }, [connectionId, idToken, status]);

  if (!connection) {
    return (
      <div className="manage-calendar-page">
        <button type="button" className="connect-back" onClick={onExit}>
          <ArrowLeft size={16} /> Back to calendars
        </button>
        <section className="calendar-empty-state">
          <strong>This calendar connection is no longer available.</strong>
          <p>It may have been removed from another session.</p>
        </section>
      </div>
    );
  }

  const provider = providerInitial(connection.provider);
  const conferencing = connectionConferencing(connection.provider);
  const blockingResources = resources.filter((item) => item.blocksAvailability);
  const editableCalendars = (directory?.calendars ?? []).map((calendar) => ({ id: calendar.id, label: calendar.name }));
  const destinationResource = resources.find((item) => item.destinationPriority > 0);
  const busy = status === "submitting";

  // What the host has changed but not yet saved, so the rail states facts rather
  // than a placeholder.
  const pendingChanges: string[] = [];
  const savedSelection = new Map((directory?.selected ?? []).map((item) => [item.providerCalendarId, item]));
  for (const calendar of editableCalendars) {
    const nowBlocking = blocking.has(calendar.id);
    if (nowBlocking !== Boolean(savedSelection.get(calendar.id)?.blocksAvailability)) {
      pendingChanges.push(`${calendar.label} ${nowBlocking ? "will block time" : "will be ignored"}`);
    }
  }
  const savedDestination = (directory?.selected ?? []).find((item) => item.destinationPriority > 0)?.providerCalendarId ?? "";
  if (directory && destinationId !== savedDestination) {
    const next = editableCalendars.find((calendar) => calendar.id === destinationId);
    pendingChanges.push(`Bookings will be created on ${next?.label ?? "a different calendar"}`);
  }
  if (eventNote.trim() !== (connection.eventNote ?? "")) {
    pendingChanges.push(
      eventNote.trim() ? `Events will carry the note "${eventNote.trim()}"` : "Events will no longer carry an added note"
    );
  }

  async function readError(response: globalThis.Response, code: string): Promise<never> {
    const body = (await response.json().catch(() => ({}))) as {
      error?: { details?: string[]; message?: string; code?: string };
    };
    throw new Error(body.error?.details?.join(", ") ?? body.error?.message ?? body.error?.code ?? `${code}_${response.status}`);
  }

  async function syncNow() {
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch(`/v1/admin/calendar-connections/${encodeURIComponent(connection!.id)}/sync`, {
        method: "POST",
        headers: { authorization: `Bearer ${idToken}` }
      });
      const body = (await response.json().catch(() => ({}))) as {
        busyCount?: number;
        error?: { code?: string; message?: string };
      };
      await refreshAdminData(idToken, onAdminDataChange);
      if (!response.ok) {
        // A failed check is the useful answer here, so it is reported rather than
        // swallowed: this is how an expired token becomes visible.
        setStatus("error");
        setMessage(
          body.error?.code === "calendar_needs_reconnect"
            ? "This calendar's access has expired. Reconnect the account to restore availability checks."
            : `Sync failed: ${body.error?.message ?? `sync_${response.status}`}`
        );
        return;
      }
      setStatus("done");
      setMessage(`Availability is up to date. ${body.busyCount ?? 0} busy ${body.busyCount === 1 ? "period" : "periods"} found in the next 7 days.`);
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "The calendar could not be synced.");
    }
  }

  async function saveSettings() {
    if (blocking.size === 0) {
      setStatus("error");
      setMessage("Keep at least one calendar checked for conflicts, or disconnect the account instead.");
      return;
    }
    if (eventNote.trim().length > EVENT_NOTE_MAX_LENGTH) {
      setStatus("error");
      setMessage(`Keep the event note to ${EVENT_NOTE_MAX_LENGTH} characters or fewer.`);
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const calendars = editableCalendars.map((calendar) => ({
        providerCalendarId: calendar.id,
        label: calendar.label,
        blocksAvailability: blocking.has(calendar.id)
      }));
      const response = await apiFetch(`/v1/admin/calendar-connections/${encodeURIComponent(connection!.id)}/calendars`, {
        method: "PUT",
        headers: { authorization: `Bearer ${idToken}`, "content-type": "application/json" },
        // Always sent, empty included: an emptied field is how the note is removed.
        body: JSON.stringify({ calendars, eventNote: eventNote.trim(), ...(destinationId ? { destinationCalendarId: destinationId } : {}) })
      });
      if (!response.ok) {
        await readError(response, "calendars_update");
      }
      await refreshAdminData(idToken, onAdminDataChange);
      setStatus("done");
      setMessage("Calendar settings saved.");
      setMode("details");
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Calendar settings could not be saved.");
    }
  }

  async function saveAccountEmail(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    if (authState.status !== "signed_in" || !connection) {
      return;
    }
    const accountEmail = String(new globalThis.FormData(event.currentTarget).get("accountEmail") ?? "").trim();
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch(`/v1/admin/calendar-connections/${encodeURIComponent(connection.id)}/account-email`, {
        method: "PUT",
        headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
        body: JSON.stringify({ accountEmail })
      });
      const body = (await response.json().catch(() => ({}))) as { error?: { message?: string; code?: string } };
      if (!response.ok) {
        throw new Error(body.error?.message ?? body.error?.code ?? `account_email_${response.status}`);
      }
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setStatus("done");
      setMessage(accountEmail ? "Account email saved." : "Account email cleared.");
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "The account email could not be saved.");
    }
  }

  async function reconnect() {
    if (!host || connection!.provider === "caldav") {
      setStatus("error");
      setMessage("This connection is renewed by updating its app-specific password, not by signing in again.");
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch("/v1/admin/calendar-connections/oauth/start", {
        method: "POST",
        headers: { authorization: `Bearer ${idToken}`, "content-type": "application/json" },
        body: JSON.stringify({
          provider: connection!.provider,
          hostId: host.id,
          // Naming the connection keeps this account's calendars and destination.
          connectionId: connection!.id,
          redirectUri: `${appOrigin}/v1/onboarding/calendar-connections/oauth/callback`
        })
      });
      const body = (await response.json().catch(() => ({}))) as { authorizationUrl?: string; error?: { code?: string; message?: string } };
      if (!response.ok || !body.authorizationUrl) {
        throw new Error(body.error?.message ?? body.error?.code ?? `oauth_start_${response.status}`);
      }
      globalThis.location.assign(body.authorizationUrl);
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Reconnecting is not available right now.");
    }
  }

  async function disconnect() {
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch(`/v1/admin/calendar-connections/${encodeURIComponent(connection!.id)}/disconnect`, {
        method: "POST",
        headers: { authorization: `Bearer ${idToken}` }
      });
      if (!response.ok) {
        await readError(response, "calendar_disconnect");
      }
      await refreshAdminData(idToken, onAdminDataChange);
      setShowDisconnect(false);
      onExit();
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "The calendar could not be disconnected.");
    }
  }

  return (
    <div className="manage-calendar-page">
      <button type="button" className="connect-back" onClick={onExit}>
        <ArrowLeft size={16} /> Back to calendars
      </button>

      <section className="manage-calendar-head">
        <div className="manage-calendar-id">
          <i className={provider.tone}>{provider.initial}</i>
          <div>
            <span>
              <span className={connection.status === "connected" ? "editor-tag green" : "editor-tag orange"}>
                {calendarStatusLabel(connection.status)}
              </span>
              {calendarProviderLabel(connection.provider)}
            </span>
            <h2>{calendarConnectionSubtitle(connection)}</h2>
            <p>
              {mode === "reconnect"
                ? "Calendar access expired. Reconnect to continue protecting your availability."
                : `${connection.lastSuccessfulCheck ? `Last synced ${syncTimeLabel(connection.lastSuccessfulCheck)}` : "Not synced yet"} · ${resources.length} ${resources.length === 1 ? "calendar" : "calendars"} selected`}
            </p>
          </div>
        </div>
        <div className="editor-head-actions">
          {mode === "details" ? (
            <>
              <button type="button" className="editor-outline-button" disabled={busy} onClick={() => setMode("reconnect")}>
                <RefreshCw size={15} /> Reconnect
              </button>
              <button type="button" className="confirm" disabled={busy} onClick={() => setMode("edit")}>Edit settings</button>
            </>
          ) : (
            <>
              <button type="button" className="editor-outline-button" disabled={busy} onClick={() => { setMode("details"); setMessage(""); }}>Cancel</button>
              <button
                type="button"
                className="confirm"
                disabled={busy}
                onClick={() => (mode === "edit" ? void saveSettings() : void reconnect())}
              >
                <Check size={16} /> {mode === "edit" ? (busy ? "Saving" : "Save changes") : busy ? "Redirecting" : `Continue to ${providerShortName(connection.provider)}`}
              </button>
            </>
          )}
        </div>
      </section>

      {message ? <p className={status === "error" ? "form-error" : "inline-note"} aria-live="polite">{message}</p> : null}

      {mode === "details" ? (
        <div className="manage-calendar-layout">
          <main>
            <section className="calendar-overview">
              <div className="editor-section-head">
                <div>
                  <h3>Connection overview</h3>
                  <p>How this account contributes to availability and new bookings.</p>
                </div>
                <span className={connection.status === "connected" ? "editor-tag green" : "editor-tag orange"}>
                  {connection.status === "connected" ? "Live sync" : "Paused"}
                </span>
              </div>
              <dl>
                <div>
                  <dt>Provider</dt>
                  <dd>
                    <strong>{calendarProviderLabel(connection.provider)}</strong>
                    <small>{connection.provider === "caldav" ? "App password connection" : "OAuth connection"}</small>
                  </dd>
                </div>
                <div>
                  <dt>Account email</dt>
                  <dd>
                    {/* Google and Microsoft report this during the connect flow. Zoho
                        refuses the profile scope to a calendar-only token, so it can
                        only be filled in by hand. */}
                    <form className="account-email-row" onSubmit={(event) => void saveAccountEmail(event)}>
                      <input
                        name="accountEmail"
                        type="email"
                        defaultValue={connection.accountEmail ?? ""}
                        placeholder="Not reported by this provider"
                        aria-label={`Account email for ${connection.label || calendarProviderLabel(connection.provider)}`}
                      />
                      <button type="submit" disabled={status === "submitting"}>
                        {status === "submitting" ? "Saving" : "Save"}
                      </button>
                    </form>
                    <small>Identifies which account this calendar belongs to.</small>
                  </dd>
                </div>
                <div>
                  <dt>Conflict calendars</dt>
                  <dd>
                    <strong>{blockingResources.length} selected</strong>
                    <small>{blockingResources.length > 0 ? blockingResources.map((item) => item.label).join(", ") : "Nothing on this account blocks time"}</small>
                  </dd>
                </div>
                <div>
                  <dt>Booking destination</dt>
                  <dd>
                    <strong>{destinationResource?.label ?? "Not set"}</strong>
                    <small>
                      {destinationResource ? calendarConnectionSubtitle(connection) : "Bookings fall back to another connected calendar"}
                    </small>
                  </dd>
                </div>
                <div>
                  <dt>Video meetings</dt>
                  <dd>
                    <strong>{conferencing.label}</strong>
                    <small>{conferencing.detail}</small>
                  </dd>
                </div>
                <div>
                  <dt>Added to event notes</dt>
                  <dd>
                    <strong>{connection.eventNote ?? "Nothing added"}</strong>
                    <small>
                      {connection.eventNote
                        ? "Written into the notes of every event created on this calendar."
                        : "Event notes carry the meeting description only."}
                    </small>
                  </dd>
                </div>
              </dl>
              <button type="button" className="manage-edit-row" onClick={() => setMode("edit")}>
                <Settings2 size={18} />
                <span>
                  <strong>Edit calendar settings</strong>
                  <small>Change which calendars block time, or where bookings are created.</small>
                </span>
                <ChevronRight size={17} />
              </button>
            </section>

            <section className="sync-activity">
              <div className="editor-section-head">
                <div>
                  <h3>Sync activity</h3>
                  <p>Recent connection health and calendar updates.</p>
                </div>
                <button type="button" className="editor-outline-button" disabled={busy} onClick={() => void syncNow()}>
                  <RefreshCw size={15} /> {busy ? "Syncing" : "Sync now"}
                </button>
              </div>
              {activityState === "loading" ? (
                <SkeletonRegion label="Loading sync activity">
                  <SkeletonRows count={3}>
                    {(index) => (
                      <article className="skeleton-activity-row" key={index}>
                        <Skeleton width="32px" height="32px" radius="10px" />
                        <div className="skeleton-stack">
                          <Skeleton width={`${52 + index * 11}%`} />
                          <Skeleton className="skeleton-line-sm" width="72%" />
                        </div>
                        <Skeleton className="skeleton-line-sm" width="58px" />
                      </article>
                    )}
                  </SkeletonRows>
                </SkeletonRegion>
              ) : activityState === "error" ? (
                <p className="empty-state">Activity could not be loaded.</p>
              ) : activity.length === 0 ? (
                <p className="empty-state">Nothing recorded for this connection yet. Run a sync to check it now.</p>
              ) : (
                activity.map((item) => (
                  <article key={item.id}>
                    <i className={item.action.endsWith("sync_failed") ? "failed" : ""}>{activityIcon(item.action)}</i>
                    <span>
                      <strong>{activityTitle(item.action)}</strong>
                      <small>{item.summary}</small>
                    </span>
                    <time dateTime={item.createdAt}>{syncTimeLabel(item.createdAt)}</time>
                  </article>
                ))
              )}
            </section>
          </main>

          <aside>
            <section className="calendar-actions">
              <h3>Connection actions</h3>
              <button type="button" onClick={() => setMode("edit")}>
                <Settings2 size={17} />
                <span>
                  <strong>Edit settings</strong>
                  <small>Calendars and destination</small>
                </span>
                <ChevronRight size={15} />
              </button>
              <button type="button" disabled={busy} onClick={() => void syncNow()}>
                <RefreshCw size={17} />
                <span>
                  <strong>Sync now</strong>
                  <small>Refresh availability</small>
                </span>
                <ChevronRight size={15} />
              </button>
              <button type="button" onClick={() => setMode("reconnect")}>
                <ShieldCheck size={17} />
                <span>
                  <strong>Reconnect account</strong>
                  <small>Refresh permissions</small>
                </span>
                <ChevronRight size={15} />
              </button>
              <button type="button" className="danger" onClick={() => { setAcknowledged(false); setShowDisconnect(true); }}>
                <X size={17} />
                <span>
                  <strong>Disconnect calendar</strong>
                  <small>Remove this account</small>
                </span>
                <ChevronRight size={15} />
              </button>
            </section>

            <section className="connection-privacy">
              <LockKeyhole size={19} />
              <h3>Protected calendar data</h3>
              <p>Guests see available times only. Event titles, attendees, and descriptions remain private.</p>
            </section>
          </aside>
        </div>
      ) : null}

      {mode === "edit" ? (
        <div className="manage-calendar-edit">
          <main>
            <section className="edit-calendar-section">
              <div className="editor-section-head">
                <div>
                  <h3>Calendars checked for conflicts</h3>
                  <p>A busy event on any selected calendar prevents that time from being offered.</p>
                </div>
                <span className="editor-tag blue">{blocking.size} selected</span>
              </div>
              {directoryState === "loading" ? (
                <SkeletonRegion label="Reading the calendars on this account">
                  <SkeletonRows count={4}>
                    {(index) => (
                      <div className="skeleton-calendar-option" key={index}>
                        <Skeleton width="20px" height="20px" radius="6px" />
                        <div className="skeleton-stack">
                          <Skeleton width={`${46 + index * 10}%`} />
                          <Skeleton className="skeleton-line-sm" width="66%" />
                        </div>
                      </div>
                    )}
                  </SkeletonRows>
                </SkeletonRegion>
              ) : editableCalendars.length === 0 ? (
                <p className="empty-state">No calendars were found on this account.</p>
              ) : (
                editableCalendars.map((calendar) => (
                  <label className="edit-calendar-option" key={calendar.id}>
                    <input
                      type="checkbox"
                      checked={blocking.has(calendar.id)}
                      onChange={(event) => {
                        const checked = event.currentTarget.checked;
                        setBlocking((current) => {
                          const next = new Set(current);
                          if (checked) {
                            next.add(calendar.id);
                          } else {
                            next.delete(calendar.id);
                          }
                          return next;
                        });
                      }}
                    />
                    <span>
                      <strong>{calendar.label}</strong>
                      <small>{calendar.id}</small>
                    </span>
                    <em>{blocking.has(calendar.id) ? "Blocks time" : "Ignored"}</em>
                  </label>
                ))
              )}
            </section>

            <section className="edit-calendar-section">
              <div className="editor-section-head">
                <div>
                  <h3>Booking destination</h3>
                  <p>New invitations and meeting links are created on this calendar.</p>
                </div>
              </div>
              {(
                <label className="booking-destination">
                  <i className={provider.tone}>{provider.initial}</i>
                  <span>
                    <strong>Calendar</strong>
                    <small>{calendarConnectionSubtitle(connection)}</small>
                  </span>
                  <select
                    aria-label="Booking destination calendar"
                    value={destinationId}
                    onChange={(event) => setDestinationId(event.currentTarget.value)}
                  >
                    <option value="">No destination</option>
                    {editableCalendars
                      .filter((calendar) => blocking.has(calendar.id))
                      .map((calendar) => (
                        <option key={calendar.id} value={calendar.id}>{calendar.label}</option>
                      ))}
                  </select>
                </label>
              )}
            </section>

            <section className="edit-calendar-section">
              <div className="editor-section-head">
                <div>
                  <h3>Added to event notes</h3>
                  <p>Text appended to the notes of every event created on this calendar.</p>
                </div>
                <span className="editor-tag blue">{eventNote.trim() ? "Active" : "Off"}</span>
              </div>
              <div className="calendar-event-note">
                <label>
                  <span>Note text</span>
                  <textarea
                    rows={3}
                    maxLength={EVENT_NOTE_MAX_LENGTH}
                    value={eventNote}
                    placeholder="push-to-exchange"
                    onChange={(event) => setEventNote(event.currentTarget.value)}
                  />
                </label>
                <p className="field-help">
                  Useful when something else reads this calendar afterwards: a sync script that only copies events carrying a
                  marker will find it here. It is added to the meeting description, so guests looking at the event can read it
                  too. Leave it empty for nothing extra.
                </p>
                <small>{eventNote.trim().length}/{EVENT_NOTE_MAX_LENGTH}</small>
              </div>
            </section>
          </main>

          <aside>
            <section className="edit-meet-settings">
              <span className="editor-tag violet">Video conferencing</span>
              <h3>{conferencing.label}</h3>
              <p>{conferencing.detail}</p>
              <p className="field-help">
                Meeting links follow each persona's default location, which you set on the persona rather than here.
              </p>
            </section>

            <section className="change-summary">
              <span>Pending changes</span>
              <h3>{pendingChanges.length === 0 ? "No changes yet" : `${pendingChanges.length} ${pendingChanges.length === 1 ? "change" : "changes"}`}</h3>
              {pendingChanges.length === 0 ? (
                <p>Modified settings will appear here before you save.</p>
              ) : (
                pendingChanges.map((change) => <p key={change}>{change}</p>)
              )}
            </section>
          </aside>
        </div>
      ) : null}

      {mode === "reconnect" ? (
        <div className="reconnect-shell">
          <section className="reconnect-card">
            <i className={provider.tone}>{provider.initial}</i>
            <span className="editor-tag orange">{connection.status === "connected" ? "Refresh access" : "Connection expired"}</span>
            <h2>Reconnect {calendarProviderLabel(connection.provider)}</h2>
            <p>Sign in again to restore calendar sync. Your selected calendars and booking preferences will be preserved.</p>
            <div className="reconnect-permissions">
              <h3>TrueAvail will request permission to:</h3>
              <p><Check size={14} /> View free and busy times</p>
              <p><Check size={14} /> Create and update scheduled meetings</p>
              {connection.provider === "google" ? <p><Check size={14} /> Generate Google Meet links</p> : null}
            </div>
            <div className="permission-note">
              <LockKeyhole size={17} />
              <span>
                <strong>Your password is never shared with TrueAvail.</strong>
                <small>You can revoke access from {providerShortName(connection.provider)} at any time.</small>
              </span>
            </div>
            <button type="button" className="confirm" disabled={busy} onClick={() => void reconnect()}>
              {busy ? "Redirecting" : `Continue to ${providerShortName(connection.provider)}`} <ArrowRight size={16} />
            </button>
            <button type="button" className="booking-link" onClick={() => setMode("details")}>Not now</button>
          </section>

          <aside className="reconnect-impact">
            <ShieldCheck size={22} />
            <h3>Until you reconnect</h3>
            <p>This calendar will not block busy time or receive new bookings.</p>
            <dl>
              <div>
                <dt>Affected personas</dt>
                <dd>{personas.length}</dd>
              </div>
              <div>
                <dt>Calendars paused</dt>
                <dd>{blockingResources.length}</dd>
              </div>
              <div>
                <dt>Existing bookings</dt>
                <dd>Unchanged</dd>
              </div>
            </dl>
          </aside>
        </div>
      ) : null}

      {showDisconnect ? (
        <div className="disconnect-overlay" role="dialog" aria-modal="true" aria-labelledby="disconnect-title">
          <section className="disconnect-dialog">
            <button type="button" className="disconnect-close" aria-label="Close" onClick={() => setShowDisconnect(false)}>
              <X size={18} />
            </button>
            <i><X size={21} /></i>
            <span className="editor-tag rose">Disconnect account</span>
            <h2 id="disconnect-title">Disconnect {calendarProviderLabel(connection.provider)}?</h2>
            <p>
              <strong>{calendarConnectionSubtitle(connection)}</strong> will stop contributing busy time and can no longer receive new bookings.
            </p>
            <div className="disconnect-impact">
              <h3>This will affect:</h3>
              <p>
                <Users size={16} />
                <span>
                  <strong>{personas.length} {personas.length === 1 ? "persona" : "personas"}</strong>
                  <small>{personas.length > 0 ? personas.map((item) => item.name).join(", ") : "No personas yet"}</small>
                </span>
              </p>
              <p>
                <CalendarCheck size={16} />
                <span>
                  <strong>{blockingResources.length} conflict {blockingResources.length === 1 ? "calendar" : "calendars"}</strong>
                  <small>Future busy events will no longer block time</small>
                </span>
              </p>
              <p>
                <Video size={16} />
                <span>
                  <strong>{conferencing.label === "Not available" ? "Booking destination" : `${conferencing.label} generation`}</strong>
                  <small>Choose another conferencing option for affected meeting types</small>
                </span>
              </p>
            </div>
            <label className="disconnect-check">
              <input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.currentTarget.checked)} />
              <span>I understand that removing this calendar could expose times when I am busy.</span>
            </label>
            <div>
              <button type="button" className="editor-outline-button" onClick={() => setShowDisconnect(false)}>Keep connected</button>
              <button type="button" className="disconnect-confirm" disabled={!acknowledged || busy} onClick={() => void disconnect()}>
                {busy ? "Disconnecting" : "Disconnect calendar"}
              </button>
            </div>
            <small>Existing bookings and calendar events will not be deleted.</small>
          </section>
        </div>
      ) : null}
    </div>
  );
}

function ConnectCalendarView({
  authState,
  adminData,
  onAdminDataChange,
  initialConnectionId,
  initialFailure,
  onExit
}: {
  authState: AuthState;
  adminData: AdminStatusResponse | null;
  onAdminDataChange: (data: AdminStatusResponse | null) => void;
  initialConnectionId?: string;
  initialFailure?: string;
  onExit: () => void;
}) {
  const [step, setStep] = useState<ConnectStep>(initialConnectionId ? "configure" : "provider");
  const [providerId, setProviderId] = useState<ConnectProviderId>("google");
  const [connectionId, setConnectionId] = useState(initialConnectionId ?? "");
  const [directory, setDirectory] = useState<CalendarDirectoryResponse | null>(null);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [blocking, setBlocking] = useState<Set<string>>(new Set());
  const [destinationId, setDestinationId] = useState("");
  const [status, setStatus] = useState<OnboardingStatus>("idle");
  const [message, setMessage] = useState(initialFailure ? connectFailureMessage(initialFailure) : "");
  const host = signedInHost(authState, adminData);
  // Returning from an OAuth redirect lands straight on the configure step, where the
  // picked provider is still the "google" default: the screen has to take its identity
  // from the connection it is configuring, not from a choice this page never saw.
  const connectedProvider =
    (adminData?.calendarConnections ?? []).find((item) => item.id === connectionId)?.provider;
  const activeProviderId = directory?.provider ?? (connectedProvider as ConnectProviderId | undefined) ?? providerId;
  const provider = CONNECT_PROVIDERS.find((item) => item.id === activeProviderId) ?? CONNECT_PROVIDERS[0]!;

  const loadDirectory = useCallback(
    async (id: string) => {
      if (authState.status !== "signed_in") {
        return;
      }
      setStatus("submitting");
      setMessage("");
      try {
        const response = await apiFetch(`/v1/admin/calendar-connections/${encodeURIComponent(id)}/available-calendars`, {
          headers: { authorization: `Bearer ${authState.idToken}` }
        });
        const body = (await response.json().catch(() => ({}))) as CalendarDirectoryResponse & {
          error?: { code?: string; message?: string };
        };
        if (body.error?.code === "calendar_scope_missing") {
          setNeedsReconnect(true);
          setStatus("error");
          setMessage(body.error.message ?? "Reconnect this account to choose calendars.");
          return;
        }
        if (response.status === 401) {
          setStatus("error");
          setMessage("Your session expired. Sign in again to choose calendars.");
          return;
        }
        if (!response.ok) {
          throw new Error(body.error?.message ?? body.error?.code ?? `calendars_${response.status}`);
        }
        setNeedsReconnect(false);
        setDirectory(body);
        // An unsaved selection from before a session expiry wins over the stored one.
        const draft = readConnectDraft(id);
        if (draft) {
          setBlocking(new Set(draft.blocking));
          setDestinationId(draft.destinationId);
          setStatus("idle");
          setMessage("Your unsaved calendar selection was restored. Choose Finish connection to save it.");
          return;
        }
        const previouslySelected = new Set((body.selected ?? []).filter((item) => item.blocksAvailability).map((item) => item.providerCalendarId));
        // First run: pre-select everything writable so availability is protected by default.
        setBlocking(previouslySelected.size > 0 ? previouslySelected : new Set(body.calendars.map((calendar) => calendar.id)));
        const previousDestination = (body.selected ?? []).find((item) => item.destinationPriority > 0)?.providerCalendarId;
        const firstWritable = body.calendars.find((calendar) => calendar.canWrite && calendar.isPrimary) ?? body.calendars.find((calendar) => calendar.canWrite);
        setDestinationId(previousDestination ?? firstWritable?.id ?? "");
        setStatus("idle");
      } catch (cause) {
        setStatus("error");
        setMessage(cause instanceof Error ? cause.message : "Could not read calendars from the provider.");
      }
    },
    [authState]
  );

  useEffect(() => {
    if (step === "configure" && connectionId && !directory) {
      void loadDirectory(connectionId);
    }
  }, [step, connectionId, directory, loadDirectory]);

  async function startOAuth() {
    if (authState.status !== "signed_in" || !host) {
      setStatus("error");
      setMessage("Sign in as a host before connecting calendars.");
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch("/v1/admin/calendar-connections/oauth/start", {
        method: "POST",
        headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
        body: JSON.stringify({
          provider: providerId,
          hostId: host.id,
          redirectUri: `${appOrigin}/v1/onboarding/calendar-connections/oauth/callback`
        })
      });
      const body = (await response.json().catch(() => ({}))) as { authorizationUrl?: string; error?: { code?: string; message?: string } };
      if (!response.ok || !body.authorizationUrl) {
        throw new Error(body.error?.message ?? body.error?.code ?? `oauth_start_${response.status}`);
      }
      globalThis.location.assign(body.authorizationUrl);
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Could not start the provider sign-in.");
    }
  }

  async function connectICloud(event: FormEvent<globalThis.HTMLFormElement>) {
    event.preventDefault();
    if (authState.status !== "signed_in" || !host) {
      setStatus("error");
      setMessage("Sign in as a host before connecting calendars.");
      return;
    }
    const form = new globalThis.FormData(event.currentTarget);
    setStatus("submitting");
    setMessage("");
    try {
      const response = await apiFetch("/v1/admin/calendar-connections", {
        method: "POST",
        headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
        body: JSON.stringify({
          provider: "caldav",
          hostId: host.id,
          label: "iCloud Calendar",
          resourceLabel: "iCloud calendar",
          username: String(form.get("username") ?? ""),
          appPassword: String(form.get("appPassword") ?? ""),
          serverUrl: String(form.get("serverUrl") ?? "https://caldav.icloud.com"),
          blocksAvailability: true,
          destinationPriority: 0
        })
      });
      const body = (await response.json().catch(() => ({}))) as {
        connection?: { id: string };
        error?: { code?: string; message?: string; details?: string[] };
      };
      if (!response.ok || !body.connection) {
        throw new Error(body.error?.message ?? body.error?.details?.join(", ") ?? body.error?.code ?? `icloud_connect_${response.status}`);
      }
      setConnectionId(body.connection.id);
      setDirectory(null);
      setStatus("idle");
      setStep("configure");
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Could not connect to iCloud.");
    }
  }

  async function finishConnection() {
    if (authState.status !== "signed_in" || !directory) {
      return;
    }
    if (blocking.size === 0 && !destinationId) {
      setStatus("error");
      setMessage("Choose at least one calendar to check for conflicts, or a calendar to receive bookings.");
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      const chosenIds = new Set(blocking);
      if (destinationId) {
        chosenIds.add(destinationId);
      }
      const calendars = directory.calendars
        .filter((calendar) => chosenIds.has(calendar.id))
        .map((calendar) => ({
          providerCalendarId: calendar.id,
          label: calendar.name,
          blocksAvailability: blocking.has(calendar.id)
        }));

      const response = await apiFetch(`/v1/admin/calendar-connections/${encodeURIComponent(connectionId)}/calendars`, {
        method: "PUT",
        headers: { authorization: `Bearer ${authState.idToken}`, "content-type": "application/json" },
        body: JSON.stringify({ calendars, ...(destinationId ? { destinationCalendarId: destinationId } : {}) })
      });
      const body = (await response.json().catch(() => ({}))) as { error?: { code?: string; message?: string; details?: string[] } };
      if (response.status === 401) {
        // Keep the selection so signing in again does not cost the host their work.
        writeConnectDraft(connectionId, { blocking: [...blocking], destinationId });
        setStatus("error");
        setMessage("Your session expired before this could be saved. Sign in again and return to this page — your calendar selection has been kept.");
        return;
      }
      if (!response.ok) {
        throw new Error(body.error?.details?.join(", ") ?? body.error?.message ?? body.error?.code ?? `save_calendars_${response.status}`);
      }
      clearConnectDraft(connectionId);
      await refreshAdminData(authState.idToken, onAdminDataChange);
      setStatus("done");
      onExit();
    } catch (cause) {
      setStatus("error");
      setMessage(cause instanceof Error ? cause.message : "Could not save the calendar selection.");
    }
  }

  function toggleBlocking(id: string) {
    setBlocking((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  const writableCalendars = (directory?.calendars ?? []).filter((calendar) => calendar.canWrite);
  const destinationCalendar = directory?.calendars.find((calendar) => calendar.id === destinationId);
  // Count only calendars that still exist on the account, so a stale stored selection
  // can never report more conflict calendars than are actually shown.
  const blockingCount = (directory?.calendars ?? []).filter((calendar) => blocking.has(calendar.id)).length;

  return (
    <section className="connect-wizard">
      <button type="button" className="connect-back" onClick={onExit}>
        <ArrowLeft size={17} /> Back to calendars
      </button>

      <div className="connect-card">
        <header className="connect-header">
          <span className="connect-eyebrow">Calendar connection</span>
          <h1>{step === "provider" ? "Connect a calendar" : step === "authorize" ? `Sign in to ${provider.name}` : "Choose how this calendar is used"}</h1>
          <p>
            {step === "provider"
              ? "Bring all of your commitments together so guests only see times when you're truly free."
              : step === "authorize"
                ? `Authorize TrueAvail to check availability${provider.canReceiveBookings ? " and create meetings" : ""}. Your event details stay private.`
                : "Select the calendars that block time and where new bookings should be added."}
          </p>
          <ol className="connect-steps">
            <ConnectStepPip index={1} label="Provider" state={step === "provider" ? "active" : "done"} />
            <ConnectStepPip index={2} label="Authorize" state={step === "authorize" ? "active" : step === "configure" ? "done" : "todo"} />
            <ConnectStepPip index={3} label="Configure" state={step === "configure" ? "active" : "todo"} />
          </ol>
        </header>

        {message ? (
          <p className={status === "error" ? "form-error connect-message" : "inline-note connect-message"} aria-live="polite">
            {message}
          </p>
        ) : null}

        {step === "provider" ? (
          <div className="connect-body connect-body-split">
            <div className="connect-provider-list">
              {CONNECT_PROVIDERS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`connect-provider${item.id === providerId ? " is-selected" : ""}`}
                  aria-pressed={item.id === providerId}
                  onClick={() => setProviderId(item.id)}
                >
                  <span className={`connect-provider-mark ${item.tone}`}>{item.initial}</span>
                  <span className="connect-provider-text">
                    <strong>{item.name}</strong>
                    <span>{item.blurb}</span>
                  </span>
                  {item.id === providerId ? <Check size={18} className="connect-provider-check" /> : <ChevronRight size={17} className="connect-provider-chevron" />}
                </button>
              ))}
            </div>
            <aside className="connect-aside">
              <span className="connect-aside-icon"><ShieldCheck size={22} /></span>
              <h2>Availability, not surveillance</h2>
              <p>TrueAvail reads only the information needed to identify busy periods. Guests never see calendar names, event titles, attendees, or descriptions.</p>
              <ul>
                <li><Check size={16} /> Encrypted connection</li>
                <li><Check size={16} /> Disconnect at any time</li>
              </ul>
              {provider.id === "caldav" ? (
                <p className="connect-aside-warning">
                  Meetings are written to your iCloud calendar, but Apple will not email the guest for an app-specific password
                  connection, so the invitation comes from TrueAvail instead.
                </p>
              ) : null}
              <button type="button" className="confirm connect-cta" onClick={() => setStep("authorize")}>
                Continue with {provider.name} <ArrowRight size={17} />
              </button>
            </aside>
          </div>
        ) : null}

        {step === "authorize" ? (
          <div className="connect-body">
            <div className="connect-auth-card">
              <span className={`connect-provider-mark large ${provider.tone}`}>{provider.initial}</span>
              <h2>Continue to {provider.name}</h2>
              <p>
                {provider.usesOAuth
                  ? `You'll sign in securely with ${provider.name}. TrueAvail will request permission to:`
                  : "iCloud does not support app sign-in, so it uses an app-specific password you generate at appleid.apple.com."}
              </p>
              <ul className="connect-permissions">
                {provider.permissions.map((permission) => (
                  <li key={permission}><Check size={16} /> {permission}</li>
                ))}
              </ul>

              {provider.usesOAuth ? (
                <>
                  <p className="connect-privacy-note">
                    <LockKeyhole size={16} /> <span><strong>Your password is never shared with TrueAvail.</strong> You can revoke access from your calendar provider at any time.</span>
                  </p>
                  <button type="button" className="confirm connect-cta" disabled={status === "submitting"} onClick={() => void startOAuth()}>
                    {status === "submitting" ? "Opening" : `Continue to ${provider.name}`} <ArrowRight size={17} />
                  </button>
                </>
              ) : (
                <form className="connect-credentials" onSubmit={(formEvent) => void connectICloud(formEvent)}>
                  <label>
                    Apple ID email
                    <input name="username" type="email" required autoComplete="username" placeholder="you@icloud.com" />
                  </label>
                  <label>
                    App-specific password
                    <input name="appPassword" type="password" required autoComplete="new-password" placeholder="xxxx-xxxx-xxxx-xxxx" />
                  </label>
                  <label>
                    CalDAV server
                    <input name="serverUrl" type="url" defaultValue="https://caldav.icloud.com" />
                  </label>
                  <p className="connect-privacy-note">
                    <LockKeyhole size={16} /> <span>Create an app-specific password at <strong>appleid.apple.com</strong>. Your normal Apple ID password will not work.</span>
                  </p>
                  <button className="confirm connect-cta" disabled={status === "submitting"}>
                    {status === "submitting" ? "Connecting" : "Connect iCloud"} <ArrowRight size={17} />
                  </button>
                </form>
              )}

              <button type="button" className="connect-linkish" onClick={() => setStep("provider")}>
                Choose a different provider
              </button>
            </div>
          </div>
        ) : null}

        {step === "configure" ? (
          <div className="connect-body connect-body-split">
            <div className="connect-configure">
              <div className="connect-account">
                <span className={`connect-provider-mark ${provider.tone}`}>{provider.initial}</span>
                <div>
                  {/* Zoho's calendar list carries no account address, so the primary
                      calendar names the account rather than repeating the provider. */}
                  <strong>{directory?.accountEmail ?? directory?.calendars.find((calendar) => calendar.isPrimary)?.name ?? provider.name}</strong>
                  <span>{provider.name} · Connected</span>
                </div>
                <span className="pill pill-live">Connected</span>
              </div>

              <h3>Check these calendars for conflicts</h3>
              <p className="connect-hint">A busy event on any selected calendar will make that time unavailable.</p>
              {needsReconnect ? (
                <div className="connect-aside-warning">
                  <p>
                    This account was connected before TrueAvail asked for permission to list your calendars, so it can't show them yet.
                    Reconnecting takes a few seconds and keeps your existing bookings untouched.
                  </p>
                  <button type="button" className="confirm connect-cta" onClick={() => { setNeedsReconnect(false); setStep("provider"); }}>
                    Reconnect {provider.name} <ArrowRight size={17} />
                  </button>
                </div>
              ) : null}
              {status === "submitting" && !directory ? <p className="inline-note">Reading your calendars…</p> : null}
              <div className="connect-calendar-list">
                {(directory?.calendars ?? []).map((calendar) => (
                  <label key={calendar.id} className={`connect-calendar${blocking.has(calendar.id) ? " is-on" : ""}`}>
                    <input type="checkbox" checked={blocking.has(calendar.id)} onChange={() => toggleBlocking(calendar.id)} />
                    <span className="connect-calendar-text">
                      <strong>{calendar.name}</strong>
                      {calendar.isPrimary ? <span>Primary calendar</span> : calendar.canWrite ? <span>You can add events</span> : <span>Read only</span>}
                    </span>
                    <span className="connect-calendar-state">{blocking.has(calendar.id) ? "Blocks time" : "Ignored"}</span>
                  </label>
                ))}
              </div>

              <h3>Add new bookings to</h3>
              <p className="connect-hint">Invitations and meeting links will be created on this calendar.</p>
              {provider.canReceiveBookings ? (
                writableCalendars.length > 0 ? (
                  <select
                    className="connect-destination"
                    value={destinationId}
                    onChange={(changeEvent) => setDestinationId(changeEvent.currentTarget.value)}
                  >
                    <option value="">Do not create bookings on this account</option>
                    {writableCalendars.map((calendar) => (
                      <option key={calendar.id} value={calendar.id}>{calendar.name}</option>
                    ))}
                  </select>
                ) : (
                  <p className="connect-aside-warning">This account has no calendars you can write to, so it cannot receive bookings.</p>
                )
              ) : (
                <p className="connect-aside-warning">This account has no calendars you can write to, so it cannot receive bookings.</p>
              )}
            </div>

            <aside className="connect-aside">
              <span className="connect-eyebrow">Connection summary</span>
              <h2>Your availability will now include this account.</h2>
              <dl className="connect-summary">
                <div><dt>Provider</dt><dd>{provider.name}</dd></div>
                <div><dt>Conflict calendars</dt><dd>{blockingCount} selected</dd></div>
                <div><dt>Booking calendar</dt><dd>{destinationCalendar?.name ?? "None"}</dd></div>
              </dl>
              <button type="button" className="confirm connect-cta" disabled={status === "submitting" || !directory} onClick={() => void finishConnection()}>
                <Check size={18} /> {status === "submitting" ? "Saving" : "Finish connection"}
              </button>
              <p className="connect-hint centered">Initial calendar sync usually completes in under a minute.</p>
            </aside>
          </div>
        ) : null}
      </div>
    </section>
  );
}

interface ActionMenuItem {
  label: string;
  onSelect?: () => void;
  href?: string;
  danger?: boolean;
  disabled?: boolean;
}

/**
 * The one row-level action menu used across the app. Closes on outside click and
 * on Escape so it never strands the user with an open popover.
 */
/**
 * Every IANA zone the browser knows, newest browsers included. Falls back to a
 * short list of common zones plus whatever value is already stored, so an existing
 * setting is never silently dropped by a browser that lacks supportedValuesOf.
 */
function timeZoneOptions(current?: string): string[] {
  const fallback = [
    "America/Los_Angeles", "America/Denver", "America/Chicago", "America/New_York",
    "America/Toronto", "America/Mexico_City", "America/Sao_Paulo",
    "Europe/London", "Europe/Dublin", "Europe/Lisbon", "Europe/Paris", "Europe/Berlin",
    "Europe/Madrid", "Europe/Rome", "Europe/Amsterdam", "Europe/Stockholm",
    "Africa/Lagos", "Africa/Johannesburg", "Africa/Nairobi", "Africa/Cairo",
    "Asia/Dubai", "Asia/Karachi", "Asia/Kolkata", "Asia/Bangkok", "Asia/Shanghai",
    "Asia/Singapore", "Asia/Hong_Kong", "Asia/Tokyo", "Asia/Seoul",
    "Australia/Perth", "Australia/Sydney", "Pacific/Auckland", "UTC"
  ];
  let zones = fallback;
  try {
    const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
    if (typeof supported === "function") {
      zones = supported("timeZone");
    }
  } catch {
    zones = fallback;
  }
  const withCurrent = current && !zones.includes(current) ? [current, ...zones] : zones;
  return [...new Set(withCurrent)];
}

function timeZoneLabel(zone: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "shortOffset" }).formatToParts(new Date());
    const offset = parts.find((part) => part.type === "timeZoneName")?.value;
    return offset ? `${zone.replace(/_/g, " ")} (${offset})` : zone.replace(/_/g, " ");
  } catch {
    return zone.replace(/_/g, " ");
  }
}

function TimeZoneSelect({ name, value, ariaLabel, onChange }: { name: string; value: string; ariaLabel?: string; onChange?: (value: string) => void }) {
  const [selected, setSelected] = useState(value || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  const options = useMemo(() => timeZoneOptions(selected), [selected]);
  return (
    <select
      name={name}
      required
      value={selected}
      {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
      onChange={(changeEvent) => {
        setSelected(changeEvent.currentTarget.value);
        onChange?.(changeEvent.currentTarget.value);
      }}
    >
      {options.map((zone) => (
        <option key={zone} value={zone}>{timeZoneLabel(zone)}</option>
      ))}
    </select>
  );
}

function ActionMenu({ label, items, align = "right" }: { label: string; items: ActionMenuItem[]; align?: "left" | "right" }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<globalThis.HTMLButtonElement | null>(null);
  const menuRef = useRef<globalThis.HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<{ top: number; left: number } | null>(null);

  // The menu lives in a portal because several of the cards it opens inside use
  // `overflow: hidden` for their rounded corners, which clipped it. Position is
  // measured from the trigger so it can also flip up near the viewport edge.
  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return;
    }
    const place = () => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) {
        return;
      }
      const triggerBox = trigger.getBoundingClientRect();
      const menuBox = menu.getBoundingClientRect();
      const margin = 8;
      const below = triggerBox.bottom + 6;
      const top =
        below + menuBox.height > globalThis.innerHeight - margin
          ? Math.max(margin, triggerBox.top - 6 - menuBox.height)
          : below;
      const preferredLeft = align === "right" ? triggerBox.right - menuBox.width : triggerBox.left;
      const maxLeft = Math.max(margin, globalThis.innerWidth - menuBox.width - margin);
      setPlacement({ top, left: Math.min(Math.max(margin, preferredLeft), maxLeft) });
    };
    place();
    globalThis.addEventListener("resize", place);
    globalThis.addEventListener("scroll", place, true);
    return () => {
      globalThis.removeEventListener("resize", place);
      globalThis.removeEventListener("scroll", place, true);
    };
  }, [open, align]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const close = () => setOpen(false);
    const onKey = (keyEvent: globalThis.KeyboardEvent) => {
      if (keyEvent.key === "Escape") {
        setOpen(false);
      }
    };
    globalThis.document.addEventListener("click", close);
    globalThis.document.addEventListener("keydown", onKey);
    return () => {
      globalThis.document.removeEventListener("click", close);
      globalThis.document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span className="action-menu-wrap" onClick={(clickEvent) => clickEvent.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        className="action-menu-trigger"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <MoreHorizontal size={17} />
      </button>
      {open
        ? createPortal(
        <div
          ref={menuRef}
          className={`action-menu is-${align}`}
          role="menu"
          style={{ top: placement?.top ?? 0, left: placement?.left ?? 0, visibility: placement ? "visible" : "hidden" }}
        >
          {items.map((item) =>
            item.href ? (
              <a key={item.label} role="menuitem" href={item.href} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>
                {item.label}
              </a>
            ) : (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                disabled={item.disabled === true}
                className={item.danger ? "action-menu-danger" : undefined}
                onClick={() => {
                  setOpen(false);
                  item.onSelect?.();
                }}
              >
                {item.label}
              </button>
            )
          )}
        </div>,
        globalThis.document.body
          )
        : null}
    </span>
  );
}

function ConnectStepPip({ index, label, state }: { index: number; label: string; state: "todo" | "active" | "done" }) {
  return (
    <li className={`connect-step is-${state}`}>
      <span className="connect-step-pip">{state === "done" ? <Check size={14} /> : index}</span>
      <span className="connect-step-label">{label}</span>
    </li>
  );
}

function connectFailureMessage(reason: string): string {
  if (reason === "expired") {
    return "That sign-in link timed out before it could be completed. Please try connecting again.";
  }
  if (reason === "invalid_client" || reason === "google_client_secret_required") {
    return "The provider rejected this app's credentials. Check the OAuth client configuration and try again.";
  }
  if (reason === "access_denied") {
    return "Permission was declined at the provider, so no calendar was connected.";
  }
  return `The provider could not complete the connection (${reason}).`;
}

async function refreshAdminData(idToken: string, onAdminDataChange: (data: AdminStatusResponse | null) => void) {
  const response = await apiFetch("/v1/admin/status", {
    headers: { authorization: `Bearer ${idToken}` }
  });
  if (response.ok) {
    onAdminDataChange((await response.json()) as AdminStatusResponse);
  }
}

function capitalizeLabel(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function providerInitial(provider: Provider): { initial: string; tone: string } {
  switch (provider) {
    case "google":
      return { initial: "G", tone: "google" };
    case "microsoft":
      return { initial: "M", tone: "microsoft" };
    case "zoho":
      return { initial: "Z", tone: "zoho" };
    case "mock":
      return { initial: "D", tone: "mock" };
    case "caldav":
      return { initial: "C", tone: "mock" };
    case "ics":
      return { initial: "I", tone: "mock" };
  }
}

function calendarConnectionSubtitle(connection: CalendarConnection): string {
  const maybeEmail = (connection as CalendarConnection & { accountEmail?: string }).accountEmail;
  return maybeEmail ?? `${calendarProviderLabel(connection.provider)} account`;
}

/**
 * What a connection is actually doing, which is not always what its status says. A
 * calendar that authenticates fine but cannot be read still blocks every requested
 * time, so it is reported as unreadable rather than connected.
 */
function calendarConnectionHealth(connection: CalendarConnection): { label: string; tone: string; blocking: boolean } {
  if (connection.lastReadFailure && connection.status !== "disabled") {
    return { label: "Not readable", tone: "unreadable", blocking: true };
  }
  return {
    label: calendarStatusLabel(connection.status),
    tone: connection.status,
    blocking: connection.status === "needs_reconnect" || connection.status === "error"
  };
}

function calendarStatusLabel(status: CalendarConnection["status"]): string {
  switch (status) {
    case "connected":
      return "Connected";
    case "needs_reconnect":
      return "Action needed";
    case "disabled":
      return "Disabled";
    case "error":
      return "Connection error";
  }
}

function primaryCalendarDestination(resources: CalendarResource[]): string {
  // Only a positive priority marks a destination; zero means "not a destination",
  // so sorting the whole list ascending used to name a calendar bookings never
  // reach.
  const resource = resources
    .filter((item) => item.destinationPriority > 0)
    .sort((left, right) => left.destinationPriority - right.destinationPriority)[0];
  return resource?.label ?? "Not set";
}

function providerSummary(connections: CalendarConnection[]): string {
  const providers = [...new Set(connections.map((connection) => calendarProviderLabel(connection.provider)))];
  if (providers.length === 0) {
    return "your providers";
  }
  if (providers.length === 1) {
    return providers[0]!;
  }
  if (providers.length === 2) {
    return `${providers[0]} and ${providers[1]}`;
  }
  return `${providers.slice(0, -1).join(", ")}, and ${providers.at(-1)}`;
}

function calendarProviderLabel(provider: Provider): string {
  switch (provider) {
    case "google":
      return "Google Calendar";
    case "microsoft":
      return "Microsoft 365";
    case "zoho":
      return "Zoho Calendar";
    case "mock":
      return "Dev calendar";
    case "caldav":
      return "iCloud Calendar";
    case "ics":
      return "ICS";
  }
}

/**
 * A placeholder in the shape of the thing that is loading.
 *
 * Skeletons are decoration: they carry no information a screen reader can use, and
 * announcing forty grey blocks is worse than announcing nothing. Each one is hidden
 * from the accessibility tree, and the surrounding `SkeletonRegion` says "loading"
 * once, politely, on the reader's behalf.
 */
function Skeleton({ width, height, radius, className }: { width?: string; height?: string; radius?: string; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={className ? `skeleton ${className}` : "skeleton"}
      style={
        {
          ...(width ? { width } : {}),
          ...(height ? { height } : {}),
          ...(radius ? { borderRadius: radius } : {})
        } as CSSProperties
      }
    />
  );
}

/** Wraps a block of skeletons and announces what is being waited on, once. */
function SkeletonRegion({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <div className={className} role="status" aria-live="polite" aria-busy="true">
      <span className="visually-hidden">{label}</span>
      {children}
    </div>
  );
}

/** Repeats a row shape, so a list reads as a list before its contents arrive. */
function SkeletonRows({ count, children }: { count: number; children: (index: number) => ReactNode }) {
  return <>{Array.from({ length: count }, (_, index) => children(index))}</>;
}

function dataStateLabel(state: LoadState, liveLabel: string) {
  switch (state) {
    case "loading":
      return "Loading";
    case "live":
      return liveLabel;
    case "error":
      return "Needs attention";
  }
}

function configStateLabel(state: "idle" | "saving" | "saved" | "error"): string {
  switch (state) {
    case "saving":
      return "Saving controls";
    case "saved":
      return "Controls saved";
    case "error":
      return "Controls could not be saved";
    case "idle":
      return "Tenant-level flags apply to new signup and provider connection attempts.";
  }
}

function defaultAdminFeatureFlags(): AdminFeatureFlags {
  return {
    ALLOW_PUBLIC_HOST_SIGNUP: false,
    ALLOW_ADMIN_INVITES: true,
    ENABLE_GOOGLE_CALENDAR: true,
    ENABLE_MICROSOFT_CALENDAR: true,
    ENABLE_ZOHO_CALENDAR: true,
    ENABLE_CALDAV_CALENDAR: true
  };
}

function signedInHost(authState: AuthState, adminData: AdminStatusResponse | null): Host | undefined {
  if (authState.status !== "signed_in" || !adminData) {
    return undefined;
  }
  if (!authState.hostId) {
    return undefined;
  }
  return adminData.hosts.find((host) => host.id === authState.hostId);
}

function onboardingStatusForHost(authState: AuthState, adminData: AdminStatusResponse | null): HostOnboardingState {
  if (authState.status !== "signed_in" || !adminData) {
    return "complete";
  }
  const host = signedInHost(authState, adminData);
  if (!host) {
    return "needs_host";
  }
  const hostPersonas = adminData.personas.filter((persona) => persona.hostId === host.id);
  const personaIds = new Set(hostPersonas.map((persona) => persona.id));
  if (hostPersonas.length === 0 || !adminData.meetingTypes.some((meetingType) => personaIds.has(meetingType.personaId))) {
    return "needs_persona";
  }
  if (!adminData.calendarConnections.some((connection) => connection.hostId === host.id)) {
    return "needs_calendar";
  }
  if (!hostPersonas.some((persona) => persona.publicState === "active")) {
    return "needs_publish";
  }
  return "complete";
}

interface CognitoInitiateAuthResponse {
  ChallengeName?: "NEW_PASSWORD_REQUIRED";
  Session?: string;
  AuthenticationResult?: {
    IdToken?: string;
    AccessToken?: string;
  };
  __type?: string;
  message?: string;
}

interface CognitoUserResponse {
  Username?: string;
  PreferredMfaSetting?: string;
  UserMFASettingList?: string[];
  UserAttributes?: Array<{ Name: string; Value: string }>;
}

async function cognitoRequest<T>(target: string, body: Record<string, unknown>): Promise<T> {
  const response = await fetch(`https://cognito-idp.${cognitoRegion}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "content-type": "application/x-amz-json-1.1",
      "x-amz-target": target
    },
    body: JSON.stringify(body)
  });
  const responseBody = (await response.json().catch(() => ({}))) as CognitoInitiateAuthResponse;
  if (!response.ok || responseBody.__type) {
    throw new Error(responseBody.message ?? responseBody.__type ?? `cognito_${response.status}`);
  }
  return responseBody as T;
}

function completeAuth(
  idToken: string | undefined,
  fallbackEmail: string,
  onAuthChange: (state: AuthState) => void,
  setExpanded: (expanded: boolean) => void,
  // Cognito's own account operations (change password, read MFA, global sign-out)
  // take an access token, not the ID token the admin API uses.
  accessToken?: string
) {
  if (!idToken) {
    throw new Error("Cognito did not return an ID token.");
  }
  const claims = decodeJwtPayload(idToken);
  const roles = String(claims["custom:roles"] ?? "").split(",").map((role) => role.trim()).filter(Boolean);
  const email = String(claims.email ?? fallbackEmail);
  const hostId = typeof claims["custom:host_id"] === "string" ? claims["custom:host_id"] : undefined;
  const next: AuthState = {
    status: "signed_in",
    email,
    idToken,
    ...(accessToken ? { accessToken } : {}),
    roles,
    version: authStorageVersion,
    ...(hostId ? { hostId } : {})
  };
  globalThis.localStorage.setItem(authStorageKey, JSON.stringify(next));
  onAuthChange(next);
  setExpanded(false);
}

function storedAuthState(): AuthState {
  try {
    const raw = globalThis.localStorage.getItem(authStorageKey);
    if (!raw) {
      return { status: "signed_out" };
    }
    const parsed = JSON.parse(raw) as AuthState;
    return parsed.status === "signed_in" && parsed.version === authStorageVersion ? parsed : { status: "signed_out" };
  } catch {
    return { status: "signed_out" };
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const payload = token.split(".")[1];
  if (!payload) {
    return {};
  }
  const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  return JSON.parse(globalThis.atob(padded)) as Record<string, unknown>;
}

function authErrorMessage(cause: unknown) {
  const message = cause instanceof Error ? cause.message : "Sign-in failed.";
  if (message.includes("Incorrect username or password")) {
    return "Incorrect email or password.";
  }
  if (message.includes("Password does not conform")) {
    return "Password does not meet the Cognito policy.";
  }
  return message;
}

function locationLabel(persona: Persona) {
  switch (persona.locationPreference.kind) {
    case "google_meet":
      return "Google Meet";
    case "microsoft_teams":
      return "Microsoft Teams";
    case "zoho_meeting":
      return "Zoho Meeting";
    case "custom_url":
      return "Custom video";
    case "phone":
      return "Phone";
    case "in_person":
      return "In person";
    case "decide_later":
      return "Decide later";
  }
}

function formatHours(value: number) {
  return value.toFixed(value % 1 === 0 ? 0 : 1);
}

function formatBookingWhen(value: string) {
  const when = new Date(value);
  return `${new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(when)} \u00b7 ${formatTime(value)}`;
}

type BookingRangeFilter = "upcoming" | "past" | "canceled" | "all";

/**
 * A booking that will not happen. The two canceled statuses record who called it
 * off, which matters in the detail view but never to the question of whether the
 * meeting is still on the host's calendar.
 */
function isCanceledBooking(booking: Booking): boolean {
  return booking.status === "canceled_by_host" || booking.status === "canceled_by_guest";
}

function bookingStatusView(status: Booking["status"]): { label: string; tone: string } {
  switch (status) {
    case "confirmed":
      return { label: "Confirmed", tone: "confirmed" };
    case "pending_approval":
      return { label: "Pending", tone: "pending" };
    case "held":
      return { label: "Held", tone: "pending" };
    case "rescheduled":
      return { label: "Rescheduled", tone: "confirmed" };
    case "canceled_by_guest":
      return { label: "Canceled by guest", tone: "canceled" };
    case "canceled_by_host":
      return { label: "Canceled", tone: "canceled" };
    case "completed":
      return { label: "Completed", tone: "neutral" };
    case "no_show":
      return { label: "No show", tone: "canceled" };
    case "failed_reconciliation_needed":
      return { label: "Needs attention", tone: "canceled" };
  }
}

function formatShortDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
}

function inviteDisplayName(email: string) {
  const localPart = email.split("@")[0] ?? email;
  return localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || email;
}

function inviteStatusLabel(invitation: Invitation) {
  switch (invitation.status) {
    case "pending":
      return "Invite sent";
    case "claimed":
      return "Accepted";
    case "revoked":
      return "Revoked";
    case "expired":
      return "Expired";
  }
}

function relativeTimeLabel(value: string) {
  const elapsedSeconds = Math.max(0, Math.round((Date.now() - Date.parse(value)) / 1000));
  if (elapsedSeconds < 60) {
    return "just now";
  }
  const elapsedMinutes = Math.round(elapsedSeconds / 60);
  if (elapsedMinutes < 60) {
    return `${elapsedMinutes} ${elapsedMinutes === 1 ? "minute" : "minutes"} ago`;
  }
  const elapsedHours = Math.round(elapsedMinutes / 60);
  return `${elapsedHours} ${elapsedHours === 1 ? "hour" : "hours"} ago`;
}

function nextMeetingSlug(baseSlug: string, existing: MeetingType[]) {
  const slugs = new Set(existing.map((meetingType) => meetingType.slug));
  let index = 1;
  let candidate = `${baseSlug}-copy`;
  while (slugs.has(candidate)) {
    index += 1;
    candidate = `${baseSlug}-copy-${index}`;
  }
  return candidate;
}

function personaLocationUrl(persona: Persona) {
  return persona.locationPreference.kind === "custom_url" ? persona.locationPreference.url : "";
}

function personaLocationInstructions(persona: Persona) {
  return persona.locationPreference.kind === "phone" || persona.locationPreference.kind === "in_person"
    ? persona.locationPreference.instructions
    : "";
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(new Date(value));
}

/** The day an instant falls on for the viewer, so a 5pm slot is not filed under tomorrow's UTC date. */
function dateKey(value: string) {
  return localDateInputValue(new Date(value));
}

/** The viewer's zone by name ("Pacific Time"), which is the zone every slot time is shown in. */
const viewerTimeZoneLabel = (() => {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "longGeneric" }).formatToParts(new Date());
    return parts.find((part) => part.type === "timeZoneName")?.value ?? zone;
  } catch {
    return zone;
  }
})();



function absoluteInviteUrl(value: string) {
  return value.startsWith("http") ? value : `${appOrigin}${value}`;
}

function defaultInvitationExpirationLocal() {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}

/** Today, in the viewer's own zone. UTC arithmetic put this a day out west of Greenwich. */
function todayDateKey() {
  return localDateInputValue(new Date());
}

/** Tomorrow: the default date a booking form opens on, not "today". */
function initialDateKey() {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return localDateInputValue(date);
}

function shiftMonth(value: string, offset: number) {
  const date = new Date(`${value}-01T00:00:00.000Z`);
  date.setUTCMonth(date.getUTCMonth() + offset);
  return date.toISOString().slice(0, 7);
}

function readManagementRoute(): ManagementRoute {
  const hash = globalThis.location.hash.startsWith("#") ? globalThis.location.hash.slice(1) : "";
  const routeText = hash || `${globalThis.location.pathname}${globalThis.location.search}`;
  const [path, query = ""] = routeText.split("?");
  if (path !== "/manage/cancel" && path !== "/manage/reschedule" && path !== "/manage/approve") {
    return null;
  }
  const params = new globalThis.URLSearchParams(query);
  const token = params.get("token") ?? "";
  if (path === "/manage/approve") {
    // The link only says which button was pressed; the answer is sent from the page.
    return { kind: "approve", token, preselect: params.get("decision") === "decline" ? "decline" : "approve" };
  }
  return { kind: path.endsWith("/cancel") ? "cancel" : "reschedule", token };
}

interface ConnectWizardRoute {
  connectionId?: string;
  failure?: string;
}

/** `/calendars/connect` is where the OAuth callback returns the browser. */
function readConnectWizardRoute(): ConnectWizardRoute | null {
  const hash = globalThis.location.hash.startsWith("#") ? globalThis.location.hash.slice(1) : "";
  const routeText = hash || `${globalThis.location.pathname}${globalThis.location.search}`;
  const [path, query = ""] = routeText.split("?");
  if (path !== "/calendars/connect") {
    return null;
  }
  const params = new globalThis.URLSearchParams(query);
  const connectionId = params.get("connection") ?? undefined;
  const failure = params.get("status") === "failed" ? (params.get("reason") ?? "unknown") : undefined;
  return {
    ...(connectionId ? { connectionId } : {}),
    ...(failure ? { failure } : {})
  };
}

function readInviteClaimRoute(): InviteClaimRoute {
  const hash = globalThis.location.hash.startsWith("#") ? globalThis.location.hash.slice(1) : "";
  const routeText = hash || `${globalThis.location.pathname}${globalThis.location.search}`;
  const [path, query = ""] = routeText.split("?");
  if (path !== "/invite/claim") {
    return null;
  }
  return { token: new globalThis.URLSearchParams(query).get("token") ?? "" };
}

/**
 * Path segments the workspace owns. A public booking URL is `/host/persona`, so any
 * internal two-segment route would otherwise be mistaken for one and render the
 * public booking page instead. Add here when introducing a new internal route.
 */
const RESERVED_ROOT_SEGMENTS = new Set(["manage", "invite", "v1", "s", "calendars", "personas", "bookings", "availability", "settings", "operations", "users"]);

function readPublicBookingRoute(): PublicBookingRoute {
  const hash = globalThis.location.hash.startsWith("#") ? globalThis.location.hash.slice(1) : "";
  const routeText = hash || globalThis.location.pathname;
  const [path = ""] = routeText.split("?");
  const [hostSlug, personaSlug, meetingSlug] = path.split("/").filter(Boolean);
  if (!hostSlug || !personaSlug || RESERVED_ROOT_SEGMENTS.has(hostSlug)) {
    return null;
  }
  return {
    hostSlug,
    personaSlug,
    ...(meetingSlug ? { meetingSlug } : {})
  };
}

/** Formatted in UTC so the label never disagrees with the date key it came from. */
function formatFullDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { timeZone: "UTC", weekday: "long", month: "long", day: "numeric" }).format(
    new Date(`${value}T12:00:00.000Z`)
  );
}

function slugFromEmail(email: string) {
  const localPart = email.split("@")[0] || "host";
  return localPart
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 62) || "host";
}

function strongSignupPassword(value: string) {
  return value.length >= 10 && /[a-z]/.test(value) && /[A-Z]/.test(value) && /[0-9]/.test(value);
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Root />
  </StrictMode>
);
